/* eslint-disable */
import React, { useState, useRef, useCallback, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

// ── SUPABASE CLIENT ──────────────────────────────────────────
const SUPABASE_URL = "https://hzrcogidatmunzizlwkv.supabase.co";
const SUPABASE_ANON_KEY = "sb_publishable__lOjZm9WZnmCph3kP7dzpA_jDDMkaJp";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── SUPABASE HELPERS ─────────────────────────────────────────
const db = {
  // Dispensaries
  getDispensaries: () => supabase.from("dispensaries").select("*").eq("active", true).order("name"),
  saveDispensary: (d) => supabase.from("dispensaries").upsert(d).select().single(),
  deleteDispensary: (id) => supabase.from("dispensaries").delete().eq("id", id),

  // Products
  getProducts: (dispensaryId) => {
    let q = supabase.from("products").select("*").order("name");
    if (dispensaryId) q = q.eq("dispensary_id", dispensaryId);
    return q;
  },
  saveProduct: (p) => supabase.from("products").upsert(p).select().single(),
  deleteProduct: (id) => supabase.from("products").delete().eq("id", id),
  bulkInsertProducts: (prods) => supabase.from("products").insert(prods).select(),

  // Consumer profiles
  getProfileByPhone: async (phone) => {
    const digits = phone.replace(/\D/g, "");
    const norm = digits.length === 10 ? "+1" + digits : "+" + digits;
    const { data, error } = await supabase.from("consumer_profiles").select("*").eq("phone", norm).single();
    if (error && error.code !== "PGRST116") throw error;
    return data || null;
  },
  saveProfile: async (profile) => {
    if (profile.phone) {
      const digits = profile.phone.replace(/\D/g, "");
      profile.phone = digits.length === 10 ? "+1" + digits : "+" + digits;
    }
    const { data, error } = await supabase.from("consumer_profiles").upsert(profile, { onConflict: "phone" }).select().single();
    if (error) throw error;
    return data;
  },
  getAllProfiles: (dispensaryId) => {
    let q = supabase.from("consumer_profiles").select("*").order("created_at", { ascending: false });
    if (dispensaryId) q = q.eq("home_dispensary_id", dispensaryId);
    return q;
  },
  deleteProfile: (id) => supabase.from("consumer_profiles").delete().eq("id", id),

  // Batches
  getBatches: (dispensaryId) => supabase.from("batches").select("*").eq("dispensary_id", dispensaryId).order("created_at", { ascending: false }),
  saveBatch: (b) => supabase.from("batches").upsert(b).select().single(),
  deleteBatch: (id) => supabase.from("batches").delete().eq("id", id),

  // Stock overrides
  getStockOverrides: async (dispensaryId) => {
    const { data, error } = await supabase.from("stock_overrides").select("*").eq("dispensary_id", dispensaryId);
    if (error) throw error;
    return Object.fromEntries((data || []).map(r => [r.product_id, r.in_stock]));
  },
  setStockOverride: (dispensaryId, productId, inStock) => supabase.from("stock_overrides").upsert(
    { dispensary_id: dispensaryId, product_id: productId, in_stock: inStock },
    { onConflict: "dispensary_id,product_id" }
  ),

  // LP partners
  getLPs: () => supabase.from("lp_partners").select("*").order("name"),
  saveLP: (lp) => supabase.from("lp_partners").upsert(lp).select().single(),
  deleteLP: (id) => supabase.from("lp_partners").delete().eq("id", id),

  // Leads
  getLeads: () => supabase.from("dispensary_leads").select("*").order("created_at", { ascending: false }),
  saveLead: (lead) => supabase.from("dispensary_leads").upsert(lead).select().single(),
  deleteLead: (id) => supabase.from("dispensary_leads").delete().eq("id", id),

  // Staff
  getStaff: (dispensaryId) => supabase.from("staff").select("*").eq("dispensary_id", dispensaryId).eq("active", true),
  saveStaff: (s) => supabase.from("staff").upsert(s).select().single(),

  // Events
  logEvent: async (type, payload = {}, role = null) => {
    try { await supabase.from("events").insert({ event_type: type, role, payload }); } catch (_e) {}
  },
  getEvents: (limit = 200) => supabase.from("events").select("*").order("created_at", { ascending: false }).limit(limit),

  // COA log (stored in batches with coa_url)
  getCOALog: () => supabase.from("batches").select("*").not("coa_url", "is", null).order("created_at", { ascending: false }),
};


// ============================================================
// GLOBAL STYLES
// ============================================================
const STYLES = "\n  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,600;1,9..144,300&display=swap');\n  * { box-sizing: border-box; margin: 0; padding: 0; }\n  ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }\n  .fade { animation: fadeIn 0.3s ease forwards; }\n  @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }\n  .card { background:#fff; border:1px solid #e8e4de; border-radius:16px; transition:all 0.2s; }\n  .card-click { cursor:pointer; } .card-click:hover { border-color:#c9c3bb; box-shadow:0 4px 16px rgba(0,0,0,0.06); }\n  .chip { display:inline-flex; align-items:center; gap:5px; padding:4px 10px; border-radius:20px; font-size:11px; font-family:'DM Mono',monospace; cursor:pointer; transition:all 0.15s; }\n  .chip:hover { filter:brightness(0.9); }\n  .nav-btn { display:flex; align-items:center; gap:6px; padding:8px 14px; border-radius:8px; font-size:12px; font-family:'DM Mono',monospace; cursor:pointer; border:none; background:transparent; color:#4b5563; transition:all 0.15s; white-space:nowrap; }\n  .nav-btn:hover { background:#f0eee9; color:#1a2e1a; } .nav-btn.active { background:#1a2e1a; color:#f0f4f0; }\n  .tab { padding:8px 16px; border-radius:8px; font-size:12px; font-family:'DM Mono',monospace; cursor:pointer; border:1px solid transparent; transition:all 0.2s; }\n  .tab.on { background:#1a2e1a; color:#f0f4f0; border-color:#1a2e1a; }\n  .tab.off { background:#fff; color:#6b7280; border-color:#e5e7eb; }\n  .tab.off:hover { border-color:#9ca3af; color:#374151; }\n  .sel { background:#fff; border:1px solid #e5e7eb; border-radius:10px; color:#374151; padding:8px 12px; font-size:12px; font-family:'DM Mono',monospace; cursor:pointer; outline:none; }\n  .sel:focus { border-color:#1a2e1a; }\n  .inp { background:#fff; border:1px solid #e5e7eb; border-radius:12px; color:#1a2e1a; padding:10px 16px; font-size:13px; font-family:'DM Mono',monospace; outline:none; width:100%; transition:border 0.2s; }\n  .inp:focus { border-color:#1a2e1a; }\n  .pill { display:inline-flex; align-items:center; padding:3px 8px; border-radius:6px; font-size:10px; font-family:'DM Mono',monospace; background:#f3f4f6; color:#374151; border:1px solid #e5e7eb; }\n  .lab-badge { display:inline-flex; align-items:center; gap:4px; padding:3px 8px; border-radius:6px; font-size:10px; font-family:'DM Mono',monospace; }\n  .lab-verified { background:#d1fae5; color:#065f46; border:1px solid #6ee7b7; }\n  .lab-estimated { background:#fef3c7; color:#92400e; border:1px solid #fcd34d; }\n  .lab-unavailable { background:#f3f4f6; color:#6b7280; border:1px solid #e5e7eb; }\n  .overlay { position:fixed; inset:0; background:rgba(0,0,0,0.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; backdrop-filter:blur(4px); }\n  .btn-primary { background:#1a2e1a; color:#f0f4f0; border:none; border-radius:10px; cursor:pointer; font-family:'DM Mono',monospace; font-size:12px; padding:10px 18px; transition:all 0.2s; }\n  .btn-primary:hover { background:#2d4a2d; }\n  .btn-ghost { background:none; border:1px solid #e8e4de; border-radius:10px; cursor:pointer; font-family:'DM Mono',monospace; font-size:12px; padding:10px 18px; color:#6b7280; transition:all 0.2s; }\n  .btn-ghost:hover { border-color:#9ca3af; color:#374151; }\n  .badge { display:inline-flex; align-items:center; padding:3px 8px; border-radius:6px; font-family:'DM Mono',monospace; font-size:10px; }\n  .badge.verified { background:#d1fae5; color:#065f46; border:1px solid #6ee7b7; }\n  .badge.lp { background:#dbeafe; color:#1e40af; border:1px solid #93c5fd; }\n  .badge.estimated { background:#fef3c7; color:#92400e; border:1px solid #fcd34d; }\n  @media (max-width: 640px) {\n    .hide-sm { display:none !important; }\n    .nav-btn { padding:6px 8px; font-size:10px; }\n    .card { border-radius:12px; }\n    h1 { font-size:22px !important; }\n    .mobile-bottom-nav { display:flex !important; }\n    main { padding-bottom: 64px; }\n  }\n  @media (max-width: 420px) {\n    .nav-btn { padding:5px 6px; font-size:9px; }\n  }\n  ::-webkit-scrollbar { width:0; height:0; }\n  .modal { background:#fff; border-radius:20px; padding:32px; max-width:580px; width:100%; max-height:88vh; overflow-y:auto; border:1px solid #e8e4de; }\n  .bar-track { background:#f3f4f6; border-radius:4px; height:6px; overflow:hidden; }\n  .bar-fill { height:100%; border-radius:4px; transition:width 0.6s ease; }\n  .stat { background:#f7f5f0; border-radius:12px; padding:16px; border:1px solid #e8e4de; }\n  .info-box { background:#f0f9f0; border:1px solid #bbf7d0; border-radius:10px; padding:12px 14px; display:flex; gap:8px; align-items:flex-start; }\n  .warn-box { background:#fffbeb; border:1px solid #fde68a; border-radius:10px; padding:12px 14px; display:flex; gap:8px; align-items:flex-start; }\n  .qr-box { background:#1a2e1a; border-radius:16px; padding:20px; display:flex; flex-direction:column; align-items:center; gap:12px; }\n  .btn-primary { padding:11px 22px; background:#1a2e1a; color:#f0f4f0; border:none; border-radius:10px; font-size:13px; font-weight:500; cursor:pointer; font-family:'DM Mono',monospace; transition:opacity 0.2s; }\n  .btn-primary:hover { opacity:0.88; }\n  .btn-ghost { padding:10px 18px; background:transparent; color:#1a2e1a; border:1px solid #e5e7eb; border-radius:10px; font-size:13px; cursor:pointer; font-family:'DM Mono',monospace; transition:all 0.2s; }\n  .btn-ghost:hover { border-color:#1a2e1a; }\n  .progress-ring { transition:stroke-dashoffset 0.5s ease; }\n  .quiz-opt { width:100%; text-align:left; padding:12px 16px; background:#f7f5f0; border:2px solid #e8e4de; border-radius:10px; font-size:13px; font-family:'DM Mono',monospace; cursor:pointer; transition:all 0.2s; margin-bottom:8px; }\n  .quiz-opt:hover { border-color:#1a2e1a; background:#f0eee9; }\n  .quiz-opt.correct { border-color:#22c55e; background:#f0fdf4; color:#065f46; }\n  .quiz-opt.wrong { border-color:#ef4444; background:#fef2f2; color:#991b1b; }\n  .algo-card { background:#f7f5f0; border:1px solid #e8e4de; border-radius:12px; padding:16px; }\n  .similarity-bar { height:8px; border-radius:4px; background:linear-gradient(90deg,#22c55e,#3b82f6); transition:width 0.8s ease; }\n  @media(max-width:640px){.hide-sm{display:none!important}}\n  /* ── Utility classes ── */\n  .lbl { font-size:9px; color:#9ca3af; font-family:'DM Mono',monospace; text-transform:uppercase; letter-spacing:0.07em; }\n  .lbl-sm { font-size:8px; color:#9ca3af; font-family:'DM Mono',monospace; text-transform:uppercase; letter-spacing:0.06em; }\n  .mono { font-family:'DM Mono',monospace; }\n  .serif { font-family:'Fraunces',serif; }\n  .section-title { font-family:'Fraunces',serif; font-size:15px; font-weight:400; color:#1a2e1a; margin-bottom:14px; }\n  .badge { display:inline-flex; align-items:center; gap:4px; font-size:9px; font-family:'DM Mono',monospace; padding:2px 8px; border-radius:6px; border:1px solid; font-weight:500; }\n  .badge-green { background:#f0fdf4; color:#15803d; border-color:#86efac; }\n  .badge-amber { background:#fffbeb; color:#92400e; border-color:#fcd34d; }\n  .badge-blue  { background:#eff6ff; color:#1d4ed8; border-color:#93c5fd; }\n  .badge-gray  { background:#f9fafb; color:#6b7280; border-color:#d1d5db; }\n  .badge-purple{ background:#faf5ff; color:#6d28d9; border-color:#c4b5fd; }\n  .page-wrap { max-width:860px; margin:0 auto; padding:28px 20px; }\n  .page-title { font-family:'Fraunces',serif; font-size:26px; font-weight:300; color:#1a2e1a; margin-bottom:4px; }\n  .page-sub { font-size:12px; color:#9ca3af; font-family:'DM Mono',monospace; margin-bottom:24px; }\n  .empty-state { text-align:center; padding:56px 24px; }\n  .empty-state .icon { font-size:32px; margin-bottom:12px; }\n  .empty-state .title { font-family:'Fraunces',serif; font-size:18px; color:#1a2e1a; margin-bottom:8px; }\n  .empty-state .sub { font-size:11px; color:#9ca3af; font-family:'DM Mono',monospace; margin-bottom:20px; }\n";

// ============================================================
// IUPAC COMPOUND DATABASE
// ============================================================
const TERPENES = {
  "β-Myrcene": {
    iupac: "(E)-7-methyl-3-methyleneocta-1,6-diene",
    commonName: "Myrcene", type:"Monoterpene", formula:"C₁₀H₁₆",
    mw:"136.23 g/mol", bp:"167°C", color:"#4a7c59",
    aroma:["Earthy","Musky","Herbal"],
    plants:["Hops","Mango","Lemongrass","Thyme"],
    summary:"β-Myrcene is the most abundant terpene in cannabis. Research describes earthy, musky characteristics. High concentrations also found in hops (Humulus lupulus) used in brewing.",
    lit:"Russo, E.B. (2011). Taming THC: potential cannabis synergy. British Journal of Pharmacology. 163(7):1344-64.",
  plain:"The most common cannabis terpene. Smells earthy and musky — like ripe mango or hops. Found in high concentrations in many indica-dominant cultivars. Researched for sedating and relaxing properties — individual responses vary.",
  },
  "β-Caryophyllene": {
    iupac:"(1R,4E,9S)-4,11,11-trimethyl-8-methylenebicyclo[7.2.0]undec-4-ene",
    commonName:"Caryophyllene", type:"Sesquiterpene", formula:"C₁₅H₂₄",
    mw:"204.35 g/mol", bp:"130°C", color:"#8b6914",
    aroma:["Spicy","Peppery","Woody"],
    plants:["Black Pepper","Cloves","Cinnamon","Rosemary"],
    summary:"β-Caryophyllene is the only known terpene that directly interacts with the endocannabinoid system as a selective CB2 receptor agonist. Classified as a dietary cannabinoid.",
    lit:"Gertsch, J. et al. (2008). Beta-caryophyllene is a dietary cannabinoid. PNAS. 105(26):9099-104.",
  plain:"Smells like black pepper and cloves — spicy and woody. The only terpene known to interact directly with CB2 receptors in the endocannabinoid system. Researched for potential stress-relieving and anti-inflammatory properties — individual responses vary.",
  },
  "(+)-Limonene": {
    iupac:"(4R)-1-methyl-4-prop-1-en-2-ylcyclohex-1-ene",
    commonName:"Limonene", type:"Monoterpene", formula:"C₁₀H₁₆",
    mw:"136.23 g/mol", bp:"176°C", color:"#c9860a",
    aroma:["Citrus","Lemon","Orange"],
    plants:["Lemon","Orange","Grapefruit","Juniper"],
    summary:"(+)-Limonene is the primary component of citrus fruit rind oils and is widely distributed in nature. Research has examined its bioavailability and interaction with biological systems.",
    lit:"Aggarwal, S.K. (2016). Cannabinergic pain medicine. Current Pain and Headache Reports. 20(3):13.",
  plain:"Smells like fresh lemons and citrus fruit. Found naturally in citrus rinds. Researched for mood-elevating and uplifting properties — one of the most studied terpenes in cannabis. Individual responses vary.",
  },
  "α-Pinene": {
    iupac:"(1S,5S)-2,6,6-trimethylbicyclo[3.1.1]hept-2-ene",
    commonName:"Pinene", type:"Monoterpene", formula:"C₁₀H₁₆",
    mw:"136.23 g/mol", bp:"155°C", color:"#2d6a4f",
    aroma:["Pine","Fresh","Woody"],
    plants:["Pine Trees","Rosemary","Basil","Dill"],
    summary:"α-Pinene is one of nature's most abundant terpenes, present in coniferous trees. Scientific literature notes bronchodilatory properties at low concentrations and acetylcholinesterase inhibition.",
    lit:"Zuzarte, M. & Salgueiro, L. (2015). Essential oils chemistry. Springer. ISBN 978-3-319-16481-7.",
  plain:"Smells exactly like a pine forest — sharp, fresh, woody. The most abundant terpene in nature, also found in rosemary and basil. Researched for effects on alertness and memory retention — individual responses vary.",
  },
  "Terpinolene": {
    iupac:"1-methyl-4-(propan-2-ylidene)cyclohex-1-ene",
    commonName:"Terpinolene", type:"Monoterpene", formula:"C₁₀H₁₆",
    mw:"136.23 g/mol", bp:"186°C", color:"#6b7fa3",
    aroma:["Floral","Herbal","Fresh"],
    plants:["Nutmeg","Tea Tree","Conifers","Apples"],
    summary:"Terpinolene presents a complex multifaceted aromatic profile. Found across many plant species, it has been studied for antioxidant and sedative properties in scientific literature.",
    lit:"Dhifi, W. et al. (2016). Essential oils chemical characterization. Medicines. 3(4):25.",
  plain:"A complex fresh, floral, herbal smell — think nutmeg or fresh herbs. Less common than Myrcene or Caryophyllene, often found in sativa-leaning cultivars. Researched for uplifting and mildly stimulating properties — individual responses vary.",
  },
  "Linalool": {
    iupac:"(3S)-3,7-dimethylocta-1,6-dien-3-ol",
    commonName:"Linalool", type:"Monoterpenoid", formula:"C₁₀H₁₈O",
    mw:"154.25 g/mol", bp:"198°C", color:"#9b72cf",
    aroma:["Floral","Lavender","Sweet"],
    plants:["Lavender","Coriander","Bergamot","Rosewood"],
    summary:"Linalool is a naturally occurring terpene alcohol found in over 200 plant species. Primary component of lavender essential oil. Widely researched for its interaction with GABA receptor systems.",
    lit:"Aprotosoaie, A.C. et al. (2017). Linalool in aromatic plants. Phytochemistry Reviews. 16:761-799.",
  plain:"Smells like lavender — floral, soft, and sweet. The same terpene responsible for lavender's aroma. Researched for calming and stress-relieving properties in aromatherapy literature. Common in indica-dominant and CBD-rich cultivars — individual responses vary.",
  },
  "β-Ocimene": {
    iupac:"(E)-3,7-dimethylocta-1,3,6-triene",
    commonName:"Ocimene", type:"Monoterpene", formula:"C₁₀H₁₆",
    mw:"136.23 g/mol", bp:"100°C", color:"#7b9e6b",
    aroma:["Sweet","Woody","Herbal"],
    plants:["Mint","Parsley","Orchids","Kumquats"],
    summary:"β-Ocimene is found across a wide range of plant species contributing sweet, herbal and woody notes. Research has examined its role in plant defense mechanisms and insect deterrence.",
    lit:"Joulain, D. & König, W.A. (1998). Atlas of Spectral Data of Sesquiterpene Hydrocarbons. E.B.-Verlag.",
  plain:"Sweet, woody, and slightly herbal — found in mint, parsley, and orchids. Less common but adds aromatic complexity. Often present in sativa-leaning profiles. Researched for mildly uplifting properties — individual responses vary.",
  },
  "α-Humulene": {
    iupac:"(1E,4E,8E)-2,6,6,9-tetramethylcycloundeca-1,4,8-triene",
    commonName:"Humulene", type:"Sesquiterpene", formula:"C₁₅H₂₄",
    mw:"204.35 g/mol", bp:"106°C", color:"#7a5c3a",
    aroma:["Earthy","Woody","Hoppy"],
    plants:["Hops","Coriander","Cloves","Basil"],
    summary:"α-Humulene is a primary constituent of Humulus lupulus (hops) responsible for beer's characteristic aroma. Present alongside β-Caryophyllene in many cannabis varieties.",
    lit:"Fernandes, E.S. et al. (2007). Anti-inflammatory effects of compounds from Cordia verbenacea. European Journal of Pharmacology. 569(3):228-236.",
  plain:"Earthy, hoppy, and woody — like the smell of a craft beer. Found in the same hop plant used in brewing. Commonly appears alongside β-Caryophyllene in earthy, indica-leaning profiles. Researched for appetite-related and anti-inflammatory properties — individual responses vary.",
  },
  "α-Bisabolol": {
    iupac:"(S)-α,4-dimethyl-α-(4-methyl-3-pentenyl)benzenemethanol",
    commonName:"Bisabolol", type:"Sesquiterpenol", formula:"C₁₅H₂₆O",
    mw:"222.37 g/mol", bp:"153°C", color:"#c4a882",
    aroma:["Floral","Sweet","Chamomile"],
    plants:["German Chamomile","Candeia Tree","Myoporum"],
    summary:"α-Bisabolol is naturally found in German chamomile essential oil. Widely used in cosmetic formulations and investigated for skin-soothing and anti-inflammatory properties.",
    lit:"Kaur, C.D. & Saraf, S. (2010). Skin hydration properties of herbal cosmetics. International Journal of Research in Cosmetic Science. 1(1):1-6.",
    plain:"Delicate floral and chamomile scent — soft and sweet. The same terpene used in German chamomile skincare. Less common in cannabis but notable when present. Researched for calming and skin-soothing properties. Often found in CBD-rich and balanced cultivars — individual responses vary.",
    rare: false,
  },

  // ── EXPANDED TERPENE LIBRARY — COA-REPORTABLE COMPOUNDS ──────
  "Fenchol": {
    iupac:"(1S,2R,4S)-1,3,3-trimethylbicyclo[2.2.1]heptan-2-ol",
    commonName:"Fenchol", type:"Monoterpenoid", formula:"C₁₀H₁₈O",
    mw:"154.25 g/mol", bp:"193°C", color:"#5b8c5a",
    aroma:["Pine","Basil","Camphor","Fresh"],
    plants:["Basil","Fennel","Aster tataricus","Conifers"],
    summary:"Fenchol (also fenchyl alcohol) is a bicyclic monoterpenoid found in basil essential oil and many coniferous resins. It is an isomer of borneol. It occurs naturally alongside α-Pinene in pine-leaning cannabis profiles and has been studied for antibacterial properties. Frequently detected in Kush-lineage cultivars at 0.01–0.1% concentrations.",
    lit:"Swamy, M.K. et al. (2016). Antimicrobial properties of plant essential oils. Evidence-Based Complementary and Alternative Medicine. 2016:3012462.",
    plain:"Smells like pine and fresh basil — a bright, herby, resinous scent. You'd recognize it in a piney strain that also has a slight spicy-green quality. A marker for certain Kush genetics. Usually present in small amounts.",
    rare: true,
  },
  "Camphene": {
    iupac:"(1R,4R)-2,2-dimethyl-3-methylenebicyclo[2.2.1]heptane",
    commonName:"Camphene", type:"Monoterpene", formula:"C₁₀H₁₆",
    mw:"136.23 g/mol", bp:"159°C", color:"#6b8f71",
    aroma:["Earthy","Fir","Damp Wood","Musky"],
    plants:["Camphor tree","Fir","Valerian","Citronella"],
    summary:"Camphene is a bicyclic monoterpene with a characteristic fir-needle and damp-wood aroma. It is a minor constituent in many cannabis varieties and can increase as flower ages due to monoterpene oxidation. Biosynthetically derived from geranyl pyrophosphate. Research has examined cardiovascular and antioxidant properties. Camphene content can serve as a freshness indicator — elevated levels may suggest terpene profile drift from oxidation.",
    lit:"Vallianou, I. et al. (2011). Camphene, a plant-derived monoterpene, reduces plasma cholesterol. PLOS ONE. 6(1):e20516.",
    plain:"An earthy, woody, slightly damp smell — like walking through a fir forest after rain. In small amounts it adds depth and earthiness to a profile. Worth noting: higher camphene can sometimes indicate flower that's been sitting for a while, as terpenes shift over time.",
    rare: true,
  },
  "δ-3-Carene": {
    iupac:"(1R,6S)-3,7,7-trimethylbicyclo[4.1.0]hept-3-ene",
    commonName:"3-Carene", type:"Monoterpene", formula:"C₁₀H₁₆",
    mw:"136.23 g/mol", bp:"168°C", color:"#c4b07a",
    aroma:["Sweet","Cedar","Earthy","Pungent"],
    plants:["Cedar","Pine","Turpentine","Basil","Bell peppers"],
    summary:"δ-3-Carene is a bicyclic monoterpene found in many conifers and cannabis cultivars. It contributes a sweet, cedar-like aroma. Notably, 3-Carene is a known sensitizer and its inhalation in high concentrations has been associated with respiratory irritation. It cross-reacts with skin sensitizers in some individuals. This is worth flagging for people with known terpene sensitivities.",
    lit:"Franks, S.J. (2003). Terpene sensitization and airway inflammation. Occupational Medicine. 53(4):279-280.",
    plain:"Sweet and cedar-like, a bit pungent. Found in strains with a sharp, almost pencil-shaving or turpentine edge to their smell. Worth knowing: at high concentrations, 3-Carene can be irritating — if you find piney strains harsh on your lungs, this compound may be why.",
    rare: true,
    sensitizer: true,
  },
  "Borneol": {
    iupac:"(1R,2S,4R)-1,7,7-trimethylbicyclo[2.2.1]heptan-2-ol",
    commonName:"Borneol", type:"Monoterpenoid", formula:"C₁₀H₁₈O",
    mw:"154.25 g/mol", bp:"213°C", color:"#8fa98b",
    aroma:["Camphor","Mint","Woody","Slightly medicinal"],
    plants:["Borneo camphor","Rosemary","Ginger","Valerian"],
    summary:"Borneol is a bicyclic monoterpenoid with a long history of use in traditional Chinese medicine. It occurs in both its (+)- and (−)-enantiomers in nature. In cannabis it appears at low concentrations (<0.1%), contributing a camphor-mint nuance to complex profiles. Research has examined its potential to enhance blood-brain barrier permeability, suggesting synergistic effects with other bioactive compounds. It is a natural insect repellent.",
    lit:"Liu, J. et al. (2013). Borneol, a novel agent that improves central nervous system drug delivery. Reviews in the Neurosciences. 24(3):253-268.",
    plain:"A cool, camphor-minty, slightly medicinal smell. Used for centuries in Asian medicine. In cannabis, it's usually present in very small amounts and adds a subtle cooling quality to earthy profiles. An interesting marker of complexity in certain cultivars.",
    rare: true,
  },
  "Geraniol": {
    iupac:"(E)-3,7-dimethylocta-2,6-dien-1-ol",
    commonName:"Geraniol", type:"Monoterpenoid", formula:"C₁₀H₁₈O",
    mw:"154.25 g/mol", bp:"229°C", color:"#e8a87c",
    aroma:["Rose","Floral","Geranium","Slightly fruity"],
    plants:["Geranium","Rose","Citronella","Palmarosa","Lemongrass"],
    summary:"Geraniol is an acyclic monoterpenoid alcohol with an intense rose-like floral fragrance. It is the primary constituent of rose oil and citronella. In cannabis it occurs at trace levels but has notable significance: it degrades readily at vaporization temperatures (bp 229°C — survives low-temp vaping better than at combustion), and may be metabolized to geranyl pyrophosphate, a building block for other terpenes. Research has examined neuroprotective and anti-tumoral properties. Also a known insect repellent.",
    lit:"Lv, J. et al. (2019). Neuroprotective effects of geraniol. Experimental and Therapeutic Medicine. 17(4):2837-2842.",
    plain:"Smells exactly like rose petals and fresh geraniums — delicate, floral, and slightly fruity. If you notice a faint rose quality in a strain, this is likely the reason. Quite rare in cannabis — a special find when it appears in notable concentrations.",
    rare: true,
  },
  "Nerolidol": {
    iupac:"(E)-3,7,11-trimethyldodeca-1,6,10-trien-3-ol",
    commonName:"Nerolidol", type:"Sesquiterpenol", formula:"C₁₅H₂₆O",
    mw:"222.37 g/mol", bp:"122°C", color:"#a3785e",
    aroma:["Woody","Floral","Fresh bark","Citrus peel"],
    plants:["Neroli oil","Ginger","Jasmine","Tea tree","Lemongrass"],
    summary:"Nerolidol (also called peruviol) is a naturally occurring sesquiterpenol with two geometric isomers (cis and trans). Its aroma evokes fresh tree bark, woody florals, and a subtle citrus-peel finish. It is found in neroli, ginger, and jasmine essential oils. In cannabis it occurs at 0.01–0.2% and is frequently found alongside Myrcene and Linalool in complex indica-leaning profiles. Research has examined antiparasitic, antifungal, and sedative properties — notably its potential to enhance transdermal drug permeability.",
    lit:"Bicas, J.L. et al. (2011). Biotechnological production of biopigments and other compounds from fungi. Chemical Engineering Research and Design. 89(6):1–8.",
    plain:"A woody, floral, fresh-bark smell — subtle but distinctive. Think of the scent of jasmine meets cut wood. Sometimes described as 'green' or complex. Found in multi-layered indica profiles. Studied for its potential synergy with other terpenes and cannabinoids.",
    rare: true,
  },
  "Guaiol": {
    iupac:"(2S,5R)-2-(2-hydroxypropan-2-yl)-5-methylcyclohexan-1-ol",
    commonName:"Guaiol", type:"Sesquiterpenol", formula:"C₁₅H₂₆O",
    mw:"222.37 g/mol", bp:"288°C", color:"#7a8f6e",
    aroma:["Pine","Rose","Woody","Slightly floral"],
    plants:["Guaiacum (Lignum Vitae)","Cypress","Pine"],
    summary:"Guaiol is a sesquiterpenol naturally derived from the wood of the Guaiacum plant and several pine species. It is relatively unusual in that it is a sesquiterpene alcohol with a high boiling point, making it thermally stable across a wider range of consumption temperatures than many monoterpenes. In cannabis it is detected in select cultivars at 0.01–0.15% concentrations. Anti-inflammatory and antiparasitic properties have been documented in the literature.",
    lit:"Rojas, J. et al. (2012). Antimicrobial activity of selected plant essential oils. Biological Research. 45(4):399-405.",
    plain:"A piney, slightly rose-edged, woody scent. Distinctly different from α-Pinene — less sharp, more rounded and slightly floral. Found in certain pine-forward cultivars. One of the more stable terpenes — survives lower-temperature vaporization well.",
    rare: true,
  },
  "Valencene": {
    iupac:"(+)-(4R,4aS,8aR)-4,8a-dimethyl-6-(propan-2-ylidene)-decahydronaphthalene",
    commonName:"Valencene", type:"Sesquiterpene", formula:"C₁₅H₂₄",
    mw:"204.35 g/mol", bp:"123°C", color:"#d97706",
    aroma:["Fresh orange","Grapefruit","Citrus peel","Woody"],
    plants:["Valencia oranges","Nootka tree","Grapefruit"],
    summary:"Valencene is a sesquiterpene hydrocarbon responsible for the characteristic aroma of Valencia oranges and grapefruit. It is named after its primary botanical source. In cannabis it occurs in select citrus-dominant cultivars, typically alongside Limonene and Ocimene, adding a distinct fresh-squeezed orange or tangerine quality that differs from Limonene's more lemon-like character. Used extensively in the fragrance and food industry. Research has examined insect-repellent and antifungal properties.",
    lit:"Ortiz de Elguea-Culebras, G. et al. (2017). Analysis of valencene and other sesquiterpene alcohols in citrus essential oils. Journal of Essential Oil Research. 29(4):298-304.",
    plain:"Fresh, sweet, squeezed orange and grapefruit — juicy and bright. Different from regular citrus terpenes: more orange-tropical than lemony. If a strain smells like actual fresh-cut citrus fruit rather than lemon cleaning products, Valencene is often why. Found in Tangie-lineage and citrus-forward strains.",
    rare: true,
  },
  "Sabinene": {
    iupac:"4-methylene-1-(1-methylethyl)bicyclo[3.1.0]hexane",
    commonName:"Sabinene", type:"Monoterpene", formula:"C₁₀H₁₆",
    mw:"136.23 g/mol", bp:"163°C", color:"#6b9e7a",
    aroma:["Spicy","Woody","Citrus","Pepper"],
    plants:["Holm oak","Black pepper","Nutmeg","Basil","Spruce"],
    summary:"Sabinene is a naturally occurring bicyclic monoterpene contributing spicy, woody, and citrus notes to complex aromatic profiles. It is a prominent constituent of black pepper and nutmeg essential oils. In cannabis it typically occurs at very low concentrations (<0.05%) but contributes significantly to aroma character when present — it is partly responsible for the 'spicy-warm' quality in certain pepper-forward profiles. Its aroma complements β-Caryophyllene. Research has examined antifungal and hepatoprotective properties.",
    lit:"Nogueira, M.N.M. et al. (2014). Sabinene as a potential anti-inflammatory and analgesic agent. Journal of Ethnopharmacology. 158:329-334.",
    plain:"Spicy, woody, with a slightly citrusy warmth — like freshly cracked black pepper mixed with a hint of orange peel. Usually present in trace amounts but adds noticeable complexity. Works alongside Caryophyllene to create that spicy, warm aromatic signature.",
    rare: true,
  },
  "p-Cymene": {
    iupac:"1-methyl-4-(propan-2-yl)benzene",
    commonName:"para-Cymene", type:"Aromatic monoterpene", formula:"C₁₀H₁₄",
    mw:"134.22 g/mol", bp:"177°C", color:"#9b8ea3",
    aroma:["Citrus","Earthy","Slightly woody","Mild"],
    plants:["Thyme","Cumin","Coriander","Oregano","Tea tree"],
    summary:"p-Cymene is an aromatic monoterpene (a benzene derivative, unlike most cannabis terpenes which are cyclohexane-based). It is found in thyme, cumin, and several culinary herbs. In cannabis it is often detected as a product of terpene oxidation — elevated p-Cymene can indicate terpene degradation or oxidative stress during cultivation, curing, or storage. It has been examined for antimicrobial properties but is pharmacologically less active than most other cannabis terpenes. Useful as a freshness / quality marker.",
    lit:"Marchese, A. et al. (2017). Antimicrobial and antifungal activities of thyme essential oils. Critical Reviews in Microbiology. 43(1):50-70.",
    plain:"Mild citrus-earthy aroma — not particularly dominant on its own, more of a background note. Interesting from a quality perspective: if a product shows elevated p-Cymene relative to other terpenes, it can be an indicator of aging or oxidation in the flower. A subtle freshness signal.",
    rare: true,
  },
  "Caryophyllene Oxide": {
    iupac:"(1R,4R,6R,10S)-4,12,12-trimethyl-9-methylene-5-oxatricyclo[8.2.0.0(4,6)]dodecane",
    commonName:"Caryophyllene Oxide", type:"Sesquiterpene oxide", formula:"C₁₅H₂₄O",
    mw:"220.35 g/mol", bp:"141°C", color:"#8b7355",
    aroma:["Spicy","Woody","Waxy","Dry"],
    plants:["Hop leaves (oxidized)","Melissa","Eucalyptus","Cloves"],
    summary:"Caryophyllene Oxide is the epoxide oxidation product of β-Caryophyllene — formed when β-Caryophyllene is exposed to oxygen. It is the compound responsible for cannabis being detected by drug-sniffing dogs (it is thermally stable and highly persistent). Unlike its precursor β-Caryophyllene, it does not bind CB2 receptors with meaningful affinity. However, it is one of the most thermally stable compounds in the cannabis terpene profile and serves as a quality/aging marker: high Caryophyllene Oxide relative to β-Caryophyllene indicates significant oxidative degradation. COA labs (Aris Global, Keystone) routinely report it separately.",
    lit:"Guo, K. et al. (2016). β-Caryophyllene and its oxide: isolation and biological activity. Phytochem Reviews. 15(5):873-890.",
    plain:"Spicy and dry — like an aged, drier version of black pepper. A byproduct of Caryophyllene oxidizing over time. Notable because it's what drug dogs smell in cannabis. Also a freshness signal: more of this relative to regular Caryophyllene can indicate older stock or storage issues. Labs report it separately on COAs.",
    rare: true,
    degradation_marker: true,
  },
  "α-Terpineol": {
    iupac:"2-(4-methylcyclohex-3-en-1-yl)propan-2-ol",
    commonName:"Terpineol", type:"Monoterpenoid", formula:"C₁₀H₁₈O",
    mw:"154.25 g/mol", bp:"217°C", color:"#b8a9d0",
    aroma:["Lilac","Pine","Clove","Floral"],
    plants:["Lilac","Pine","Cajuput","Petitgrain","Tea tree"],
    summary:"α-Terpineol is a monoterpene alcohol with a pleasant lilac and pine-clove aroma. It is one of the most common naturally occurring terpene alcohols and is frequently used in perfumery. In cannabis it appears at trace concentrations, often detectable in lavender or floral-forward profiles alongside Linalool and Bisabolol. It has been investigated for sedative and antibacterial properties. Its high boiling point (217°C) means it survives to higher temperatures during vaporization than many monoterpenes.",
    lit:"Quintans-Júnior, L.J. et al. (2011). Terpineol and its antimicrobial, anti-inflammatory, and antinociceptive properties. Pharmaceutical Biology. 49(5):525-532.",
    plain:"A delicate lilac-floral-pine scent — soft, clean, and slightly sweet. Similar to Linalool (lavender) but with a more powdery, floral-piney quality. Rare in cannabis and usually present in tiny amounts. When it shows up alongside Linalool and Bisabolol, the overall profile tends to be very floral and gentle.",
    rare: true,
  },
};

const CANNABINOIDS = {
  "Δ9-THC": {
    iupac:"(−)-(6aR,10aR)-6,6,9-trimethyl-3-pentyl-6a,7,8,10a-tetrahydro-6H-benzo[c]chromen-1-ol",
    commonName:"THC", fullName:"Delta-9-Tetrahydrocannabinol",
    type:"Phytocannabinoid", formula:"C₂₁H₃₀O₂", mw:"314.46 g/mol",
    discovery:"1964 — Gaoni & Mechoulam, Weizmann Institute", color:"#22c55e",
    receptors:["CB1 (Ki ~10nM)","CB2 (Ki ~24nM)"],
    summary:"Δ9-THC is the primary psychoactive constituent of Cannabis sativa, first isolated and synthesized in 1964. Exerts effects through partial agonism at CB1 receptors in the central nervous system.",
    lit:"Gaoni, Y. & Mechoulam, R. (1964). Isolation, structure, and partial synthesis of an active constituent of hashish. JACS. 86(8):1646-1647."
  },
  "CBD": {
    iupac:"2-[(1R,6R)-6-isopropenyl-3-methylcyclohex-2-en-1-yl]-5-pentylbenzene-1,3-diol",
    commonName:"CBD", fullName:"Cannabidiol",
    type:"Phytocannabinoid", formula:"C₂₁H₃₀O₂", mw:"314.46 g/mol",
    discovery:"1940 — Roger Adams, University of Illinois", color:"#3b82f6",
    receptors:["TRPV1","5-HT1A","GPR55","CB1 (negative allosteric modulator)"],
    summary:"CBD is a non-intoxicating phytocannabinoid with a complex multi-receptor pharmacology. Does not directly bind CB1 receptors with high affinity. Approved as Epidiolex for epilepsy treatment.",
    lit:"Adams, R. et al. (1940). Structure of cannabidiol. JACS. 62(8):2197-2200."
  },
  "CBG": {
    iupac:"2-[(1E)-3,7-dimethylocta-2,6-dien-1-yl]-5-pentylbenzene-1,3-diol",
    commonName:"CBG", fullName:"Cannabigerol",
    type:"Phytocannabinoid", formula:"C₂₁H₃₂O₂", mw:"316.48 g/mol",
    discovery:"1964 — Gaoni & Mechoulam", color:"#f59e0b",
    receptors:["CB1","CB2","α2-adrenergic","5-HT1A (antagonist)"],
    summary:"CBG is the non-acidic form of CBGA, the biosynthetic precursor to all major cannabinoids. Most cannabis varieties contain low concentrations (<1%) as CBGA converts to other cannabinoids during maturation.",
    lit:"Gaoni, Y. & Mechoulam, R. (1964). Cannabis constituents. JACS. 86(8):1646."
  },
  "CBN": {
    iupac:"6,6,9-trimethyl-3-pentyl-6H-benzo[c]chromen-1-ol",
    commonName:"CBN", fullName:"Cannabinol",
    type:"Phytocannabinoid (oxidative metabolite)", formula:"C₂₁H₂₆O₂", mw:"310.43 g/mol",
    discovery:"1896 — Wood, Spivey & Easterfield", color:"#8b5cf6",
    receptors:["CB1 (Ki ~211nM)","CB2 (Ki ~126nM)","TRPA1"],
    summary:"CBN forms through oxidative degradation of Δ9-THC. Prevalence increases with product age and improper storage. Mildly psychoactive with approximately 10% the CB1 affinity of Δ9-THC.",
    lit:"Wood, T.B. et al. (1896). Charas: the resin of Indian hemp. Chemical News. 73:329."
  },
  "THCA": {
    iupac:"(6aR,10aR)-1-hydroxy-6,6,9-trimethyl-3-pentyl-6a,7,8,10a-tetrahydro-6H-benzo[c]chromene-2-carboxylic acid",
    commonName:"THCA", fullName:"Tetrahydrocannabinolic Acid",
    type:"Phytocannabinoid (acidic precursor)", formula:"C₂₂H₃₀O₄", mw:"358.47 g/mol",
    discovery:"1965 — Mechoulam & Gaoni", color:"#10b981",
    receptors:["Non-psychoactive in raw form — decarboxylation required"],
    summary:"THCA is the predominant form of THC in living cannabis plants. Non-psychoactive until decarboxylation via heat converts it to Δ9-THC. Raw cannabis juice contains THCA without psychoactive effect.",
    lit:"Mechoulam, R. (1970). Marihuana chemistry. Science. 168(3936):1159-1166."
  },
  "THCV": {
    iupac:"(6aR,10aR)-6,6,9-trimethyl-3-propyl-6a,7,8,10a-tetrahydro-6H-benzo[c]chromen-1-ol",
    commonName:"THCV", fullName:"Tetrahydrocannabivarin",
    type:"Phytocannabinoid (varin series)", formula:"C₁₉H₂₆O₂", mw:"286.41 g/mol",
    discovery:"1971 — Gill, Paton & Pertwee, University of Edinburgh", color:"#f97316",
    receptors:["CB1 (antagonist at low dose / partial agonist at high dose)","CB2 (partial agonist)","GPR55"],
    summary:"THCV differs from Δ9-THC by a propyl rather than pentyl side chain. At low doses it acts as a CB1 neutral antagonist, potentially moderating THC intoxication. At high doses it shifts to partial agonism. Found in elevated concentrations in some African landrace varieties. Subject of ongoing pharmacological research.",
    lit:"Gill, E.W. et al. (1970). Preliminary experiments on the chemistry and pharmacology of cannabis. Nature. 228:134-136."
  },
  "CBDV": {
    iupac:"2-[(1R,6R)-6-isopropenyl-3-methylcyclohex-2-en-1-yl]-5-propylbenzene-1,3-diol",
    commonName:"CBDV", fullName:"Cannabidivarin",
    type:"Phytocannabinoid (varin series)", formula:"C₁₉H₂₆O₂", mw:"286.41 g/mol",
    discovery:"1969 — Vollner, Bieniek & Korte", color:"#06b6d4",
    receptors:["TRPV1","TRPV2","TRPA1","CB1 (low affinity)","CB2 (low affinity)"],
    summary:"CBDV is the propyl analogue of CBD, sharing its non-intoxicating profile. It interacts primarily with TRP channels implicated in nociception and neurological signalling. GW Pharmaceuticals has investigated CBDV (GWP42006) in clinical trials for autism spectrum disorder and Rett syndrome. More abundant in indica-type landraces.",
    lit:"Vollner, L. et al. (1969). Haschisch XX. Cannabidivarin. Tetrahedron Letters. 10(3):145-147."
  },
  "CBC": {
    iupac:"(6aR,10aR)-3-methyl-6,6,9-trimethyl-6a,7,8,9,10,10a-hexahydrobenzo[c]chromene",
    commonName:"CBC", fullName:"Cannabichromene",
    type:"Phytocannabinoid", formula:"C₂₁H₃₀O₂", mw:"314.46 g/mol",
    discovery:"1966 — Gaoni & Mechoulam", color:"#ec4899",
    receptors:["TRPA1","TRPV1","TRPV3","TRPV4","CB2 (low affinity)"],
    summary:"CBC is one of the most abundant phytocannabinoids after THC and CBD. It does not bind CB1 or CB2 with meaningful affinity. Its pharmacology is dominated by TRP channel interactions. CBC shares a biosynthetic origin with THC and CBD (both derive from CBGA). Not psychoactive. Concentrations can be significant in some cultivars — up to 0.3% in dried flower.",
    lit:"Gaoni, Y. & Mechoulam, R. (1966). Cannabichromene, a new active principle in hashish. Chemical Communications. 1:20."
  },
  "Δ8-THC": {
    iupac:"(6aR,10aR)-6,6,9-trimethyl-3-pentyl-6a,7,8,9-tetrahydro-6H-benzo[c]chromen-1-ol",
    commonName:"Δ8-THC", fullName:"Delta-8-Tetrahydrocannabinol",
    type:"Phytocannabinoid / Semi-synthetic isomer", formula:"C₂₁H₃₀O₂", mw:"314.46 g/mol",
    discovery:"1941 — Roger Adams, University of Illinois", color:"#7c3aed",
    receptors:["CB1 (Ki ~44nM — lower affinity than Δ9)","CB2"],
    summary:"Δ8-THC is a positional isomer of Δ9-THC differing in the location of the double bond in the cyclohexene ring. Occurs naturally in cannabis at trace concentrations (<0.1%). Most commercial Δ8-THC products are produced semi-synthetically from CBD via acid-catalysed isomerization — a process that can generate novel byproducts of uncertain safety. Estimated to be 50–75% as potent as Δ9-THC.",
    lit:"Adams, R. et al. (1941). Structure of cannabidiol. VI. Isomerization of cannabidiol. JACS. 63(8):2194."
  },
  "CBDA": {
    iupac:"(6aR,10aR)-1-hydroxy-6,6,9-trimethyl-3-pentyl-6a,7,8,10a-tetrahydro-6H-benzo[c]chromene-2-carboxylic acid",
    commonName:"CBDA", fullName:"Cannabidiolic Acid",
    type:"Phytocannabinoid (acidic precursor)", formula:"C₂₂H₃₀O₄", mw:"358.47 g/mol",
    discovery:"1955 — Krejčí & Šantavý", color:"#0284c7",
    receptors:["5-HT1A (high affinity)","TRPA1","COX-1 / COX-2 (selective inhibitor)","CB1 / CB2 (very low)"],
    summary:"CBDA is the biosynthetic precursor to CBD in living cannabis plants. Upon decarboxylation by heat or UV light, the carboxylic acid group is removed and CBDA becomes CBD. CBDA itself has shown high-affinity serotonin 5-HT1A agonism and COX enzyme inhibition in vitro. Research interest has grown following findings on its potential anti-nausea properties at very low doses.",
    lit:"Krejčí, Z. & Šantavý, F. (1955). Isolation of two new cannabinoid acids from cannabis sativa. Acta Univ. Palacki. Olomuc. 6:59-66."
  },
  "CBGa": {
    iupac:"3-[(2E)-3,7-dimethylocta-2,6-dien-1-yl]-2,4-dihydroxy-6-pentylbenzoic acid",
    commonName:"CBGa", fullName:"Cannabigerolic Acid",
    type:"Phytocannabinoid (universal precursor, acidic form)", formula:"C₂₂H₃₂O₄", mw:"360.49 g/mol",
    discovery:"1964 — Mechoulam & Gaoni", color:"#d97706",
    receptors:["TRPA1 (low)","PPAR-γ (potential)","Non-psychoactive"],
    summary:"CBGa is the biosynthetic precursor to all major cannabinoids in the cannabis plant — THCA, CBDA, and CBCA all derive from CBGa through enzymatic conversion. It is synthesized from olivetolic acid and geranyl pyrophosphate by the enzyme OA-geranyltransferase. High residual CBGa in a COA can indicate early harvest (before full enzymatic conversion) or a high-CBG cultivar bred to limit that conversion. The amount of CBGa on a COA is a useful cultivation timing indicator.",
    lit:"Gaoni, Y. & Mechoulam, R. (1964). Cannabichromenic acid. JACS. 86(8):1646.",
    plain_staff:"The 'mother cannabinoid' — converts into THC, CBD, and CBC as the plant matures. If a COA shows significant CBGa, the plant was likely harvested slightly early, or it's a specially bred high-CBG cultivar."
  },
  "CBCa": {
    iupac:"(6aR,10aR)-1-hydroxy-6,6,9-trimethyl-3-pentyl-6a,7,8,9,10,10a-hexahydrobenzo[c]chromene-2-carboxylic acid",
    commonName:"CBCa", fullName:"Cannabichromenic Acid",
    type:"Phytocannabinoid (acidic precursor)", formula:"C₂₂H₃₀O₄", mw:"358.47 g/mol",
    discovery:"1970 — Shani & Mechoulam", color:"#be185d",
    receptors:["TRPA1 (upon decarboxylation to CBC)","Non-psychoactive in acidic form"],
    summary:"CBCa is the acidic precursor to CBC (cannabichromene). It is formed from CBGa via CBC synthase and converts to CBC upon decarboxylation. CBCa itself has been detected on COAs from Canadian labs, particularly in fresh or uncured biomass. It appears at very low concentrations (<0.05%) in most dried flower products. Some labs report CBC and CBCa separately, while others combine them. Its presence indicates the plant's full acidic cannabinoid profile is being captured.",
    lit:"Shani, A. & Mechoulam, R. (1970). Cannabichromenic acid. Chemical Communications. 1970:273.",
    plain_staff:"The acidic form of CBC. Converts to CBC when the product is heated. Usually present in very small amounts. More informative as a data completeness indicator — labs that report CBCa are doing a thorough full-spectrum analysis."
  },
  "Total THC (Label)": {
    iupac:"N/A — Calculated field",
    commonName:"Total THC", fullName:"Total Available THC (Label-equivalent)",
    type:"Calculated composite", formula:"(THCA × 0.877) + Δ9-THC", mw:"N/A",
    discovery:"Health Canada regulatory formula — Cannabis Regulations SOR/2018-144", color:"#16a34a",
    receptors:["N/A — calculated value, not a distinct molecule"],
    summary:"Total THC is the potency figure printed on licensed cannabis product labels in Canada, calculated per Health Canada's formula: (THCA × 0.877) + Δ9-THC. The factor 0.877 accounts for the loss of CO₂ (44 g/mol) during decarboxylation of THCA (358 g/mol) to Δ9-THC (314 g/mol): 314/358 = 0.877. This represents the maximum theoretical Δ9-THC obtainable if all THCA were fully converted — achievable during combustion but not necessarily during vaporization at lower temperatures. THCA alone is non-psychoactive.",
    lit:"Cannabis Regulations, SOR/2018-144, Schedule 2 — Health Canada label requirements for cannabis.",
    plain_staff:"The number on the package. Calculated from THCA × 0.877 + THC. Represents the maximum potency if the flower is fully combusted. Vaporizing at lower temps may not convert all THCA — actual psychoactive delivery depends on temperature."
  },
};

// ============================================================
// TERPENE EFFECT TAGS — consumer-facing aroma descriptors
// ============================================================
const TERPENE_EFFECT_TAGS = {
  "β-Myrcene":        { tag:"Earthy · Musky",      color:"#6366f1" },
  "β-Caryophyllene":  { tag:"Pepper · Spice",      color:"#92400e" },
  "(+)-Limonene":     { tag:"Citrus · Lemon",       color:"#f59e0b" },
  "α-Pinene":         { tag:"Pine · Fresh",          color:"#059669" },
  "Terpinolene":      { tag:"Herbal · Woody",        color:"#0ea5e9" },
  "Linalool":         { tag:"Floral · Lavender",     color:"#8b5cf6" },
  "β-Ocimene":        { tag:"Sweet · Bright",        color:"#ec4899" },
  "α-Humulene":       { tag:"Hoppy · Earthy",        color:"#78716c" },
  "α-Bisabolol":      { tag:"Chamomile · Delicate",  color:"#f472b6" },
  "Fenchol":          { tag:"Pine · Basil",           color:"#5b8c5a" },
  "Camphene":         { tag:"Fir · Earthy",           color:"#6b8f71" },
  "Geraniol":         { tag:"Rose · Floral",          color:"#e8a87c" },
  "Nerolidol":        { tag:"Woody · Floral",         color:"#a3785e" },
  "Valencene":        { tag:"Fresh Orange",            color:"#d97706" },
  "Guaiol":           { tag:"Pine · Rosy",            color:"#7a8f6e" },
  "Sabinene":         { tag:"Spicy · Warm",           color:"#6b9e7a" },
  "α-Terpineol":      { tag:"Lilac · Pine",           color:"#b8a9d0" },
  "Borneol":          { tag:"Camphor · Cool",          color:"#8fa98b" },
  "Caryophyllene Oxide": { tag:"Dry · Woody",         color:"#8b7355" },
};

// ============================================================
// PRODUCT TYPE DATA
// ============================================================
const PRODUCT_TYPES = {
  "Dried Flower": {
    icon:"🌿",
    color:"#22c55e",
    tagline:"The whole-plant experience",
    format:"Inhaled (smoked or dry-herb vaporized)",
    thcRange:"15 – 35% THC (as-purchased; reflects THCA converted at combustion)",
    terpeneRange:"0.5 – 4.0% total terpenes typical; premium indoor often 2–4%",
    onset:"Seconds to 3 minutes (inhaled)",
    duration:"1 – 3 hours",
    description:"Dried flower is the harvested, cured, and trimmed inflorescence (bud) of the cannabis plant. It retains the full complement of cannabinoids, terpenes, flavonoids, and minor compounds produced by the plant — giving it the broadest chemical complexity of any product format. When combusted, THCA undergoes decarboxylation to Δ9-THC. Vaporizing at lower temperatures (160–185°C) preserves a greater fraction of volatile terpenes than combustion.",
    considerations:"Combustion produces carbon monoxide and particulate matter. Dry-herb vaporization significantly reduces harmful byproducts. COA percentages show THCA, not active THC — multiply by 0.877 and add any Δ9-THC for potency estimate.",
    bestFor:["Full-spectrum terpene profiles","Strain comparison and exploration","COA-verified terpene matching","Experienced adult consumers"]
  },
  "Pre-Roll": {
    icon:"🚬",
    color:"#84cc16",
    tagline:"Flower, ready to go",
    format:"Inhaled (combusted)",
    thcRange:"12 – 30% THC (inherits flower potency)",
    terpeneRange:"Varies with flower quality used; infused pre-rolls can exceed 4%",
    onset:"Seconds to 3 minutes",
    duration:"1 – 3 hours",
    description:"Pre-rolls are factory- or hand-rolled cannabis cigarettes. Single-strain pre-rolls (typically 0.5 g or 1 g) use whole or milled flower. Infused pre-rolls incorporate concentrate (kief, hash, live resin, diamonds) either inside the cone or as an external coating, substantially increasing potency and terpene content. Machine-rolled products may use trim or shake; premium hand-rolled products use whole-bud flower.",
    considerations:"Infused pre-rolls can exceed 40–50% total cannabinoids — not suitable for new consumers. Paper quality and filter type affect draw resistance and burn. Check COA to distinguish whole-flower vs. trim-based products.",
    bestFor:["Convenience and portability","Single-session dosing","Infused formats for experienced users"]
  },
  "Vape Cartridge": {
    icon:"💨",
    color:"#38bdf8",
    tagline:"Portable concentrate in vapour form",
    format:"Inhaled (vaporized)",
    thcRange:"60 – 90% Δ9-THC (concentrate base)",
    terpeneRange:"5 – 15% terpenes in live resin carts; 1–5% in distillate + botanical terpene blends",
    onset:"Seconds to 2 minutes",
    duration:"1 – 2 hours",
    description:"Vape cartridges contain cannabis-derived oil in a 510-threaded glass or ceramic reservoir attached to a battery. The oil is either distillate (a refined, nearly odourless THC concentrate with terpenes re-added post-process) or live resin / rosin (full-spectrum extracts made from fresh-frozen or solventless-processed flower, preserving native terpene profiles). Battery temperature settings significantly affect terpene delivery — lower voltage (2.4–3.2V) preserves more volatile monoterpenes.",
    considerations:"Distillate carts with synthetic terpenes lack the entourage complexity of live resin. Check whether terpenes listed are cannabis-derived or food-grade botanical. EVALI outbreaks in 2019 were associated with vitamin E acetate used as a cutting agent — this practice is now prohibited under Canadian LPs.",
    bestFor:["Discreet, odour-minimized use","Rapid onset","On-the-go consumption","Precise dose-per-puff estimation"]
  },
  "Disposable Vape": {
    icon:"🔋",
    color:"#818cf8",
    tagline:"All-in-one, no setup required",
    format:"Inhaled (vaporized)",
    thcRange:"60 – 90% Δ9-THC",
    terpeneRange:"Comparable to cartridge format (1 – 15% depending on oil type)",
    onset:"Seconds to 2 minutes",
    duration:"1 – 2 hours",
    description:"Disposable vapes integrate battery, atomizer, and oil reservoir into a single sealed unit — no charging or cartridge attachment required. Most are draw-activated. Some premium disposables include USB-C charging ports and preheat functions. Oil types mirror cartridge offerings (distillate vs. live resin). Typical fill sizes range from 0.3 g to 1 g. The sealed format makes tamper detection easier and prevents refilling.",
    considerations:"Non-rechargeable disposables create more electronic waste per gram consumed. Same oil quality considerations as cartridges apply. Verify brand reputation — unregulated disposables have been a vector for counterfeit or adulterated products in illicit markets.",
    bestFor:["First-time vape users (no setup)","Travel","No cleaning or maintenance required"]
  },
  "Concentrate": {
    icon:"💎",
    color:"#fbbf24",
    tagline:"Highly refined cannabis extract",
    format:"Inhaled (dabbed or vaporized) / Added to flower",
    thcRange:"65 – 95% Δ9-THC depending on type",
    terpeneRange:"1 – 20%+ in live resin / rosin; <1% in isolated distillate",
    onset:"Seconds to 2 minutes (dabbed)",
    duration:"2 – 4 hours",
    description:"Concentrates encompass a range of extraction outputs: shatter (glass-like BHO), budder/wax (whipped, opaque BHO), live resin (BHO from fresh-frozen plant — high terpene retention), rosin (solventless, heat-and-pressure extraction), and isolates (>99% single cannabinoid). Hash rosin and live rosin occupy the premium end, extracting full-spectrum oil without hydrocarbon solvents. HTFSE (High-Terpene Full Spectrum Extract) and HCFSE (High-Cannabinoid Full Spectrum Extract) are separated fractions from refined live resin.",
    considerations:"Requires a dab rig, e-rig (e.g. Puffco), or capable portable vaporizer. Extremely high potency — not appropriate for new consumers. Solvent-based products (BHO, CO₂) must pass residual solvent testing on COA. Rosin requires no solvent certification.",
    bestFor:["Experienced adult consumers","Highest terpene concentration formats","COA-intensive product evaluation","Requires appropriate equipment (rig, e-rig, or capable vaporizer)"]
  },
  "Edible": {
    icon:"🍫",
    color:"#f97316",
    tagline:"Ingested — a different metabolic pathway",
    format:"Oral ingestion",
    thcRange:"Dosed in mg (not %) — 2.5 mg to 10 mg per piece typical; 10 mg per package limit in Canada",
    terpeneRange:"Generally absent — terpenes do not survive food processing and digestion",
    onset:"30 minutes – 2 hours (highly variable: food intake, metabolism, body composition)",
    duration:"4 – 8 hours (sometimes longer)",
    description:"Edibles deliver cannabinoids via digestion rather than inhalation. Δ9-THC is metabolized by the liver into 11-hydroxy-THC, a more potent and longer-lasting metabolite that crosses the blood-brain barrier more readily than Δ9-THC. This first-pass hepatic metabolism makes edibles substantially more intense and longer-lasting than inhalation for equivalent doses. In Canada, edible product classes include chocolates, gummies, beverages, mints, and baked goods — all capped at 10 mg THC per package.",
    considerations:"Delayed onset is the primary risk factor: new consumers often re-dose before the initial dose has taken effect. Always wait the full 2 hours before considering additional intake. Onset varies widely between individuals. High-fat meals increase absorption of fat-soluble cannabinoids. Microdosing (1–2.5 mg) is recommended for inexperienced consumers.",
    bestFor:["Smoke-free consumption","Extended duration needs","Precise low-dose microdosing","Discreet consumption"]
  },
  "Oil / Tincture": {
    icon:"🧪",
    color:"#a78bfa",
    tagline:"Precise dosing, flexible delivery",
    format:"Sublingual or oral (swallowed / added to food)",
    thcRange:"Dosed in mg/mL — typically 1 mg/mL to 30 mg/mL; ratio products (THC:CBD) common",
    terpeneRange:"Minimal — most oils are distillate or isolate in MCT/hemp seed oil carrier",
    onset:"Sublingual: 15 – 45 minutes | Swallowed: 45 – 120 minutes",
    duration:"Sublingual: 2 – 4 hours | Swallowed: 4 – 8 hours (similar to edibles)",
    description:"Cannabis oils suspend cannabinoid distillate or isolate in a carrier oil — most commonly MCT (medium-chain triglyceride) oil derived from coconut, or hemp seed oil. Sublingual administration (held under the tongue for 60–90 seconds) allows partial absorption through the sublingual mucosa, bypassing first-pass hepatic metabolism and producing a faster, more predictable onset than fully swallowed oils. When swallowed, they behave pharmacokinetically like edibles. Capsules and softgels use the same oil formulation in a sealed form for odour-free ingestion.",
    considerations:"MCT carrier improves cannabinoid bioavailability vs. water-based vehicles. Ratio products (e.g. 1:1 or 1:20 THC:CBD) allow precise control of the cannabinoid ratio. Oils rarely retain meaningful terpene content — not the ideal format for terpene-driven effects. Verify mg/mL concentration carefully; dosing errors are common when switching between products.",
    bestFor:["New consumers seeking precise low-dose titration","Smoke-free / vapour-free consumption","Cannabinoid ratio products (e.g. 1:1, 1:20 THC:CBD)","Accurate mg-based dosing"]
  },
};

// ============================================================
// CULTIVATION DATA
// ============================================================
const CULTIVATION_METHODS = {
  "Indoor": {
    description:"Cultivated in fully controlled indoor environments under artificial lighting.",
    terpeneImpact:"Consistent terpene expression due to controlled temperature, humidity, and light spectrum management. Generally higher terpene preservation.",
    lightSource:"LED / HPS / CMH artificial lighting",
    environmentControl:"Full — temperature, humidity, CO₂, VPD"
  },
  "Outdoor": {
    description:"Cultivated in natural sunlight in open-air environments.",
    terpeneImpact:"Terpene profiles influenced by seasonal variation, soil composition, and natural stress responses. Can produce complex terpenoid diversity.",
    lightSource:"Natural sunlight",
    environmentControl:"Minimal — weather dependent"
  },
  "Greenhouse": {
    description:"Cultivated under glass or polycarbonate structures using supplemental lighting.",
    terpeneImpact:"Hybrid approach allows terpene consistency with some natural variation. UV light penetration affects secondary metabolite production.",
    lightSource:"Natural light + supplemental LED/HPS",
    environmentControl:"Partial — temperature and humidity managed"
  },
  "Mixed Light": {
    description:"Greenhouse cultivation with full-spectrum supplemental lighting during light-deficient periods.",
    terpeneImpact:"Combines natural spectrum benefits with consistent supplementation. Growing interest in effects of specific light spectra on terpene biosynthesis.",
    lightSource:"Natural + full spectrum supplemental",
    environmentControl:"Moderate"
  },
};

const GROWING_MEDIA = {
  "Soil": { description:"Natural or amended soil substrate.", notes:"Mineral and microbial complexity of living soil may contribute to terpene diversity through plant-microbiome interactions." },
  "Hydroponics": { description:"Soilless cultivation in inert media with nutrient solution.", notes:"Precise nutrient delivery. Faster growth cycles. Terpene profiles may differ from soil-grown equivalents." },
  "Coco Coir": { description:"Coconut husk fibre substrate used with nutrient solution.", notes:"Inert substrate with good drainage and aeration. Widely used in licensed cannabis production." },
  "Aeroponics": { description:"Root systems suspended in air, misted with nutrient solution.", notes:"Highly efficient water and nutrient use. Research ongoing regarding effects on secondary metabolite production." },
  "Living Soil": { description:"Biologically active soil with diverse microbial ecosystem.", notes:"Emerging research suggests plant-microbiome interactions may influence terpene biosynthesis pathways." },
};

const CURE_METHODS = {
  "Slow Cure (Boveda)": {
    duration:"21–60 days",
    humidity:"58–62% RH",
    temp:"15–18°C",
    description:"Extended cure in sealed vessels with humidity regulation packs.",
    terpeneImpact:"Preserves volatile monoterpenes that would otherwise evaporate. Enzymatic breakdown of chlorophyll improves flavour profile. Considered optimal for terpene preservation.",
    volatileRetention:"High"
  },
  "Standard Cure": {
    duration:"14–21 days",
    humidity:"55–65% RH",
    temp:"18–21°C",
    description:"Industry standard curing in controlled environment.",
    terpeneImpact:"Adequate terpene preservation for most commercial applications. Balance of speed and quality.",
    volatileRetention:"Moderate"
  },
  "Fast Dry": {
    duration:"3–7 days",
    humidity:"Variable",
    temp:"Variable",
    description:"Accelerated drying process.",
    terpeneImpact:"Significant loss of volatile monoterpenes due to heat and speed. Chlorophyll may not fully break down.",
    volatileRetention:"Low"
  },
  "Freeze Dry": {
    duration:"24–72 hours",
    humidity:"N/A (sublimation)",
    temp:"-40°C to -50°C",
    description:"Lyophilization process used for live rosin and some specialty products.",
    terpeneImpact:"Exceptional preservation of volatile terpenes including highly unstable compounds. Used in premium concentrate production.",
    volatileRetention:"Very High"
  },
};

const HARVEST_METHODS = {
  "Hand Trimmed": { description:"Flowers trimmed manually by trained staff.", trichomeImpact:"Minimal mechanical disruption to trichome structures. Preferred for terpene and cannabinoid preservation." },
  "Machine Trimmed": { description:"Automated trimming via tumbler or blade system.", trichomeImpact:"Some trichome loss through mechanical contact. Efficient for large-scale operations." },
  "Hand Bucked": { description:"Flowers removed from stems by hand.", trichomeImpact:"Gentle handling preserves intact trichome heads." },
  "Whole Plant Harvest": { description:"Entire plant harvested simultaneously.", trichomeImpact:"Standard approach. Timing affects overall cannabinoid and terpene maturity." },
};

// ============================================================
// DEMO ALL_PRODUCTS — Enhanced with full cultivation data
// ============================================================
const DISPENSARIES = [];

const PRODUCTS = [];



// ============================================================
// TERPENE SIMILARITY ALGORITHM (demo)
// ============================================================
function cosineSimilarity(profileA, profileB) {
  const allKeys = [...new Set([...Object.keys(profileA), ...Object.keys(profileB)])];
  const vecA = allKeys.map(k => profileA[k] || 0);
  const vecB = allKeys.map(k => profileB[k] || 0);
  const dot = vecA.reduce((s, a, i) => s + a * vecB[i], 0);
  const magA = Math.sqrt(vecA.reduce((s, a) => s + a * a, 0));
  const magB = Math.sqrt(vecB.reduce((s, b) => s + b * b, 0));
  return magA && magB ? dot / (magA * magB) : 0;
}

function findSimilarProducts(targetProduct, allProducts, topN = 3) {
  return allProducts
    .filter(p => p.id !== targetProduct.id)
    .map(p => ({
      product: p,
      similarity: cosineSimilarity(targetProduct.terpenes, p.terpenes),
    }))
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, topN);
}

// ============================================================
// BUDTENDER TRAINING CONTENT
// ============================================================
const TRAINING_MODULES = [
  {
    id:"tm1", title:"Introduction to Terpenes",
    duration:"8 min", level:"Foundation",
    lessons:[
      { title:"What are terpenes?", content:"Terpenes are volatile aromatic compounds produced by many plants and some insects. In cannabis, they are synthesized in the trichome glands alongside cannabinoids. Over 150 different terpenes have been identified in Cannabis sativa. They contribute to the characteristic aroma and flavour of each cultivar. Terpenes are not unique to cannabis — they are found throughout the plant kingdom and are the primary constituents of essential oils.", key:"Terpenes are aromatic compounds found in many plants, synthesized in cannabis trichomes." },
      { title:"Terpene biosynthesis", content:"Cannabis terpenes are synthesized through two main biochemical pathways: the methylerythritol phosphate (MEP) pathway produces monoterpenes and diterpenes, while the mevalonate (MVA) pathway produces sesquiterpenes. Environmental factors including light spectrum, temperature, humidity, and grow medium influence terpene expression. This is why the same genetic cultivar grown under different conditions can produce different terpene profiles.", key:"Terpenes are synthesized via MEP and MVA pathways. Environmental conditions influence their expression." },
      { title:"Reading a terpene certificate", content:"A Certificate of Analysis (COA) for terpenes typically reports individual compound concentrations as a percentage of total sample weight. For example, 0.52% β-Myrcene means 5.2mg of Myrcene per gram of product. The testing methodology matters — GC-MS (Gas Chromatography-Mass Spectrometry) and HS-GC-FID (Headspace Gas Chromatography-Flame Ionization Detection) are standard methods. Always note the lab name, testing date, and batch number when referencing COA data.", key:"Terpene COAs report concentration as % of sample weight. Note lab, method, and batch when referencing." },
    ],
    quiz:[
      { q:"Where are terpenes primarily synthesized in the cannabis plant?", opts:["Roots","Leaves","Trichome glands","Seeds"], correct:2 },
      { q:"Which pathway produces sesquiterpenes like β-Caryophyllene?", opts:["MEP pathway","MVA pathway","Shikimate pathway","Fatty acid pathway"], correct:1 },
      { q:"What does 0.52% β-Myrcene mean on a COA?", opts:["52mg per gram","5.2mg per gram","0.52mg per gram","520mg per gram"], correct:1 },
    ]
  },
  {
    id:"tm2", title:"Cannabinoid Science",
    duration:"10 min", level:"Foundation",
    lessons:[
      { title:"The endocannabinoid system", content:"The endocannabinoid system (ECS) is a biological signalling system present in all mammals. It consists of endocannabinoids (lipid-based signalling molecules produced naturally by the body), cannabinoid receptors (CB1 and CB2), and enzymes that synthesize and degrade endocannabinoids. CB1 receptors are primarily located in the central nervous system. CB2 receptors are primarily located in immune tissue. Phytocannabinoids from cannabis interact with this system.", key:"The ECS consists of endocannabinoids, CB1/CB2 receptors, and metabolic enzymes present in all mammals." },
      { title:"Δ9-THC pharmacology", content:"Δ9-THC exerts its primary effects as a partial agonist at CB1 receptors. The psychoactive effects result from this interaction in the central nervous system. THC is reported as a percentage of total sample weight on Canadian product labels. THCA is the non-psychoactive acidic precursor — decarboxylation via heat converts THCA to Δ9-THC. This is why raw cannabis is not psychoactive without heating.", key:"Δ9-THC is a partial CB1 agonist. THCA is its non-psychoactive precursor, converted by decarboxylation." },
      { title:"CBD and other cannabinoids", content:"CBD has a complex multi-receptor pharmacology that does not primarily involve direct CB1 agonism. It acts on TRPV1, 5-HT1A, and GPR55 receptors among others. CBG (Cannabigerol) is the biosynthetic precursor to THC, CBD, and CBC. CBN forms through oxidative degradation of THC and increases with product age. Understanding these compounds helps explain why products with similar THC percentages may differ in their chemical profiles.", key:"CBD acts on multiple receptors beyond CB1/CB2. CBG is the cannabinoid precursor. CBN forms from THC oxidation." },
    ],
    quiz:[
      { q:"Which receptor type is primarily located in the central nervous system?", opts:["CB2","TRPV1","CB1","GPR55"], correct:2 },
      { q:"What converts THCA to Δ9-THC?", opts:["Cold storage","UV light exposure","Decarboxylation via heat","Water contact"], correct:2 },
      { q:"CBN is primarily formed through:", opts:["Biosynthesis in young plants","Oxidative degradation of THC","Conversion from CBD","Decarboxylation of CBNA"], correct:1 },
    ]
  },
  {
    id:"tm3", title:"Cultivation & Cure Science",
    duration:"7 min", level:"Intermediate",
    lessons:[
      { title:"How cultivation method affects terpenes", content:"The growing environment significantly influences terpene biosynthesis. Indoor cultivation allows precise control of environmental variables — temperature, humidity, CO₂ levels, and light spectrum — resulting in consistent terpene profiles across batches. Outdoor cultivation exposes plants to UV radiation, seasonal temperature variation, and environmental stress, which can trigger diverse secondary metabolite production. Greenhouse cultivation represents a hybrid approach. Living soil cultivation introduces plant-microbiome interactions that may further influence terpene expression.", key:"Growing environment directly influences terpene biosynthesis. Indoor = consistency, outdoor = potential diversity." },
      { title:"The science of curing", content:"Curing is the post-harvest process of controlled drying. Its primary purpose is moisture reduction to safe storage levels (10-14% moisture content) while preserving volatile terpenes. Slow curing in sealed vessels at 58-62% relative humidity allows enzymatic breakdown of chlorophyll and starches, improving flavour. Critically, volatile monoterpenes (including Limonene, Terpinolene, β-Pinene) have low boiling points and evaporate rapidly at elevated temperatures. Slow curing at cool temperatures preserves these compounds that fast drying destroys.", key:"Slow curing preserves volatile terpenes lost to heat. Enzymatic breakdown during cure improves flavour profile." },
      { title:"Harvest timing and trichomes", content:"Trichome maturity at harvest determines the final cannabinoid and terpene profile. Cannabis trichomes progress through stages: clear (immature, low THC), cloudy/milky (peak THC, maximum terpene expression), and amber (THC degrading to CBN, terpenes oxidizing). Professional cultivators use trichoscopy — microscopic examination of trichome heads — to determine optimal harvest timing. Harvesting too early reduces potency; too late allows degradation. The harvest window is typically 7-14 days wide.", key:"Trichome colour indicates maturity: clear=immature, cloudy=peak, amber=degrading. Trichoscopy determines timing." },
    ],
    quiz:[
      { q:"Which curing method best preserves volatile monoterpenes?", opts:["Fast dry","Machine dry at 40°C","Slow cure at 58-62% RH","Freeze dry is worst"], correct:2 },
      { q:"What does cloudy/milky trichome appearance indicate?", opts:["Immature — harvest too early","Peak cannabinoid and terpene expression","THC degrading to CBN","Mould contamination"], correct:1 },
      { q:"At what moisture content range is cannabis safely stored?", opts:["5-8%","10-14%","18-22%","25-30%"], correct:1 },
    ]
  },
];

// ============================================================
// MAIN APP
// ============================================================
export default function ITerpsV2() {
  const [page, setPage] = useState("home");
  const [dispDetailId, setDispDetailId] = useState(null);
  const [budtenderMode, setBudtenderMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [prevPage, setPrevPage] = useState("explorer");
  // Batch history — tracks terpene profile changes across batches per product name+brand
  const [batchHistory, setBatchHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iterps_batches") || "{}"); }
    catch (_e) { return {}; }
  });
  // Batch Tracker page state
  const [batchTrackerLP, setBatchTrackerLP] = useState("all");
  const [batchTrackerBrand, setBatchTrackerBrand] = useState("all");
  const [batchTrackerProduct, setBatchTrackerProduct] = useState(null);
  const [batchTrackerTerpene, setBatchTrackerTerpene] = useState("all");
  const [flaggedDrifts, setFlaggedDrifts] = useState(() => { try { return JSON.parse(localStorage.getItem("iterps_drift_flags")||"{}"); } catch (_e) { return {}; } });
  const [customerSort, setCustomerSort] = useState("name"); // name | recent | products | lapsed
  // ── LOG FOR CUSTOMER modal state ─────────────────────────
  const [logForCustModal, setLogForCustModal] = useState(null); // { product } — open = product set
  const [logForCustSearch, setLogForCustSearch] = useState("");
  const [logForCustDone, setLogForCustDone] = useState(null); // { customerName }
  // ── CROSS-TAB SEED — carry finder result terpene into Browse ──
  const [finderSeedTerpene, setFinderSeedTerpene] = useState(null); // terpene name to pre-filter Browse
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [exportTerpFilter, setExportTerpFilter] = useState("");
  // Similar To This
  const [similarModal, setSimilarModal] = useState(null);
  // Terpene Flash Quiz
  const [quizFlashIdx, setQuizFlashIdx] = useState(0);
  const [quizFlashAnswer, setQuizFlashAnswer] = useState(null);
  const [quizFlashScore, setQuizFlashScore] = useState(0);
  const [quizFlashDone, setQuizFlashDone] = useState(false);
  const [quizFlashActive, setQuizFlashActive] = useState(false);
  const [quizFlashBank, setQuizFlashBank] = useState([]);
  // Dispensary Benchmarking
  const [benchmarkDispId, setBenchmarkDispId] = useState("all");
  // New to cannabis mode
  const [newbieModeActive, setNewbieModeActive] = useState(false);
  // Passport QR
  const [passportQR, setPassportQR] = useState(false);
  const [compareA, setCompareA] = useState(null);
  const [compareB, setCompareB] = useState(null);
  const [finderStep, setFinderStep] = useState(0);
  const [finderAnswers, setFinderAnswers] = useState({});
  const [finderResults, setFinderResults] = useState(null);
  const [compound, setCompound] = useState(null);
  const [compoundType, setCompoundType] = useState("terpene");
  const [cultivationModal, setCultivationModal] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [filters, setFilters] = useState({ terpene:"", category:"", plantType:"", city:"", dispensary:"", terpSource:"", sortBy:"name", thcMin:"", thcMax:"", terpMin:"", terpMax:"" });
  const [search, setSearch] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [homeTab, setHomeTab] = useState("terpenes");
  const [ageOk, setAgeOk] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [onboardDone, setOnboardDone] = useState(false);
  const [activeDispensary, setActiveDispensary] = useState(""); // logged-in store for staff
  const [activeLp, setActiveLp] = useState(""); // logged-in LP name
  const [kioskMode, setKioskMode] = useState(false); // tablet locked to a store
  const [kioskDispensary, setKioskDispensary] = useState(""); // locked dispensary id
  const [kioskPin, setKioskPin] = useState(""); // admin PIN to exit kiosk
  const [showKioskSetup, setShowKioskSetup] = useState(false); // kiosk setup modal
  const [kioskPinEntry, setKioskPinEntry] = useState(""); // PIN entry for exit
  const [kioskWelcome, setKioskWelcome] = useState(true); // show welcome screen vs active session
  const [dbDispensaries, setDbDispensaries] = useState([]); // dispensaries from Supabase
  const [dbProducts, setDbProducts] = useState([]); // products from Supabase
  const [eventLog, setEventLog] = useState([]); // events from Supabase
  const [kioskStoreName, setKioskStoreName] = useState(""); // display name for kiosk store
  const [kioskExitEntry, setKioskExitEntry] = useState(""); // PIN entry for kiosk exit
  const [kioskShowExit, setKioskShowExit] = useState(false); // show exit PIN modal
  const [consumerPhoneInput, setConsumerPhoneInput] = useState(""); // phone entry on kiosk/web
  const [consumerPhoneStep, setConsumerPhoneStep] = useState("idle"); // idle|entry|found|new
  const [transparencyView, setTransparencyView] = useState(null); // { product, section }
  const [onboardStep, setOnboardStep] = useState(0);
  const [onboardRole, setOnboardRole] = useState("");
  const [founderMode, setFounderMode] = useState(false);
  const [managerMode, setManagerMode] = useState(false);
  const [founderPassphrase, setFounderPassphrase] = useState("");
  const [founderPassphraseError, setFounderPassphraseError] = useState(false);
  const [founderTab, setFounderTab] = useState("overview");
  const [founderNotes, setFounderNotes] = useState(() => { try { return localStorage.getItem("iterps_founder_notes")||""; } catch (_e) { return ""; } });
  const [founderAddingLP, setFounderAddingLP] = useState(false);
  const [founderNewLPName, setFounderNewLPName] = useState("");
  const [showFounderUnlock, setShowFounderUnlock] = useState(false);
  const logoClickTimer = useRef(null);
  const kioskIdleTimerRef = useRef(null);
  const FOUNDER_PASSPHRASE = "iterps";
  const showToast = (message, type="success") => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3200);
  };

  const [feedbackModal, setFeedbackModal] = useState(null); // { context, productId, productName }

  // ── KEYBOARD SHORTCUTS ───────────────────────────────────
  const [smsModal, setSmsModal] = useState(null); // { products: [], customerName: "" }
  React.useEffect(() => {
    const handler = (e) => {
      // ⌘K / Ctrl+K → global search
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowGlobalSearch(true); setGlobalSearch("");
      }
      // Escape → close any open modal/overlay
      if (e.key === "Escape") {
        if (showGlobalSearch) { setShowGlobalSearch(false); setGlobalSearch(""); }
        else if (logForCustModal) { setLogForCustModal(null); setLogForCustSearch(""); setLogForCustDone(null); }
        else if (smsModal) { setSmsModal(null); setSmsSent(false); setSmsPhone(""); }
        else if (feedbackModal) { setFeedbackModal(null); }
        else if (qrModal) { setQrModal(null); }
        else if (similarModal) { setSimilarModal(null); }
        else if (showFounderUnlock) { setShowFounderUnlock(false); setFounderPassphrase(""); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showGlobalSearch, logForCustModal, smsModal, feedbackModal, qrModal, similarModal, showFounderUnlock]);

  // ── MONETIZATION STATE ────────────────────────────────────
  // Subscription / plan state (simulated — real impl needs Stripe)


  // LP verified badge state
  const [lpVerifiedMap, setLpVerifiedMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iterps_lp_verified") || "{}"); } catch (_e) { return {}; }
  }); // { "LP Name": { tier: "verified"|"partner"|"featured", since: date } }

  // Dispensary onboarding pipeline
  const [showDispOnboard, setShowDispOnboard] = useState(false);
  const [dispOnboardStep, setDispOnboardStep] = useState(0);
  const [dispOnboardData, setDispOnboardData] = useState({ name:"", city:"", email:"", phone:"", pos:"", skus:"", interest:"" });
  const [dispOnboardDone, setDispOnboardDone] = useState(false);
  const [dispOnboardLeads, setDispOnboardLeads] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iterps_disp_leads") || "[]"); } catch (_e) { return []; }
  });

  // Stock alerts (consumer opt-in)
  const [stockAlerts, setStockAlerts] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iterps_stock_alerts") || "[]"); } catch (_e) { return []; }
  }); // [{ productId, productName, brand, dispensaryId, email, createdAt, fired }]
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertProduct, setAlertProduct] = useState(null);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertDone, setAlertDone] = useState(false);

  const saveDispLead = async (lead) => {
    const newLead = { ...lead, created_at: new Date().toISOString() };
    try {
      const { data } = await db.saveLead(newLead);
      if (data) {
        setDispOnboardLeads(prev => [data, ...prev]);
        return;
      }
    } catch (_e) {}
    // Fallback to local
    const updated = [...dispOnboardLeads, { ...newLead, id: Date.now() }];
    setDispOnboardLeads(updated);
    try { localStorage.setItem("iterps_disp_leads", JSON.stringify(updated)); } catch (_e) {}
  };

  const saveStockAlert = async (alert) => {
    const newAlert = { ...alert, created_at: new Date().toISOString(), fired: false };
    try {
      await supabase.from("stock_alerts").insert(newAlert);
    } catch (_e) {}
    const updated = [...stockAlerts, { ...newAlert, id: Date.now() }];
    setStockAlerts(updated);
    try { localStorage.setItem("iterps_stock_alerts", JSON.stringify(updated)); } catch (_e) {}
  };


  const [trainingModule, setTrainingModule] = useState(null);
  const [lessonIdx, setLessonIdx] = useState(0);
  const [quizMode, setQuizMode] = useState(false);
  const [quizIdx, setQuizIdx] = useState(0);
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [quizScore, setQuizScore] = useState(0);
  const [quizDone, setQuizDone] = useState(false);
  const [completedModules, setCompletedModules] = useState(() => { try { return JSON.parse(localStorage.getItem("iterps_completed_modules")||"[]"); } catch (_e) { return []; } });
  const [algoTab, setAlgoTab] = useState("explorer");
  const [consumerMode, setConsumerMode] = useState(false);
  const [consumerTab, setConsumerTab] = useState("find");
  const [tasteProfile, setTasteProfile] = useState(null);
  // Consumer finder state — lifted to parent to survive re-renders
  const [cStep, setCStep] = useState(0);
  const [finderSearchAll, setFinderSearchAll] = useState(false); // staff finder: search BC or just this store
  const [cAnswers, setCAnswers] = useState({});
  const [cResults, setCResults] = useState(null);
  const [cSelectedProduct, setCSelectedProduct] = useState(null);
  // Staff FinderPage state — lifted to parent for same reason
  const [fStep, setFStep] = useState(0);
  const [fAnswers, setFAnswers] = useState({});
  const [fResults, setFResults] = useState(null);
  const [fAvoidSelected, setFAvoidSelected] = useState([]);
  const [fExpandedBreakdown, setFExpandedBreakdown] = useState(null);
  const [fCompareA, setFCompareA] = useState(null);
  const [fCompareB, setFCompareB] = useState(null);
  // Location picker state (shared across both finders)
  const [pickerCity, setPickerCity] = useState("any");
  const [pickerDisp, setPickerDisp] = useState("any");

  // ── CUSTOMER PROFILE SYSTEM ─────────────────────────────────
  const PROFILES_KEY = "iterps_profiles";
  const loadProfiles = useCallback(() => {
    try { return JSON.parse(localStorage.getItem(PROFILES_KEY) || "[]"); }
    catch (_e) { return []; }
  }, []);
  const saveProfiles = useCallback(async (ps) => {
    // Write to Supabase (upsert by phone)
    try {
      for (const p of ps) {
        if (p.phone) await db.saveProfile(p);
      }
    } catch (_e) {}
    // Keep local fallback
    try { localStorage.setItem(PROFILES_KEY, JSON.stringify(ps)); } catch (_e) {}
  }, []);

  const [profiles, setProfiles] = useState(() => { try { return JSON.parse(localStorage.getItem("iterps_profiles") || "[]"); } catch (_e) { return []; } });
  const [staffCustomerPage, setStaffCustomerPage] = useState("list");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerFor, setRegisterFor] = useState(null);
  // New feature state
  const [batchCustomers, setBatchCustomers] = useState([]);
  const [showBatchFinder, setShowBatchFinder] = useState(false);
  const [dashTab, setDashTab] = useState("overview");
  const [completedCerts, setCompletedCerts] = useState(() => { try { return JSON.parse(localStorage.getItem("iterps_certs")||"{}"); } catch (_e) { return {}; } });
  const [newArrivalMatches, setNewArrivalMatches] = useState(null); // {product, matches:[]}
  const [lpPortalTab, setLpPortalTab] = useState("overview");
  const [staffNoteText, setStaffNoteText] = useState("");
  const [staffNoteSaved, setStaffNoteSaved] = useState(false);
  // --- ALL INNER COMPONENT STATE HOISTED TO PARENT (prevents React #310) ---
  // ComparePage
  const [cmpSelA, setCmpSelA] = useState(null);
  const [cmpSelB, setCmpSelB] = useState(null);
  const [cmpSearchA, setCmpSearchA] = useState("");
  const [cmpSearchB, setCmpSearchB] = useState("");
  const [cmpFocusA, setCmpFocusA] = useState(false);
  const [cmpFocusB, setCmpFocusB] = useState(false);
  // AlgorithmPage
  const [algoA, setAlgoA] = useState(null);
  const [algoB, setAlgoB] = useState(null);
  // LPPortal
  const [lpTabState, setLpTabState] = useState("overview");
  const [coaStepState, setCoaStepState] = useState(0);
  const [coaSubmittedState, setCoaSubmittedState] = useState(false);
  const [coaFormState, setCoaFormState] = useState({
    lpName:"", brand:"", productName:"", batchNumber:"", lotNumber:"",
    thc:"", cbd:"", totalTerpenes:"",
    myrcene:"", caryophyllene:"", limonene:"", pinene:"", terpinolene:"",
    linalool:"", ocimene:"", humulene:"", bisabolol:"",
    cultivationMethod:"Indoor", medium:"", cure:"", cycledays:"", harvest:"",
    lineage:"", breeder:"", labName:"", labMethod:"", testDate:"",
    contactName:"", contactEmail:"", notes:""
  });
  // ── DEMO TOUR STATE ─────────────────────────────────────────
  const [demoActive, setDemoActive] = useState(false);
  const [demoStep, setDemoStep]     = useState(0);
  const [demoRole, setDemoRole]     = useState("staff"); // "staff" | "lp" | "consumer"

  // ── NEW B2B STATE ────────────────────────────────────────
  // Send to Phone
  const [smsPhone, setSmsPhone] = useState("");
  const [smsSent, setSmsSent] = useState(false);
  // Staff Performance
  const [staffPerfTab, setStaffPerfTab] = useState("overview");
  const [staffQuizLog, setStaffQuizLog] = useState(() => { try { return JSON.parse(localStorage.getItem("iterps_quizlog")||"[]"); } catch (_e) { return []; } });
  const saveQuizScore = (staffName, score, total) => {
    const entry = { staffName: staffName||"Anonymous", score, total, pct: Math.round((score/total)*100), ts: new Date().toISOString() };
    const updated = [entry, ...staffQuizLog.slice(0,99)];
    setStaffQuizLog(updated);
    try { localStorage.setItem("iterps_quizlog", JSON.stringify(updated)); db.logEvent("quiz_completed", { updated }); } catch (_e) {}
    return entry;
  };
  // Reorder intelligence
  const [reorderDisp, setReorderDisp] = useState("all");
  // Demand gap
  const [demandGapDisp, setDemandGapDisp] = useState(null);
  // National rollout
  const [nationalTab, setNationalTab] = useState("pipeline");
  // BC Demand Index tab (inside LP portal)
  const [demandIndexPeriod, setDemandIndexPeriod] = useState("current");
  // Feedback flag system
  const [feedbackLog, setFeedbackLog] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  // Email trigger log (placeholder hooks)
  const [emailLog, setEmailLog] = useState([]);
  const fireEmailTrigger = (event, data) => {
    const entry = { event, data, ts: new Date().toISOString() };
    setEmailLog(prev => [entry, ...prev.slice(0,49)]);
  };

  // ── RICH SESSION EVENT TRACKER ─────────────────────────────
  const trackEvent = (type, payload = {}) => {
    const entry = {
      id: Date.now() + Math.random().toString(36).slice(2,7),
      type,
      payload,
      role: onboardRole || "unknown",
      ts: new Date().toISOString(),
      date: new Date().toLocaleDateString("en-CA"),
    };
    // Log to Supabase (non-blocking)
    db.logEvent(type, payload, onboardRole || "unknown");
    setSessionEvents(prev => {
      const updated = [entry, ...prev].slice(0, 500);
      try { localStorage.setItem("iterps_events", JSON.stringify(updated)); } catch (_e) {}
      return updated;
    });
  };

  // ── VIEW PRODUCT (tracks recently viewed) ─────────────────
  const viewProduct = (p, fromPage) => {
    setPrevPage(fromPage || page);
    setSelectedProduct(p);
    setPage("product");
    trackEvent("product_viewed", { id:p.id, name:p.name, brand:p.brand, terpSource:p.terpene_source, topTerpene:Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0]?.[0] });
    setRecentlyViewed(prev => {
      const next = [{ id:p.id, name:p.name, brand:p.brand, category:p.category }, ...prev.filter(r=>r.id!==p.id)].slice(0,12);
      try { localStorage.setItem("iterps_recently_viewed", JSON.stringify(next)); } catch (_e) {}
      return next;
    });
  };

  // ── COA SUBMISSION LOGGER ──────────────────────────────────
  const logCOASubmission = (data) => {
    const entry = {
      id: Date.now().toString(),
      ts: new Date().toISOString(),
      date: new Date().toLocaleDateString("en-CA"),
      status: data.status || "parsed",
      lp: data.lpName || "—",
      product: data.productName || "—",
      batch: data.batchNumber || "—",
      lab: data.labName || "—",
      thc: data.thc || null,
      totalTerpenes: data.totalTerpenes || null,
      terpenesExtracted: Object.keys(data.terpenes || {}).length,
      cannabinoidsExtracted: ["thc","thca","cbd","cbda","cbg","cbn","cbga","cbca"].filter(k => data[k]).length,
      qualityFlags: data.qualityFlags || [],
      parseMethod: data.parseMethod || "ai_coa_parser",
    };
    setCoaLog(prev => {
      const updated = [entry, ...prev].slice(0, 200);
      try { localStorage.setItem("iterps_coa_log", JSON.stringify(updated)); } catch (_e) {}
      return updated;
    });
    trackEvent("coa_submission", { lp: entry.lp, product: entry.product, status: entry.status });
  };
  // Landing page
  const [landingSection, setLandingSection] = useState("dispensary");
  // COA Upload / AI Parser state
  const [coaUploadFile, setCoaUploadFile] = useState(null);
  const [coaUploadB64, setCoaUploadB64] = useState(null);
  const [coaParseStatus, setCoaParseStatus] = useState("idle"); // idle | parsing | review | confirmed | error
  const [coaParseResult, setCoaParseResult] = useState(null);
  const [coaParseError, setCoaParseError] = useState("");
  const [coaParseEdits, setCoaParseEdits] = useState({});
  const [coaShowAllFields, setCoaShowAllFields] = useState(false);

  // ── FOUNDER DATA COLLECTION STATE ─────────────────────────
  const [sessionEvents, setSessionEvents] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iterps_events")||"[]"); } catch (_e) { return []; }
  });
  const [coaLog, setCoaLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem("iterps_coa_log")||"[]"); } catch (_e) { return []; }
  });
  const [lpPipelineState, setLpPipelineState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("iterps_lp_pipeline")||"null");
      if (saved) return saved;
    } catch (_e) {}
    return [];
  });
  const [revenueData, setRevenueData] = useState(() => {
    try {
      const s = localStorage.getItem("iterps_revenue");
      return s ? JSON.parse(s) : { leads:3, trials:1, paidDisp:0, paidLP:3, dispPrice:99, lpPrice:299 };
    } catch (_e) { return { leads:3, trials:1, paidDisp:0, paidLP:3, dispPrice:99, lpPrice:299 }; }
  });
  // LP Demo contact form
  const [lpDemoForm, setLpDemoForm] = useState({ name:"", company:"", email:"", phone:"", skus:"", message:"" });
  const [lpDemoSent, setLpDemoSent] = useState(false);
  // CSV inventory upload
  const [csvUploadData, setCsvUploadData] = useState(null);
  const [csvUploadError, setCsvUploadError] = useState("");
  const [csvPreviewRows, setCsvPreviewRows] = useState([]);
  const [csvImported, setCsvImported] = useState(false);
  const [importedProducts, setImportedProducts] = useState([]);


  // ── STOCK ALERT CHECK (fires on page change) ─────────────

  const ALL_PRODUCTS = [
    ...PRODUCTS,
    ...dbProducts,
    ...importedProducts,
  ];
  const DB_DISPENSARIES = [...DISPENSARIES, ...dbDispensaries];
  // RetailerPage
  React.useEffect(() => {
    if (!page || onboardRole !== "consumer") return;
    const alerts = stockAlerts || [];
    if (!alerts.length) return;
    // Check each alerted product
    alerts.forEach(alert => {
      const prod = ALL_PRODUCTS.find(p=>p.id===alert.productId);
      if (!prod) return;
      const inStock = stockOverrides[prod.id] !== undefined ? stockOverrides[prod.id] : prod.in_stock;
      if (inStock && !alert.notified) {
        showToast("🌿 " + (prod.name) + " is back in stock!", "success");
        // Mark notified
        setStockAlerts(prev => prev.map(a => a.productId===alert.productId ? {...a, notified:true} : a));
      }
    });
  }, [page]);
  const [retSelDisp, setRetSelDisp] = useState("");
  const [retTab, setRetTab] = useState("inventory");
  const [retSearch, setRetSearch] = useState("");
  const [recentlyViewed, setRecentlyViewed] = useState(() => { try { return JSON.parse(localStorage.getItem("iterps_recently_viewed")||"[]"); } catch (_e) { return []; } });
  const [savedFinderAnswers, setSavedFinderAnswers] = useState(() => { try { return JSON.parse(localStorage.getItem("iterps_saved_finder")||"null"); } catch (_e) { return null; } });
  const [retStockFilter, setRetStockFilter] = useState("all"); // all | in | out
  const [stockOverrides, setStockOverrides] = useState(() => { try { return JSON.parse(localStorage.getItem("iterps_stock_overrides")||"{}"); } catch (_e) { return {}; } });
  // ConsumerBrowse
  const [cbBrowseCity, setCbBrowseCity] = useState("any");
  const [cbSearch, setCbSearch] = useState("");
  const [cbCategory, setCbCategory] = useState("");
  const [cbViewProd, setCbViewProd] = useState(null);
  const [cbSort, setCbSort] = useState("");
  // ConsumerProfile
  const [cpLookupPhone, setCpLookupPhone] = useState("");
  const [cpLookupError, setCpLookupError] = useState("");
  const [cpLookupResult, setCpLookupResult] = useState(null);
  // BatchFinderModal
  const [bfSelected, setBfSelected] = useState([]);
  const [bfResults, setBfResults] = useState(null);
  const [bfSearch, setBfSearch] = useState("");
  // RegisterModal
  const [rmName, setRmName] = useState("");
  const [rmPhone, setRmPhone] = useState("");
  const [rmCity, setRmCity] = useState("");
  const [rmConsent, setRmConsent] = useState(false);
  const [rmAgeConsent, setRmAgeConsent] = useState(false);
  const [rmError, setRmError] = useState("");
  const [rmStep, setRmStep] = useState("form");
  // CertificationPage
  const [certName, setCertName] = useState("");
  const [certMods, setCertMods] = useState([]);
  const [certIssuedState, setCertIssuedState] = useState(null);
  // DataExportPage
  const [exportDataState, setExportDataState] = useState(null);
  const [exportGenState, setExportGenState] = useState(false);
  // BatchFinderModal state (hoisted to parent for hook stability)
  // RegisterModal state (hoisted to parent)
  // CertificationPage state (hoisted to parent)
  // DataExportPage state (hoisted to parent)


  // ── SUPABASE: AUTH STATE ─────────────────────────────────────
  const [supaSession, setSupaSession] = useState(null);
  const [supaLoading, setSupaLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSupaSession(session);
      setSupaLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSupaSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── SUPABASE: LOAD DB_DISPENSARIES ──────────────────────────────
  useEffect(() => {
    db.getDispensaries().then(({ data }) => {
      if (data && data.length > 0) setDbDispensaries(data);
    }).catch(() => {});
  }, []);

  // ── SUPABASE: LOAD PRODUCTS ───────────────────────────────────
  useEffect(() => {
    db.getProducts().then(({ data }) => {
      if (data && data.length > 0) setDbProducts(data);
    }).catch(() => {});
  }, []);

  // ── SUPABASE: LOAD PROFILES ───────────────────────────────────
  useEffect(() => {
    db.getAllProfiles().then(({ data }) => {
      if (data) setProfiles(data);
    }).catch(() => {});
  }, []);

  // ── SUPABASE: LOAD LP PIPELINE ────────────────────────────────
  useEffect(() => {
    db.getLPs().then(({ data }) => {
      if (data) setLpPipelineState(data);
    }).catch(() => {});
  }, []);

  // ── SUPABASE: LOAD LEADS ──────────────────────────────────────
  useEffect(() => {
    db.getLeads().then(({ data }) => {
      if (data) setDispOnboardLeads(data);
    }).catch(() => {});
  }, []);

  // ── SUPABASE: LOAD EVENTS ─────────────────────────────────────
  useEffect(() => {
    db.getEvents().then(({ data }) => {
      if (data) setEventLog(data);
    }).catch(() => {});
  }, []);

  // ── SUPABASE: LOAD BATCHES (when dispensary set) ──────────────
  useEffect(() => {
    if (!activeDispensary) return;
    const disp = DB_DISPENSARIES.find(d => d.id === activeDispensary || d.name === activeDispensary);
    if (!disp) return;
    db.getBatches(disp.id).then(({ data }) => {
      if (data) setBatchHistory(prev => {
        const merged = { ...prev };
        data.forEach(b => { merged[b.id] = b; });
        return merged;
      });
    }).catch(() => {});
    db.getStockOverrides(disp.id).then(overrides => {
      if (overrides) setStockOverrides(overrides);
    }).catch(() => {});
  }, [activeDispensary]);


  // Reset helpers for hoisted modal state

  const upsertProfile = useCallback((profile) => {
    setProfiles(prev => {
      const idx = prev.findIndex(p => p.phone === profile.phone);
      const now = new Date().toISOString();
      const next = idx >= 0
        ? prev.map((p,i) => i === idx ? { ...p, ...profile, updatedAt: now } : p)
        : [...prev, { ...profile, createdAt: now, updatedAt: now, productHistory: [], visits: 1 }];
      saveProfiles(next);
      showToast(idx >= 0 ? "Profile updated" : "Customer profile created", "success");
      return next;
    });
  }, [saveProfiles]);

  const addProductToHistory = useCallback((phone, product, rating) => {
    setProfiles(prev => {
      const idx = prev.findIndex(p => p.phone === phone);
      if (idx < 0) return prev;
      const history = prev[idx].productHistory || [];
      const ei = history.findIndex(h => h.productId === product.id);
      const entry = { productId:product.id, name:product.name, brand:product.brand,
        category:product.category, thc:product.thc, terpenes:product.terpenes,
        rating, triedAt:new Date().toISOString(), triedCount: ei >= 0 ? (history[ei].triedCount||1)+1 : 1 };
      const newHistory = ei >= 0 ? history.map((h,i)=>i===ei?entry:h) : [entry,...history];
      const next = prev.map((p,i) => i===idx ? {...p, productHistory:newHistory, updatedAt:new Date().toISOString()} : p);
      saveProfiles(next);
      return next;
    });
  }, [saveProfiles]);

  const buildTerpeneFingerprint = (profile) => {
    const KEYS = ["β-Myrcene","β-Caryophyllene","(+)-Limonene","α-Pinene","Terpinolene","Linalool","β-Ocimene","α-Humulene","α-Bisabolol"];
    const scores = Object.fromEntries(KEYS.map(k=>[k,0]));
    const feelOpts = { relax:["β-Myrcene","Linalool","α-Bisabolol"], energy:["Terpinolene","(+)-Limonene","β-Ocimene"],
      focus:["α-Pinene","(+)-Limonene","Terpinolene"], social:["(+)-Limonene","β-Caryophyllene","Linalool"],
      body:["β-Myrcene","β-Caryophyllene","α-Humulene"], sleep:["β-Myrcene","Linalool","α-Bisabolol"] };
    const answers = profile.finderAnswers || profile.consumerAnswers || {};
    const feeling = answers.feeling || answers.vibe;
    if (feeling && feelOpts[feeling]) feelOpts[feeling].forEach((t,i)=>{ if(scores[t]!==undefined) scores[t]+=(3-i)*10; });
    (profile.productHistory||[]).forEach(h => {
      if (!h.rating || !h.terpenes) return;
      const mult = h.rating==="up"?8:h.rating==="down"?-5:0;
      Object.entries(h.terpenes).forEach(([t,v])=>{ if(scores[t]!==undefined) scores[t]+=mult*v*10; });
    });
    const max = Math.max(...Object.values(scores), 1);
    return Object.fromEntries(Object.entries(scores).map(([k,v])=>[k,Math.max(0,Math.round((v/max)*100))]));
  };

  // ── FRESHNESS HELPER ──────────────────────────────────────
  const getFreshness = (p) => {
    const dateStr = p.packaged_date || p.tested;
    if (!dateStr) return null;
    const packed = new Date(dateStr);
    const now = new Date(); // current date
    const daysSince = Math.floor((now - packed) / (1000 * 60 * 60 * 24));
    const monthsSince = daysSince / 30.44;
    // Terpene degradation reference: meaningful loss starts ~6mo, significant by 12mo
    let label, color, score, note;
    if (monthsSince < 3)       { label = "Fresh";        color = "#16a34a"; score = 100; note = "Packaged " + (Math.round(monthsSince * 30)) + " days ago"; }
    else if (monthsSince < 6)  { label = "Good";         color = "#65a30d"; score = 85;  note = "Packaged " + (Math.round(monthsSince)) + " months ago"; }
    else if (monthsSince < 9)  { label = "Moderate";     color = "#ca8a04"; score = 65;  note = (Math.round(monthsSince)) + " months since packaging — terpenes beginning to degrade"; }
    else if (monthsSince < 12) { label = "Aging";        color = "#ea580c"; score = 45;  note = (Math.round(monthsSince)) + " months — expect terpene loss, especially monoterpenes"; }
    else                        { label = "Old Stock";    color = "#dc2626"; score = 20;  note = (Math.round(monthsSince)) + " months on shelf — significant terpene degradation likely"; }
    return { label, color, score, daysSince: Math.round(daysSince), monthsSince: parseFloat(monthsSince.toFixed(1)), note, dateStr };
  };

  const buildAnonymizedExport = () => {
    const ps = loadProfiles();
    if (ps.length < 3) return null;
    const KEYS = ["β-Myrcene","β-Caryophyllene","(+)-Limonene","α-Pinene","Terpinolene","Linalool","β-Ocimene","α-Humulene","α-Bisabolol"];
    const avg = {};
    KEYS.forEach(k => { const vals=ps.map(p=>buildTerpeneFingerprint(p)[k]||0); avg[k]=Math.round(vals.reduce((a,b)=>a+b,0)/vals.length); });
    const dist = (fn) => { const d={}; ps.forEach(p=>{const v=fn(p)||"unknown"; d[v]=(d[v]||0)+1;}); return d; };
    const topRated = {};
    ps.forEach(p=>(p.productHistory||[]).filter(h=>h.rating==="up").forEach(h=>{
      topRated[h.productId]=topRated[h.productId]||{name:h.name,brand:h.brand,count:0};
      topRated[h.productId].count++;
    }));
    return {
      generatedAt: new Date().toISOString(),
      totalProfiles: ps.length,
      methodology: "All data anonymized. Minimum cell size ≥3 before reporting. No PII included. Terpene fingerprints derived from finder answers and product ratings.",
      avgTerpenePreferences: avg,
      formatDistribution: dist(p=>(p.finderAnswers||p.consumerAnswers||{}).format),
      potencyDistribution: dist(p=>(p.finderAnswers||{}).intensity||(p.consumerAnswers||{}).strength),
      cityDistribution: dist(p=>p.city),
      sensitivityFlags: dist(p=>((p.finderAnswers||{}).avoid||[]).join("+")||"none"),
      topRatedProducts: Object.values(topRated).filter(p=>p.count>=2)
        .sort((a,b)=>b.count-a.count).slice(0,10)
        .map(p=>({name:p.name,brand:p.brand,positiveRatings:p.count})),
    };
  };

  // ── CSV → PRODUCT NORMALISER ──────────────────────────────
  const parseCSVToProducts = useCallback((rows, dispensaryId) => {
    const COL = (row, ...keys) => {
      for (const k of keys) {
        const val = row[k] || row[k.toLowerCase()] || row[k.replace(/_/g,' ')] || "";
        if (val) return val;
      }
      return "";
    };
    const TERPENE_MAP = {
      myrcene:"β-Myrcene", caryophyllene:"β-Caryophyllene", limonene:"(+)-Limonene",
      pinene:"α-Pinene", terpinolene:"Terpinolene", linalool:"Linalool",
      ocimene:"β-Ocimene", humulene:"α-Humulene", bisabolol:"α-Bisabolol",
    };
    return rows.map((row, i) => {
      const name = COL(row, "name", "product_name", "product name", "strain");
      const brand = COL(row, "brand", "lp", "producer", "manufacturer") || "Unknown Brand";
      const thc = parseFloat(COL(row, "thc", "thc %", "thc%")) || 0;
      const cbd = parseFloat(COL(row, "cbd", "cbd %", "cbd%")) || 0;
      const category = COL(row, "category", "type", "product_type", "format") || "Dried Flower";
      const totalTerp = parseFloat(COL(row, "total_terpenes", "total terpenes", "terpenes%", "total_terp")) || null;
      const price = parseFloat(COL(row, "price", "retail_price", "price_per_unit")) || null;
      const sku = COL(row, "sku", "barcode", "sku_id", "product_id") || "csv-" + (Date.now()) + "-" + (i);

      // Parse individual terpenes
      const terpenes = {};
      Object.entries(TERPENE_MAP).forEach(([key, canonical]) => {
        const val = parseFloat(COL(row, key, key+"%", key+" %", key+"_pct") || "0");
        if (val > 0) terpenes[canonical] = val / 100; // assume % input
      });
      // If no terpenes provided, estimate based on category/thc
      if (Object.keys(terpenes).length === 0) {
        terpenes["β-Myrcene"] = 0.003 + Math.random() * 0.003;
        terpenes["β-Caryophyllene"] = 0.002 + Math.random() * 0.002;
      }

      return {
        id: "csv_" + (sku),
        dispensary_id: dispensaryId || "d01",
        name, brand, category,
        plant_type: COL(row, "plant_type", "type_classification", "indica_sativa") || "Unknown",
        thc, cbd,
        batch: COL(row, "batch", "batch_number", "lot") || "CSV-IMPORT",
        lot: COL(row, "lot", "lot_number") || "",
        lab: COL(row, "lab", "testing_lab") || "",
        lab_method: "",
        tested: COL(row, "test_date", "tested", "lab_date") || "",
        total_terpenes: totalTerp,
        terpenes,
        terpene_source: Object.keys(terpenes).length > 2 ? "lp_provided" : "estimated",
        aroma: [],
        genetics: { lineage: COL(row, "lineage", "genetics") || "Unknown", breeder: "", generation: "" },
        cultivation: {
          method: COL(row, "cultivation", "grow_method", "method") || "Unknown",
          medium: "", cure: "", harvest: "",
          cycle_days: parseInt(COL(row, "cycle_days")) || null,
          lp: brand, lp_notes: "Imported from CSV"
        },
        in_stock: true,
        price,
        csv_imported: true,
      };
    }).filter(p => p.name);
  }, []);

  // Merged product list — imported products are overlaid on base dataset

  const filtered = ALL_PRODUCTS.filter(p => {
    if (search) {
      const q = search.toLowerCase();
      const hit = p.name.toLowerCase().includes(q)
        || p.brand.toLowerCase().includes(q)
        || (p.category||"").toLowerCase().includes(q)
        || (p.plant_type||"").toLowerCase().includes(q)
        || Object.keys(p.terpenes).some(t => t.toLowerCase().includes(q))
        || (p.aroma||[]).some(a => a.toLowerCase().includes(q))
        || (p.cultivation?.lp||"").toLowerCase().includes(q)
        || (p.cultivation?.method||"").toLowerCase().includes(q)
        || (p.cultivation?.lineage||"").toLowerCase().includes(q)
        || (p.genetics?.lineage||"").toLowerCase().includes(q)
        || (p.cultivation?.lp_notes||"").toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (filters.terpene && !Object.keys(p.terpenes).includes(filters.terpene)) return false;
    if (filters.category && p.category !== filters.category) return false;
    if (filters.plantType && p.plant_type !== filters.plantType) return false;
    if (filters.city) {
      const disp = DB_DISPENSARIES.find(d => d.id === p.dispensary_id);
      if (!disp || disp.city !== filters.city) return false;
    }
    if (filters.dispensary && p.dispensary_id !== filters.dispensary) return false;
    if (filters.terpSource && p.terpene_source !== filters.terpSource) return false;
    // THC range
    if (filters.thcMin && p.thc < parseFloat(filters.thcMin)) return false;
    if (filters.thcMax && p.thc > parseFloat(filters.thcMax)) return false;
    if (filters.terpMin && (p.total_terpenes||0) < parseFloat(filters.terpMin)) return false;
    if (filters.terpMax && (p.total_terpenes||0) > parseFloat(filters.terpMax)) return false;
    return true;
  }).sort((a, b) => {
    if (filters.sortBy === "thc_desc") return b.thc - a.thc;
    if (filters.sortBy === "thc_asc") return a.thc - b.thc;
    if (filters.sortBy === "terpenes_desc") return (b.total_terpenes||0) - (a.total_terpenes||0);
    if (filters.sortBy === "terpenes_asc") return (a.total_terpenes||0) - (b.total_terpenes||0);
    if (filters.sortBy === "brand") return a.brand.localeCompare(b.brand);
    return a.name.localeCompare(b.name); // default: name
  });

  const dispName = id => DB_DISPENSARIES.find(d => d.id === id)?.name || id;

  // ── AGE GATE ──────────────────────────────────────────────

  const saveNote = useCallback((phone, note) => {
    setProfiles(prev => {
      const next = prev.map(p => p.phone===phone ? {...p, staffNote:note, noteUpdatedAt:new Date().toISOString()} : p);
      saveProfiles(next); return next;
    });
  }, [saveProfiles]);

  // Wishlist helpers
  const toggleWishlist = useCallback((phone, product) => {
    setProfiles(prev => {
      const idx = prev.findIndex(p=>p.phone===phone);
      if (idx<0) return prev;
      const wishlist = prev[idx].wishlist || [];
      const has = wishlist.find(w=>w.productId===product.id);
      const newWishlist = has ? wishlist.filter(w=>w.productId!==product.id)
        : [...wishlist, {productId:product.id, name:product.name, brand:product.brand, category:product.category, addedAt:new Date().toISOString()}];
      const next = prev.map((p,i)=>i===idx?{...p,wishlist:newWishlist,updatedAt:new Date().toISOString()}:p);
      saveProfiles(next); return next;
    });
  }, [saveProfiles]);

  // Save journey snapshot (terpene fingerprint at a point in time)
  const saveJourneySnapshot = useCallback((phone, fingerprint, label) => {
    setProfiles(prev => {
      const idx = prev.findIndex(p=>p.phone===phone);
      if (idx<0) return prev;
      const journey = prev[idx].journey || [];
      const snap = {fingerprint, label, date:new Date().toISOString()};
      const next = prev.map((p,i)=>i===idx?{...p,journey:[...journey,snap],updatedAt:new Date().toISOString()}:p);
      saveProfiles(next); return next;
    });
  }, [saveProfiles]);

  // Save staff certification
  const saveCert = useCallback((staffName, modules) => {
    const cert = { staffName, modules, date:new Date().toISOString(), id:"CERT-" + (Date.now()) };
    const updated = {...completedCerts, [cert.id]:cert};
    setCompletedCerts(updated);
    try { localStorage.setItem("iterps_certs", JSON.stringify(updated)); } catch (_e) {}
    return cert;
  }, [completedCerts]);

  // New arrival matcher — find top matching customer profiles for a product
  const matchNewArrival = useCallback((product) => {
    const scored = profiles.map(profile => {
      const answers = profile.finderAnswers || profile.consumerAnswers || {};
      const result = scoreProduct(product, answers);
      return { profile, pct: result.pct, score: result.score };
    }).filter(x=>x.score>-999).sort((a,b)=>b.pct-a.pct).slice(0,8);
    setNewArrivalMatches({ product, matches: scored });
  }, [profiles]);

  // Batch scorer — find products that score well across multiple profiles
  const scoreBatch = useCallback((selectedProfiles) => {
    return ALL_PRODUCTS.filter(p=>p.in_stock).map(product => {
      const scores = selectedProfiles.map(profile => {
        const answers = profile.finderAnswers || profile.consumerAnswers || {};
        return scoreProduct(product, answers).pct;
      });
      const min = Math.min(...scores);
      const avg = Math.round(scores.reduce((a,b)=>a+b,0)/scores.length);
      return { product, min, avg, scores };
    }).filter(x=>x.min>30).sort((a,b)=>b.min===a.min?b.avg-a.avg:b.min-a.min).slice(0,6);
  }, []);

  // Cultivar gap analysis — find terpene profiles with high preference but low supply
  const buildGapAnalysis = useCallback(() => {
    const ps = loadProfiles();
    if (ps.length < 3) return null;
    const KEYS = ["β-Myrcene","β-Caryophyllene","(+)-Limonene","α-Pinene","Terpinolene","Linalool","β-Ocimene","α-Humulene","α-Bisabolol"];

    // Average consumer preference per terpene (0-100)
    const demand = {};
    KEYS.forEach(k => {
      const vals = ps.map(p=>buildTerpeneFingerprint(p)[k]||0);
      demand[k] = Math.round(vals.reduce((a,b)=>a+b,0)/vals.length);
    });

    // Average supply per terpene (avg across in-stock products, normalized)
    const supply = {};
    const inStock = ALL_PRODUCTS.filter(p=>p.in_stock);
    KEYS.forEach(k => {
      const vals = inStock.map(p=>(p.terpenes[k]||0));
      const avg = vals.reduce((a,b)=>a+b,0)/vals.length;
      supply[k] = Math.round((avg / 1.5) * 100); // normalize ~1.5% as max common level
    });

    // Gap = demand minus supply (positive = underserved)
    const gaps = KEYS.map(k => ({
      terpene: k,
      demand: demand[k],
      supply: Math.min(100, supply[k]),
      gap: demand[k] - Math.min(100, supply[k]),
    })).sort((a,b)=>b.gap-a.gap);

    // Find top products that fill each gap
    const gapProducts = gaps.slice(0,3).map(g => ({
      ...g,
      topProducts: inStock.filter(p=>(p.terpenes[g.terpene]||0)>0.3)
        .sort((a,b)=>(b.terpenes[g.terpene]||0)-(a.terpenes[g.terpene]||0))
        .slice(0,3).map(p=>({name:p.name,brand:p.brand,level:p.terpenes[g.terpene]}))
    }));

    return { demand, supply, gaps, gapProducts, profileCount: ps.length };
  }, [loadProfiles]);

  const ageGateScreen = (
    <div style={{ minHeight:"100vh", background:"#f7f5f0", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Mono',monospace", padding:20 }}>
      <style>{STYLES}</style>
      <div style={{ maxWidth:440, width:"100%", textAlign:"center" }}>
        <div style={{ width:60, height:60, borderRadius:"50%", background:"#1a2e1a", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", fontSize:26 }}>⚗️</div>
        <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:30, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>iTerps</h1>
        <p style={{ fontSize:12, color:"#6b7280", marginBottom:28, fontFamily:"'DM Mono',monospace" }}>Educational Cannabis Compound Information Platform</p>
        <div className="card" style={{ padding:28, marginBottom:20 }}>
          <p style={{ fontSize:14, color:"#1a2e1a", fontWeight:500, marginBottom:8 }}>Age Verification Required</p>
          <p style={{ fontSize:12, color:"#6b7280", marginBottom:20, lineHeight:1.6 }}>You must be 19 years of age or older to access cannabis product information.</p>
          <button className="btn-primary" style={{ width:"100%", marginBottom:10 }} onClick={() => setAgeOk(true)}>I am 19 years of age or older →</button>
          <p style={{ fontSize:10, color:"#9ca3af" }}>By continuing you confirm you meet the minimum age requirement in your province.</p>
        </div>
        <p style={{ fontSize:10, color:"#9ca3af", lineHeight:1.5 }}>Educational use only · Cannabis Act (S.C. 2018, c. 16) Compliant</p>
      </div>
    </div>
  );

  const aboutScreen = (
    <div style={{ fontFamily:"'DM Mono',monospace", minHeight:"100vh", background:"#f7f5f0" }}>
      <style>{STYLES}</style>
      <div style={{ background:"linear-gradient(160deg,#0f1f0f 0%,#1a2e1a 60%,#2d4a2d 100%)", padding:"52px 20px 44px", textAlign:"center" }}>
        <div style={{ maxWidth:680, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:28 }}>
            <div style={{ width:40, height:40, borderRadius:10, background:"rgba(255,255,255,0.1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>⚗️</div>
            <span style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0" }}>iTerps</span>
          </div>
          <div style={{ display:"inline-flex", gap:2, background:"rgba(255,255,255,0.08)", borderRadius:12, padding:3, marginBottom:28 }}>
            {[["dispensary","🌿 Dispensaries"],["lp","🏭 Producers"],["consumer","👤 Consumers"]].map(([k,l]) => (
              <button key={k} onClick={() => setLandingSection(k)}
                style={{ padding:"7px 16px", borderRadius:9, border:"none", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer",
                  background: landingSection===k ? "#fff" : "transparent",
                  color: landingSection===k ? "#1a2e1a" : "rgba(255,255,255,0.55)",
                  fontWeight: landingSection===k ? 600 : 400, transition:"all 0.2s" }}>
                {l}
              </button>
            ))}
          </div>
          {(() => {
            const headlines = { dispensary:"Your staff already know what the customer wants. Now they can prove it.", lp:"Your COA data deserves to be seen. Right now it's filed in a PDF.", consumer:"Find cannabis that actually works for you." };
            const subs = { dispensary:"iTerps gives dispensary teams a terpene-first product finder, customer passport system, staff training, and real COA data — in one tool built for the floor.", lp:"iTerps gives Licensed Producers a verified presence in the only platform built around terpene chemistry — not THC percentage.", consumer:"Not by strain name. Not by THC percentage. By what it actually smells like — and the chemistry behind it." };
            const points = {
              dispensary:[["🔍","Strain Finder","Recommends products by terpene profile, format, and potency — not strain name."],["🪪","Terpene Passport","Build a chemical fingerprint for regulars. They scan a QR, you see their full preference history instantly."],["📊","Batch Tracker","Track terpene drift across batches. Know before your customers when a cultivar's profile has shifted."],["🎓","Staff Training","Structured terpene science modules and a 20-question flash quiz."],["📈","Benchmarking","See how your store's terpene diversity compares to the BC network average."],["✓","Compliant","Every consumer-facing line follows Health Canada's promotional restrictions."]],
              lp:[["⚡","AI COA Parser","Drag in a PDF from any accredited Canadian lab. Extracted in seconds."],["🔬","Batch Consistency","Track terpene profile across harvests. Document consistency for retailers and investors."],["🏷","Verified Badge","LP-verified data gets a badge across all retailer and consumer-facing views."],["📡","Consumer Match Data","See which terpene profiles consumers are searching for most."],["📋","Retailer Intelligence","Know which stores carry your closest competitors."],["🌍","Global Scale","Canada now, Germany, Netherlands, and US states next."]],
              consumer:[["👃","Aroma-First Matching","Answer five questions about how things smell. We match on chemistry, not marketing."],["🪪","Terpene Passport","Save your preferences. Build a profile that gets more accurate over time."],["📱","Show a Budtender","Your passport generates a QR code for any participating store."],["🔍","Find Similar Products","Loved something? Find everything with a matching terpene profile."],["🌱","New to Cannabis?","A simpler 3-question guide that skips the jargon."],["✓","No Effect Claims","We show you the chemistry. You decide."]]
            };
            const pts = points[landingSection] || points.dispensary;
            return (
              <>
                <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:34, fontWeight:300, color:"#f0f4f0", marginBottom:14, lineHeight:1.2 }}>{headlines[landingSection]}</h1>
                <p style={{ fontSize:13, color:"rgba(240,244,240,0.6)", lineHeight:1.7, marginBottom:36, fontFamily:"'DM Mono',monospace", maxWidth:540, margin:"0 auto 36px" }}>{subs[landingSection]}</p>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10, maxWidth:700, margin:"0 auto 36px", textAlign:"left" }}>
                  {pts.map(([icon,title,body]) => (
                    <div key={title} style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:12, padding:"16px 14px" }}>
                      <div style={{ fontSize:20, marginBottom:8 }}>{icon}</div>
                      <div style={{ fontFamily:"'Fraunces',serif", fontSize:14, color:"#f0f4f0", marginBottom:4 }}>{title}</div>
                      <div style={{ fontSize:11, color:"rgba(240,244,240,0.5)", lineHeight:1.5 }}>{body}</div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
          <button onClick={() => setShowAbout(true)}
            style={{ padding:"13px 36px", borderRadius:10, border:"none", background:"#f0f4f0", color:"#1a2e1a", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:600 }}>
            Get Started →
          </button>
          <p style={{ fontSize:10, color:"rgba(240,244,240,0.3)", marginTop:14, fontFamily:"'DM Mono',monospace" }}>Educational use only · Cannabis Act (S.C. 2018, c. 16) Compliant</p>
        </div>
      </div>
    </div>
  );

  // ── ONBOARDING FLOW ───────────────────────────────────────
  const OnboardScreen = () => {
    const STEPS = [
      {
        id: "welcome",
        render: () => (
          <div style={{ textAlign:"center" }}>
            <div style={{ width:72, height:72, borderRadius:20, background:"#1a2e1a", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 24px", fontSize:32 }}>⚗️</div>
            <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:34, fontWeight:300, color:"#1a2e1a", marginBottom:10, lineHeight:1.2 }}>Welcome to iTerps</h1>
            <p style={{ fontSize:13, color:"#6b7280", lineHeight:1.7, marginBottom:28, fontFamily:"'DM Mono',monospace", maxWidth:400, margin:"0 auto 28px" }}>
              The cannabis industry&#39;s first terpene-first education and matching platform. Built on chemistry, not marketing.
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:10, maxWidth:340, margin:"0 auto 24px" }}>
              {[
                ["staff", "🌿", "I'm dispensary staff", "Budtender tools, customer matching, inventory"],
                ["lp", "🏭", "I represent a Licensed Producer", "Terpene data, retailer intelligence, COA portal"],
                ["consumer", "👤", "I am a cannabis consumer", "Find products matched to my preferences"],
                ["kiosk", "📱", "Set up in-store tablet", "Lock this tablet to a dispensary for customer self-serve"],
              ].map(([role, icon, label, sub]) => (
                <button key={role} onClick={() => setOnboardRole(role)}
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 18px", borderRadius:12,
                    border:"2px solid " + (onboardRole===role ? "#4a7c59" : "#e8e4de"),
                    background: onboardRole===role ? "#f0fdf4" : "#fff",
                    cursor:"pointer", textAlign:"left", transition:"all 0.15s" }}>
                  <span style={{ fontSize:22, flexShrink:0 }}>{icon}</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace" }}>{label}</div>
                    <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{sub}</div>
                  </div>
                  {onboardRole===role && <span style={{ marginLeft:"auto", color:"#4a7c59", fontSize:18 }}>✓</span>}
                </button>
              ))}
            </div>
            <button onClick={() => onboardRole && setOnboardStep(1)} disabled={!onboardRole}
              style={{ padding:"12px 32px", borderRadius:10, border:"none", background: onboardRole?"#1a2e1a":"#e8e4de",
                color: onboardRole?"#fff":"#9ca3af", fontSize:13, fontFamily:"'DM Mono',monospace",
                cursor: onboardRole?"pointer":"default", transition:"all 0.2s", fontWeight:500 }}>
              Continue →
            </button>
          </div>
        )
      },
      {
        id: "features",
        render: () => {
          const content = {
            staff: {
              headline: "Your dispensary's terpene command centre",
              points: [
                ["🔍", "Find My Strain", "7-question guided matcher that recommends products by terpene profile, sensitivity, and intended effect"],
                ["👤", "Customer Profiles", "Build terpene passports for regulars. Log ratings, track preferences, batch-match groups"],
                ["📋", "Retailer Dashboard", "Your store inventory analysed by terpene chemistry. No guesswork, just data"],
                ["🎓", "Staff Certifications", "Module-based training with printable certs. Raise your team's science literacy"],
              ]
            },
            lp: {
              headline: "Terpene intelligence for cultivators",
              points: [
                ["📊", "Market Gap Analysis", "See which terpene profiles have high consumer demand but low current supply"],
                ["🔬", "COA Submission Portal", "Submit lab test data directly. Get your products matched to real consumer preferences"],
                ["📈", "Retailer Intelligence", "Understand how your cultivars perform across dispensaries in the network"],
                ["🌐", "Global Expansion Ready", "Terpene data is jurisdiction-agnostic. Same platform, every legal market"],
              ]
            },
            consumer: {
              headline: "Find what actually works for you",
              points: [
                ["🌿", "Terpene Matching", "Answer 5 questions about how you want to feel. We match by chemistry, not strain names"],
                ["📖", "Browse & Learn", "Sort 200+ products by terpene, read plain-language science behind each compound"],
                ["🪪", "Terpene Passport", "Track what you&#39;ve tried, rate it, build a chemical fingerprint of your preferences"],
                ["🔒", "Privacy First", "Your data stays on your device. No accounts, no ads, no data selling"],
              ]
            }
          };
          const c = content[onboardRole] || content.staff;
          return (
            <div>
              <div style={{ textAlign:"center", marginBottom:28 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>{c.headline}</h2>
                <p style={{ fontSize:12, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Here&#39;s what&#39;s waiting for you</p>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:28 }}>
                {c.points.map(([icon, title, desc]) => (
                  <div key={title} style={{ display:"flex", gap:14, padding:"14px 16px", background:"#fff", borderRadius:12, border:"1px solid #e8e4de", alignItems:"flex-start" }}>
                    <span style={{ fontSize:22, flexShrink:0, marginTop:1 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{title}</div>
                      <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={() => setOnboardStep(0)} style={{ padding:"11px 20px", borderRadius:10, border:"1px solid #e8e4de", background:"#fff", color:"#6b7280", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>← Back</button>
                <button onClick={() => setOnboardStep(2)} style={{ flex:1, padding:"12px 0", borderRadius:10, border:"none", background:"#1a2e1a", color:"#fff", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:500 }}>See how it works →</button>
              </div>
            </div>
          );
        }
      },
      {
        id: "start",
        render: () => {
          const destinations = {
            staff: { page: "finder", label: "Open Finder", icon: "🔍", sub: "Start matching customers to products" },
            lp: { page: "lp", label: "Open LP Portal", icon: "🏭", sub: "Submit COA data and explore insights" },
            consumer: { mode: "consumer", label: "Find My Strain", icon: "🌿", sub: "Answer 5 questions to get matched" },
            kiosk: { mode: "consumer", label: "Configure Kiosk", icon: "📱", sub: "Set store and PIN for tablet mode" },
          };
          // Kiosk setup flow
          if (onboardRole === "kiosk") {
            const cities = [...new Set(DB_DISPENSARIES.map(d=>d.city))].sort();
            const [kcity, setKcity] = React.useState("");
            const [kdisp, setKdisp] = React.useState("");
            const [kpin1, setKpin1] = React.useState("");
            const [kpin2, setKpin2] = React.useState("");
            const pinMatch = kpin1.length >= 4 && kpin1 === kpin2;
            return (
              <div>
                <div style={{ textAlign:"center", marginBottom:24 }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>📱</div>
                  <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Set up in-store tablet</h2>
                  <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>Customers will only see products from your store. Enter a PIN to exit kiosk mode.</p>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:20 }}>
                  <select className="sel" value={kcity} onChange={e=>{setKcity(e.target.value); setKdisp("");}} style={{ fontSize:13, padding:"10px 14px" }}>
                    <option value="">— Select city —</option>
                    {cities.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                  {kcity && (
                    <select className="sel" value={kdisp} onChange={e=>setKdisp(e.target.value)} style={{ fontSize:13, padding:"10px 14px" }}>
                      <option value="">— Select your store —</option>
                      {DB_DISPENSARIES.filter(d=>d.city===kcity).map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  )}
                  {kdisp && (
                    <>
                      <div style={{ padding:"10px 14px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, fontSize:12, fontFamily:"'DM Mono',monospace", color:"#166534" }}>
                        ✓ {DB_DISPENSARIES.find(d=>d.id===kdisp)?.name} — {ALL_PRODUCTS.filter(p=>p.dispensary_id===kdisp).length} products available
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Set admin PIN (4+ digits to exit kiosk mode)</div>
                        <div style={{ display:"flex", gap:8 }}>
                          <input type="password" inputMode="numeric" placeholder="PIN" value={kpin1} onChange={e=>setKpin1(e.target.value)}
                            style={{ flex:1, padding:"9px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:13, fontFamily:"'DM Mono',monospace", outline:"none", textAlign:"center", letterSpacing:"0.2em" }} />
                          <input type="password" inputMode="numeric" placeholder="Confirm PIN" value={kpin2} onChange={e=>setKpin2(e.target.value)}
                            style={{ flex:1, padding:"9px 12px", border:"1px solid " + (kpin2 && !pinMatch?"#f87171":kpin2&&pinMatch?"#86efac":"#e8e4de"), borderRadius:8, fontSize:13, fontFamily:"'DM Mono',monospace", outline:"none", textAlign:"center", letterSpacing:"0.2em" }} />
                        </div>
                        {kpin2 && !pinMatch && <div style={{ fontSize:10, color:"#dc2626", fontFamily:"'DM Mono',monospace", marginTop:4 }}>PINs don&#39;t match</div>}
                        {pinMatch && <div style={{ fontSize:10, color:"#16a34a", fontFamily:"'DM Mono',monospace", marginTop:4 }}>✓ PIN set</div>}
                      </div>
                    </>
                  )}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>setOnboardStep(1)} style={{ padding:"11px 20px", borderRadius:10, border:"1px solid #e8e4de", background:"#fff", color:"#6b7280", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>← Back</button>
                  <button onClick={()=>{ if(kdisp && pinMatch){ setKioskMode(true); setKioskDispensary(kdisp); setKioskPin(kpin1); setOnboardDone(true); setConsumerMode(true); setPage("consumer"); setConsumerTab("find"); }}}
                    disabled={!kdisp || !pinMatch}
                    style={{ flex:1, padding:"12px 0", borderRadius:10, border:"none", background:kdisp&&pinMatch?"#1a2e1a":"#e8e4de", color:kdisp&&pinMatch?"#fff":"#9ca3af", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:kdisp&&pinMatch?"pointer":"default", fontWeight:500 }}>
                    Launch Kiosk →
                  </button>
                </div>
              </div>
            );
          }
          const dest = destinations[onboardRole] || destinations.staff;
          // Staff: show store picker first if no store selected
          if (onboardRole === "staff" && !activeDispensary) {
            const cities = [...new Set(DB_DISPENSARIES.map(d=>d.city))].sort();
            const [picCity, setPicCity] = React.useState("");
            return (
              <div>
                <div style={{ textAlign:"center", marginBottom:24 }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
                  <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Which store are you at?</h2>
                  <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>This sets your inventory view and location context throughout the app.</p>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
                  <select className="sel" value={picCity} onChange={e=>{setPicCity(e.target.value);}} style={{ fontSize:13, padding:"10px 14px" }}>
                    <option value="">— Select your city —</option>
                    {cities.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                  {picCity && (
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {DB_DISPENSARIES.filter(d=>d.city===picCity).map(d=>(
                        <button key={d.id} onClick={()=>{ setActiveDispensary(d.id); setRetSelDisp(d.id); }}
                          style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderRadius:12,
                            border:"2px solid " + (activeDispensary===d.id?"#4a7c59":"#e8e4de"),
                            background: activeDispensary===d.id?"#f0fdf4":"#fff", cursor:"pointer", textAlign:"left" }}>
                          <span style={{ fontSize:20 }}>🏪</span>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace" }}>{d.name}</div>
                            <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{d.address}</div>
                          </div>
                          {activeDispensary===d.id && <span style={{ marginLeft:"auto", color:"#4a7c59", fontSize:18 }}>✓</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>setOnboardStep(1)} style={{ padding:"11px 20px", borderRadius:10, border:"1px solid #e8e4de", background:"#fff", color:"#6b7280", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>← Back</button>
                  <button onClick={()=>{ if(activeDispensary) setOnboardDone(true); setPage("finder"); }} disabled={!activeDispensary}
                    style={{ flex:1, padding:"12px 0", borderRadius:10, border:"none", background:activeDispensary?"#1a2e1a":"#e8e4de", color:activeDispensary?"#fff":"#9ca3af", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:activeDispensary?"pointer":"default", fontWeight:500 }}>
                    Enter {activeDispensary ? DB_DISPENSARIES.find(d=>d.id===activeDispensary)?.name : "store"} →
                  </button>
                </div>
                <button onClick={()=>{ setOnboardDone(true); setPage("finder"); }} style={{ width:"100%", marginTop:8, padding:"9px 0", borderRadius:10, border:"none", background:"transparent", color:"#9ca3af", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Skip — explore without a store context</button>
              </div>
            );
          }
          // LP: show LP company picker if no activeLp set
          if (onboardRole === "lp" && !activeLp) {
            const lpNames = [...new Set(ALL_PRODUCTS.filter(p=>p.cultivation?.lp).map(p=>p.cultivation.lp))].sort();
            return (
              <div>
                <div style={{ textAlign:"center", marginBottom:24 }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>🏭</div>
                  <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Which Licensed Producer are you?</h2>
                  <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>Your portal will only show your products and data. Network-level demand intelligence is available to all LPs.</p>
                </div>
                <div style={{ marginBottom:16 }}>
                  <select className="sel" value={activeLp} onChange={e=>setActiveLp(e.target.value)} style={{ width:"100%", fontSize:13, padding:"10px 14px" }}>
                    <option value="">— Select your company —</option>
                    {lpNames.map(lp=><option key={lp} value={lp}>{lp}</option>)}
                  </select>
                  {activeLp && (
                    <div style={{ marginTop:12, padding:"12px 16px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10 }}>
                      <div style={{ fontSize:11, color:"#166534", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>Your verified products in the network:</div>
                      <div style={{ fontSize:22, fontWeight:500, fontFamily:"'Fraunces',serif", color:"#1a2e1a" }}>
                        {ALL_PRODUCTS.filter(p=>p.cultivation?.lp===activeLp).length} SKUs
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginBottom:8, padding:"10px 14px", background:"#fef9f0", border:"1px solid #fed7aa", borderRadius:10, fontSize:11, color:"#92400e", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>
                  ⚠ Don&#39;t see your company? Use the COA submission portal to get your products added.
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={()=>setOnboardStep(1)} style={{ padding:"11px 20px", borderRadius:10, border:"1px solid #e8e4de", background:"#fff", color:"#6b7280", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>← Back</button>
                  <button onClick={()=>{ if(activeLp){ setOnboardDone(true); setPage("lp"); }}} disabled={!activeLp}
                    style={{ flex:1, padding:"12px 0", borderRadius:10, border:"none", background:activeLp?"#1a2e1a":"#e8e4de", color:activeLp?"#fff":"#9ca3af", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:activeLp?"pointer":"default", fontWeight:500 }}>
                    Enter LP Portal →
                  </button>
                </div>
                <button onClick={()=>{ setOnboardDone(true); setPage("lp"); }} style={{ width:"100%", marginTop:8, padding:"9px 0", borderRadius:10, border:"none", background:"transparent", color:"#9ca3af", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Skip — browse as guest</button>
              </div>
            );
          }
          return (
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:56, marginBottom:16 }}>🎯</div>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>You&#39;re all set</h2>
              <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:28, lineHeight:1.7 }}>
                iTerps is built on peer-reviewed terpene science and Cannabis Act compliant language.<br/>No health claims. No hype. Just chemistry.
              </p>
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:14, padding:"20px 24px", marginBottom:24, textAlign:"left" }}>
                <div style={{ fontSize:10, color:"#4a7c59", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Recommended starting point</div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:26 }}>{dest.icon}</span>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace" }}>{dest.label}</div>
                    <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{dest.sub}</div>
                  </div>
                </div>
              </div>
              <button onClick={() => {
                setOnboardDone(true);
                if (dest.mode === "consumer") { setConsumerMode(true); setPage("consumer"); setConsumerTab("find"); setDemoRole("consumer"); setDemoActive(true); setDemoStep(0); }
                else { setPage(dest.page || "home"); setDemoRole(onboardRole === "lp" ? "lp" : "staff"); setDemoActive(true); setDemoStep(0); }
              }} style={{ width:"100%", padding:"13px 0", borderRadius:10, border:"none", background:"#1a2e1a", color:"#fff", fontSize:14, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:500, marginBottom:12 }}>
                {dest.label} →
              </button>
              <button onClick={() => {
                setOnboardDone(true);
                if (dest.mode === "consumer") {
                  setConsumerMode(true);
                  setPage("consumer");
                  setConsumerTab("browse");
                } else {
                  setPage(onboardRole === "lp" ? "lp" : "home");
                }
              }}
                style={{ width:"100%", padding:"11px 0", borderRadius:10, border:"1px solid #e8e4de", background:"transparent", color:"#6b7280", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                Explore on my own
              </button>
              <p style={{ fontSize:10, color:"#d1d5db", marginTop:16, fontFamily:"'DM Mono',monospace" }}>Cannabis Act (S.C. 2018, c. 16) Compliant · Educational Use Only</p>
            </div>
          );
        }
      }
    ];

    const step = STEPS[onboardStep];
    const progress = ((onboardStep) / (STEPS.length - 1)) * 100;

    return (
      <div style={{ minHeight:"100vh", background:"#f7f5f0", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'DM Mono',monospace", padding:20 }}>
        <style>{STYLES}</style>
        <div style={{ maxWidth:480, width:"100%" }}>
          <button onClick={()=>{ if(logoClickTimer.current){ clearTimeout(logoClickTimer.current); logoClickTimer.current=null; setShowFounderUnlock(true); } else { logoClickTimer.current=setTimeout(()=>{ logoClickTimer.current=null; },280); }}} style={{ display:"flex", alignItems:"center", gap:8, background:"none", border:"none", cursor:"pointer", marginBottom:24 }} title="Double-click: founder access">
            <div style={{ width:28, height:28, borderRadius:7, background:"#1a2e1a", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>⚗️</div>
            <span style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a" }}>iTerps</span>
          </button>
          {/* Progress */}
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:28 }}>
            <div style={{ flex:1, height:3, background:"#e8e4de", borderRadius:2 }}>
              <div style={{ height:3, background:"#4a7c59", borderRadius:2, width:(progress) + "%", transition:"width 0.4s ease" }} />
            </div>
            <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{onboardStep + 1} of {STEPS.length}</span>
          </div>
          <div className="card" style={{ padding:28 }}>
            {step.render()}
          </div>
        </div>
      </div>
    );
  };
  const onboardScreen = <OnboardScreen />;

  // ── QR CODE MODAL ─────────────────────────────────────────


  const QRModal = () => {
    if (!qrModal) return null;
    const url = "https://iterps.ca/products/" + (qrModal.id) + "?ref=qr";
    const qrData = "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=" + (encodeURIComponent(url)) + "&bgcolor=1a2e1a&color=f0f4f0&margin=10";
    return (
      <div className="overlay" onClick={() => setQrModal(null)}>
        <div className="modal" style={{ maxWidth:400, textAlign:"center" }} onClick={e => e.stopPropagation()}>
          <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.1em", marginBottom:6 }}>SHELF TAG QR CODE</div>
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:400, color:"#1a2e1a", marginBottom:4 }}>{qrModal.name}</h2>
          <p style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:24 }}>{qrModal.brand}</p>
          <div className="qr-box" style={{ marginBottom:20 }}>
            <img src={qrData} alt="QR Code" style={{ width:180, height:180, borderRadius:8 }} />
            <p style={{ fontSize:10, color:"rgba(240,244,240,0.6)", fontFamily:"'DM Mono',monospace" }}>Scan to view chemical composition</p>
          </div>
          <div style={{ background:"#f7f5f0", borderRadius:10, padding:14, marginBottom:16 }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>PRODUCT URL</div>
            <div style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", wordBreak:"break-all" }}>{url}</div>
          </div>
          <div className="info-box" style={{ marginBottom:16, textAlign:"left" }}>
            <span>ℹ️</span>
            <p style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Place this QR code on shelf tags or product displays. Consumers scan to access factual chemical composition data. No purchase facilitation.</p>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn-primary" style={{ flex:1 }} onClick={() => { window.open(qrData, '_blank'); }}>Download QR</button>
            <button className="btn-ghost" style={{ flex:1 }} onClick={() => setQrModal(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  // ── COMPOUND MODAL ─────────────────────────────────────────
  const CompoundModal = () => {
    if (!compound) return null;
    const data = compoundType === "terpene" ? TERPENES[compound] : CANNABINOIDS[compound];
    if (!data) return null;
    return (
      <div className="overlay" onClick={() => setCompound(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.1em" }}>
                  {compoundType === "terpene" ? "TERPENE" : "CANNABINOID"} · {data.type}
                </div>
                {data.rare && <span style={{ fontSize:9, background:"#faf5ff", color:"#7c3aed", border:"1px solid #ddd6fe", borderRadius:4, padding:"1px 6px", fontFamily:"'DM Mono',monospace" }}>Rare</span>}
                {data.sensitizer && <span style={{ fontSize:9, background:"#fffbeb", color:"#b45309", border:"1px solid #fde68a", borderRadius:4, padding:"1px 6px", fontFamily:"'DM Mono',monospace" }}>⚠ Sensitizer</span>}
                {data.degradation_marker && <span style={{ fontSize:9, background:"#f0f9ff", color:"#0369a1", border:"1px solid #7dd3fc", borderRadius:4, padding:"1px 6px", fontFamily:"'DM Mono',monospace" }}>📊 Freshness marker</span>}
              </div>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:400, color:"#1a2e1a" }}>{compound}</h2>
              {data.commonName && data.commonName !== compound && <p style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Common name: {data.commonName}</p>}
              {data.fullName && <p style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{data.fullName}</p>}
            </div>
            <button onClick={() => setCompound(null)} style={{ background:"#f3f4f6", border:"none", borderRadius:8, padding:"6px 12px", cursor:"pointer", color:"#6b7280" }}>✕</button>
          </div>

          {/* Plain English first — most important for consumers */}
          {compoundType === "terpene" && data.plain && (
            <div style={{ background:"#1a2e1a", borderRadius:12, padding:16, marginBottom:14 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                <span style={{ fontSize:16 }}>💬</span>
                <div style={{ fontSize:9, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace", letterSpacing:"0.1em" }}>IN PLAIN ENGLISH</div>
              </div>
              <p style={{ fontSize:13, color:"#f0f4f0", lineHeight:1.75, margin:0 }}>{data.plain}</p>
            </div>
          )}
          {compoundType === "cannabinoid" && data.plain_staff && (
            <div style={{ background:"#1a2e1a", borderRadius:12, padding:16, marginBottom:14 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                <span style={{ fontSize:16 }}>💬</span>
                <div style={{ fontSize:9, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace", letterSpacing:"0.1em" }}>WHAT THIS MEANS</div>
              </div>
              <p style={{ fontSize:13, color:"#f0f4f0", lineHeight:1.75, margin:0 }}>{data.plain_staff}</p>
            </div>
          )}

          {/* Sensitizer warning box */}
          {data.sensitizer && (
            <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"10px 14px", marginBottom:14 }}>
              <div style={{ fontSize:11, color:"#92400e", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:2 }}>⚠ Known Sensitizer</div>
              <div style={{ fontSize:11, color:"#92400e", lineHeight:1.5 }}>At high concentrations this compound can cause respiratory irritation. Individuals with terpene sensitivities should be aware. If piney strains feel harsh, this may be a factor.</div>
            </div>
          )}

          <div style={{ background:"#f7f5f0", borderRadius:10, padding:12, marginBottom:16 }}>
            <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", marginBottom:4 }}>IUPAC NAME</div>
            <p style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", fontStyle:"italic", lineHeight:1.5 }}>{data.iupac}</p>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
            {[
              ["Formula", data.formula],
              ["Mol. Weight", data.mw],
              ["Boiling Point", data.bp || "N/A"],
              compoundType === "terpene" ? ["Found In", data.plants?.slice(0,3).join(", ")] : ["Discovery", data.discovery],
              compoundType === "terpene" ? ["Aroma Notes", data.aroma?.join(", ")] : ["Receptors", data.receptors?.slice(0,2).join(", ")],
              ...(compoundType === "cannabinoid" && data.formula !== "N/A" ? [["Type", data.type]] : []),
            ].filter(Boolean).map(([l,v]) => (
              <div key={l} className="stat">
                <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{l.toUpperCase()}</div>
                <div style={{ fontSize:12, color:"#1a2e1a", fontWeight:500 }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ background:"#f7f5f0", borderRadius:10, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", marginBottom:6 }}>RESEARCH SUMMARY</div>
            <p style={{ fontSize:12, color:"#374151", lineHeight:1.7 }}>{data.summary}</p>
          </div>
          <div className="info-box" style={{ marginBottom:12 }}>
            <span>📚</span>
            <p style={{ fontSize:10, color:"#374151", fontStyle:"italic", fontFamily:"'DM Mono',monospace" }}>{data.lit}</p>
          </div>
          <p style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Educational purposes only. Not medical advice. Information derived from published scientific literature.</p>
        </div>
      </div>
    );
  };

  // ── CULTIVATION MODAL ─────────────────────────────────────
  const CultivationModal = () => {
    if (!cultivationModal) return null;
    const p = cultivationModal;
    const method = p.cultivation?.method ? CULTIVATION_METHODS[p.cultivation.method] : null;
    const medium = p.cultivation?.medium ? GROWING_MEDIA[p.cultivation.medium] : null;
    const cure = p.cultivation?.cure ? CURE_METHODS[p.cultivation.cure] : null;
    const harvest = p.cultivation?.harvest ? HARVEST_METHODS[p.cultivation.harvest] : null;
    return (
      <div className="overlay" onClick={() => setCultivationModal(null)}>
        <div className="modal" onClick={e => e.stopPropagation()}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
            <div>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.1em", marginBottom:4 }}>CULTIVATION PROFILE</div>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:400, color:"#1a2e1a" }}>{p.name}</h2>
              <p style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", display:"flex", alignItems:"center", gap:6 }}>
                LP: {p.cultivation.lp} <LPBadge lpName={p.cultivation.lp} />
              </p>
            </div>
            <button onClick={() => setCultivationModal(null)} style={{ background:"#f3f4f6", border:"none", borderRadius:8, padding:"6px 12px", cursor:"pointer", color:"#6b7280" }}>✕</button>
          </div>

          {/* Genetics */}
          {p.genetics && <div className="card" style={{ padding:16, marginBottom:12 }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", marginBottom:10 }}>GENETIC LINEAGE</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
              {[["Lineage",p.genetics?.lineage],["Breeder",p.genetics?.breeder],["Generation",p.genetics?.generation]].map(([l,v])=>(
                <div key={l} className="stat">
                  <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{l.toUpperCase()}</div>
                  <div style={{ fontSize:11, color:"#1a2e1a", fontWeight:500 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>}

          {/* Cultivation Method */}
          {method && (
            <div className="card" style={{ padding:16, marginBottom:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em" }}>CULTIVATION METHOD</div>
                <span className="pill">{p.cultivation.method}</span>
              </div>
              <p style={{ fontSize:12, color:"#374151", marginBottom:8 }}>{method.description}</p>
              <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>LIGHT SOURCE: {method.lightSource}</div>
              <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>ENVIRONMENT: {method.environmentControl}</div>
              <div className="info-box">
                <span>🔬</span>
                <p style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Terpene impact: {method.terpeneImpact}</p>
              </div>
            </div>
          )}

          {/* Growing Medium */}
          {medium && (
            <div className="card" style={{ padding:16, marginBottom:12 }}>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", marginBottom:8 }}>GROWING MEDIUM — {p.cultivation?.medium||"—"}</div>
              <p style={{ fontSize:12, color:"#374151", marginBottom:6 }}>{medium.description}</p>
              <p style={{ fontSize:11, color:"#6b7280" }}>{medium.notes}</p>
            </div>
          )}

          {/* Cure Method */}
          {cure && p.cultivation?.cure !== "N/A (extraction)" && (
            <div className="card" style={{ padding:16, marginBottom:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em" }}>CURE METHOD</div>
                <span className="pill" style={{ background: cure.volatileRetention === "High" || cure.volatileRetention === "Very High" ? "#d1fae5" : cure.volatileRetention === "Moderate" ? "#fef3c7" : "#fef2f2", color: cure.volatileRetention === "High" || cure.volatileRetention === "Very High" ? "#065f46" : cure.volatileRetention === "Moderate" ? "#92400e" : "#991b1b" }}>
                  Terpene retention: {cure.volatileRetention}
                </span>
              </div>
              <p style={{ fontSize:12, color:"#374151", marginBottom:10 }}>{cure.description}</p>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:10 }}>
                {[["Duration",cure.duration],["Humidity",cure.humidity],["Temperature",cure.temp]].map(([l,v])=>(
                  <div key={l} className="stat">
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{l.toUpperCase()}</div>
                    <div style={{ fontSize:11, color:"#1a2e1a", fontWeight:500 }}>{v}</div>
                  </div>
                ))}
              </div>
              <div className="info-box">
                <span>🌡️</span>
                <p style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Terpene impact: {cure.terpeneImpact}</p>
              </div>
            </div>
          )}

          {/* Harvest */}
          {harvest && (
            <div className="card" style={{ padding:16, marginBottom:12 }}>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", marginBottom:8 }}>HARVEST METHOD — {p.cultivation?.harvest||"—"}</div>
              <p style={{ fontSize:12, color:"#374151", marginBottom:6 }}>{harvest.description}</p>
              <p style={{ fontSize:11, color:"#6b7280" }}>Trichome impact: {harvest.trichomeImpact}</p>
            </div>
          )}

          {/* LP Notes */}
          {p.cultivation.lp_notes && (
            <div style={{ background:"#f7f5f0", border:"1px solid #e8e4de", borderRadius:10, padding:12, marginBottom:12 }}>
              <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>LP NOTES</div>
              <p style={{ fontSize:11, color:"#374151", lineHeight:1.6 }}>{p.cultivation.lp_notes}</p>
            </div>
          )}

          <p style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Cultivation data provided by or derived from LP documentation. Educational purposes only.</p>
        </div>
      </div>
    );
  };

  // ── TRAINING MODAL ────────────────────────────────────────
  const TrainingModal = () => {
    if (!trainingModule) return null;
    const mod = TRAINING_MODULES.find(m => m.id === trainingModule);
    if (!mod) return null;
    const lesson = mod.lessons[lessonIdx];
    const progress = Math.round(((lessonIdx) / mod.lessons.length) * 100);

    const handleQuizAnswer = (idx) => {
      setQuizAnswer(idx);
      if (idx === mod.quiz[quizIdx].correct) setQuizScore(s => s + 1);
      setTimeout(() => {
        if (quizIdx < mod.quiz.length - 1) { setQuizIdx(i => i + 1); setQuizAnswer(null); }
        else setQuizDone(true);
      }, 1200);
    };

    const completeModule = () => {
      if (!completedModules.includes(trainingModule)) {
        const updated = [...completedModules, trainingModule];
        setCompletedModules(updated);
        try { localStorage.setItem("iterps_completed_modules", JSON.stringify(updated)); } catch (_e) {}
        showToast("Module complete: " + (trainingModule), "success");
      }
      setTrainingModule(null); setLessonIdx(0); setQuizMode(false); setQuizIdx(0); setQuizAnswer(null); setQuizScore(0); setQuizDone(false);
    };

    return (
      <div className="overlay" onClick={() => { setTrainingModule(null); setLessonIdx(0); setQuizMode(false); setQuizAnswer(null); }}>
        <div className="modal" style={{ maxWidth:620 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 }}>
            <div>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", marginBottom:4 }}>BUDTENDER TRAINING · {mod.level.toUpperCase()}</div>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:400, color:"#1a2e1a" }}>{mod.title}</h2>
            </div>
            <button onClick={() => { setTrainingModule(null); setLessonIdx(0); setQuizMode(false); setQuizIdx(0); setQuizAnswer(null); setQuizScore(0); setQuizDone(false); }} style={{ background:"#f3f4f6", border:"none", borderRadius:8, padding:"6px 12px", cursor:"pointer", color:"#6b7280" }}>✕</button>
          </div>

          {/* Progress */}
          <div style={{ marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{quizMode ? "Quiz" : "Lesson " + (lessonIdx + 1) + " of " + (mod.lessons.length)}</span>
              <span style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{quizMode ? (quizIdx + 1) + "/" + (mod.quiz.length) : (progress) + "%"}</span>
            </div>
            <div className="bar-track">
              <div className="bar-fill" style={{ width:(quizMode ? Math.round(((quizIdx)/(mod.quiz.length))*100) : progress) + "%", background:"#1a2e1a" }} />
            </div>
          </div>

          {!quizMode && !quizDone && (
            <div>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:400, color:"#1a2e1a", marginBottom:12 }}>{lesson.title}</h3>
              <p style={{ fontSize:13, color:"#374151", lineHeight:1.8, marginBottom:16 }}>{lesson.content}</p>
              <div className="info-box" style={{ marginBottom:20 }}>
                <span>💡</span>
                <p style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Key takeaway: {lesson.key}</p>
              </div>
              <div style={{ display:"flex", gap:8, justifyContent:"space-between" }}>
                {lessonIdx > 0 && <button className="btn-ghost" onClick={() => setLessonIdx(i => i-1)}>← Previous</button>}
                <button className="btn-primary" style={{ marginLeft:"auto" }} onClick={() => {
                  if (lessonIdx < mod.lessons.length - 1) setLessonIdx(i => i+1);
                  else { setQuizMode(true); setQuizIdx(0); setQuizAnswer(null); setQuizScore(0); }
                }}>{lessonIdx < mod.lessons.length - 1 ? "Next Lesson →" : "Take Quiz →"}</button>
              </div>
            </div>
          )}

          {quizMode && !quizDone && (
            <div>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a", marginBottom:20 }}>Q{quizIdx+1}: {mod.quiz[quizIdx].q}</h3>
              {mod.quiz[quizIdx].opts.map((opt, i) => (
                <button key={i} className={"quiz-opt " + (quizAnswer !== null ? (i === mod.quiz[quizIdx].correct ? "correct" : quizAnswer === i ? "wrong" : "") : "")}
                  onClick={() => quizAnswer === null && handleQuizAnswer(i)} disabled={quizAnswer !== null}>
                  <span style={{ marginRight:8, fontFamily:"'DM Mono',monospace" }}>{["A","B","C","D"][i]}.</span>{opt}
                </button>
              ))}
            </div>
          )}

          {quizDone && (
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:48, marginBottom:16 }}>{quizScore === mod.quiz.length ? "🎓" : quizScore >= mod.quiz.length/2 ? "✅" : "📚"}</div>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:400, color:"#1a2e1a", marginBottom:8 }}>
                {quizScore === mod.quiz.length ? "Perfect Score!" : quizScore >= mod.quiz.length/2 ? "Module Complete" : "Keep Studying"}
              </h3>
              <p style={{ fontSize:13, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>Score: {quizScore} / {mod.quiz.length}</p>
              {quizScore >= mod.quiz.length/2 ? (
                <div>
                  <div className="info-box" style={{ marginBottom:20, textAlign:"left" }}>
                    <span>🏆</span>
                    <p style={{ fontSize:12, color:"#065f46", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Module completed and recorded. Your completion will appear in the staff training log.</p>
                  </div>
                  <button className="btn-primary" onClick={completeModule}>Complete & Return</button>
                </div>
              ) : (
                <div>
                  <div className="warn-box" style={{ marginBottom:20, textAlign:"left" }}>
                    <span>📖</span>
                    <p style={{ fontSize:12, color:"#92400e", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Review the lesson material and try the quiz again to complete this module.</p>
                  </div>
                  <button className="btn-ghost" onClick={() => { setQuizMode(false); setLessonIdx(0); setQuizDone(false); setQuizAnswer(null); setQuizScore(0); setQuizIdx(0); }}>Review Lessons</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── PRODUCT PAGE ──────────────────────────────────────────
  const ProductPage = ({ p }) => {
    const dispensary = DB_DISPENSARIES.find(d => d.id === p.dispensary_id);
    const topTerpenes = Object.entries(p.terpenes).sort(([,a],[,b]) => b-a);
    const similar = findSimilarProducts(p, ALL_PRODUCTS);
    const topTerpName = topTerpenes[0]?.[0];
    const topTerpInfo = TERPENES[topTerpName];

    // Batch history — key by "brand|name"
    const batchKey = (p.brand) + "|" + (p.name);
    const existingHistory = batchHistory[batchKey] || [];
    const currentBatchLogged = existingHistory.some(b => b.batch === p.batch);

    const logBatch = () => {
      if (currentBatchLogged) return;
      const entry = {
        batch: p.batch || "Unknown",
        lot: p.lot || null,
        tested: p.tested || new Date().toISOString().split("T")[0],
        thc: p.thc,
        cbd: p.cbd,
        total_terpenes: p.total_terpenes,
        terpenes: { ...p.terpenes },
        lab: p.lab || null,
        loggedAt: new Date().toISOString(),
      };
      const updated = { ...batchHistory, [batchKey]: [...existingHistory, entry] };
      setBatchHistory(updated);
      try { localStorage.setItem("iterps_batches", JSON.stringify(updated)); } catch (_e) {}
    };

    // Auto-log current batch on mount if not already there and batch info exists
    if (p.batch && !currentBatchLogged && existingHistory.length > 0) {
      logBatch();
    }

    // ── BUDTENDER MODE ──────────────────────────────────────────────────
    if (budtenderMode) return (
      <div className="fade" style={{ maxWidth:500, margin:"0 auto", padding:"20px 16px" }}>
        {/* Header strip */}
        <div style={{ background:"#1a2e1a", borderRadius:16, padding:"20px 20px 16px", marginBottom:14, color:"#f0f4f0" }}>
          <div style={{ fontSize:10, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{p.brand} · {p.plant_type}</div>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, marginBottom:12 }}>{p.name}</div>
          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
            <div><div style={{ fontSize:28, fontWeight:300, fontFamily:"'Fraunces',serif", color:"#4ade80" }}>{p.thc}%</div><div style={{ fontSize:9, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace" }}>THC</div></div>
            {p.total_terpenes && <div><div style={{ fontSize:28, fontWeight:300, fontFamily:"'Fraunces',serif", color:"#86efac" }}>{p.total_terpenes.toFixed(2)}%</div><div style={{ fontSize:9, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace" }}>Total Terpenes</div></div>}
            {p.cbd > 0.1 && <div><div style={{ fontSize:28, fontWeight:300, fontFamily:"'Fraunces',serif", color:"#93c5fd" }}>{p.cbd}%</div><div style={{ fontSize:9, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace" }}>CBD</div></div>}
          </div>
        </div>

        {/* Plain-language terpene summary */}
        {topTerpInfo && (
          <div style={{ background:(topTerpInfo.color) + "15", border:"1px solid " + (topTerpInfo.color) + "40", borderRadius:14, padding:18, marginBottom:12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <span style={{ width:10, height:10, borderRadius:"50%", background:topTerpInfo.color, display:"inline-block", flexShrink:0 }} />
              <span style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a" }}>Primary: {topTerpName}</span>
              <span style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>({topTerpenes[0]?.[1].toFixed(2)}%)</span>
            </div>
            <p style={{ fontSize:13, color:"#374151", lineHeight:1.6, marginBottom:0, fontFamily:"'DM Mono',monospace" }}>{topTerpInfo.plain}</p>
          </div>
        )}

        {/* What to expect */}
        <div className="card" style={{ padding:18, marginBottom:12 }}>
          <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:10, textTransform:"uppercase", letterSpacing:1 }}>What to expect</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
            {(p.aroma||[]).map(a => <span key={a} className="chip" style={{ background:"#f9fafb", border:"1px solid #e8e4de", color:"#374151" }}>{a}</span>)}
          </div>
          {topTerpenes.slice(0,3).map(([t,v]) => {
            const ti = TERPENES[t];
            return ti ? (
              <div key={t} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, padding:"8px 10px", background:"#f9fafb", borderRadius:8 }}>
                <span style={{ width:8,height:8,borderRadius:"50%",background:ti.color,display:"inline-block",flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11, color:"#1a2e1a", fontWeight:500 }}>{t} <span style={{ color:"#9ca3af", fontWeight:400 }}>· {v.toFixed(2)}%</span></div>
                  <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.4 }}>{ti.plain?.split('.')[0]}.</div>
                </div>
              </div>
            ) : null;
          })}
        </div>

        {/* Cultivation snapshot */}
        <div className="card" style={{ padding:18, marginBottom:12 }}>
          <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:10, textTransform:"uppercase", letterSpacing:1 }}>How it was grown</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            {[p.cultivation?.method, p.cultivation?.medium, p.cultivation?.cure, p.cultivation?.lp].filter(Boolean).map((v,i) => (
              <span key={i} className="chip" style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", color:"#166534" }}>{v}</span>
            ))}
          </div>
          {p.cultivation?.lp_notes && <p style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginTop:10, lineHeight:1.5 }}>{p.cultivation.lp_notes}</p>}
        </div>

        {/* Location */}
        <div style={{ padding:"12px 16px", background:"#f9fafb", borderRadius:10, marginBottom:14, fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>
          📍 {dispensary?.name} · {dispensary?.address} · {dispensary?.city}
        </div>

        {/* Actions */}
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => setBudtenderMode(false)} style={{ flex:1, padding:"11px 0", background:"none", border:"1px solid #e8e4de", borderRadius:10, fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace", color:"#6b7280" }}>Full Profile</button>
          <button onClick={() => setQrModal(p)} className="btn-primary" style={{ flex:1, padding:"11px 0", fontSize:12 }}>QR Code 🔗</button>
          <button onClick={() => setSimilarModal(p)} style={{ flex:1, padding:"11px 0", background:"none", border:"1px solid #e8e4de", borderRadius:10, fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace", color:"#374151" }}>Similar →</button>
        </div>
      </div>
    );

    // ── FULL PRODUCT PAGE ───────────────────────────────────────────────
    return (
      <div className="fade">
        <div style={{ maxWidth:740, margin:"0 auto", padding:"28px 20px" }}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:8}}>
            <button onClick={() => { setPage(prevPage || "explorer"); setSelectedProduct(null); }} style={{ display:"flex", alignItems:"center", gap:6, background:"none", border:"none", color:"#6b7280", cursor:"pointer", fontFamily:"'DM Mono',monospace", fontSize:12 }}>← Back to Explorer</button>
            {profiles.length>0&&(
              <button onClick={()=>{ matchNewArrival(p); setPage("customers"); }} className="btn-ghost" style={{fontSize:11}}>
                👥 Match to Customers
              </button>
            )}
            {onboardRole === "staff" && profiles.length > 0 && (
              <button onClick={()=>{ setLogForCustModal({ product: p }); setLogForCustSearch(""); setLogForCustDone(null); }}
                className="btn-ghost" style={{ fontSize:11, background:"#f0fdf4", borderColor:"#86efac", color:"#15803d", fontWeight:600 }}>
                📋 Log for customer
              </button>
            )}
          </div>

          {/* Header */}
          <div className="card" style={{ padding:24, marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16, flexWrap:"wrap", marginBottom:16 }}>
              <div>
                <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:8, flexWrap:"wrap" }}>
                  <span className="pill">{p.category}</span>
                  <span className="pill">{p.plant_type}</span>
                  <span className={"lab-badge " + (p.terpene_source === "lab_tested" ? "lab-verified" : p.terpene_source === "lp_provided" ? "lab-verified" : "lab-estimated")}>
                    {p.terpene_source === "lab_tested" ? "✓ Lab Tested" : p.terpene_source === "lp_provided" ? "✓ LP Provided" : "~ Estimated"}
                  </span>
                  {p.cultivation?.lp_verified && (
                    <span style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 9px", borderRadius:6, fontSize:10, fontFamily:"'DM Mono',monospace", background:"#f0fdf4", color:"#15803d", border:"1px solid #86efac", fontWeight:600 }}>
                      🏷 LP Verified
                    </span>
                  )}
                  {(() => { const f = getFreshness(p); return f ? (
                    <span title={f.note} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 9px", borderRadius:6, fontSize:10, fontFamily:"'DM Mono',monospace", background:(f.color) + "15", color:f.color, border:"1px solid " + (f.color) + "40" }}>
                      ◉ {f.label} · {f.daysSince}d
                    </span>
                  ) : null; })()}
                </div>
                <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:400, color:"#1a2e1a", marginBottom:4 }}>{p.name}</h1>
                <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{p.brand}</p>
              </div>
              <button onClick={() => setQrModal(p)} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, padding:"10px 16px", background:"#f7f5f0", border:"1px solid #e8e4de", borderRadius:12, cursor:"pointer", flexShrink:0 }}>
                <span style={{ fontSize:22 }}>📱</span>
                <span style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>QR Code</span>
              </button>
            </div>

            {/* Batch info */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px,1fr))", gap:8, marginBottom:p.packaged_date ? 8 : 16 }}>
              {[["Batch",p.batch],["Lot",p.lot],["Lab",p.lab],["Tested",p.tested]].map(([l,v])=>(
                <div key={l} style={{ background:"#f7f5f0", borderRadius:8, padding:"8px 12px" }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:2 }}>{l.toUpperCase()}</div>
                  <div style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{v}</div>
                </div>
              ))}
            </div>
            {(() => {
              const f = getFreshness(p);
              if (!f) return null;
              return (
                <div style={{ display:"flex", alignItems:"center", gap:12, background:(f.color) + "0d", border:"1px solid " + (f.color) + "30", borderRadius:8, padding:"10px 14px", marginBottom:16 }}>
                  <span style={{ fontSize:18 }}>{f.score >= 85 ? "🌿" : f.score >= 65 ? "🍃" : f.score >= 45 ? "🍂" : "⚠️"}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                      <span style={{ fontSize:12, fontWeight:600, color:f.color, fontFamily:"'DM Mono',monospace" }}>{f.label}</span>
                      <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Packaged {p.packaged_date} · {f.daysSince} days ago</span>
                    </div>
                    <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.4 }}>{f.note}</div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontSize:18, fontWeight:600, fontFamily:"'Fraunces',serif", color:f.color }}>{f.score}</div>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>FRESHNESS</div>
                  </div>
                </div>
              );
            })()}

            {/* Drift flag alert — shown when manager has flagged this batch */}
            {(() => {
              const flagKey = (p.brand) + "|" + (p.name) + "|" + (p.batch);
              const flag = flaggedDrifts[flagKey];
              if (!flag) return null;
              return (
                <div style={{background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:10,padding:"12px 16px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:20}}>🚩</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600,color:"#b91c1c",fontFamily:"'DM Mono',monospace",marginBottom:2}}>Batch Drift Alert — Advise Customers</div>
                    <div style={{fontSize:11,color:"#6b7280",fontFamily:"'DM Mono',monospace"}}>This batch ({p.batch}) was flagged by your manager. Terpene profile has drifted {flag.drift}% from the reference batch. The aroma and chemical character may differ from what regulars expect.</div>
                  </div>
                </div>
              );
            })()}

            <div style={{ background:"#f7f5f0", borderRadius:8, padding:"8px 12px", marginBottom:14 }}>
              <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:2 }}>TESTING METHODOLOGY</div>
              <div style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{p.lab_method}</div>
            </div>

            {/* Cannabinoids */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(80px,1fr))", gap:8 }}>
              {[
                ...(p.total_thc ? [{ label:"Total THC", value:(p.total_thc) + "%", color:"#16a34a", key:"Total THC (Label)", note:"Label" }] : []),
                { label:"Δ9-THC", value:(p.thc) + "%", color:"#22c55e", key:"Δ9-THC" },
                ...(p.thca ? [{ label:"THCA", value:(p.thca) + "%", color:"#4ade80", key:"THCA" }] : []),
                { label:"CBD", value:(p.cbd) + "%", color:"#3b82f6", key:"CBD" },
                ...(p.cbg ? [{ label:"CBG", value:(p.cbg) + "%", color:"#f59e0b", key:"CBG" }] : []),
                ...(p.cbn ? [{ label:"CBN", value:(p.cbn) + "%", color:"#8b5cf6", key:"CBN" }] : []),
                ...(p.cbc ? [{ label:"CBC", value:(p.cbc) + "%", color:"#ec4899", key:"CBC" }] : []),
              ].map(cb => (
                <div key={cb.label} className="stat" style={{ cursor:"pointer", textAlign:"center", position:"relative" }} onClick={() => { setCompound(cb.key); setCompoundType("cannabinoid"); }}>
                  {cb.note && <div style={{ position:"absolute", top:-4, right:-4, fontSize:7, background:"#16a34a", color:"#fff", padding:"1px 4px", borderRadius:4, fontFamily:"'DM Mono',monospace" }}>{cb.note}</div>}
                  <div style={{ fontSize:20, fontWeight:500, color:cb.color, fontFamily:"'Fraunces',serif" }}>{cb.value}</div>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{cb.label}</div>
                  <div style={{ fontSize:9, color:"#6b7280", marginTop:3 }}>Tap to learn</div>
                </div>
              ))}
            </div>
            {p.total_thc && p.thca && (
              <div style={{ marginTop:10, padding:"7px 12px", background:"#f0fdf4", borderRadius:8, fontSize:10, color:"#166534", fontFamily:"'DM Mono',monospace" }}>
                Total THC = THCA ({p.thca}%) × 0.877 + Δ9-THC ({p.thc}%) = <strong>{p.total_thc}%</strong> — Health Canada label formula
              </div>
            )}
          </div>

          {/* Terpene Profile */}
          <div className="card" style={{ padding:22, marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:19, fontWeight:400, color:"#1a2e1a", marginBottom:4 }}>Terpene Profile</h2>
                {p.total_terpenes && (
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Total terpenes:</span>
                    <span style={{ fontSize:14, fontWeight:600, color:"#1a2e1a", fontFamily:"'Fraunces',serif" }}>{p.total_terpenes.toFixed(2)}%</span>
                    <span style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace", background:"#f3f4f6", borderRadius:4, padding:"2px 6px" }}>
                      {p.total_terpenes >= 1.2 ? "🌿 Exceptional" : p.total_terpenes >= 0.9 ? "🌿 High" : p.total_terpenes >= 0.6 ? "Moderate" : "Low"}
                    </span>
                  </div>
                )}
              </div>
              <span className={"lab-badge " + (p.terpene_source === "lab_tested" || p.terpene_source === "lp_provided" ? "lab-verified" : "lab-estimated")}>
                {p.terpene_source === "lab_tested" ? "✓ GC-MS Verified" : p.terpene_source === "lp_provided" ? "✓ LP Provided" : "~ Estimated"}
              </span>
            </div>

            {p.terpene_source === "estimated" && (
              <div className="warn-box" style={{ marginBottom:14 }}>
                <span>⚠️</span>
                <p style={{ fontSize:11, color:"#92400e", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{p.terpene_note}</p>
              </div>
            )}

            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {topTerpenes.map(([t, v]) => {
                const info = TERPENES[t];
                const pct = Math.round((v / topTerpenes[0][1]) * 100);
                return (
                  <div key={t}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:5 }}>
                      <button onClick={() => { setCompound(t); setCompoundType("terpene"); }} style={{ display:"flex", alignItems:"center", gap:6, background:"none", border:"none", cursor:"pointer" }}>
                        <span className="chip" style={{ background:(info?.color||"#6b7280") + "18", color:info?.color||"#6b7280", border:"1px solid " + (info?.color||"#6b7280") + "30" }}>{t}</span>
                        <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{info?.aroma?.slice(0,2).join(" · ")}</span>
                      </button>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        {p.total_terpenes && <span style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{((v/p.total_terpenes)*100).toFixed(0)}% of total</span>}
                        <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{v.toFixed(2)}%</span>
                        <span style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{(v*10).toFixed(1)} mg/g</span>
                      </div>
                    </div>
                    <div className="bar-track"><div className="bar-fill" style={{ width:(pct) + "%", background:info?.color||"#6b7280" }} /></div>
                  </div>
                );
              })}
            </div>

            {/* Rare / Minor Terpenes from COA */}
            {p.otherTerpenes && Object.keys(p.otherTerpenes).length > 0 && (() => {
              const rareSorted = Object.entries(p.otherTerpenes).sort((ea,eb)=>eb[1]-ea[1]);
              return (
                <div style={{ marginTop:18, borderTop:"1px solid #e8e4de", paddingTop:14 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em" }}>Minor Terpenes — COA Detected</div>
                    <span style={{ fontSize:9, background:"#faf5ff", color:"#7c3aed", border:"1px solid #ddd6fe", borderRadius:4, padding:"2px 6px", fontFamily:"'DM Mono',monospace" }}>Rare Profile</span>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:8 }}>
                    {rareSorted.map(([t, v]) => {
                      const info = TERPENES[t];
                      return (
                        <div key={t} style={{ background:"#faf5ff", border:"1px solid #ede9fe", borderRadius:10, padding:"10px 12px", cursor:"pointer" }}
                          onClick={() => { setCompound(t); setCompoundType("terpene"); }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                              {info?.color && <div style={{ width:6, height:6, borderRadius:"50%", background:info.color, flexShrink:0 }}/>}
                              <span style={{ fontSize:10, color:"#5b21b6", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{t}</span>
                            </div>
                            <span style={{ fontSize:9, color:"#7c3aed", fontFamily:"'DM Mono',monospace" }}>{(v*10).toFixed(1)} mg/g</span>
                          </div>
                          <div style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{info?.aroma?.slice(0,3).join(" · ") || "—"}</div>
                          {info?.sensitizer && <div style={{ fontSize:8, color:"#b45309", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:4, padding:"2px 5px", display:"inline-block" }}>⚠ Known sensitizer</div>}
                          {info?.degradation_marker && <div style={{ fontSize:8, color:"#6b7280", background:"#f3f4f6", borderRadius:4, padding:"2px 5px", display:"inline-block" }}>📊 Freshness marker</div>}
                          <div style={{ fontSize:8, color:"#9ca3af", marginTop:3 }}>Tap to learn more →</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <p style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:12 }}>Tap any terpene name for IUPAC compound information →</p>
          </div>

          {/* Cultivation Profile */}
          <div className="card card-click" style={{ padding:22, marginBottom:14 }} onClick={() => setCultivationModal(p)}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:19, fontWeight:400, color:"#1a2e1a" }}>Cultivation Profile</h2>
              <span style={{ fontSize:12, color:"#1a2e1a", fontFamily:"'DM Mono',monospace" }}>View details →</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(120px,1fr))", gap:8 }}>
              {[
                ["Method", p.cultivation.method],
                ["Medium", p.cultivation?.medium||"—"],
                ["Cure", (p.cultivation?.cure||"—").split(" ")[0]],
                ["Harvest", (p.cultivation?.harvest||"—").split(" ")[0]],
                ["Cycle", p.cultivation?.cycle_days ? (p.cultivation.cycle_days) + "d" : "—"],
              ].map(([l,v]) => (
                <div key={l} className="stat">
                  <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{l.toUpperCase()}</div>
                  <div style={{ fontSize:11, color:"#1a2e1a", fontWeight:500 }}>{v}</div>
                </div>
              ))}
            </div>
            <p style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:10 }}>Tap to view full cultivation and cure science →</p>
          </div>

          {/* Genetic Lineage */}
          {p.genetics && <div className="card" style={{ padding:22, marginBottom:14 }}>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:19, fontWeight:400, color:"#1a2e1a", marginBottom:14 }}>Genetic Lineage</h2>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
              {[["Lineage",p.genetics?.lineage],["Breeder",p.genetics?.breeder],["Generation",p.genetics?.generation]].map(([l,v])=>(
                <div key={l} className="stat">
                  <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{l.toUpperCase()}</div>
                  <div style={{ fontSize:11, color:"#1a2e1a", fontWeight:500 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>}

          {/* Irradiation Transparency */}
          {(() => {
            const irr = p.cultivation?.irradiation || "not_disclosed";
            const irrNote = p.cultivation?.irradiation_note || "Irradiation status not disclosed for this product.";
            const irrConfig = {
              non_irradiated: { label:"Non-Irradiated", color:"#16a34a", bg:"#f0fdf4", border:"#86efac", icon:"✓", desc:"This product has not been subjected to post-harvest irradiation. The terpene profile and natural compounds are preserved in their original state." },
              gamma:          { label:"Gamma Irradiated", color:"#92400e", bg:"#fffbeb", border:"#fcd34d", icon:"☢", desc:"Gamma irradiation (Cobalt-60 or Caesium-137) is used by this LP for microbial compliance. It effectively eliminates pathogens but can alter terpene molecular structure and reduce aromatic intensity. Some terpenes (particularly monoterpenes like Myrcene) are more susceptible than sesquiterpenes." },
              e_beam:         { label:"E-Beam Irradiated", color:"#7c3aed", bg:"#faf5ff", border:"#ddd6fe", icon:"⚡", desc:"Electron beam (e-beam) irradiation is used for microbial compliance. E-beam processing is faster than gamma and uses no radioactive material. Research suggests slightly lower terpene degradation than gamma, though some loss of volatile monoterpenes still occurs." },
              x_ray:          { label:"X-Ray Irradiated", color:"#7c3aed", bg:"#faf5ff", border:"#ddd6fe", icon:"📡", desc:"X-ray irradiation used for microbial compliance. Similar terpene impact profile to e-beam. Less common in Canadian cannabis industry." },
              not_disclosed:  { label:"Irradiation Status — Not Disclosed", color:"#6b7280", bg:"#f9fafb", border:"#e5e7eb", icon:"?", desc:"This LP or brand has not publicly disclosed their irradiation practice for this product. We have not been able to confirm status from COA documentation or public LP materials. Consumers who want to know can request confirmation from the dispensary or LP directly." },
            };
            const cfg = irrConfig[irr] || irrConfig.not_disclosed;
            return (
              <div className="card" style={{ padding:22, marginBottom:14, background:cfg.bg, border:"1px solid " + (cfg.border) }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Post-Harvest Processing</div>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:18 }}>{cfg.icon}</span>
                      <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:400, color:cfg.color }}>{cfg.label}</h2>
                    </div>
                  </div>
                  <span style={{ fontSize:9, background:cfg.color + "20", color:cfg.color, border:"1px solid " + (cfg.color) + "40", borderRadius:4, padding:"2px 7px", fontFamily:"'DM Mono',monospace", fontWeight:600, whiteSpace:"nowrap" }}>
                    {irr === "not_disclosed" ? "UNCONFIRMED" : irr === "non_irradiated" ? "CONFIRMED" : "DISCLOSED"}
                  </span>
                </div>
                <p style={{ fontSize:12, color:"#374151", lineHeight:1.7, marginBottom:10 }}>{cfg.desc}</p>
                <div style={{ background:"rgba(255,255,255,0.6)", borderRadius:8, padding:"10px 12px", fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>
                  <strong style={{ color:"#374151" }}>LP note:</strong> {irrNote}
                </div>
                {(irr === "gamma" || irr === "e_beam") && (
                  <div style={{ marginTop:10, padding:"8px 12px", background:"rgba(255,255,255,0.5)", borderRadius:8, fontSize:10, color:"#92400e", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>
                    📊 <strong>Terpene impact:</strong> Irradiated products may show lower-than-expected terpene intensities relative to pre-irradiation COA values, particularly for volatile monoterpenes (Myrcene, Limonene, Pinene). COA testing occurs before irradiation in some LP workflows.
                  </div>
                )}
              </div>
            );
          })()}

          {/* Batch History */}
          <div className="card" style={{ padding:22, marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
              <div>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:19, fontWeight:400, color:"#1a2e1a", marginBottom:4 }}>Batch History</h2>
                <p style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:0 }}>
                  Terpene profile variance across logged batches — {p.brand} · {p.name}
                </p>
              </div>
              {!currentBatchLogged && (
                <button onClick={logBatch}
                  style={{ flexShrink:0, marginLeft:12, padding:"7px 14px", borderRadius:8, border:"1px solid #bbf7d0", background:"#f0fdf4", color:"#166534", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                  + Log This Batch
                </button>
              )}
            </div>

            {existingHistory.length === 0 ? (
              <div style={{ textAlign:"center", padding:"28px 16px", color:"#9ca3af", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                <div style={{ fontSize:24, marginBottom:8 }}>📋</div>
                No batches logged yet for this product.
                <div style={{ marginTop:8 }}>
                  <button onClick={logBatch}
                    style={{ padding:"8px 20px", borderRadius:8, border:"1px solid #e8e4de", background:"#fff", color:"#374151", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    Log Current Batch ({p.batch || "this batch"})
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Terpene drift chart */}
                {existingHistory.length >= 2 && (() => {
                  const allTerps = [...new Set(existingHistory.flatMap(b => Object.keys(b.terpenes)))].sort();
                  const maxV = Math.max(...existingHistory.flatMap(b => allTerps.map(t => b.terpenes[t]||0)));
                  return (
                    <div style={{ marginBottom:14, overflowX:"auto" }}>
                      <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, letterSpacing:"0.06em", textTransform:"uppercase" }}>Terpene Drift Across {existingHistory.length} Batches</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8, minWidth:400 }}>
                        {allTerps.map(t => {
                          const info = TERPENES[t];
                          const vals = existingHistory.map(b => b.terpenes[t]||0);
                          const minV = Math.min(...vals), maxBV = Math.max(...vals);
                          const drift = maxBV - minV;
                          const driftPct = maxV > 0 ? Math.round(drift/maxV*100) : 0;
                          return (
                            <div key={t}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                  <div style={{ width:7, height:7, borderRadius:"50%", background:info?.color||"#4a7c59" }}/>
                                  <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                                </div>
                                <span style={{ fontSize:9, color: driftPct > 20 ? "#dc2626" : driftPct > 10 ? "#f59e0b" : "#16a34a", fontFamily:"'DM Mono',monospace" }}>
                                  {driftPct > 0 ? "±" + (driftPct) + "% drift" : "Stable"}
                                </span>
                              </div>
                              <div style={{ display:"flex", gap:3, alignItems:"center", height:14 }}>
                                {vals.map((v, i) => (
                                  <div key={i} style={{ flex:1, position:"relative" }}>
                                    <div style={{ height:14, background:(info?.color||"#4a7c59") + "20", borderRadius:3, position:"relative", overflow:"hidden" }}>
                                      <div style={{ position:"absolute", bottom:0, left:0, right:0, background:info?.color||"#4a7c59", borderRadius:3, height:(maxV > 0 ? (v/maxV)*100 : 0) + "%", opacity:0.7 }}/>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        <div style={{ display:"flex", gap:3, marginTop:4 }}>
                          {existingHistory.map((b, i) => (
                            <div key={i} style={{ flex:1, fontSize:8, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textAlign:"center", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                              {b.batch || "Batch " + (i+1)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Batch table */}
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                    <thead>
                      <tr style={{ borderBottom:"1px solid #e8e4de" }}>
                        {["Batch","Tested","THC","Total Terps","Top Terpene","Logged"].map(h => (
                          <th key={h} style={{ textAlign:"left", padding:"6px 10px", fontSize:9, color:"#9ca3af", letterSpacing:"0.06em", fontWeight:400 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...existingHistory].reverse().map((b, i) => {
                        const topT = Object.entries(b.terpenes).sort(([,a],[,bb])=>bb-a)[0];
                        const isCurrent = b.batch === p.batch;
                        return (
                          <tr key={i} style={{ borderBottom:"1px solid #f9fafb", background: isCurrent ? "#f0fdf4" : "transparent" }}>
                            <td style={{ padding:"8px 10px", color: isCurrent ? "#166534" : "#374151", fontWeight: isCurrent ? 600 : 400 }}>
                              {b.batch || "—"} {isCurrent && <span style={{ fontSize:8, background:"#bbf7d0", color:"#166534", padding:"1px 5px", borderRadius:8, marginLeft:4 }}>current</span>}
                            </td>
                            <td style={{ padding:"8px 10px", color:"#6b7280" }}>{b.tested || "—"}</td>
                            <td style={{ padding:"8px 10px", color:"#22c55e", fontWeight:600 }}>{b.thc}%</td>
                            <td style={{ padding:"8px 10px", color:"#6b7280" }}>{b.total_terpenes ? ((b.total_terpenes).toFixed(2)) + "%" : "—"}</td>
                            <td style={{ padding:"8px 10px" }}>
                              {topT && (
                                <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                                  <span style={{ width:6, height:6, borderRadius:"50%", background:TERPENES[topT[0]]?.color||"#4a7c59", display:"inline-block" }}/>
                                  {topT[0]} ({(topT[1]*100).toFixed(0)}mg/g)
                                </span>
                              )}
                            </td>
                            <td style={{ padding:"8px 10px", color:"#9ca3af", fontSize:9 }}>{b.loggedAt ? new Date(b.loggedAt).toLocaleDateString("en-CA") : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {currentBatchLogged && (
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:10 }}>Current batch already logged. Visit again after a new batch release to track profile drift.</div>
                )}
              </>
            )}
          </div>

          {/* Available At — multi-location */}
          {(() => {
            const sameName = ALL_PRODUCTS.filter(s => s.brand === p.brand && s.name === p.name);
            if (sameName.length <= 1) return null;
            return (
              <div className="card" style={{ padding:20, marginBottom:14 }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a", marginBottom:4 }}>Available At</div>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12 }}>{sameName.length} locations carry this product</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {sameName.map(sku => {
                    const disp = DB_DISPENSARIES.find(d=>d.id===sku.dispensary_id);
                    const isCurrent = sku.id === p.id;
                    return disp ? (
                      <button key={sku.id} onClick={()=>{ if(!isCurrent){ setSelectedProduct(sku); }}}
                        style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", background:isCurrent?"#1a2e1a":"#f9fafb", border:"1px solid " + (isCurrent?"#1a2e1a":"#e8e4de"), borderRadius:20, fontSize:11, fontFamily:"'DM Mono',monospace", color:isCurrent?"#fff":"#374151", cursor:isCurrent?"default":"pointer" }}>
                        <span style={{ width:6,height:6,borderRadius:"50%",background:sku.in_stock?"#22c55e":"#9ca3af",display:"inline-block",flexShrink:0 }}/>
                        {disp.name}
                        <span style={{ fontSize:9, color:isCurrent?"rgba(255,255,255,0.6)":"#9ca3af" }}>{disp.city}</span>
                        {isCurrent && <span style={{ fontSize:8, background:"rgba(255,255,255,0.2)", padding:"1px 5px", borderRadius:8 }}>here</span>}
                      </button>
                    ) : null;
                  })}
                </div>
              </div>
            );
          })()}

          {/* Chemically Similar Products */}
          <div className="card" style={{ padding:22, marginBottom:14 }}>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:19, fontWeight:400, color:"#1a2e1a", marginBottom:4 }}>Similar Chemical Profiles</h2>
            <p style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Products with comparable terpene composition — calculated via cosine similarity algorithm</p>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {similar.map(({ product:sp, similarity }) => (
                <div key={sp.id} className="card card-click" style={{ padding:14, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }} onClick={() => setSelectedProduct(sp)}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:500, fontSize:13, color:"#1a2e1a", marginBottom:2 }}>{sp.name}</div>
                    <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{sp.brand} · {dispName(sp.dispensary_id)}</div>
                  </div>
                  <div style={{ flexShrink:0, textAlign:"right" }}>
                    <div style={{ fontSize:14, fontWeight:500, color:"#22c55e", fontFamily:"'Fraunces',serif" }}>{Math.round(similarity * 100)}%</div>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>similarity</div>
                    <div className="similarity-bar" style={{ width:(Math.round(similarity*80)) + "px", marginTop:4 }} />
                  </div>
                </div>
              ))}
            </div>
            <p style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:12 }}>Similarity calculated from terpene vector cosine distance. Educational comparison only.</p>
          </div>

          {/* Availability */}
          <div className="card" style={{ padding:22, marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
              <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:19, fontWeight:400, color:"#1a2e1a", margin:0 }}>Available At</h2>
              {!p.in_stock && (
                <button onClick={()=>{ setAlertProduct(p); setAlertEmail(""); setAlertDone(false); setShowAlertModal(true); }}
                  style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:8, border:"1px solid #e8e4de", background:"#fff", color:"#374151", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                  🔔 Alert Me
                </button>
              )}
            </div>
            <p style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Informational availability from licensed retailers only</p>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 16px", background:"#f7f5f0", borderRadius:12, border:"1px solid #e8e4de" }}>
              <div>
                <div style={{ fontWeight:500, fontSize:13, color:"#1a2e1a", marginBottom:2 }}>{dispensary?.name}</div>
                <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{dispensary?.city}, {dispensary?.province} · {dispensary?.license}</div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ width:7, height:7, borderRadius:"50%", background:p.in_stock?"#22c55e":"#ef4444", display:"inline-block" }} />
                <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:p.in_stock?"#065f46":"#7f1d1d" }}>{p.in_stock?"In Stock":"Out of Stock"}</span>
              </div>
            </div>
            {p.cultivation?.lp && (
              <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10, padding:"8px 12px", background:"#f7f5f0", borderRadius:8, border:"1px solid #e8e4de" }}>
                <span style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>🏭 {p.cultivation.lp}</span>
                <LPBadge lpName={p.cultivation.lp} size="sm" />
              </div>
            )}
            {!p.in_stock && stockAlerts.find(a=>a.productId===p.id&&!a.fired) && (
              <div style={{ marginTop:8, fontSize:10, color:"#166534", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, padding:"6px 12px", fontFamily:"'DM Mono',monospace" }}>
                🔔 You have a stock alert set for this product.
              </div>
            )}
            <p style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:10 }}>Contact retailer directly for purchases. iTerps does not facilitate cannabis transactions.</p>
          </div>

          <div style={{ background:"#f7f5f0", border:"1px solid #e8e4de", borderRadius:12, padding:14, fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>
            Effects vary between individuals. Information is for educational purposes only. Not medical advice. Purchase only from licensed retailers in accordance with applicable law. Cannabis Act (S.C. 2018, c. 16) compliant.
          </div>
        </div>
      </div>
    );
  };

  // ── EXPLORER PAGE ─────────────────────────────────────────
  const ExplorerPage = () => (
    <div className="fade">
      <div style={{ maxWidth:980, margin:"0 auto", padding:"28px 20px" }}>
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Product Composition Explorer</h1>
          <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Explore licensed cannabis products by chemical composition. Factual data from connected BC retailers.</p>
        </div>
        {onboardRole === "lp" && activeLp && (
          <div style={{ marginBottom:16, padding:"10px 16px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
            <div style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#1d4ed8" }}>
              🏭 Viewing as <strong>{activeLp}</strong> — use filters to compare your products to the market.
            </div>
            <button onClick={()=>setFilters(p=>({...p, dispensary:"", city:""}))}
              style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#1d4ed8", background:"none", border:"1px solid #bfdbfe", borderRadius:6, padding:"3px 8px", cursor:"pointer" }}>
              Show all ↗
            </button>
          </div>
        )}
        <div className="info-box" style={{ marginBottom:20 }}>
          <span>🔬</span>
          <p style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>
            <strong>Sample data for demonstration.</strong> In production, live SKU data is fetched from connected licensed dispensaries via POS integration. Data schema follows IUPAC nomenclature for global interoperability.
          </p>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8, alignItems:"center" }}>
          {/* Search */}
          <input className="inp" placeholder="Search product, brand, terpene, aroma, lineage…" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth:220, flex:"1 1 220px", maxWidth:340 }} />
          {/* Category */}
          <select className="sel" value={filters.category} onChange={e => setFilters(p=>({...p,category:e.target.value}))}>
            <option value="">All Categories</option>
            {[...new Set(ALL_PRODUCTS.map(p=>p.category))].sort().map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          {/* Plant Type */}
          <select className="sel" value={filters.plantType} onChange={e => setFilters(p=>({...p,plantType:e.target.value}))}>
            <option value="">All Types</option>
            {[...new Set(ALL_PRODUCTS.map(p=>p.plant_type))].map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          {/* Primary Terpene */}
          <select className="sel" value={filters.terpene} onChange={e => setFilters(p=>({...p,terpene:e.target.value}))}>
            <option value="">All Terpenes</option>
            {Object.keys(TERPENES).map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          {/* City */}
          <select className="sel" value={filters.city} onChange={e => setFilters(p=>({...p,city:e.target.value,dispensary:""}))}>
            <option value="">All Cities</option>
            {[...new Set(DB_DISPENSARIES.map(d=>d.city))].sort().map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          {/* Retailer — cascades from city */}
          <select className="sel" value={filters.dispensary} onChange={e => setFilters(p=>({...p,dispensary:e.target.value}))}>
            <option value="">{filters.city ? "All " + (filters.city) + " stores" : "All Retailers"}</option>
            {(filters.city ? DB_DISPENSARIES.filter(d=>d.city===filters.city) : DB_DISPENSARIES).map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          {/* Data source */}
          <select className="sel" value={filters.terpSource} onChange={e => setFilters(p=>({...p,terpSource:e.target.value}))}>
            <option value="">All Data Sources</option>
            <option value="lab_tested">✓ Lab Verified</option>
            <option value="lp_provided">✓ LP Provided</option>
            <option value="estimated">~ Estimated</option>
          </select>
          {/* Sort */}
          <select className="sel" value={filters.sortBy} onChange={e => setFilters(p=>({...p,sortBy:e.target.value}))}>
            <option value="name">Sort: A–Z</option>
            <option value="brand">Sort: Brand</option>
            <option value="thc_desc">Sort: THC ↓</option>
            <option value="thc_asc">Sort: THC ↑</option>
            <option value="terpenes_desc">Sort: Terpenes ↓</option>
            <option value="terpenes_asc">Sort: Terpenes ↑</option>
          </select>
        </div>
        {/* Active filter clear strip */}
        {(search || filters.category || filters.plantType || filters.terpene || filters.city || filters.dispensary || filters.terpSource || (filters.thcMin && filters.thcMin !== "") || (filters.thcMax && filters.thcMax !== "")) && (
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
            <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Active filters:</span>
            {search && <span style={{ fontSize:10, background:"#f0fdf4", color:"#15803d", border:"1px solid #86efac", borderRadius:12, padding:"2px 10px", fontFamily:"'DM Mono',monospace" }}>"{search}"</span>}
            {filters.terpene && <span style={{ fontSize:10, background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", borderRadius:12, padding:"2px 10px", fontFamily:"'DM Mono',monospace" }}>{filters.terpene}</span>}
            {filters.category && <span style={{ fontSize:10, background:"#fef3c7", color:"#92400e", border:"1px solid #fcd34d", borderRadius:12, padding:"2px 10px", fontFamily:"'DM Mono',monospace" }}>{filters.category}</span>}
            {filters.city && <span style={{ fontSize:10, background:"#f5f3ff", color:"#6d28d9", border:"1px solid #ddd6fe", borderRadius:12, padding:"2px 10px", fontFamily:"'DM Mono',monospace" }}>{filters.city}</span>}
            <button onClick={()=>{ setSearch(""); setFilters({ terpene:"", category:"", plantType:"", city:"", dispensary:"", terpSource:"", thcMin:"", thcMax:"", terpMin:"", terpMax:"", sortBy:"name" }); }}
              style={{ fontSize:10, color:"#dc2626", background:"none", border:"none", cursor:"pointer", fontFamily:"'DM Mono',monospace", marginLeft:"auto", padding:0, fontWeight:600 }}>
              Clear all ×
            </button>
          </div>
        )}
        {/* THC + Terpene range row */}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8, alignItems:"center" }}>
          <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>THC range:</span>
          <input type="number" placeholder="Min %" value={filters.thcMin} onChange={e => setFilters(p=>({...p,thcMin:e.target.value}))}
            style={{ width:70, padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none" }} />
          <span style={{ fontSize:11, color:"#9ca3af" }}>—</span>
          <input type="number" placeholder="Max %" value={filters.thcMax} onChange={e => setFilters(p=>({...p,thcMax:e.target.value}))}
            style={{ width:70, padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none" }} />
          <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginLeft:8 }}>Terpene %:</span>
          <input type="number" step="0.1" placeholder="Min %" value={filters.terpMin} onChange={e => setFilters(p=>({...p,terpMin:e.target.value}))}
            style={{ width:70, padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none" }} />
          <span style={{ fontSize:11, color:"#9ca3af" }}>—</span>
          <input type="number" step="0.1" placeholder="Max %" value={filters.terpMax} onChange={e => setFilters(p=>({...p,terpMax:e.target.value}))}
            style={{ width:70, padding:"6px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", outline:"none" }} />
          {/* Active filter chips */}
          {[
            filters.city && { label: "📍 " + (filters.city), key:"city" },
            filters.dispensary && { label: DB_DISPENSARIES.find(d=>d.id===filters.dispensary)?.name||filters.dispensary, key:"dispensary" },
            filters.category && { label: filters.category, key:"category" },
            filters.plantType && { label: filters.plantType, key:"plantType" },
            filters.terpene && { label: filters.terpene, key:"terpene" },
            filters.terpSource && { label: filters.terpSource==="lab_tested"?"Lab Verified":filters.terpSource==="lp_provided"?"LP Provided":"Estimated", key:"terpSource" },
            (filters.thcMin||filters.thcMax) && { label:"THC " + (filters.thcMin||"0") + "–" + (filters.thcMax||"∞") + "%", key:"thc" },
            (filters.terpMin||filters.terpMax) && { label:"Terp " + (filters.terpMin||"0") + "–" + (filters.terpMax||"∞") + "%", key:"terp" },
          ].filter(Boolean).map(({label, key}) => (
            <span key={key} onClick={() => key==="thc" ? setFilters(p=>({...p,thcMin:"",thcMax:""})) : key==="terp" ? setFilters(p=>({...p,terpMin:"",terpMax:""})) : setFilters(p=>({...p,[key]:"" }))}
              style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", background:"#e8f4eb", border:"1px solid #86efac", borderRadius:20, fontSize:10, fontFamily:"'DM Mono',monospace", color:"#166534", cursor:"pointer" }}>
              {label} ✕
            </span>
          ))}
          {(filters.terpene||filters.category||filters.plantType||filters.city||filters.dispensary||filters.terpSource||filters.thcMin||filters.thcMax||filters.terpMin||filters.terpMax||search) && (
            <button onClick={()=>{setFilters({terpene:"",category:"",plantType:"",city:"",dispensary:"",terpSource:"",sortBy:"name",thcMin:"",thcMax:"",terpMin:"",terpMax:""});setSearch("");}}
              style={{ padding:"5px 12px", background:"none", border:"1px solid #e5e7eb", borderRadius:8, fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace", color:"#9ca3af" }}>
              Clear all ✕
            </button>
          )}
        </div>
        {/* Contextual range explanations */}
        {(filters.thcMin || filters.thcMax || filters.terpMin || filters.terpMax) && (
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12 }}>
            {(filters.thcMin || filters.thcMax) && (
              <div style={{ padding:"8px 12px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8, fontSize:11, color:"#166534", fontFamily:"'DM Mono',monospace", lineHeight:1.5, flex:"1 1 280px" }}>
                <strong>THC %</strong> — measured as Δ9-THC by dry weight. Flower typically ranges 15–28%. Vape concentrates 65–90%. Edibles listed per piece (mg), not %.
              </div>
            )}
            {(filters.terpMin || filters.terpMax) && (
              <div style={{ padding:"8px 12px", background:"#fef9f0", border:"1px solid #fed7aa", borderRadius:8, fontSize:11, color:"#9a3412", fontFamily:"'DM Mono',monospace", lineHeight:1.5, flex:"1 1 280px" }}>
                <strong>Terpene %</strong> — total terpenes by weight. Dried flower: 0.5–2.5% is typical, above 2% is considered terpene-rich. Live resin/rosin vape carts: 5–10%. Distillate carts with CDT may read lower despite added terpenes.
              </div>
            )}
          </div>
        )}
        <p style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:16 }}>{filtered.length} product{filtered.length!==1?"s":""} from {DB_DISPENSARIES.length} connected retailers</p>
        {recentlyViewed.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em" }}>🕐 Recently Viewed</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {recentlyViewed.slice(0,6).map(r => {
                const prod = ALL_PRODUCTS.find(p=>p.id===r.id);
                if (!prod) return null;
                return (
                  <button key={r.id} onClick={()=>viewProduct(prod)}
                    style={{ padding:"5px 12px", borderRadius:18, border:"1px solid #e8e4de", background:"#fff", color:"#374151", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    {r.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(min(300px,100%),1fr))", gap:12 }}>
          {filtered.map(p => {
            const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
            const ti = topT ? TERPENES[topT[0]] : null;
            return (
              <div key={p.id} className="card card-click" style={{ padding:18 }} onClick={()=>{setSelectedProduct(p);setPage("product");}}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", gap:5, marginBottom:6, flexWrap:"wrap" }}>
                      <span className="pill">{p.category}</span>
                      <span className={"lab-badge " + (p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided"?"lab-verified":"lab-estimated")} style={{ fontSize:9 }}>
                        {p.terpene_source==="lab_tested"?"✓ Lab":p.terpene_source==="lp_provided"?"✓ LP":"~ Est."}
                      </span>
                      {p.cultivation?.lp_verified && <span style={{ fontSize:9, background:"#f0fdf4", color:"#15803d", border:"1px solid #86efac", borderRadius:4, padding:"2px 6px", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>🏷 LP</span>}
                      {(() => { const f = getFreshness(p); return f ? <span title={f.note} style={{ display:"inline-flex", alignItems:"center", gap:3, padding:"2px 7px", borderRadius:6, fontSize:9, fontFamily:"'DM Mono',monospace", background:(f.color) + "15", color:f.color, border:"1px solid " + (f.color) + "40", cursor:"default" }}>◉ {f.label}</span> : null; })()}
                    </div>
                    <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:16, fontWeight:400, color:"#1a2e1a", marginBottom:2 }}>{p.name}</h3>
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                      <p style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", margin:0 }}>{p.brand} · {dispName(p.dispensary_id)}</p>
                      {p.cultivation?.irradiation === "non_irradiated" && <span style={{ fontSize:8, background:"#f0fdf4", color:"#15803d", border:"1px solid #86efac", borderRadius:4, padding:"1px 5px", fontFamily:"'DM Mono',monospace" }}>✓ Non-Irradiated</span>}
                      {(p.cultivation?.irradiation === "gamma" || p.cultivation?.irradiation === "e_beam") && <span style={{ fontSize:8, background:"#fffbeb", color:"#92400e", border:"1px solid #fcd34d", borderRadius:4, padding:"1px 5px", fontFamily:"'DM Mono',monospace" }}>☢ Irradiated</span>}
                    </div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0, marginLeft:8 }}>
                    <div style={{ fontSize:17, fontWeight:500, color:"#22c55e", fontFamily:"'Fraunces',serif" }}>{p.thc}%</div>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Δ9-THC</div>
                    {p.cbd > 1 && <><div style={{ fontSize:14, fontWeight:500, color:"#3b82f6", fontFamily:"'Fraunces',serif" }}>{p.cbd}%</div><div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>CBD</div></>}
                    {p.total_terpenes && <><div style={{ fontSize:13, fontWeight:500, color:"#4a7c59", fontFamily:"'Fraunces',serif", marginTop:4 }}>{p.total_terpenes.toFixed(2)}%</div><div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Terpenes</div></>}
                  </div>
                </div>
                {ti && topT && (
                  <div style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 10px", background:(ti.color) + "10", borderRadius:8, marginBottom:8, border:"1px solid " + (ti.color) + "25", flexWrap:"wrap" }}>
                    <span className="chip" style={{ background:(ti.color) + "20", color:ti.color, fontSize:10 }}>Primary: {topT[0]}</span>
                    <span style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{ti.aroma.slice(0,2).join(" · ")}</span>
                    {p.total_terpenes && <span style={{ fontSize:10, color:"#1a2e1a", fontFamily:"'DM Mono',monospace", marginLeft:"auto", fontWeight:600 }}>{p.total_terpenes.toFixed(2)}% terp</span>}
                  </div>
                )}
                <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:8 }}>
                  {Object.keys(p.terpenes).map(t=>{const ti2=TERPENES[t];return <span key={t} className="chip" style={{ background:(ti2?.color||"#6b7280") + "15",color:ti2?.color||"#6b7280",border:"1px solid " + (ti2?.color||"#6b7280") + "25",fontSize:10 }}>{t}</span>;})}
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:10, borderTop:"1px solid #f3f4f6" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    <span className="pill" style={{ fontSize:9 }}>{p.cultivation.method}</span>
                    <span className="pill" style={{ fontSize:9 }}>{(p.cultivation?.cure||"—").split(" ")[0]} cure</span>
                  </div>
                  <span style={{ fontSize:11, color:"#1a2e1a", fontFamily:"'DM Mono',monospace" }}>View →</span>
                </div>
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"56px 24px" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>🌿</div>
            <p style={{ fontFamily:"'Fraunces',serif", fontSize:20, color:"#1a2e1a", marginBottom:8 }}>No products match these filters</p>
            <p style={{ fontSize:12, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>Try adjusting your search or clearing a filter</p>
            <button onClick={() => { setFilters({ terpene:"", category:"", plantType:"", dispensary:"", terpSource:"", sortBy:"name", thcMin:"", thcMax:"", terpMin:"", terpMax:"" }); setSearch(""); }}
              style={{ background:"#1a2e1a", color:"#fff", border:"none", borderRadius:10, padding:"10px 24px", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ── FIND YOUR STRAIN PAGE ─────────────────────────────────
  const FINDER_QUESTIONS = [
    // Q1 — Consumption method (hard filter — most important)
    { id:"format", question:"How do you prefer to consume?", emoji:"🌬️", subtitle:"This determines which product types we will match",
      options:[
        { label:"Dried flower",       value:"flower",      categories:["Dried Flower"],               desc:"Smoked or vaporized — full terpene variety, most COA data" },
        { label:"Pre-roll",            value:"preroll",     categories:["Pre-Roll"],                   desc:"Ready to use — whole flower or infused, single-session" },
        { label:"Vape cart or pen",    value:"vape",        categories:["Vape Cart","Disposable Vape"],desc:"Inhaled concentrate — discreet, faster onset than flower" },
        { label:"Edible or capsule",   value:"edible",      categories:["Edible"],                     desc:"Ingested — onset 30 min to 2 hours, longer duration" },
        { label:"Oil or tincture",     value:"oil",         categories:["Oil"],                        desc:"Sublingual or added to food — precise dosing, flexible onset" },
        { label:"Beverage",            value:"beverage",    categories:["Beverage"],                   desc:"Infused drinks — typically faster onset than solid edibles" },
        { label:"Concentrate",         value:"concentrate", categories:["Concentrate"],                desc:"Hash, rosin, live resin — highest terpene concentration format" },
        { label:"No preference",       value:"any",         categories:null,                           desc:"Match across all product formats" },
      ]},
    // Q2 — Desired experience
    { id:"feeling", question:"What kind of terpene profile are you drawn to?", emoji:"🌿", subtitle:"We match on aroma chemistry — individual responses to cannabis vary",
      options:[
        { label:"Earthy & musky",           value:"relax",   terpenes:["β-Myrcene","Linalool","α-Bisabolol"],        type:"indica",   desc:"Myrcene and linalool-dominant — the classic heavy indica aroma" },
        { label:"Citrusy & bright",         value:"energy",  terpenes:["Terpinolene","(+)-Limonene","β-Ocimene"],     type:"sativa",   desc:"Terpinolene and limonene-forward — fresh, zesty, sativa-leaning" },
        { label:"Piney & herbal",           value:"focus",   terpenes:["α-Pinene","(+)-Limonene","Terpinolene"],      type:"sativa",   desc:"α-Pinene dominant — the same compound in pine trees and rosemary" },
        { label:"Spicy & complex",          value:"social",  terpenes:["(+)-Limonene","β-Caryophyllene","Linalool"],  type:"balanced", desc:"Limonene and caryophyllene together — balanced, layered aroma" },
        { label:"Earthy & hoppy",           value:"body",    terpenes:["β-Myrcene","β-Caryophyllene","α-Humulene"],   type:"indica",   desc:"Myrcene, caryophyllene, humulene — the hops-and-pepper combination" },
        { label:"Floral & heavy",           value:"sleep",   terpenes:["β-Myrcene","Linalool","α-Bisabolol"],         type:"indica",   desc:"Linalool and myrcene-forward — lavender-soft, dense indica profile" },
        { label:"No preference",            value:"any",     terpenes:[],                                             type:null,       desc:"Let aroma family and format drive the match instead" },
      ]},
    // Q3 — Intensity / experience level
    { id:"intensity", question:"What THC level are you looking for?", emoji:"📊", subtitle:"THC % as shown on Canadian product labels — individual responses vary",
      options:[
        { label:"Mild (under 15% THC)",   value:"mild",     thcMax:15,  thcMin:0,   desc:"Lower potency — a common starting point for newer consumers" },
        { label:"Moderate (15–22% THC)",  value:"moderate", thcMax:22,  thcMin:15,  desc:"The most common potency range in the Canadian regulated market" },
        { label:"Strong (22–28% THC)",    value:"strong",   thcMax:28,  thcMin:22,  desc:"Higher potency — Health Canada advises starting low regardless of experience" },
        { label:"No preference",          value:"any",      thcMax:null, thcMin:null, desc:"Match across all potency levels" },
      ]},
    // Q4 — Terpene character
    { id:"aroma", question:"What kind of aroma appeals to you?", emoji:"👃", subtitle:"Aroma is the best predictor of terpene profile",
      options:[
        { label:"Citrus / Tropical",   value:"citrus",  terpenes:["(+)-Limonene","Terpinolene","β-Ocimene"],     aromas:["Citrus","Lemon","Orange","Tropical","Mango","Tangerine"] },
        { label:"Earthy / Pine",       value:"earthy",  terpenes:["α-Pinene","β-Myrcene","α-Humulene"],          aromas:["Earthy","Pine","Herbal","Woody","Spice","Sage"] },
        { label:"Floral / Sweet",      value:"floral",  terpenes:["Linalool","α-Bisabolol","β-Ocimene"],         aromas:["Floral","Sweet","Vanilla","Berry","Lavender","Creamy"] },
        { label:"Fuel / Gas / Diesel", value:"fuel",    terpenes:["β-Myrcene","β-Caryophyllene","α-Humulene"],   aromas:["Diesel","Gas","Fuel","Skunk","Pungent","Earthy"] },
        { label:"No preference",       value:"any",     terpenes:[],                                             aromas:[] },
      ]},
    // Q5 — Cultivation preference
    { id:"cultivation", question:"Any preference on how it was grown?", emoji:"☀️", subtitle:"Growing method affects terpene expression and character",
      options:[
        { label:"Indoor craft",        value:"indoor",     method:"Indoor",     desc:"Controlled environment — most consistent, highest terpene precision" },
        { label:"Sun-grown / Outdoor", value:"outdoor",    method:"Outdoor",    desc:"Natural sunlight — complex terpene expression, terroir character" },
        { label:"Greenhouse",          value:"greenhouse", method:"Greenhouse", desc:"Best of both — natural light with environmental protection" },
        { label:"No preference",       value:"any",        method:null,         desc:"Match across all growing methods" },
      ]},
    // Q6 — Anything to avoid (multi-select)
    { id:"avoid", question:"Anything you'd like to deprioritize?", emoji:"🚫", subtitle:"Select all that apply — we'll weight these out of your results", multiSelect:true,
      options:[
        { label:"High-myrcene / heavier indica profiles", value:"no_sedation",  excludeTerpenes:["β-Myrcene"], excludeType:"indica",  excludeAroma:[] },
        { label:"Favour linalool & bisabolol profiles",   value:"no_anxiety",   excludeTerpenes:[],             excludeType:null,     preferTerpenes:["Linalool","α-Bisabolol","β-Caryophyllene"] },
        { label:"Strong / skunky aromas",                 value:"no_skunk",     excludeTerpenes:[],             excludeType:null,     excludeAroma:["Skunk","Pungent","Garlic","Gas","Diesel","Fuel"] },
        { label:"Estimated terpene data (LP-provided only)", value:"no_estimated", excludeTerpenes:[],           excludeType:null,     requireVerified:true },
        { label:"No preferences",                         value:"none",         excludeTerpenes:[],             excludeType:null,     excludeAroma:[], exclusive:true },
      ]},
    // Q7 — Location filter (locationSelect type)
    { id:"location", question:"Filter by location?", emoji:"📍", subtitle:"Narrow results to a specific city or store", locationSelect:true },
  ];

  const scoreProduct = (p, answers) => {
    let score = 50; // base
    const breakdown = []; // for transparency display
    const isConcentrate = ["Vape Cart","Disposable Vape","Concentrate"].includes(p.category);
    const maxPossible = 50 + 36 + 25 + 24 + 22 + 30 + 10 + 8; // base+feel+thc+aroma+cult+avoid+terpRich+dataQ

    // Q1 — FORMAT hard filter (category match)
    const fmtQ = FINDER_QUESTIONS[0].options.find(o=>o.value===answers.format);
    if (fmtQ?.categories) {
      if (!fmtQ.categories.includes(p.category)) return { score:-999, breakdown:[], pct:0 };
    }

    // Q2 — FEELING (terpene scoring)
    const feelQ = FINDER_QUESTIONS[1].options.find(o=>o.value===answers.feeling);
    if (feelQ && answers.feeling !== "any") {
      let feelScore = 0;
      feelQ.terpenes.forEach((t,i) => { if (p.terpenes[t]) feelScore += (3-i) * p.terpenes[t] * 12; });
      if (feelQ.type === "indica" && p.plant_type?.toLowerCase().includes("indica")) feelScore += 18;
      if (feelQ.type === "sativa" && p.plant_type?.toLowerCase().includes("sativa")) feelScore += 18;
      if (feelQ.type === "balanced") feelScore += 8;
      feelScore = Math.min(36, feelScore);
      score += feelScore;
      if (feelScore >= 20) breakdown.push({ label:"Effect profile", icon:"✓", positive:true });
      else if (feelScore < 8) breakdown.push({ label:"Effect profile", icon:"~", positive:false });
    }

    // Q3 — INTENSITY — skip for concentrates (THC% not comparable)
    const intQ = FINDER_QUESTIONS[2].options.find(o=>o.value===answers.intensity);
    if (!isConcentrate && intQ && intQ.thcMax !== null) {
      let thcScore = 0;
      if (p.thc >= intQ.thcMin && p.thc <= intQ.thcMax) {
        thcScore = 25;
        breakdown.push({ label:"THC range", icon:"✓", positive:true });
      } else if (p.thc > intQ.thcMax) {
        thcScore = -Math.min(30, (p.thc - intQ.thcMax) * 2);
        breakdown.push({ label:"THC range", icon:"↑", positive:false, note:(p.thc) + "% — above preferred" });
      } else {
        thcScore = -Math.min(15, (intQ.thcMin - p.thc) * 1.5);
        breakdown.push({ label:"THC range", icon:"↓", positive:false, note:(p.thc) + "% — below preferred" });
      }
      score += thcScore;
    } else if (isConcentrate && intQ && intQ.thcMax !== null) {
      // concentrates: skip THC penalty, give neutral note
      breakdown.push({ label:"THC range", icon:"—", positive:true, note:"N/A for concentrates" });
    }

    // Q4 — AROMA (terpene + aroma scoring)
    const aromaQ = FINDER_QUESTIONS[3].options.find(o=>o.value===answers.aroma);
    if (aromaQ && answers.aroma !== "any") {
      let aromaScore = 0;
      aromaQ.terpenes.forEach((t,i) => { if (p.terpenes[t]) aromaScore += (3-i) * p.terpenes[t] * 8; });
      const prodAroma = (p.aroma||[]).map(a=>a.toLowerCase());
      const matchedAromas = aromaQ.aromas.filter(a => prodAroma.some(pa => pa.includes(a.toLowerCase())));
      aromaScore += matchedAromas.length * 10;
      aromaScore = Math.min(24, aromaScore);
      score += aromaScore;
      if (aromaScore >= 12) breakdown.push({ label:"Aroma match", icon:"✓", positive:true });
      else if (aromaScore < 5) breakdown.push({ label:"Aroma match", icon:"~", positive:false });
    }

    // Q5 — CULTIVATION
    const cultQ = FINDER_QUESTIONS[4].options.find(o=>o.value===answers.cultivation);
    if (cultQ?.method) {
      if (p.cultivation?.method === cultQ.method) {
        score += 22;
        breakdown.push({ label:"Grow method", icon:"✓", positive:true });
      } else {
        breakdown.push({ label:"Grow method", icon:"✗", positive:false, note:p.cultivation?.method });
      }
    }

    // Q6 — AVOID (now multi-select array)
    const avoidArr = Array.isArray(answers.avoid) ? answers.avoid : (answers.avoid ? [answers.avoid] : []);
    let avoidScore = 0;
    let avoidFlags = [];
    avoidArr.forEach(av => {
      const avoidQ = FINDER_QUESTIONS[5].options.find(o=>o.value===av);
      if (!avoidQ || av === "none") return;
      (avoidQ.excludeTerpenes||[]).forEach(t => { if ((p.terpenes[t]||0) > 0.3) avoidScore -= 30; });
      (avoidQ.preferTerpenes||[]).forEach(t => { if (p.terpenes[t]) avoidScore += p.terpenes[t] * 10; });
      if (avoidQ.excludeAroma?.length) {
        const prodAroma = (p.aroma||[]).map(a=>a.toLowerCase());
        if (avoidQ.excludeAroma.some(ex => prodAroma.some(pa => pa.includes(ex.toLowerCase())))) {
          avoidScore -= 35;
          avoidFlags.push(avoidQ.label);
        }
      }
      if (avoidQ.excludeType && p.plant_type?.toLowerCase().includes(avoidQ.excludeType)) avoidScore -= 25;
      if (avoidQ.requireVerified && p.terpene_source === "estimated") avoidScore -= 40;
    });
    score += Math.max(-60, avoidScore);
    if (avoidFlags.length) breakdown.push({ label:"Avoid filter", icon:"✗", positive:false, note:avoidFlags.join(", ") });

    // Q7 — LOCATION hard filter
    if (answers.locationDispensary && answers.locationDispensary !== "any") {
      if (p.dispensary_id !== answers.locationDispensary) return { score:-999, breakdown:[], pct:0 };
      breakdown.push({ label:"Location match", icon:"✓", positive:true });
    } else if (answers.locationCity && answers.locationCity !== "any") {
      const disp = DB_DISPENSARIES.find(d => d.id === p.dispensary_id);
      if (disp?.city !== answers.locationCity) return { score:-999, breakdown:[], pct:0 };
      breakdown.push({ label:"City match", icon:"✓", positive:true });
    }

    // Terpene richness bonus (category-normalised)
    if (p.total_terpenes) {
      const normVal = isConcentrate ? p.total_terpenes / 9 : p.total_terpenes / 2;
      const richBonus = Math.min(10, normVal * 10);
      score += richBonus;
      if (richBonus >= 7) breakdown.push({ label:"Terpene richness", icon:"✓", positive:true });
    }

    // Data quality bonus
    if (p.terpene_source === "lab_tested") { score += 8; breakdown.push({ label:"Lab-verified data", icon:"✓", positive:true }); }
    else if (p.terpene_source === "lp_provided") score += 4;

    const pct = Math.max(0, Math.min(100, Math.round((score / maxPossible) * 100)));
    return { score, breakdown, pct };
  };

  // Save budtender note to profile
  const TerpeneRadar = ({ fingerprint, size = 200 }) => {
    const KEYS = ["β-Myrcene","β-Caryophyllene","(+)-Limonene","α-Pinene","Terpinolene","Linalool","β-Ocimene","α-Humulene","α-Bisabolol"];
    const SHORT = ["Myrcene","Caryoph.","Limonene","Pinene","Terpinol.","Linalool","Ocimene","Humulene","Bisabolol"];
    const cx = size/2, cy = size/2, r = size*0.34, labelR = size*0.47;
    const n = KEYS.length;
    const angle = (i) => (Math.PI * 2 * i / n) - Math.PI/2;
    const pt = (i, radius) => ({ x: cx + radius * Math.cos(angle(i)), y: cy + radius * Math.sin(angle(i)) });
    const rings = [0.25, 0.5, 0.75, 1.0];
    const dataPoints = KEYS.map((k,i) => pt(i, r * ((fingerprint[k]||0)/100)));
    const polygon = dataPoints.map((p,i) => (p.x) + "," + (p.y)).join(" ");
    const gridRing = (ratio) => KEYS.map((_,i) => pt(i, r*ratio)).map((p,i)=>(i===0?"M":"L") + (p.x) + "," + (p.y)).join(" ")+"Z";
    return (
      <svg width={size} height={size} style={{overflow:"visible"}}>
        {rings.map(ri=><path key={ri} d={gridRing(ri)} fill="none" stroke="#e8e4de" strokeWidth="1"/>)}
        {KEYS.map((_,i)=>{const p=pt(i,r);return<line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#e8e4de" strokeWidth="1"/>;})}
        <polygon points={polygon} fill="#4a7c5930" stroke="#4a7c59" strokeWidth="2" strokeLinejoin="round"/>
        {dataPoints.map((p,i)=><circle key={i} cx={p.x} cy={p.y} r="3" fill="#4a7c59"/>)}
        {KEYS.map((_,i)=>{
          const lp=pt(i,labelR); const val=fingerprint[KEYS[i]]||0;
          return <g key={i}>
            <text x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle" fontSize="7" fill="#6b7280" fontFamily="'DM Mono',monospace">{SHORT[i]}</text>
            {val>20&&<text x={pt(i,r*(val/100)).x} y={pt(i,r*(val/100)).y-8} textAnchor="middle" fontSize="7" fill="#4a7c59" fontFamily="'DM Mono',monospace">{val}%</text>}
          </g>;
        })}
      </svg>
    );
  };

  // ── BATCH FINDER MODAL ───────────────────────────────────────
  const BatchFinderModal = ({ onClose }) => {
    const selected=bfSelected, setSelected=setBfSelected;
    const results=bfResults, setResults=setBfResults;
    const search=bfSearch, setSearch=setBfSearch;

    const filtered = profiles.filter(p =>
      !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.phone?.includes(search.replace(/\D/g,""))
    );

    const toggle = (profile) => {
      setSelected(prev => prev.find(p=>p.phone===profile.phone)
        ? prev.filter(p=>p.phone!==profile.phone)
        : prev.length < 5 ? [...prev, profile] : prev
      );
    };

    const run = () => {
      if (selected.length < 2) return;
      setResults(scoreBatch(selected));
    };

    const TERPENE_COLORS = {"β-Myrcene":"#6366f1","β-Caryophyllene":"#92400e","(+)-Limonene":"#f59e0b","α-Pinene":"#059669","Terpinolene":"#0ea5e9","Linalool":"#8b5cf6","β-Ocimene":"#ec4899","α-Humulene":"#78716c","α-Bisabolol":"#f472b6"};

    return (
      <div className="overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
        <div className="modal" style={{maxWidth:680,maxHeight:"90vh",overflow:"auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <div>
              <div style={{fontFamily:"'Fraunces',serif",fontSize:20,color:"#1a2e1a"}}>Batch Finder</div>
              <div style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginTop:2}}>Find products that work for a group — 2 to 5 customers</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#9ca3af"}}>✕</button>
          </div>

          {!results ? (<>
            <input className="inp" value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search customers..." style={{marginBottom:12}}/>
            <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:10}}>
              Selected: {selected.length}/5 {selected.length>0&&"— " + (selected.map(p=>p.name.split(" ")[0]).join(", "))}
            </div>
            {selected.length>0&&(
              <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
                {selected.map(p=>(
                  <span key={p.phone} style={{display:"inline-flex",alignItems:"center",gap:6,background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:20,padding:"4px 12px",fontSize:11,fontFamily:"'DM Mono',monospace"}}>
                    {p.name.split(" ")[0]}
                    <button onClick={()=>toggle(p)} style={{background:"none",border:"none",cursor:"pointer",color:"#6b7280",padding:0,fontSize:12}}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:320,overflowY:"auto",marginBottom:16}}>
              {filtered.map(p=>{
                const sel = !!selected.find(s=>s.phone===p.phone);
                const fp = buildTerpeneFingerprint(p);
                const top = Object.entries(fp).sort((ea,eb)=>eb[1]-ea[1])[0];
                return (
                  <div key={p.phone} onClick={()=>toggle(p)}
                    style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",border:"1px solid " + (sel?"#4a7c59":"#e8e4de"),
                      background:sel?"#f0fdf4":"#fff",borderRadius:10,cursor:"pointer",transition:"all 0.15s"}}>
                    <div style={{width:20,height:20,borderRadius:5,border:"2px solid " + (sel?"#4a7c59":"#d1d5db"),
                      background:sel?"#4a7c59":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {sel&&<span style={{color:"#fff",fontSize:12}}>✓</span>}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,color:"#1a2e1a",fontWeight:500}}>{p.name}</div>
                      <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{p.city||"BC"} · {top?top[0]:"no data"}</div>
                    </div>
                  </div>
                );
              })}
              {filtered.length===0&&<div style={{textAlign:"center",color:"#9ca3af",padding:"20px 0",fontSize:12}}>No customers found</div>}
            </div>
            <button onClick={run} disabled={selected.length<2} className="btn-primary" style={{width:"100%",padding:"12px 0",opacity:selected.length<2?0.5:1}}>
              Find Matches for {selected.length} {selected.length===1?"Customer":"Customers"} →
            </button>
          </>) : (<>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:6}}>MATCHED FOR</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {selected.map(p=><span key={p.phone} className="pill" style={{background:"#f0fdf4",color:"#065f46",border:"1px solid #bbf7d0"}}>{p.name.split(" ")[0]}</span>)}
              </div>
            </div>
            {results.length===0&&<div style={{textAlign:"center",color:"#9ca3af",padding:"32px 0",fontSize:13}}>No products scored above 30% for all selected customers. Try fewer people or different profiles.</div>}
            {results.map(({product:p,min,avg,scores},i)=>{
              const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
              const disp = DB_DISPENSARIES.find(d=>d.id===p.dispensary_id);
              return (
                <div key={p.id} className="card" style={{padding:18,marginBottom:12,border:i===0?"2px solid #4a7c59":"1px solid #e8e4de",position:"relative"}}>
                  {i===0&&<div style={{position:"absolute",top:-10,left:16,background:"#4a7c59",color:"#fff",fontSize:9,fontFamily:"'DM Mono',monospace",padding:"3px 10px",borderRadius:20}}>🌿 BEST GROUP MATCH</div>}
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:2}}>{p.brand} · {p.category}</div>
                      <div style={{fontFamily:"'Fraunces',serif",fontSize:17,color:"#1a2e1a",marginBottom:8}}>{p.name}</div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
                        <div style={{display:"flex",alignItems:"center",gap:4}}>
                          <span style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>Group min:</span>
                          <span style={{fontSize:12,fontWeight:600,color:"#4a7c59",fontFamily:"'DM Mono',monospace"}}>{min}%</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:4}}>
                          <span style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>Avg:</span>
                          <span style={{fontSize:12,fontWeight:600,color:"#6b7280",fontFamily:"'DM Mono',monospace"}}>{avg}%</span>
                        </div>
                      </div>
                      {/* Per-person scores */}
                      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                        {selected.map((sp,si)=>(
                          <span key={sp.phone} style={{fontSize:10,fontFamily:"'DM Mono',monospace",padding:"2px 8px",borderRadius:12,
                            background:scores[si]>=60?"#d1fae5":scores[si]>=40?"#fef3c7":"#fef2f2",
                            color:scores[si]>=60?"#065f46":scores[si]>=40?"#92400e":"#991b1b"}}>
                            {sp.name.split(" ")[0]}: {scores[si]}%
                          </span>
                        ))}
                      </div>
                    </div>
                    <div style={{textAlign:"center",flexShrink:0}}>
                      <div style={{fontSize:18,fontWeight:700,color:"#4a7c59",fontFamily:"'DM Mono',monospace"}}>{min}%</div>
                      <div style={{fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>min score</div>
                    </div>
                  </div>
                  {topT&&<div style={{marginTop:8,fontSize:10,color:"#6b7280",fontFamily:"'DM Mono',monospace"}}>
                    Top terpene: <span style={{color:TERPENE_COLORS[topT[0]]||"#4a7c59"}}>{topT[0]} {topT[1]}%</span> · 📍 {disp?.name}, {disp?.city}
                  </div>}
                </div>
              );
            })}
            <button onClick={()=>setResults(null)} className="btn-ghost" style={{width:"100%",marginTop:8}}>← Change Selection</button>
          </>)}
        </div>
      </div>
    );
  };

  // ── REGISTER MODAL ───────────────────────────────────────────
  const RegisterModal = ({ mode, finderAnswers, onSave, onClose }) => {
    const name=rmName, setName=setRmName;
    const phone=rmPhone, setPhone=setRmPhone;
    const city=rmCity, setCity=setRmCity;
    const consent=rmConsent, setConsent=setRmConsent;
    const ageConsent=rmAgeConsent, setAgeConsent=setRmAgeConsent;
    const error=rmError, setError=setRmError;
    const step=rmStep, setStep=setRmStep;

    const formatPhone = (v) => {
      const d = v.replace(/\D/g,"").slice(0,10);
      if (d.length<=3) return d;
      if (d.length<=6) return "(" + (d.slice(0,3)) + ") " + (d.slice(3));
      return "(" + (d.slice(0,3)) + ") " + (d.slice(3,6)) + "-" + (d.slice(6));
    };
    const rawPhone = phone.replace(/\D/g,"");

    const handleSave = () => {
      if (!name.trim()) return setError("Name is required.");
      if (rawPhone.length !== 10) return setError("Enter a valid 10-digit phone number.");
      if (!consent || !ageConsent) return setError("Please confirm both consent checkboxes.");
      const existing = profiles.find(p=>p.phone===rawPhone);
      if (existing && mode==="create") return setError("A profile already exists for this number: " + (existing.name));
      const profile = {
        name: name.trim(), phone: rawPhone, city: city.trim(),
        finderAnswers: mode==="staff" ? finderAnswers : null,
        consumerAnswers: mode==="consumer" ? finderAnswers : null,
        productHistory: existing?.productHistory || [],
        visits: (existing?.visits||0)+1,
        consentDate: new Date().toISOString(),
      };
      upsertProfile(profile);
      // Take initial terpene journey snapshot
      const fp = buildTerpeneFingerprint({...profile, productHistory:[]});
      setTimeout(()=>saveJourneySnapshot(rawPhone, fp, "Initial passport"), 100);
      setStep("success");
      setTimeout(()=>onSave(profile), 1200);
    };

    if (step==="success") return (
      <div className="overlay"><div className="modal" style={{textAlign:"center",padding:40}}>
        <div style={{fontSize:48,marginBottom:12}}>✅</div>
        <div style={{fontFamily:"'Fraunces',serif",fontSize:22,color:"#1a2e1a",marginBottom:6}}>Profile saved</div>
        <div style={{fontSize:12,color:"#6b7280",fontFamily:"'DM Mono',monospace"}}>Terpene passport created for {name}</div>
      </div></div>
    );

    return (
      <div className="overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
        <div className="modal">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
            <div>
              <div style={{fontFamily:"'Fraunces',serif",fontSize:20,color:"#1a2e1a"}}>Create Terpene Passport</div>
              <div style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginTop:2}}>Preferences are saved for in-store recommendations only</div>
            </div>
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#9ca3af"}}>✕</button>
          </div>

          {[["Full name","text",name,v=>setName(v),"e.g. Alex Chen"],
            ["Phone number","tel",phone,v=>setPhone(formatPhone(v)),"(604) 555-0100"],
            ["City (optional)","text",city,v=>setCity(v),"e.g. Vancouver"]
          ].map(([label,type,val,fn,ph])=>(
            <div key={label} style={{marginBottom:14}}>
              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.06em"}}>{label}</div>
              <input className="inp" type={type} value={val} onChange={e=>fn(e.target.value)} placeholder={ph}/>
            </div>
          ))}

          <div style={{background:"#f7f5f0",borderRadius:10,padding:14,marginBottom:16}}>
            {[
              [consent,setConsent,"My terpene preferences and visit history may be stored for in-store product recommendations. This data will not be used to send me marketing communications."],
              [ageConsent,setAgeConsent,"I confirm I am 19 years of age or older (required under the Cannabis Act)."],
            ].map(([checked,fn,text],i)=>(
              <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:i===0?10:0}}>
                <input type="checkbox" checked={checked} onChange={e=>fn(e.target.checked)} style={{marginTop:2,flexShrink:0,cursor:"pointer"}}/>
                <div style={{fontSize:11,color:"#374151",lineHeight:1.5}}>{text}</div>
              </div>
            ))}
          </div>

          {error && <div style={{fontSize:11,color:"#991b1b",background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:8,padding:"8px 12px",marginBottom:12,fontFamily:"'DM Mono',monospace"}}>{error}</div>}

          <div style={{display:"flex",gap:10}}>
            <button onClick={onClose} className="btn-ghost" style={{flex:1}}>Cancel</button>
            <button onClick={handleSave} className="btn-primary" style={{flex:2,padding:"11px 0"}}>Save Terpene Passport →</button>
          </div>
          <div style={{fontSize:10,color:"#9ca3af",textAlign:"center",marginTop:12,fontFamily:"'DM Mono',monospace",lineHeight:1.5}}>
            Data stored locally on this device only. Used solely for in-store terpene matching. PIPEDA compliant.
          </div>
        </div>
      </div>
    );
  };

  // ── SHARED LOCATION PICKER STEP ────────────────────────────
  const LocationPickerStep = ({ onConfirm, onBack, stepLabel, compact = false }) => {
    const allCities = [...new Set(DB_DISPENSARIES.map(d => d.city))].sort();
    const cityDisps = pickerCity !== "any"
      ? DB_DISPENSARIES.filter(d => d.city === pickerCity).sort((a,b) => a.name.localeCompare(b.name))
      : [];

    const handleConfirm = () => {
      onConfirm({
        locationCity: pickerCity,
        locationDispensary: pickerDisp,
      });
    };

    const locationSummary = pickerDisp !== "any"
      ? DB_DISPENSARIES.find(d => d.id === pickerDisp)?.name
      : pickerCity !== "any" ? "All stores in " + (pickerCity)
      : "All locations";

    return (
      <div className="fade" style={{ maxWidth: compact ? 480 : 540, margin:"0 auto", padding:"28px 20px" }}>
        {stepLabel && (
          <div style={{ marginBottom:28 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
              <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{stepLabel}</span>
            </div>
          </div>
        )}
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:40, marginBottom:10 }}>📍</div>
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize: compact ? 20 : 24, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>
            {compact ? "Which store are you at?" : "Filter by location?"}
          </h2>
          <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>
            {compact ? "Only show products from your store" : "Narrow results to a specific city or store — or skip to see everything"}
          </p>
        </div>

        {/* City picker */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.06em" }}>City</div>
          <select value={pickerCity} onChange={e => { setPickerCity(e.target.value); setPickerDisp("any"); }}
            style={{ width:"100%", padding:"12px 14px", border:"1px solid #e8e4de", borderRadius:10, fontSize:13,
              fontFamily:"'DM Mono',monospace", background:"#fff", cursor:"pointer", outline:"none",
              appearance:"none", backgroundImage:"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
              backgroundRepeat:"no-repeat", backgroundPosition:"right 14px center" }}>
            <option value="any">All cities in BC</option>
            {allCities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Dispensary picker — only shown when city is selected */}
        {pickerCity !== "any" && cityDisps.length > 0 && (
          <div style={{ marginBottom:14 }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.06em" }}>
              Store in {pickerCity} ({cityDisps.length})
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <button onClick={() => setPickerDisp("any")}
                style={{ padding:"11px 16px", border: pickerDisp==="any" ? "2px solid #4a7c59" : "1px solid #e8e4de",
                  borderRadius:10, cursor:"pointer", textAlign:"left", background: pickerDisp==="any" ? "#f0fdf4" : "#fff",
                  fontSize:13, fontFamily:"'DM Mono',monospace", color:"#374151", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span>All stores in {pickerCity}</span>
                {pickerDisp==="any" && <span style={{ color:"#4a7c59", fontSize:11 }}>✓</span>}
              </button>
              {cityDisps.map(d => (
                <button key={d.id} onClick={() => setPickerDisp(d.id)}
                  style={{ padding:"11px 16px", border: pickerDisp===d.id ? "2px solid #4a7c59" : "1px solid #e8e4de",
                    borderRadius:10, cursor:"pointer", textAlign:"left", background: pickerDisp===d.id ? "#f0fdf4" : "#fff",
                    display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:"#1a2e1a" }}>{d.name}</div>
                    {d.neighbourhood && <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:1 }}>{d.neighbourhood}{d.address ? " · " + (d.address) : ""}</div>}
                  </div>
                  {pickerDisp===d.id && <span style={{ color:"#4a7c59", fontSize:11, flexShrink:0 }}>✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Summary & confirm */}
        <div style={{ marginTop:20, padding:"12px 16px", background:"#f7f5f0", borderRadius:10, marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:16 }}>📍</span>
          <div>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Showing results from</div>
            <div style={{ fontSize:13, fontWeight:500, color:"#1a2e1a" }}>{locationSummary}</div>
          </div>
        </div>

        <button onClick={handleConfirm} className="btn-primary" style={{ width:"100%", padding:"12px 0", fontSize:13 }}>
          {pickerCity === "any" ? "Show all locations →" : "Search " + (locationSummary) + " →"}
        </button>
        {onBack && (
          <div style={{ textAlign:"center", marginTop:12 }}>
            <button onClick={onBack} style={{ background:"none", border:"none", color:"#9ca3af", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>← Back</button>
          </div>
        )}
      </div>
    );
  };

  const DashboardPage = () => {
    const TERPENE_COLORS = {"β-Myrcene":"#6366f1","β-Caryophyllene":"#92400e","(+)-Limonene":"#f59e0b","α-Pinene":"#059669","Terpinolene":"#0ea5e9","Linalool":"#8b5cf6","β-Ocimene":"#ec4899","α-Humulene":"#78716c","α-Bisabolol":"#f472b6"};
    const gap = buildGapAnalysis();
    const totalProfiles = profiles.length;
    const totalRatings = profiles.reduce((a,p)=>(p.productHistory||[]).length+a,0);
    const upRatings = profiles.reduce((a,p)=>(p.productHistory||[]).filter(h=>h.rating==="up").length+a,0);
    const totalCerts = Object.keys(completedCerts).length;

    // Aggregate terpene preference across all profiles
    const KEYS = ["β-Myrcene","β-Caryophyllene","(+)-Limonene","α-Pinene","Terpinolene","Linalool","β-Ocimene","α-Humulene","α-Bisabolol"];
    const avgFP = {};
    if (totalProfiles > 0) {
      KEYS.forEach(k => {
        const vals = profiles.map(p=>buildTerpeneFingerprint(p)[k]||0);
        avgFP[k] = Math.round(vals.reduce((a,b)=>a+b,0)/vals.length);
      });
    }

    // Top rated products
    const productRatings = {};
    profiles.forEach(p=>(p.productHistory||[]).filter(h=>h.rating==="up").forEach(h=>{
      productRatings[h.productId] = productRatings[h.productId]||{name:h.name,brand:h.brand,count:0};
      productRatings[h.productId].count++;
    }));
    const topRated = Object.values(productRatings).sort((a,b)=>b.count-a.count).slice(0,5);

    // Format distribution
    const formatDist = {};
    profiles.forEach(p=>{
      const f=(p.finderAnswers||p.consumerAnswers||{}).format||"unknown";
      formatDist[f]=(formatDist[f]||0)+1;
    });

    // City distribution
    const cityDist = {};
    profiles.forEach(p=>{ const c=p.city||"Unknown"; cityDist[c]=(cityDist[c]||0)+1; });

    const tabs = [["overview","Overview"],["terpenes","Terpene Trends"],["products","Products"],["certs","Certifications"]];

    return (
      <div className="fade" style={{maxWidth:800,margin:"0 auto",padding:"28px 20px"}}>
        <div style={{marginBottom:24}}>
          <h1 style={{fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:300,color:"#1a2e1a",marginBottom:4}}>Dispensary Dashboard</h1>
          <p style={{fontSize:12,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>Store performance, customer insights, and team certifications</p>
        </div>

        <div style={{display:"flex",gap:8,marginBottom:24,flexWrap:"wrap"}}>
          {tabs.map(([t,l])=>(
            <button key={t} onClick={()=>setDashTab(t)} className={"tab " + (dashTab===t?"on":"off")}>{l}</button>
          ))}
        </div>

        {dashTab==="overview"&&(
          <div className="fade">
            {/* KPI row */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12,marginBottom:20}}>
              {["Passports","Products Tried","Satisfaction","Certified Staff"].map((_,kpiIdx)=>{
                const kpiData=[
                  ["Passports","👥",totalProfiles,"total created","customers"],
                  ["Products Tried","🌿",totalRatings,"across all customers","customers"],
                  ["Satisfaction","👍",totalRatings>0?Math.round((upRatings/totalRatings)*100)+"%":"—","positive ratings","customers"],
                  ["Certified Staff","🎓",totalCerts,"terpene advisors","certs"],
                ];
                const [l,e,v,sub,dest]=kpiData[kpiIdx];
                return (
                <button key={l} onClick={()=>dest&&setPage(dest)} className="card"
                  style={{padding:16,textAlign:"center",cursor:dest?"pointer":"default",width:"100%",border:"1px solid #e8e4de",background:"#fff",borderRadius:12,transition:"box-shadow 0.15s"}}
                  onMouseOver={ev=>dest&&(ev.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.08)")}
                  onMouseOut={ev=>ev.currentTarget.style.boxShadow="none"}>
                  <div style={{fontSize:22,marginBottom:4}}>{e}</div>
                  <div style={{fontFamily:"'Fraunces',serif",fontSize:22,color:"#1a2e1a",marginBottom:2}}>{v}</div>
                  <div style={{fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{l}</div>
                  <div style={{fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{sub}</div>
                  {dest&&<div style={{fontSize:9,color:"#4a7c59",fontFamily:"'DM Mono',monospace",marginTop:4}}>→ view</div>}
                </button>
                );
              })}            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
              {/* Format distribution */}
              <div className="card" style={{padding:18}}>
                <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>Format Preferences</div>
                {Object.entries(formatDist).length===0&&<div style={{fontSize:12,color:"#9ca3af"}}>No data yet.</div>}
                {Object.entries(formatDist).sort((ea,eb)=>eb[1]-ea[1]).map(([k,v])=>(
                  <div key={k} style={{marginBottom:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:11,color:"#374151"}}>{k}</span>
                      <span style={{fontSize:11,color:"#4a7c59",fontFamily:"'DM Mono',monospace"}}>{v}</span>
                    </div>
                    <div style={{height:5,background:"#f3f4f6",borderRadius:3}}>
                      <div style={{height:5,background:"#4a7c59",borderRadius:3,width:((v/totalProfiles)*100) + "%"}}/>
                    </div>
                  </div>
                ))}
              </div>
              {/* City distribution */}
              <div className="card" style={{padding:18}}>
                <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>Customer Cities</div>
                {Object.entries(cityDist).length===0&&<div style={{fontSize:12,color:"#9ca3af"}}>No data yet.</div>}
                {Object.entries(cityDist).sort((ea,eb)=>eb[1]-ea[1]).slice(0,6).map(([k,v])=>(
                  <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:"1px solid #f3f4f6"}}>
                    <span style={{fontSize:11,color:"#374151"}}>{k}</span>
                    <span style={{fontSize:11,color:"#4a7c59",fontFamily:"'DM Mono',monospace"}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Gap alert if data */}
            {gap&&gap.gaps[0]?.gap>15&&(
              <div className="card" style={{padding:16,background:"#fffbeb",border:"1px solid #fcd34d"}}>
                <div style={{fontSize:10,color:"#92400e",fontFamily:"'DM Mono',monospace",marginBottom:6,textTransform:"uppercase"}}>⚠️ Inventory Gap Detected</div>
                <div style={{fontSize:13,color:"#374151"}}>
                  Customer demand for <strong>{gap.gaps[0].terpene}</strong> is {gap.gaps[0].demand}% but current supply scores only {gap.gaps[0].supply}%.
                  Consider stocking more {gap.gapProducts[0]?.topProducts[0]?.name || "high-"+gap.gaps[0].terpene+" products"}.
                </div>
                <button onClick={()=>setPage("data")} style={{marginTop:8,background:"none",border:"none",color:"#92400e",fontSize:11,cursor:"pointer",fontFamily:"'DM Mono',monospace",textDecoration:"underline"}}>
                  View Full Gap Analysis →
                </button>
              </div>
            )}
          </div>
        )}

        {dashTab==="terpenes"&&(
          <div className="fade">
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
              <div className="card" style={{padding:20}}>
                <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em"}}>Average Customer Fingerprint</div>
                {totalProfiles===0&&<div style={{fontSize:12,color:"#9ca3af"}}>No profiles yet.</div>}
                {totalProfiles>0&&Object.entries(avgFP).sort((ea,eb)=>eb[1]-ea[1]).map(([t,v])=>(
                  <div key={t} style={{marginBottom:8}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:11,color:"#1a2e1a"}}>{t}</span>
                      <span style={{fontSize:11,color:TERPENE_COLORS[t]||"#4a7c59",fontFamily:"'DM Mono',monospace"}}>{v}%</span>
                    </div>
                    <div style={{height:6,background:"#f3f4f6",borderRadius:3}}>
                      <div style={{height:6,background:TERPENE_COLORS[t]||"#4a7c59",borderRadius:3,width:(v+"%"),opacity:0.8}}/>
                    </div>
                  </div>
                ))}
              </div>
              <div className="card" style={{padding:20,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em",alignSelf:"flex-start"}}>Store Terpene Radar</div>
                {totalProfiles>0
                  ? <TerpeneRadar fingerprint={avgFP} size={200}/>
                  : <div style={{fontSize:12,color:"#9ca3af",textAlign:"center",padding:40}}>Add customer profiles to see the store radar.</div>
                }
                <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginTop:8}}>n={totalProfiles} profiles</div>
              </div>
            </div>
          </div>
        )}

        {dashTab==="products"&&(
          <div className="fade">
            <div className="card" style={{padding:20,marginBottom:14}}>
              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em"}}>Top Rated Products (by customer votes)</div>
              {topRated.length===0&&<div style={{fontSize:12,color:"#9ca3af",textAlign:"center",padding:"24px 0"}}>No product ratings yet. Ask customers to rate products after purchase.</div>}
              {topRated.map((p,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:i<topRated.length-1?"1px solid #f3f4f6":"none"}}>
                  <div>
                    <div style={{fontSize:13,color:"#1a2e1a",fontWeight:500}}>#{i+1} {p.name}</div>
                    <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{p.brand}</div>
                  </div>
                  <span style={{fontSize:12,color:"#4a7c59",fontFamily:"'DM Mono',monospace"}}>👍 {p.count}</span>
                </div>
              ))}
            </div>

            {/* Wishlist demand */}
            <div className="card" style={{padding:20}}>
              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em"}}>Customer Wishlists (out-of-stock demand)</div>
              {(() => {
                const wishes = {};
                profiles.forEach(p=>(p.wishlist||[]).forEach(w=>{
                  wishes[w.productId]=wishes[w.productId]||{name:w.name,brand:w.brand,count:0};
                  wishes[w.productId].count++;
                }));
                const sorted = Object.values(wishes).sort((a,b)=>b.count-a.count);
                return sorted.length===0
                  ? <div style={{fontSize:12,color:"#9ca3af",textAlign:"center",padding:"16px 0"}}>No wishlist items yet.</div>
                  : sorted.map((w,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:i<sorted.length-1?"1px solid #f3f4f6":"none"}}>
                      <div>
                        <div style={{fontSize:12,color:"#1a2e1a",fontWeight:500}}>{w.name}</div>
                        <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{w.brand}</div>
                      </div>
                      <span style={{fontSize:12,color:"#f59e0b",fontFamily:"'DM Mono',monospace"}}>⭐ {w.count} waiting</span>
                    </div>
                  ));
              })()}
            </div>
          </div>
        )}

        {dashTab==="overview"&&(
          <div className="fade" style={{marginTop:16}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:8}}>
              {[
                {label:"Demand Gap",icon:"📊",sub:"Unmet terpene demand",page:"demand"},
                {label:"Staff Performance",icon:"👥",sub:"Certs & quiz scores",page:"staffperf"},
                {label:"Benchmarking",icon:"📈",sub:"vs BC network avg",page:"benchmark"},
                {label:"Batch Drift",icon:"⚠",sub:"Reorder intelligence",dest:"retailer",tab:"reorder"},
              ].map(item=>(
                <button key={item.label}
                  onClick={()=>{ if(item.dest){setPage(item.dest);setRetTab(item.tab);}else setPage(item.page); }}
                  style={{padding:"14px 12px",border:"1px solid #e8e4de",borderRadius:12,background:"#fff",cursor:"pointer",textAlign:"left",transition:"border-color 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="#4a7c59"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="#e8e4de"}>
                  <div style={{fontSize:20,marginBottom:6}}>{item.icon}</div>
                  <div style={{fontSize:12,fontWeight:600,color:"#1a2e1a",fontFamily:"'DM Mono',monospace",marginBottom:2}}>{item.label}</div>
                  <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{item.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {dashTab==="certs"&&(
          <div className="fade">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <div style={{fontSize:12,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{totalCerts} certification{totalCerts!==1?"s":""} issued</div>
              <button onClick={()=>setPage("certs")} className="btn-primary" style={{fontSize:12}}>+ Issue New Cert</button>
            </div>
            {totalCerts===0&&(
              <div style={{textAlign:"center",padding:"48px 0",color:"#9ca3af"}}>
                <div style={{fontSize:36,marginBottom:12}}>🎓</div>
                <div style={{fontFamily:"'Fraunces',serif",fontSize:18,color:"#374151",marginBottom:6}}>No certifications yet</div>
                <div style={{fontSize:12,fontFamily:"'DM Mono',monospace"}}>Complete training modules and issue certifications to staff.</div>
              </div>
            )}
            {Object.values(completedCerts).sort((a,b)=>new Date(b.date)-new Date(a.date)).map(cert=>(
              <div key={cert.id} className="card" style={{padding:16,marginBottom:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontFamily:"'Fraunces',serif",fontSize:15,color:"#1a2e1a",marginBottom:2}}>{cert.staffName}</div>
                  <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>
                    {cert.modules.length} module{cert.modules.length!==1?"s":""} · {new Date(cert.date).toLocaleDateString("en-CA")} · {cert.id}
                  </div>
                </div>
                <button onClick={()=>setPage("certs")} className="btn-ghost" style={{fontSize:11}}>View →</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── STAFF CERTIFICATION PAGE ──────────────────────────────────
  const CertificationPage = () => {
    const staffName=certName, setStaffName=setCertName;
    const selectedMods=certMods, setSelectedMods=setCertMods;
    const issued=certIssuedState, setIssued=setCertIssuedState;
    const modules = ["Terpene Science Fundamentals","Cannabinoid Pharmacology","Cultivation & Cure Science","Consumer Matching Methodology","Cannabis Act Compliance"];

    const issue = () => {
      if (!staffName.trim() || selectedMods.length===0) return;
      const cert = saveCert(staffName.trim(), selectedMods);
      setIssued(cert);
      showToast("Certificate issued for " + (staffName.trim()), "success");
    };

    if (issued) return (
      <div className="fade" style={{maxWidth:600,margin:"0 auto",padding:"28px 20px"}}>
        <button onClick={()=>setPage("dashboard")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:20}}>← Dashboard</button>
        {/* Certificate card */}
        <div style={{border:"2px solid #1a2e1a",borderRadius:16,padding:36,textAlign:"center",background:"#fafaf7",position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:0,left:0,right:0,height:6,background:"linear-gradient(90deg,#4a7c59,#1a2e1a,#4a7c59)"}}/>
          <div style={{fontSize:32,marginBottom:12}}>🌿</div>
          <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",letterSpacing:"0.12em",marginBottom:4}}>iTERPS TERPENE ADVISOR</div>
          <div style={{fontFamily:"'Fraunces',serif",fontSize:14,color:"#6b7280",marginBottom:8}}>Certificate of Completion</div>
          <div style={{fontFamily:"'Fraunces',serif",fontSize:28,fontWeight:400,color:"#1a2e1a",marginBottom:4}}>{issued.staffName}</div>
          <div style={{fontSize:12,color:"#6b7280",marginBottom:20}}>has successfully completed the following training modules</div>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:20}}>
            {issued.modules.map(m=>(
              <div key={m} style={{display:"flex",alignItems:"center",gap:8,background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,padding:"8px 14px",textAlign:"left"}}>
                <span style={{color:"#4a7c59",fontSize:14}}>✓</span>
                <span style={{fontSize:12,color:"#1a2e1a"}}>{m}</span>
              </div>
            ))}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",paddingTop:16,borderTop:"1px solid #e8e4de"}}>
            <div style={{textAlign:"left"}}>
              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>Issued</div>
              <div style={{fontSize:12,color:"#374151"}}>{new Date(issued.date).toLocaleDateString("en-CA",{year:"numeric",month:"long",day:"numeric"})}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>Certificate ID</div>
              <div style={{fontSize:11,color:"#374151",fontFamily:"'DM Mono',monospace"}}>{issued.id}</div>
            </div>
          </div>
          <div style={{fontSize:9,color:"#9ca3af",marginTop:16,fontFamily:"'DM Mono',monospace"}}>
            iTerps · BC Cannabis Education Platform · iterps.ca
          </div>
        </div>
        <div style={{display:"flex",gap:10,marginTop:16}}>
          <button onClick={()=>{ setIssued(null); setStaffName(""); setSelectedMods([]); }} className="btn-ghost" style={{flex:1}}>Issue Another</button>
          <button onClick={()=>window.print()} className="btn-primary" style={{flex:1}}>🖨 Print Certificate</button>
        </div>
      </div>
    );

    return (
      <div className="fade" style={{maxWidth:580,margin:"0 auto",padding:"28px 20px"}}>
        <button onClick={()=>setPage("dashboard")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:20}}>← Dashboard</button>
        <h1 style={{fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:300,color:"#1a2e1a",marginBottom:6}}>Issue Staff Certification</h1>
        <p style={{fontSize:12,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:24}}>Document terpene education for staff compliance records</p>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>Staff name</div>
          <input className="inp" value={staffName} onChange={e=>setStaffName(e.target.value)} placeholder="e.g. Jordan Smith"/>
        </div>
        <div style={{marginBottom:24}}>
          <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.06em"}}>Completed modules ({selectedMods.length} selected)</div>
          {modules.map(m=>{
            const sel=selectedMods.includes(m);
            return (
              <div key={m} onClick={()=>setSelectedMods(prev=>sel?prev.filter(x=>x!==m):[...prev,m])}
                style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",marginBottom:8,border:"1px solid " + (sel?"#4a7c59":"#e8e4de"),
                  background:sel?"#f0fdf4":"#fff",borderRadius:10,cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{width:20,height:20,borderRadius:5,border:"2px solid " + (sel?"#4a7c59":"#d1d5db"),
                  background:sel?"#4a7c59":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {sel&&<span style={{color:"#fff",fontSize:12}}>✓</span>}
                </div>
                <span style={{fontSize:13,color:sel?"#1a2e1a":"#374151"}}>{m}</span>
              </div>
            );
          })}
        </div>
        <button onClick={issue} disabled={!staffName.trim()||selectedMods.length===0} className="btn-primary"
          style={{width:"100%",padding:"13px 0",fontSize:13,opacity:(!staffName.trim()||selectedMods.length===0)?0.5:1}}>
          Issue Certificate →
        </button>
      </div>
    );
  };


  const FinderPage = () => {
    // State lifted to parent — prevents reset on sibling state changes
    const step = fStep, setStep = setFStep;
    const answers = fAnswers, setAnswers = setFAnswers;
    const results = fResults, setResults = setFResults;
    const avoidSelected = fAvoidSelected, setAvoidSelected = setFAvoidSelected;
    const expandedBreakdown = fExpandedBreakdown, setExpandedBreakdown = setFExpandedBreakdown;
    const compareA = fCompareA, setCompareA = setFCompareA;
    const compareB = fCompareB, setCompareB = setFCompareB;

    const addToCompare = (p) => {
      if (!compareA || compareA.id === p.id) { setCompareA(p); return; }
      if (!compareB || compareB.id === p.id) { setCompareB(p); return; }
      // Both slots full — shift: B→A, new→B
      setCompareA(compareB); setCompareB(p);
    };
    const isInCompare = (p) => compareA?.id === p.id || compareB?.id === p.id;
    const compareSlot = (p) => compareA?.id === p.id ? "A" : compareB?.id === p.id ? "B" : null;

    const currentQ = FINDER_QUESTIONS[step];
    const isAvoidStep = currentQ?.multiSelect;

    const handleAnswer = (qId, value) => {
      const next = { ...answers, [qId]: value };
      setAnswers(next);
      if (step < FINDER_QUESTIONS.length - 1) {
        setStep(step + 1);
      } else {
        const finderPool = (activeDispensary && !finderSearchAll)
          ? ALL_PRODUCTS.filter(p => p.dispensary_id === activeDispensary)
          : ALL_PRODUCTS;
        const scored = finderPool.map(p => {
          const result = scoreProduct(p, next);
          return { p, ...result };
        }).filter(x => x.score > -999)
          .sort((a,b) => b.score - a.score)
          .slice(0, 5);
        setResults(scored);
        // Save answers for prefill on next session
        try { localStorage.setItem("iterps_saved_finder", JSON.stringify(next)); setSavedFinderAnswers(next); } catch (_e) {}
        // Seed Explorer terpenene filter with top terpene from best match
        if (scored[0]) {
          const topT = Object.entries(scored[0].p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0]?.[0];
          if (topT) setFilters(f => ({ ...f, terpene: topT }));
        }
        setStep(FINDER_QUESTIONS.length);
        trackEvent("finder_completed", {
          mode: "staff",
          answers: next,
          resultCount: scored.length,
          topMatch: scored[0] ? { name: scored[0].p.name, brand: scored[0].p.brand, score: scored[0].score } : null,
          topTerpene: scored[0] ? Object.entries(scored[0].p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0]?.[0] : null,
        });
      }
    };

    const handleAvoidToggle = (value) => {
      const excl = FINDER_QUESTIONS[5].options.find(o=>o.value===value)?.exclusive;
      if (excl) { setAvoidSelected([value]); return; }
      setAvoidSelected(prev => {
        const without = prev.filter(v => {
          const opt = FINDER_QUESTIONS[5].options.find(o=>o.value===v);
          return !opt?.exclusive;
        });
        return without.includes(value) ? without.filter(v=>v!==value) : [...without, value];
      });
    };

    const confirmAvoid = () => {
      const val = avoidSelected.length ? avoidSelected : ["none"];
      handleAnswer("avoid", val);
    };

    const reset = () => { setStep(0); setAnswers({}); setResults(null); setAvoidSelected([]); setExpandedBreakdown(null); setPickerCity("any"); setPickerDisp("any"); };
    const q = FINDER_QUESTIONS[step];
    const progress = (step / FINDER_QUESTIONS.length) * 100;

    if (step === FINDER_QUESTIONS.length && results) return (
      <div className="fade" style={{ maxWidth:680, margin:"0 auto", padding:"32px 20px" }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:36, marginBottom:8 }}>✨</div>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Your Top Matches</h1>
          <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Ranked by terpene compatibility with your preferences</p>
          {activeDispensary && (
            <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginTop:8, flexWrap:"wrap" }}>
              <div style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"4px 12px", background: finderSearchAll?"#f9fafb":"#f0fdf4", border:"1px solid " + (finderSearchAll?"#e8e4de":"#bbf7d0"), borderRadius:20 }}>
                <span style={{ fontSize:11 }}>📍</span>
                <span style={{ fontSize:11, color: finderSearchAll?"#9ca3af":"#065f46", fontFamily:"'DM Mono',monospace" }}>
                  {finderSearchAll ? "All BC stores" : DB_DISPENSARIES.find(d=>d.id===activeDispensary)?.name}
                </span>
              </div>
              <button onClick={()=>setFinderSearchAll(f=>!f)}
                style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#4a7c59", background:"none", border:"none", cursor:"pointer", textDecoration:"underline" }}>
                {finderSearchAll ? "← Show my store only" : "Search all BC stores →"}
              </button>
            </div>
          )}
          {(!activeDispensary && answers.locationDispensary && answers.locationDispensary !== "any") ? (
            <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:8, padding:"4px 12px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:20 }}>
              <span style={{ fontSize:11 }}>📍</span>
              <span style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace" }}>{DB_DISPENSARIES.find(d=>d.id===answers.locationDispensary)?.name}</span>
            </div>
          ) : (answers.locationCity && answers.locationCity !== "any") ? (
            <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:8, padding:"4px 12px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:20 }}>
              <span style={{ fontSize:11 }}>📍</span>
              <span style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace" }}>All stores in {answers.locationCity}</span>
            </div>
          ) : null}
        </div>
        {results.length === 0 && (
          <div style={{ textAlign:"center", padding:"40px 20px" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>🔍</div>
            <p style={{ fontFamily:"'Fraunces',serif", fontSize:20, color:"#1a2e1a", marginBottom:8 }}>No matches found</p>
            <p style={{ fontSize:12, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>Your filter combination returned no products. Try a different location or aroma preference.</p>
            <button onClick={reset} style={{ background:"#1a2e1a", color:"#fff", border:"none", borderRadius:10, padding:"10px 24px", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              Start over
            </button>
          </div>
        )}
        {results.map(({ p, pct, breakdown }, i) => {
          const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
          const ti = TERPENES[topT?.[0]];
          const disp = DB_DISPENSARIES.find(d=>d.id===p.dispensary_id);
          const isOpen = expandedBreakdown === p.id;
          const pctColor = pct >= 75 ? "#22c55e" : pct >= 55 ? "#f59e0b" : "#9ca3af";
          return (
            <div key={p.id} className="card" style={{ padding:22, marginBottom:14, border:i===0?"2px solid #4a7c59":"1px solid #e8e4de", position:"relative" }}>
              {i===0 && <div style={{ position:"absolute", top:-10, left:20, background:"#4a7c59", color:"#fff", fontSize:9, fontFamily:"'DM Mono',monospace", padding:"3px 10px", borderRadius:20 }}>🌿 BEST MATCH</div>}

              {/* Header row */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12, flexWrap:"wrap" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{p.brand}</div>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:19, fontWeight:400, color:"#1a2e1a", marginBottom:8 }}>{p.name}</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
                    <span className="chip">{p.plant_type}</span>
                    <span className="chip">{p.cultivation?.method||p.category}</span>
                    <span className="chip">{p.category}</span>
                    {p.in_stock
                      ? <span className="chip" style={{ background:"#d1fae5", color:"#065f46", border:"1px solid #6ee7b7" }}>✓ In Stock</span>
                      : <span className="chip" style={{ background:"#fef2f2", color:"#991b1b", border:"1px solid #fca5a5" }}>✗ Out of Stock</span>}
                  </div>
                  {ti && topT && (
                    <div style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 10px", background:(ti.color) + "10", borderRadius:8, border:"1px solid " + (ti.color) + "25", marginBottom:8 }}>
                      <span style={{ width:8,height:8,borderRadius:"50%",background:ti.color,display:"inline-block" }}/>
                      <span style={{ fontSize:11, color:"#1a2e1a", fontWeight:500 }}>Primary: {topT[0]}</span>
                      <span style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>· {ti.plain?.split('.')[0]}</span>
                    </div>
                  )}
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
                    📍 <button onClick={()=>setDispDetailId(p.dispensary_id)}
                      style={{ background:"none", border:"none", cursor:"pointer", color:"#4a7c59", fontFamily:"'DM Mono',monospace", fontSize:10, padding:0, textDecoration:"underline", textDecorationStyle:"dotted" }}>
                      {disp?.name}
                    </button> · {disp?.city}{disp?.address ? " · " + (disp.address) : ""}
                  </div>
                </div>

                {/* Match % ring */}
                <div style={{ textAlign:"center", flexShrink:0 }}>
                  <div style={{ position:"relative", width:64, height:64, margin:"0 auto 4px" }}>
                    <svg width="64" height="64" style={{ transform:"rotate(-90deg)" }}>
                      <circle cx="32" cy="32" r="26" fill="none" stroke="#e8e4de" strokeWidth="5"/>
                      <circle cx="32" cy="32" r="26" fill="none" stroke={pctColor} strokeWidth="5"
                        strokeDasharray={2*Math.PI*26}
                        strokeDashoffset={2*Math.PI*26*(1-pct/100)}
                        strokeLinecap="round" style={{ transition:"stroke-dashoffset 0.8s ease" }}/>
                    </svg>
                    <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ fontSize:13, fontWeight:600, color:pctColor, fontFamily:"'DM Mono',monospace" }}>{pct}%</span>
                    </div>
                  </div>
                  <div style={{ fontSize:8, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textAlign:"center" }}>match</div>
                  <div style={{ fontSize:18, fontWeight:500, color:"#22c55e", fontFamily:"'Fraunces',serif", marginTop:6 }}>{p.thc}%</div>
                  <div style={{ fontSize:8, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>THC</div>
                  {p.total_terpenes && <>
                    <div style={{ fontSize:13, fontWeight:500, color:"#4a7c59", fontFamily:"'Fraunces',serif", marginTop:2 }}>{p.total_terpenes.toFixed(2)}%</div>
                    <div style={{ fontSize:8, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Terpenes</div>
                  </>}
                </div>
              </div>

              {/* Match breakdown toggle */}
              {breakdown.length > 0 && (
                <button onClick={()=>setExpandedBreakdown(isOpen ? null : p.id)}
                  style={{ width:"100%", background:"#f7f5f0", border:"none", borderRadius:8, padding:"7px 12px", fontSize:10, color:"#6b7280", cursor:"pointer", fontFamily:"'DM Mono',monospace", textAlign:"left", marginTop:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span>Why this match?</span>
                  <span>{isOpen ? "▲" : "▼"}</span>
                </button>
              )}
              {isOpen && (
                <div style={{ marginTop:8, padding:"10px 12px", background:"#f7f5f0", borderRadius:8, display:"flex", flexWrap:"wrap", gap:6 }}>
                  {breakdown.map((b,bi) => (
                    <span key={bi} style={{ fontSize:10, fontFamily:"'DM Mono',monospace", padding:"3px 8px", borderRadius:20,
                      background: b.positive ? "#d1fae5" : "#fef2f2",
                      color: b.positive ? "#065f46" : "#991b1b",
                      border: "1px solid " + (b.positive ? "#6ee7b7" : "#fca5a5") }}>
                      {b.icon} {b.label}{b.note ? " (" + (b.note) + ")" : ""}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display:"flex", gap:8, marginTop:12 }}>
                <button onClick={()=>{ matchNewArrival(p); setPage("customers"); }} className="btn-ghost" style={{ flex:1, padding:"9px 0", fontSize:11 }}>
                  👥 Match Customers
                </button>
                <button onClick={()=>addToCompare(p)}
                  style={{ flex:1, padding:"9px 0", fontSize:11, borderRadius:8, border:"1px solid " + (isInCompare(p)?"#4a7c59":"#e8e4de"),
                    background: isInCompare(p) ? "#f0fdf4" : "#fff",
                    color: isInCompare(p) ? "#166534" : "#6b7280",
                    cursor:"pointer", fontFamily:"'DM Mono',monospace", transition:"all 0.15s" }}>
                  {isInCompare(p) ? "⊕ Slot " + (compareSlot(p)) : "⊕ Compare"}
                </button>
                <button onClick={()=>{ viewProduct(p) }} className="btn-primary" style={{ flex:1, padding:"9px 0", fontSize:12 }}>
                  View Profile →
                </button>
                {profiles.length > 0 && (
                  <button onClick={()=>{ setLogForCustModal({ product: p }); setLogForCustSearch(""); setLogForCustDone(null); }}
                    style={{ padding:"9px 12px", borderRadius:8, border:"1px solid #86efac", background:"#f0fdf4", color:"#15803d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:600, flexShrink:0, whiteSpace:"nowrap" }}>
                    📋 Log
                  </button>
                )}
                <FlagButton context="Staff Finder Result" productId={p.id} productName={p.name} />
              </div>
            </div>
          );
        })}
        {results.length > 0 && (
          <div style={{ marginTop:12, textAlign:"right" }}>
            <button onClick={() => setSmsModal({ products: results.map(r=>r.p), customerName: "" })}
              style={{ fontSize:11, padding:"7px 16px", background:"#1a2e1a", color:"#fff", border:"none", borderRadius:8, cursor:"pointer", fontFamily:"'DM Mono',monospace", display:"inline-flex", alignItems:"center", gap:6 }}>
              📱 Send results to customer&#39;s phone
            </button>
          </div>
        )}
        <div style={{ display:"flex", gap:10, marginTop:20, alignItems:"center", justifyContent:"center" }}>
          <button onClick={reset} style={{ background:"none", border:"1px solid #e8e4de", borderRadius:8, padding:"9px 20px", fontSize:12, cursor:"pointer", color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>↺ Start Over</button>
          <button onClick={()=>{ setRegisterFor("staff"); setShowRegisterModal(true); }} className="btn-primary" style={{ padding:"9px 20px", fontSize:12, display:"flex", alignItems:"center", gap:6 }}>
            🪪 Save Customer Profile
          </button>
        </div>

        {/* ── INLINE COMPARE PANEL ── */}
        {(compareA || compareB) && (
          <div className="fade" style={{ marginTop:24, border:"2px solid #4a7c59", borderRadius:14, overflow:"hidden" }}>
            <div style={{ background:"#1a2e1a", padding:"12px 18px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:12, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
                ⊕ Comparing {compareA && compareB ? "2 products" : "1 product — pick another above"}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                {compareA && compareB && (
                  <button onClick={()=>{ setCmpSelA(compareA); setCmpSelB(compareB); setPage("compare"); }}
                    style={{ padding:"5px 14px", borderRadius:8, border:"1px solid rgba(255,255,255,0.3)", background:"transparent", color:"#f0f4f0", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    Full Compare →
                  </button>
                )}
                <button onClick={()=>{ setCompareA(null); setCompareB(null); }}
                  style={{ background:"none", border:"none", color:"rgba(240,244,240,0.4)", cursor:"pointer", fontSize:16, lineHeight:1 }}>✕</button>
              </div>
            </div>

            <div style={{ padding:18 }}>
              {/* Slot headers */}
              <div style={{ display:"grid", gridTemplateColumns:"180px 1fr 1fr", gap:12, marginBottom:14, alignItems:"center" }}>
                <div />
                {[compareA, compareB].map((p, i) => p ? (
                  <div key={i} style={{ textAlign:"center" }}>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:2 }}>Slot {i===0?"A":"B"} · {p.brand}</div>
                    <div style={{ fontFamily:"'Fraunces',serif", fontSize:15, fontWeight:400, color:"#1a2e1a", lineHeight:1.2 }}>{p.name}</div>
                    <div style={{ fontSize:11, color:"#22c55e", fontWeight:600, fontFamily:"'DM Mono',monospace", marginTop:3 }}>{p.thc}% THC</div>
                  </div>
                ) : (
                  <div key={i} style={{ textAlign:"center", color:"#d1d5db", fontSize:11, fontFamily:"'DM Mono',monospace", padding:12, border:"2px dashed #e8e4de", borderRadius:10 }}>
                    ⊕ Add product
                  </div>
                ))}
              </div>

              {/* Terpene rows */}
              {(() => {
                const allTerps = [...new Set([
                  ...Object.keys(compareA?.terpenes||{}),
                  ...Object.keys(compareB?.terpenes||{})
                ])].sort((a,b) => ((compareA?.terpenes[b]||0)+(compareB?.terpenes[b]||0)) - ((compareA?.terpenes[a]||0)+(compareB?.terpenes[a]||0)));
                const maxVal = Math.max(...allTerps.flatMap(t => [compareA?.terpenes[t]||0, compareB?.terpenes[t]||0]));
                return allTerps.map(t => {
                  const vA = compareA?.terpenes[t]||0;
                  const vB = compareB?.terpenes[t]||0;
                  const info = TERPENES[t];
                  const winner = vA > vB ? "A" : vB > vA ? "B" : "tie";
                  return (
                    <div key={t} style={{ display:"grid", gridTemplateColumns:"180px 1fr 1fr", gap:12, alignItems:"center", padding:"7px 0", borderBottom:"1px solid #f3f4f6" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ width:8, height:8, borderRadius:"50%", background:info?.color||"#4a7c59", flexShrink:0 }}/>
                        <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                      </div>
                      {[vA, vB].map((v, i) => {
                        const isWinner = (i===0 && winner==="A") || (i===1 && winner==="B");
                        const barW = maxVal > 0 ? Math.round((v/maxVal)*100) : 0;
                        return (
                          <div key={i} style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ flex:1, height:6, background:"#f3f4f6", borderRadius:3 }}>
                              <div style={{ height:6, background: isWinner ? (info?.color||"#4a7c59") : "#d1d5db", borderRadius:3, width:(barW) + "%", transition:"width 0.4s ease" }}/>
                            </div>
                            <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace", fontWeight: isWinner?600:400,
                              color: v===0 ? "#d1d5db" : isWinner ? "#166534" : "#6b7280", minWidth:42, textAlign:"right" }}>
                              {v > 0 ? ((v*100).toFixed(0)) + "mg/g" : "—"}
                              {isWinner && winner!=="tie" && " ✓"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                });
              })()}

              {/* Summary row */}
              {compareA && compareB && (
                <div style={{ display:"grid", gridTemplateColumns:"180px 1fr 1fr", gap:12, marginTop:10, paddingTop:10, borderTop:"2px solid #e8e4de" }}>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Terpene similarity</div>
                  {(() => {
                    const all = [...new Set([...Object.keys(compareA.terpenes),...Object.keys(compareB.terpenes)])];
                    let dot=0,mA=0,mB=0;
                    all.forEach(t=>{ const va=compareA.terpenes[t]||0,vb=compareB.terpenes[t]||0; dot+=va*vb; mA+=va*va; mB+=vb*vb; });
                    const sim = mA&&mB ? Math.round((dot/(Math.sqrt(mA)*Math.sqrt(mB)))*100) : 0;
                    const simColor = sim>=70?"#4a7c59":sim>=40?"#f59e0b":"#6b7280";
                    return (
                      <div style={{ gridColumn:"2 / 4", display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ flex:1, height:6, background:"#f3f4f6", borderRadius:3 }}>
                          <div style={{ height:6, background:simColor, borderRadius:3, width:(sim) + "%" }}/>
                        </div>
                        <span style={{ fontSize:12, fontFamily:"'DM Mono',monospace", fontWeight:600, color:simColor }}>{sim}%</span>
                        <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
                          {sim>=70?"Very similar profiles":sim>=40?"Moderate overlap":"Quite different"}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
        {showRegisterModal && registerFor==="staff" && (
          <RegisterModal mode="staff" finderAnswers={answers}
            onSave={(profile)=>{ setShowRegisterModal(false); setRmName(""); setRmPhone(""); setRmCity(""); setRmConsent(false); setRmAgeConsent(false); setRmError(""); setRmStep("form"); setSelectedCustomer(profile); setStaffCustomerPage("view"); setPage("customers"); }}
            onClose={closeRegModal}/>
        )}
      </div>
    );

    // Multi-select avoid step
    // Location picker step
    if (q?.locationSelect) return (
      <LocationPickerStep
        stepLabel={"Question " + (step+1) + " of " + (FINDER_QUESTIONS.length)}
        onConfirm={(loc) => {
          const next = { ...answers, ...loc };
          setAnswers(next);
          const scored = ALL_PRODUCTS.map(p => {
            const result = scoreProduct(p, next);
            return { p, ...result };
          }).filter(x => x.score > -999)
            .sort((a,b) => b.score - a.score)
            .slice(0, 5);
          setResults(scored);
          setStep(FINDER_QUESTIONS.length);
        }}
        onBack={() => setStep(step - 1)}
      />
    );

    if (isAvoidStep) return (
      <div className="fade" style={{ maxWidth:540, margin:"0 auto", padding:"32px 20px" }}>
        <div style={{ marginBottom:32 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Question {step+1} of {FINDER_QUESTIONS.length}</span>
            <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{Math.round(progress)}% complete</span>
          </div>
          <div style={{ height:3, background:"#e8e4de", borderRadius:2 }}>
            <div style={{ height:3, background:"#4a7c59", borderRadius:2, width:(progress) + "%", transition:"width 0.4s ease" }}/>
          </div>
        </div>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:40, marginBottom:10 }}>{q.emoji}</div>
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>{q.question}</h2>
          <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{q.subtitle}</p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
          {q.options.map(opt => {
            const selected = avoidSelected.includes(opt.value);
            return (
              <button key={opt.value} onClick={()=>handleAvoidToggle(opt.value)}
                style={{ padding:"14px 20px", background: selected ? "#f0fdf4" : "#fff",
                  border: selected ? "2px solid #4a7c59" : "1px solid #e8e4de",
                  borderRadius:12, cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:500, color:"#1a2e1a", fontFamily:"'Fraunces',serif" }}>{opt.label}</div>
                </div>
                <div style={{ width:20, height:20, borderRadius:4, border:"2px solid " + (selected?"#4a7c59":"#d1d5db"),
                  background: selected ? "#4a7c59" : "transparent",
                  display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {selected && <span style={{ color:"#fff", fontSize:12, lineHeight:1 }}>✓</span>}
                </div>
              </button>
            );
          })}
        </div>
        <button onClick={confirmAvoid} className="btn-primary" style={{ width:"100%", padding:"12px 0", fontSize:13 }}>
          {avoidSelected.length === 0 || avoidSelected.includes("none") ? "Nothing to avoid →" : "Apply " + (avoidSelected.length) + " filter" + (avoidSelected.length>1?"s":"") + " →"}
        </button>
        <div style={{ textAlign:"center", marginTop:12 }}>
          <button onClick={()=>setStep(step-1)} style={{ background:"none", border:"none", color:"#9ca3af", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>← Back</button>
        </div>
      </div>
    );

    return (
      <div className="fade" style={{ maxWidth:540, margin:"0 auto", padding:"32px 20px" }}>
        {step === 0 && savedFinderAnswers && (
          <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"12px 16px", marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <div style={{ fontSize:12, color:"#15803d", fontFamily:"'DM Mono',monospace" }}>
              🌿 Resume last session?
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>{ setAnswers(savedFinderAnswers); setStep(FINDER_QUESTIONS.length); }} style={{ padding:"5px 14px", borderRadius:8, border:"none", background:"#4a7c59", color:"#fff", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:600 }}>
                Resume →
              </button>
              <button onClick={()=>{ setSavedFinderAnswers(null); try{localStorage.removeItem("iterps_saved_finder");}catch (_e) {} }} style={{ padding:"5px 12px", borderRadius:8, border:"1px solid #86efac", background:"none", color:"#15803d", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                Start fresh
              </button>
            </div>
          </div>
        )}
        <div style={{ marginBottom:32 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Question {step+1} of {FINDER_QUESTIONS.length}</span>
            <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{Math.round(progress)}% complete</span>
          </div>
          <div style={{ height:3, background:"#e8e4de", borderRadius:2 }}>
            <div style={{ height:3, background:"#4a7c59", borderRadius:2, width:(progress) + "%", transition:"width 0.4s ease" }}/>
          </div>
        </div>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:40, marginBottom:10 }}>{q.emoji}</div>
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>{q.question}</h2>
          <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{q.subtitle}</p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {q.options.map(opt => {
            const hints = opt.terpenes?.slice(0,2).map(t=>TERPENES[t]?.commonName||t).filter(Boolean).join(", ");
            return (
              <button key={opt.value} onClick={()=>handleAnswer(q.id, opt.value)}
                style={{ padding:"16px 20px", background:"#fff", border:"1px solid #e8e4de", borderRadius:12, cursor:"pointer", textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}
                onMouseEnter={e=>{ e.currentTarget.style.borderColor="#4a7c59"; e.currentTarget.style.background="#f0fdf4"; }}
                onMouseLeave={e=>{ e.currentTarget.style.borderColor="#e8e4de"; e.currentTarget.style.background="#fff"; }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:500, color:"#1a2e1a", fontFamily:"'Fraunces',serif", marginBottom:4 }}>{opt.label}</div>
                  {opt.desc && <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.4, marginBottom:hints?3:0 }}>{opt.desc}</div>}
                  {hints && <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>↳ Terpenes: {hints}</div>}
                </div>
                <span style={{ color:"#d1d5db", fontSize:16, flexShrink:0 }}>→</span>
              </button>
            );
          })}
        </div>
        {step > 0 && <div style={{ textAlign:"center", marginTop:16 }}>
          <button onClick={()=>setStep(step-1)} style={{ background:"none", border:"none", color:"#9ca3af", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>← Back</button>
        </div>}
      </div>
    );
  };

  // ── BATCH COMPARISON PAGE ──────────────────────────────────
  const ComparePage = () => {
    const selA=cmpSelA, setSelA=setCmpSelA;
    const selB=cmpSelB, setSelB=setCmpSelB;
    const searchA=cmpSearchA, setSearchA=setCmpSearchA;
    const searchB=cmpSearchB, setSearchB=setCmpSearchB;
    const focusA=cmpFocusA, setFocusA=setCmpFocusA;
    const focusB=cmpFocusB, setFocusB=setCmpFocusB;

    const filterProds = q => q.trim().length === 0
      ? uniqueProds.slice(0, 50)
      : ALL_PRODUCTS.filter(p => p.name.toLowerCase().includes(q.toLowerCase()) || p.brand.toLowerCase().includes(q.toLowerCase())).slice(0, 50);

    // Unique products (deduplicated by brand+name) sorted alphabetically
    const uniqueProds = (() => {
      const seen = new Set();
      return ALL_PRODUCTS
        .filter(p => { const k = p.brand+"|"+p.name; if (seen.has(k)) return false; seen.add(k); return true; })
        .sort((a,b) => a.name.localeCompare(b.name));
    })();

    const getOverlap = (a,b) => {
      if (!a||!b) return { shared:[], onlyA:[], onlyB:[], pct:0 };
      const kA = Object.keys(a.terpenes), kB = Object.keys(b.terpenes);
      const shared = kA.filter(t=>kB.includes(t));
      const all = [...new Set([...kA,...kB])];
      return { shared, onlyA:kA.filter(t=>!kB.includes(t)), onlyB:kB.filter(t=>!kA.includes(t)), pct:Math.round((shared.length/all.length)*100) };
    };

    const getSimilarity = (a,b) => {
      if (!a||!b) return 0;
      const all = [...new Set([...Object.keys(a.terpenes),...Object.keys(b.terpenes)])];
      let dot=0,mA=0,mB=0;
      all.forEach(t=>{ const va=a.terpenes[t]||0,vb=b.terpenes[t]||0; dot+=va*vb; mA+=va*va; mB+=vb*vb; });
      return mA&&mB ? Math.round((dot/(Math.sqrt(mA)*Math.sqrt(mB)))*100) : 0;
    };

    const ov = getOverlap(selA,selB);
    const sim = getSimilarity(selA,selB);

    const Picker = ({ label, sel, onSel, search, setSearch, focus, setFocus }) => {
      const results = filterProds(search);
      return (
        <div style={{ flex:1, minWidth:240 }}>
          <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>{label}</div>
          {sel ? (
            <div className="card" style={{ padding:16, position:"relative" }}>
              <button onClick={()=>{ onSel(null); setSearch(""); }} style={{ position:"absolute", top:8, right:8, background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:14, lineHeight:1 }}>✕</button>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{sel.brand}</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:17, color:"#1a2e1a", margin:"4px 0 8px" }}>{sel.name}</div>
              <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                <span style={{ fontSize:13, fontWeight:600, color:"#22c55e", fontFamily:"'Fraunces',serif" }}>{sel.thc}% THC</span>
                {sel.total_terpenes && <span style={{ fontSize:13, fontWeight:600, color:"#4a7c59", fontFamily:"'Fraunces',serif" }}>{sel.total_terpenes.toFixed(2)}% Terp</span>}
              </div>
              {Object.entries(sel.terpenes).sort((ea,eb)=>eb[1]-ea[1]).map(([t,v])=>{
                const ti=TERPENES[t];
                return <div key={t} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                  <span style={{ width:7,height:7,borderRadius:"50%",background:ti?.color||"#6b7280",display:"inline-block",flexShrink:0 }}/>
                  <span style={{ fontSize:10, color:"#374151", fontFamily:"'DM Mono',monospace", flex:1 }}>{t}</span>
                  <span style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{v.toFixed(2)}%</span>
                </div>;
              })}
            </div>
          ) : (
            <div style={{ position:"relative" }}>
              {/* Search/filter input */}
              <input value={search} onChange={e=>setSearch(e.target.value)}
                onFocus={()=>setFocus(true)} onBlur={()=>setTimeout(()=>setFocus(false),200)}
                placeholder={"Search or browse " + (uniqueProds.length) + " products…"}
                style={{ width:"100%", padding:"11px 14px", border:"1px solid " + (focus?"#4a7c59":"#e8e4de"), borderRadius:focus?"10px 10px 0 0":"10px",
                  fontSize:13, fontFamily:"'DM Mono',monospace", background:"#fff", boxSizing:"border-box", outline:"none", transition:"border-color 0.15s" }}/>
              {/* Dropdown — opens on focus, filters on type */}
              {focus && (
                <div style={{ position:"absolute", top:"100%", left:0, right:0, background:"#fff",
                  border:"1px solid #4a7c59", borderTop:"none", borderRadius:"0 0 10px 10px",
                  zIndex:100, boxShadow:"0 12px 32px rgba(0,0,0,0.1)", maxHeight:300, overflowY:"auto" }}>
                  {results.length === 0 ? (
                    <div style={{ padding:"12px 14px", fontSize:12, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>No products match "{search}"</div>
                  ) : (
                    <>
                      {search.trim().length === 0 && (
                        <div style={{ padding:"8px 14px 6px", fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em", borderBottom:"1px solid #f3f4f6" }}>
                          All products · type to filter
                        </div>
                      )}
                      {results.map(p => {
                        const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
                        const ti = topT ? TERPENES[topT[0]] : null;
                        return (
                          <button key={p.id} onMouseDown={()=>{ onSel(p); setSearch(""); setFocus(false); }}
                            style={{ width:"100%", padding:"9px 14px", background:"none", border:"none",
                              borderBottom:"1px solid #f9f7f4", cursor:"pointer", textAlign:"left",
                              display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{p.brand}</div>
                              <div style={{ fontSize:13, color:"#1a2e1a", fontWeight:500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.name}</div>
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                              {ti && topT && (
                                <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                                  <div style={{ width:6,height:6,borderRadius:"50%",background:ti.color }}/>
                                  <span style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{topT[0].split("-").pop().split(" ").pop()}</span>
                                </div>
                              )}
                              <span style={{ fontSize:11, color:"#22c55e", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{p.thc}%</span>
                            </div>
                          </button>
                        );
                      })}
                      {search.trim().length === 0 && results.length === 50 && (
                        <div style={{ padding:"8px 14px", fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textAlign:"center", borderTop:"1px solid #f3f4f6" }}>
                          Showing first 50 — type to narrow results
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      );
    };

    const rows = selA && selB ? [
      { label:"THC",            a:(selA.thc) + "%", b:(selB.thc) + "%", win:selA.thc>selB.thc?"a":selA.thc<selB.thc?"b":null },
      { label:"CBD",            a:(selA.cbd) + "%", b:(selB.cbd) + "%", win:selA.cbd>selB.cbd?"a":selA.cbd<selB.cbd?"b":null },
      { label:"Total Terpenes", a:selA.total_terpenes?(selA.total_terpenes.toFixed(2)) + "%":"—", b:selB.total_terpenes?(selB.total_terpenes.toFixed(2)) + "%":"—", win:(selA.total_terpenes||0)>(selB.total_terpenes||0)?"a":(selA.total_terpenes||0)<(selB.total_terpenes||0)?"b":null },
      { label:"Plant Type",     a:selA.plant_type, b:selB.plant_type, win:null },
      { label:"Cultivation",    a:selA.cultivation?.method||"—", b:selB.cultivation?.method||"—", win:null },
      { label:"Cure",           a:selA.cultivation?.cure||"—", b:selB.cultivation?.cure||"—", win:null },
      { label:"Cycle Days",     a:selA.cultivation?.cycle_days?(selA.cultivation.cycle_days) + "d":"—", b:selB.cultivation?.cycle_days?(selB.cultivation.cycle_days) + "d":"—", win:null },
      { label:"Lab Method",     a:selA.lab_method||"—", b:selB.lab_method||"—", win:null },
      { label:"Lineage",         a:selA.genetics?.lineage||"—", b:selB.genetics?.lineage||"—", win:null, wrap:true },
      { label:"Breeder",         a:selA.genetics?.breeder||"—", b:selB.genetics?.breeder||"—", win:null },
      { label:"Generation",      a:selA.genetics?.generation||"—", b:selB.genetics?.generation||"—", win:null },
    ] : [];

    const downloadCompareCSV = () => {
      if (!selA || !selB) return;
      const TERP_KEYS = Object.keys(selA.terpenes);
      const headers = ["Metric", selA.name, selB.name];
      const dataRows = [
        ["Brand", selA.brand, selB.brand],
        ["Category", selA.category, selB.category],
        ["THC%", selA.thc, selB.thc],
        ["CBD%", selA.cbd, selB.cbd],
        ["Total Terpenes%", selA.total_terpenes||"", selB.total_terpenes||""],
        ["Data Source", selA.terpene_source, selB.terpene_source],
        ["Cultivation", selA.cultivation?.method||"", selB.cultivation?.method||""],
        ["LP", selA.cultivation?.lp||"", selB.cultivation?.lp||""],
        ["Batch", selA.batch||"", selB.batch||""],
        ["Tested", selA.tested||"", selB.tested||""],
        ...TERP_KEYS.map(t => [t, selA.terpenes[t]||0, selB.terpenes[t]||0]),
      ];
      const csv = [headers, ...dataRows].map(r=>r.map(v=>String(v).includes(",")? "\"" + (v) + "\"":v).join(",")).join("\n");
      const blob = new Blob([csv], {type:"text/csv"});
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url;
      a.download="iterps-compare-" + (selA.name.replace(/\s/g,"_")) + "-vs-" + (selB.name.replace(/\s/g,"_")) + ".csv"; a.click();
      showToast("Comparison exported to CSV", "success");
    };

    return (
      <div className="fade" style={{ maxWidth:900, margin:"0 auto", padding:"32px 20px" }}>
        <div style={{ marginBottom:24, display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
          <div>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Batch Comparison</h1>
          <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Side-by-side terpene overlap, chemical similarity score, and cultivation delta</p>
          </div>
          {selA && selB && (
            <button onClick={downloadCompareCSV} style={{ padding:"8px 16px", borderRadius:8, border:"1px solid #e8e4de", background:"#fff", color:"#374151", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", flexShrink:0 }}>
              ↓ Export CSV
            </button>
          )}
        </div>

        <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginBottom:28, alignItems:"flex-start" }}>
          <Picker label="Product A" sel={selA} onSel={setSelA} search={searchA} setSearch={setSearchA} focus={focusA} setFocus={setFocusA}/>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, color:"#d1d5db", paddingTop:selA||selB?32:0, flexShrink:0 }}>⇄</div>
          <Picker label="Product B" sel={selB} onSel={setSelB} search={searchB} setSearch={setSearchB} focus={focusB} setFocus={setFocusB}/>
        </div>

        {selA && selB && (
          <div className="fade">
            {/* Similarity score */}
            <div className="card" style={{ padding:24, marginBottom:14, textAlign:"center" }}>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>Terpene Similarity Score</div>
              <div style={{ fontSize:56, fontWeight:300, fontFamily:"'Fraunces',serif", color:sim>=70?"#4a7c59":sim>=40?"#f59e0b":"#ef4444", lineHeight:1 }}>{sim}</div>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:4 }}>out of 100 · cosine similarity</div>
              <div style={{ marginTop:10, fontSize:12, color:"#374151", maxWidth:440, margin:"10px auto 0" }}>
                {sim>=75?"Very similar — these products will feel chemically close. Great for cross-selling or step-up recommendations.":
                 sim>=50?"Moderately similar — same family, different character. Good for introducing variety to a loyal customer.":
                 sim>=25?"Quite different — distinct terpene profiles. These serve different consumer occasions.":
                 "Very different — contrasting profiles. Would appeal to completely different consumer preferences."}
              </div>
            </div>

            {/* Terpene overlap */}
            <div className="card" style={{ padding:24, marginBottom:14 }}>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a", marginBottom:14 }}>Terpene Overlap · <span style={{ color:"#4a7c59" }}>{ov.pct}% shared</span></h3>
              <div style={{ display:"grid", gridTemplateColumns:"1fr auto 1fr", gap:"0 16px" }}>
                <div>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>Only in {selA.name}</div>
                  {ov.onlyA.length===0 ? <div style={{ fontSize:11, color:"#d1d5db" }}>—</div> :
                    ov.onlyA.map(t=>{ const ti=TERPENES[t]; return <div key={t} style={{ display:"flex", alignItems:"center", gap:5, marginBottom:5 }}>
                      <span style={{ width:7,height:7,borderRadius:"50%",background:ti?.color||"#6b7280",display:"inline-block" }}/>
                      <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", flex:1 }}>{t}</span>
                      <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{selA.terpenes[t].toFixed(2)}%</span>
                    </div>; })}
                </div>
                <div style={{ borderLeft:"1px solid #e8e4de", borderRight:"1px solid #e8e4de", padding:"0 16px" }}>
                  <div style={{ fontSize:10, color:"#4a7c59", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>Shared</div>
                  {ov.shared.length===0 ? <div style={{ fontSize:11, color:"#d1d5db" }}>None</div> :
                    ov.shared.map(t=>{ const ti=TERPENES[t]; return <div key={t} style={{ display:"flex", alignItems:"center", gap:5, marginBottom:5 }}>
                      <span style={{ width:7,height:7,borderRadius:"50%",background:ti?.color||"#6b7280",display:"inline-block" }}/>
                      <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                    </div>; })}
                </div>
                <div>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>Only in {selB.name}</div>
                  {ov.onlyB.length===0 ? <div style={{ fontSize:11, color:"#d1d5db" }}>—</div> :
                    ov.onlyB.map(t=>{ const ti=TERPENES[t]; return <div key={t} style={{ display:"flex", alignItems:"center", gap:5, marginBottom:5 }}>
                      <span style={{ width:7,height:7,borderRadius:"50%",background:ti?.color||"#6b7280",display:"inline-block" }}/>
                      <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", flex:1 }}>{t}</span>
                      <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{selB.terpenes[t].toFixed(2)}%</span>
                    </div>; })}
                </div>
              </div>
            </div>

            {/* Head to head */}
            <div className="card" style={{ padding:24, marginBottom:14 }}>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a", marginBottom:14 }}>Head-to-Head</h3>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 130px 1fr" }}>
                {rows.map(row=> row.wrap ? (
                  /* Wide rows — span full grid width, stacked A / B */
                  <React.Fragment key={row.label}>
                    <div style={{ gridColumn:"1/-1", padding:"10px 0 6px", borderBottom:"1px solid #f3f4f6" }}>
                      <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:0.5, marginBottom:6 }}>{row.label}</div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                        <div>
                          <div style={{ fontSize:9, color:"#4a7c59", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>A · {selA.name}</div>
                          <div style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{row.a}</div>
                        </div>
                        <div>
                          <div style={{ fontSize:9, color:"#4a7c59", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>B · {selB.name}</div>
                          <div style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{row.b}</div>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ) : (
                  /* Standard 3-col rows */
                  <React.Fragment key={row.label}>
                    <div style={{ padding:"9px 0", borderBottom:"1px solid #f3f4f6", textAlign:"right" }}>
                      <span style={{ fontSize:12, fontFamily:"'DM Mono',monospace", color:row.win==="a"?"#4a7c59":"#374151", fontWeight:row.win==="a"?600:400 }}>{row.a}</span>
                    </div>
                    <div style={{ padding:"9px 12px", borderBottom:"1px solid #f3f4f6", textAlign:"center" }}>
                      <span style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:0.5 }}>{row.label}</span>
                    </div>
                    <div style={{ padding:"9px 0", borderBottom:"1px solid #f3f4f6" }}>
                      <span style={{ fontSize:12, fontFamily:"'DM Mono',monospace", color:row.win==="b"?"#4a7c59":"#374151", fontWeight:row.win==="b"?600:400 }}>{row.b}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Aroma delta */}
            <div className="card" style={{ padding:24, marginBottom:14 }}>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a", marginBottom:14 }}>Aroma Delta</h3>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                {[selA,selB].map((p,pi)=>( <div key={pi}>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>{p.name}</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                    {(p.aroma||[]).map(a=>{ const other=pi===0?selB:selA; const shared=(other.aroma||[]).includes(a);
                      return <span key={a} className="chip" style={{ background:shared?"#dcfce7":"#f9fafb", color:shared?"#166534":"#6b7280", border:shared?"1px solid #86efac":"1px solid #e8e4de" }}>{a}</span>;
                    })}
                  </div>
                </div>))}
              </div>
              <div style={{ marginTop:10, fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>🟢 Green = shared aroma notes between both products</div>
            </div>

            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <button onClick={()=>{ setSelectedProduct(selA); setPage("product"); }} className="btn-primary" style={{ flex:1, padding:"11px 0", fontSize:12 }}>Full Profile: {selA.name} →</button>
              <button onClick={()=>{ setSelectedProduct(selB); setPage("product"); }} className="btn-primary" style={{ flex:1, padding:"11px 0", fontSize:12 }}>Full Profile: {selB.name} →</button>
            </div>
          </div>
        )}

        {(!selA||!selB) && (
          <div style={{ textAlign:"center", padding:"60px 20px", color:"#9ca3af" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>⇄</div>
            <div style={{ fontFamily:"'DM Mono',monospace", fontSize:12 }}>Search and select two products above to compare</div>
          </div>
        )}
      </div>
    );
  };

  // ── HOME PAGE ─────────────────────────────────────────────
  const HomePage = () => {
    // ── Role-specific home pages ─────────────────────────────

    // ── STAFF HOME ────────────────────────────────────────────
    if (onboardRole === "staff" || onboardRole === "") {
      // Scope to active store if logged in; fallback to full network
      const homePool = activeDispensary
        ? ALL_PRODUCTS.filter(p => p.dispensary_id === activeDispensary)
        : ALL_PRODUCTS;

      const freshnessAlerts = homePool
        .map(p => ({ p, f: getFreshness(p) }))
        .filter(({f}) => f && f.score < 65)
        .sort((a,b) => a.f.score - b.f.score)
        .slice(0, 4);

      const terpFreq = {};
      homePool.forEach(p => Object.entries(p.terpenes).forEach(([t, v]) => {
        if (!terpFreq[t]) terpFreq[t] = { count: 0, totalV: 0 };
        terpFreq[t].count++; terpFreq[t].totalV += v;
      }));
      const topTerps = Object.entries(terpFreq).sort(([,a],[,b]) => b.count - a.count).slice(0, 5);
      const driftCount = Object.keys(flaggedDrifts).length;
      const labVerified = homePool.filter(p => p.terpene_source === "lab_tested" || p.terpene_source === "lp_provided").length;
      const irrBreakdown = homePool.reduce((acc, p) => {
        const k = p.cultivation?.irradiation || "not_disclosed";
        acc[k] = (acc[k] || 0) + 1; return acc;
      }, {});
      const recentBatches = Object.entries(batchHistory)
        .flatMap(([key, batches]) => batches.map(b => ({ key, ...b })))
        .sort((a,b) => new Date(b.date) - new Date(a.date)).slice(0, 4);

      const today = new Date().toLocaleDateString("en-CA", { weekday:"long", month:"long", day:"numeric" });

      return (
        <div className="fade" style={{ maxWidth:880, margin:"0 auto", padding:"32px 20px" }}>

          {/* ── Store context banner ── */}
          {activeDispensary && (
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"10px 16px", marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:18 }}>📍</span>
                <div>
                  <div style={{ fontSize:13, fontWeight:600, fontFamily:"'DM Mono',monospace", color:"#1a2e1a" }}>{DB_DISPENSARIES.find(d=>d.id===activeDispensary)?.name}</div>
                  <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{homePool.length} SKUs · {DB_DISPENSARIES.find(d=>d.id===activeDispensary)?.city}</div>
                </div>
              </div>
              <button onClick={()=>setPage("retailer")} style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#4a7c59", background:"none", border:"1px solid #86efac", borderRadius:8, padding:"5px 12px", cursor:"pointer" }}>View Inventory →</button>
            </div>
          )}
          {/* ── No store selected CTA ── */}
          {!activeDispensary && onboardRole === "staff" && (
            <div style={{ background:"linear-gradient(135deg,#f0fdf4,#ecfdf5)", border:"2px solid #4a7c59", borderRadius:14, padding:"20px 24px", marginBottom:24, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:44, height:44, borderRadius:12, background:"#1a2e1a", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>📍</div>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, fontFamily:"'DM Mono',monospace", color:"#1a2e1a", marginBottom:3 }}>Select your store to get started</div>
                  <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Inventory view, customer matching, and freshness alerts are scoped to your store once selected.</div>
                </div>
              </div>
              <button onClick={()=>{ setOnboardDone(false); setOnboardStep(2); }}
                style={{ padding:"10px 20px", borderRadius:10, border:"none", background:"#1a2e1a", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:600, flexShrink:0, whiteSpace:"nowrap" }}>
                Select store →
              </button>
            </div>
          )}
          {/* ── Header row ── */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28, flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.08em" }}>{today}</div>
              <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:30, fontWeight:300, color:"#1a2e1a", margin:0 }}>
                {managerMode ? "Manager Overview" : "Welcome back"}
              </h1>
              <p style={{ fontSize:12, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:4, marginBottom:0 }}>
                {managerMode ? "Inventory health, freshness alerts, and operational intel." : "Your floor tools. Start with a match or check what's fresh today."}
              </p>
            </div>
            {!managerMode && (
              <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                <button onClick={()=>setPage("finder")} className="btn-primary" style={{ padding:"10px 20px", fontSize:12 }}>🌿 Find My Strain</button>
                <button onClick={()=>setPage("customers")} style={{ padding:"10px 18px", borderRadius:10, border:"1px solid #e8e4de", background:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", color:"#374151", cursor:"pointer" }}>👥 Customers</button>
              </div>
            )}
          </div>

          {/* ── KPI strip ── */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10, marginBottom:20 }}>
            {[
              { label:"Total SKUs",    value:homePool.length,  icon:"📦", sub:activeDispensary ? "in your store" : "in BC network", color:"#1a2e1a" },
              { label:"Lab Verified",  value:labVerified,      icon:"✓",  sub:(Math.round(labVerified/homePool.length*100)) + "% of inventory", color:"#15803d" },
              { label:"Freshness ⚠",  value:freshnessAlerts.length,icon:"⏳", sub:freshnessAlerts.length>0?"need attention":"all good", color:freshnessAlerts.length>0?"#b45309":"#15803d" },
              { label:"Batch Flags",   value:driftCount,            icon:"🚩", sub:driftCount>0?"drift alerts":"no active flags", color:driftCount>0?"#dc2626":"#9ca3af" },
            ].map(item => (
              <div key={item.label} className="card" style={{ padding:"14px 16px", textAlign:"center" }}>
                <div style={{ fontSize:18, marginBottom:6 }}>{item.icon}</div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, color:item.color, fontWeight:400, lineHeight:1 }}>{item.value}</div>
                <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>{item.label}</div>
                <div style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{item.sub}</div>
              </div>
            ))}
          </div>

          {/* ── Budtender quick tools ── */}
          {!managerMode && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:10, marginBottom:20 }}>
              {[
                { icon:"🔍", label:"Find My Strain",    sub:"7-question terpene matcher",  page:"finder",    accent:"#4a7c59" },
                { icon:"🔬", label:"Explorer",          sub:"Browse & filter full inventory", page:"explorer",  accent:"#0ea5e9" },
                { icon:"🎓", label:"Training",          sub:"Terpene science + flash quiz",   page:"training",  accent:"#8b5cf6" },
              ].map(item => (
                <button key={item.page} onClick={()=>setPage(item.page)}
                  className="card card-click"
                  style={{ padding:"20px 18px", textAlign:"left", border:"1px solid #e8e4de", cursor:"pointer", background:"#fff", transition:"all 0.15s" }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor=item.accent; e.currentTarget.style.background="#fafffe"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor="#e8e4de"; e.currentTarget.style.background="#fff"; }}>
                  <div style={{ fontSize:24, marginBottom:10 }}>{item.icon}</div>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:15, color:"#1a2e1a", marginBottom:4 }}>{item.label}</div>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{item.sub}</div>
                </button>
              ))}
            </div>
          )}

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>

            {/* Freshness alerts */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em" }}>⏳ Freshness Alerts</div>
                <button onClick={()=>setPage("explorer")} style={{ fontSize:9, color:"#4a7c59", fontFamily:"'DM Mono',monospace", background:"none", border:"none", cursor:"pointer" }}>View all →</button>
              </div>
              {freshnessAlerts.length === 0 ? (
                <div style={{ textAlign:"center", padding:"20px 0" }}>
                  <div style={{ fontSize:24, marginBottom:8 }}>✓</div>
                  <div style={{ fontSize:12, color:"#15803d", fontFamily:"'DM Mono',monospace" }}>All inventory within freshness window</div>
                </div>
              ) : freshnessAlerts.map(({ p, f }) => (
                <div key={p.id} onClick={()=>{ viewProduct(p) }}
                  style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 0", borderBottom:"1px solid #f3f4f6", cursor:"pointer" }}
                  onMouseEnter={e=>e.currentTarget.style.background="#fafffe"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <div>
                    <div style={{ fontSize:12, color:"#1a2e1a", fontWeight:500 }}>{p.name}</div>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{p.brand} · {f.daysSince}d since packaging</div>
                  </div>
                  <span style={{ fontSize:9, background:(f.color) + "18", color:f.color, border:"1px solid " + (f.color) + "40", borderRadius:20, padding:"3px 9px", fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap", fontWeight:600 }}>{f.label}</span>
                </div>
              ))}
            </div>

            {/* Top terpenes */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>🌿 Network Terpene Frequency</div>
              {topTerps.map(([t, data]) => {
                const info = TERPENES[t];
                const pct = Math.round((data.count / ALL_PRODUCTS.length) * 100);
                return (
                  <div key={t} style={{ marginBottom:11 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <div style={{ width:7, height:7, borderRadius:"50%", background:info?.color||"#9ca3af", flexShrink:0 }}/>
                        <span style={{ fontSize:11, color:"#1a2e1a" }}>{t}</span>
                      </div>
                      <span style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{pct}% · avg {(data.totalV/data.count).toFixed(2)}%</span>
                    </div>
                    <div style={{ height:4, background:"#f3f4f6", borderRadius:2 }}>
                      <div style={{ height:4, background:info?.color||"#9ca3af", borderRadius:2, width:(pct) + "%", opacity:0.75 }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Manager row ── */}
          {managerMode && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>

              <div className="card" style={{ padding:20 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>☢ Irradiation Status</div>
                {[
                  ["non_irradiated","Non-Irradiated","#15803d","#f0fdf4","#86efac"],
                  ["gamma","Gamma","#92400e","#fffbeb","#fcd34d"],
                  ["e_beam","E-Beam","#6d28d9","#faf5ff","#c4b5fd"],
                  ["not_disclosed","Not Disclosed","#6b7280","#f9fafb","#e5e7eb"],
                ].map(([key, label, color, bg, border]) => {
                  const count = irrBreakdown[key] || 0;
                  const pct = Math.round(count / ALL_PRODUCTS.length * 100);
                  return (
                    <div key={key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 10px", borderRadius:8, background:bg, border:"1px solid " + (border), marginBottom:6 }}>
                      <span style={{ fontSize:11, color, fontFamily:"'DM Mono',monospace", fontWeight:500 }}>{label}</span>
                      <span style={{ fontSize:12, color:"#374151", fontFamily:"'Fraunces',serif" }}>{count} <span style={{ fontSize:9, color:"#9ca3af" }}>({pct}%)</span></span>
                    </div>
                  );
                })}
              </div>

              <div className="card" style={{ padding:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em" }}>📋 Recent Batch Logs</div>
                  <button onClick={()=>setPage("batches")} style={{ fontSize:9, color:"#4a7c59", fontFamily:"'DM Mono',monospace", background:"none", border:"none", cursor:"pointer" }}>Batch Tracker →</button>
                </div>
                {recentBatches.length === 0 ? (
                  <div style={{ fontSize:12, color:"#9ca3af", textAlign:"center", padding:"20px 0", fontFamily:"'DM Mono',monospace" }}>No batches logged yet</div>
                ) : recentBatches.map((b, i) => (
                  <div key={i} style={{ padding:"8px 0", borderBottom:"1px solid #f3f4f6" }}>
                    <div style={{ fontSize:11, color:"#1a2e1a", fontWeight:500 }}>{b.key.split("|")[1]}</div>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{b.key.split("|")[0]} · {b.batch} · {b.date}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Manager quick links ── */}
          {managerMode && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:8 }}>
              {[["📊","Retailer Analytics","retailer"],["👥","Customers","customers"],["📤","Data Export","data"],["📈","Demand Gap","demand"]].map(([icon,label,pg]) => (
                <button key={pg} onClick={()=>setPage(pg)} className="card card-click"
                  style={{ padding:"14px 10px", textAlign:"center", border:"none", cursor:"pointer", background:"#fff" }}>
                  <div style={{ fontSize:20, marginBottom:6 }}>{icon}</div>
                  <div style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{label}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    // ── LP HOME ───────────────────────────────────────────────
    if (onboardRole === "lp") {
      const verifiedLPs = [...new Set(ALL_PRODUCTS.filter(p => p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").map(p => p.cultivation?.lp).filter(Boolean))];
      const myProducts = ALL_PRODUCTS.filter(p => p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided");
      const terpFreq = {};
      ALL_PRODUCTS.forEach(p => Object.entries(p.terpenes).forEach(([t,v]) => {
        if (!terpFreq[t]) terpFreq[t] = { count:0, demand:0 };
        terpFreq[t].count++; terpFreq[t].demand += v;
      }));
      const demandLeaders = Object.entries(terpFreq).sort(([,a],[,b]) => b.demand - a.demand).slice(0,5);

      return (
        <div className="fade" style={{ maxWidth:880, margin:"0 auto", padding:"32px 20px" }}>

          {/* ── LP header ── */}
          <div style={{ background:"linear-gradient(135deg, #0f1f0f 0%, #1a2e1a 100%)", borderRadius:18, padding:"36px 32px", marginBottom:20, position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-20, right:-20, width:180, height:180, borderRadius:"50%", background:"rgba(74,124,89,0.12)", pointerEvents:"none" }}/>
            <div style={{ position:"absolute", bottom:-30, right:60, width:100, height:100, borderRadius:"50%", background:"rgba(74,124,89,0.08)", pointerEvents:"none" }}/>
            <div style={{ fontSize:10, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:12 }}>🏭 LP Portal</div>
            <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:32, fontWeight:300, color:"#f0f4f0", margin:"0 0 10px" }}>Your products in the network</h1>
            <p style={{ fontSize:12, color:"rgba(240,244,240,0.6)", fontFamily:"'DM Mono',monospace", margin:"0 0 24px", lineHeight:1.7 }}>
              Submit COA data, track terpene consistency, and see how your cultivars match against real consumer demand in BC.
            </p>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <button onClick={()=>{ setPage("lp"); setLpTabState("upload"); }} className="btn-primary"
                style={{ background:"#f0f4f0", color:"#1a2e1a", padding:"11px 22px", fontSize:12 }}>
                ⚡ Submit COA Data
              </button>
              <button onClick={()=>setPage("lp")}
                style={{ padding:"11px 20px", borderRadius:10, border:"1px solid rgba(255,255,255,0.2)", background:"transparent", color:"rgba(240,244,240,0.8)", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                Open LP Portal →
              </button>
            </div>
          </div>

          {/* ── LP KPIs ── */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10, marginBottom:20 }}>
            {[
              { label:"Verified LPs",    value:verifiedLPs.length,  icon:"🏷",  color:"#0369a1", sub:"in BC network" },
              { label:"Lab-Verified SKUs",value:myProducts.length,   icon:"✓",  color:"#15803d", sub:"with terpene data" },
              { label:"BC Dispensaries", value:DB_DISPENSARIES.length,  icon:"🏪", color:"#7c3aed", sub:"retailer touchpoints" },
              { label:"Terpenes Mapped", value:Object.keys(TERPENES).length, icon:"🌿", color:"#0d9488", sub:"reportable compounds" },
            ].map(item => (
              <div key={item.label} className="card" style={{ padding:"14px 16px", textAlign:"center" }}>
                <div style={{ fontSize:18, marginBottom:6 }}>{item.icon}</div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, color:item.color, fontWeight:400 }}>{item.value}</div>
                <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.06em", marginTop:4 }}>{item.label}</div>
                <div style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{item.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>

            {/* Consumer terpene demand */}
            <div className="card" style={{ padding:20 }}>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>📡 Consumer Terpene Demand</div>
              <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:14 }}>By total mg/g volume across all BC SKUs</div>
              {demandLeaders.map(([t, data]) => {
                const info = TERPENES[t];
                const maxDemand = demandLeaders[0][1].demand;
                return (
                  <div key={t} style={{ marginBottom:11 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                        <div style={{ width:7, height:7, borderRadius:"50%", background:info?.color||"#6b7280" }}/>
                        <span style={{ fontSize:11, color:"#1a2e1a" }}>{t}</span>
                      </div>
                      <span style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{data.count} SKUs</span>
                    </div>
                    <div style={{ height:5, background:"#f3f4f6", borderRadius:3 }}>
                      <div style={{ height:5, background:info?.color||"#6b7280", borderRadius:3, width:((data.demand/maxDemand)*100) + "%", opacity:0.8 }}/>
                    </div>
                  </div>
                );
              })}
              <button onClick={()=>setPage("demand")} style={{ marginTop:8, fontSize:10, color:"#4a7c59", fontFamily:"'DM Mono',monospace", background:"none", border:"none", cursor:"pointer" }}>Full demand analysis →</button>
            </div>

            {/* LP quick actions */}
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {[
                { icon:"⚡", label:"AI COA Parser",      sub:"Drag in a PDF — extract 21 terpenes + 14 cannabinoids",  action:()=>{ setPage("lp"); setLpTabState("upload"); }, accent:"#4a7c59" },
                { icon:"📊", label:"Batch Tracker",       sub:"Track terpene drift across harvests",                     action:()=>setPage("batches"),  accent:"#0ea5e9" },
                { icon:"📈", label:"Demand Gap Analysis", sub:"See which profiles consumers want but stores lack",       action:()=>setPage("demand"),   accent:"#8b5cf6" },
                { icon:"🔬", label:"Explorer",            sub:"Browse all verified BC products",                         action:()=>setPage("explorer"), accent:"#f59e0b" },
              ].map(item => (
                <button key={item.label} onClick={item.action}
                  className="card card-click"
                  style={{ padding:"14px 16px", textAlign:"left", cursor:"pointer", background:"#fff", border:"1px solid #e8e4de", display:"flex", alignItems:"center", gap:14, transition:"all 0.15s" }}
                  onMouseEnter={e=>{ e.currentTarget.style.borderColor=item.accent; e.currentTarget.style.transform="translateX(3px)"; }}
                  onMouseLeave={e=>{ e.currentTarget.style.borderColor="#e8e4de"; e.currentTarget.style.transform="none"; }}>
                  <span style={{ fontSize:22, flexShrink:0 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize:13, color:"#1a2e1a", fontFamily:"'Fraunces',serif", marginBottom:2 }}>{item.label}</div>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{item.sub}</div>
                  </div>
                  <span style={{ marginLeft:"auto", color:"#9ca3af", fontSize:14, flexShrink:0 }}>→</span>
                </button>
              ))}
            </div>
          </div>

          {/* Compliance notice */}
          <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:12, padding:"14px 18px", display:"flex", gap:12, alignItems:"flex-start" }}>
            <span style={{ fontSize:18, flexShrink:0 }}>⚖️</span>
            <p style={{ fontSize:11, color:"#92400e", lineHeight:1.6, margin:0, fontFamily:"'DM Mono',monospace" }}>
              All LP data submitted through iTerps is displayed using sensory descriptors only, in compliance with Canada&#39;s Cannabis Act. No health, therapeutic, or effect-based claims are made. COA data is shown verbatim from accredited laboratory results.
            </p>
          </div>
        </div>
      );
    }

    // ── CONSUMER HOME (when not in consumerMode tabs) ─────────
    const kioskDisp = kioskMode && kioskDispensary ? DB_DISPENSARIES.find(d=>d.id===kioskDispensary) : null;
    return (
      <div className="fade" style={{ maxWidth:600, margin:"0 auto", padding:"40px 20px" }}>
        {kioskDisp && (
          <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:12, padding:"12px 16px", marginBottom:24, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:22 }}>📍</span>
            <div>
              <div style={{ fontSize:13, fontWeight:600, fontFamily:"'DM Mono',monospace", color:"#1a2e1a" }}>{kioskDisp.name}</div>
              <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{kioskDisp.city} · {ALL_PRODUCTS.filter(p=>p.dispensary_id===kioskDispensary).length} products available in store</div>
            </div>
          </div>
        )}
        <div style={{ textAlign:"center", marginBottom:36 }}>
          <div style={{ width:64, height:64, borderRadius:20, background:"#1a2e1a", display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:28, marginBottom:20 }}>🌿</div>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:30, fontWeight:300, color:"#1a2e1a", marginBottom:10 }}>Find your match</h1>
          <p style={{ fontSize:13, color:"#6b7280", lineHeight:1.7, fontFamily:"'DM Mono',monospace" }}>Not by strain name. Not by THC. By what it smells like — and the chemistry behind it.</p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:32 }}>
          {[
            { icon:"🌿", label:"Find My Strain",       sub:"5 aroma questions → matched products",     action:()=>setConsumerTab("find"),   accent:"#4a7c59", primary:true },
            { icon:"🔍", label:"Browse Products",       sub:"Explore all products with terpene filters", action:()=>setConsumerTab("browse"), accent:"#0ea5e9", primary:false },
            { icon:"🪪", label:"My Terpene Passport",  sub:"View saved preferences and rated products",  action:()=>setConsumerTab("profile"),accent:"#8b5cf6", primary:false },
            { icon:"🌱", label:"New to Cannabis?",      sub:"Simpler 3-question guide, no jargon",       action:()=>{ setConsumerTab("newbie"); setPage("consumer"); }, accent:"#16a34a", primary:false },
          ].map(item => (
            <button key={item.label} onClick={item.action}
              style={{
                display:"flex", alignItems:"center", gap:16, padding:"18px 20px",
                borderRadius:14, cursor:"pointer", textAlign:"left", transition:"all 0.15s",
                border: item.primary ? "none" : "1px solid #e8e4de",
                background: item.primary ? "#1a2e1a" : "#fff",
                color: item.primary ? "#f0f4f0" : "#1a2e1a",
              }}
              onMouseEnter={e=>{ if(!item.primary){ e.currentTarget.style.borderColor=item.accent; e.currentTarget.style.transform="translateY(-1px)"; } }}
              onMouseLeave={e=>{ if(!item.primary){ e.currentTarget.style.borderColor="#e8e4de"; e.currentTarget.style.transform="none"; } }}>
              <span style={{ fontSize:24, flexShrink:0 }}>{item.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontFamily:"'Fraunces',serif", fontWeight:400, marginBottom:3 }}>{item.label}</div>
                <div style={{ fontSize:10, opacity:item.primary?0.7:1, color:item.primary?"#f0f4f0":"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{item.sub}</div>
              </div>
              <span style={{ fontSize:16, opacity:0.5 }}>→</span>
            </button>
          ))}
        </div>

        {/* Trust signals */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
          {[
            ["✓", (ALL_PRODUCTS.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").length) + " lab-verified products"],
            ["✓", (Object.keys(TERPENES).length) + " terpenes with plain-language science"],
            ["✓", "No health claims — Cannabis Act compliant"],
            ["✓", "Your data stays on your device"],
          ].map(([icon, text]) => (
            <div key={text} style={{ display:"flex", gap:8, alignItems:"flex-start", padding:"10px 14px", background:"#f7f5f0", borderRadius:10 }}>
              <span style={{ color:"#4a7c59", fontWeight:700, fontSize:12, flexShrink:0 }}>{icon}</span>
              <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{text}</span>
            </div>
          ))}
        </div>

        <div style={{ background:"#fff", border:"1px solid #e8e4de", borderRadius:12, padding:"14px 18px", display:"flex", gap:10, alignItems:"flex-start" }}>
          <span style={{ fontSize:16, flexShrink:0 }}>⚖️</span>
          <p style={{ fontSize:10, color:"#9ca3af", lineHeight:1.6, margin:0, fontFamily:"'DM Mono',monospace" }}>
            iTerps describes cannabis by its chemical composition and sensory characteristics only. No therapeutic, health, or lifestyle claims are made. For guidance on responsible use, refer to Health Canada&#39;s resources.
          </p>
        </div>

        {/* ── New Arrivals ── */}
        {(() => {
          const pool = activeDispensary ? homePool : ALL_PRODUCTS;
          const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 90);
          const newArrivals = pool
            .filter(p => p.tested && new Date(p.tested) >= cutoff)
            .sort((a,b) => new Date(b.tested) - new Date(a.tested))
            .slice(0, 6);
          if (!newArrivals.length) return null;
          return (
            <div style={{ marginTop:24 }}>
              <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.06em" }}>🆕 New Arrivals — tested last 90 days</div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {newArrivals.map(p => {
                  const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
                  const color = TERPENES[topT?.[0]]?.color || "#4a7c59";
                  return (
                    <button key={p.id} onClick={()=>viewProduct(p)} style={{ flex:"1 1 160px", minWidth:140, maxWidth:220, background:"#fff", border:"1px solid " + (color) + "30", borderRadius:12, padding:"12px 14px", cursor:"pointer", textAlign:"left", transition:"box-shadow 0.15s" }}
                      onMouseOver={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.08)"}
                      onMouseOut={e=>e.currentTarget.style.boxShadow="none"}>
                      <div style={{ fontSize:12, fontWeight:600, color:"#1a2e1a", marginBottom:4, lineHeight:1.3 }}>{p.name}</div>
                      <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>{p.brand} · {p.thc}% THC</div>
                      {topT && <span style={{ fontSize:10, background:(color) + "15", color, padding:"2px 8px", borderRadius:12, fontFamily:"'DM Mono',monospace" }}>{topT[0]}</span>}
                      <div style={{ fontSize:9, color:"#d1d5db", fontFamily:"'DM Mono',monospace", marginTop:6 }}>tested {p.tested}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── Recently Viewed ── */}
        {recentlyViewed.length > 0 && (
          <div style={{ marginTop:24 }}>
            <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.06em" }}>🕐 Recently Viewed</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {recentlyViewed.slice(0,8).map(r => {
                const prod = ALL_PRODUCTS.find(p=>p.id===r.id);
                if (!prod) return null;
                return (
                  <button key={r.id} onClick={()=>viewProduct(prod)}
                    style={{ padding:"6px 14px", borderRadius:20, border:"1px solid #e8e4de", background:"#f9fafb", color:"#374151", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    {r.name}
                  </button>
                );
              })}
              <button onClick={()=>{ setRecentlyViewed([]); try{localStorage.removeItem("iterps_recently_viewed");}catch (_e) {} }}
                style={{ padding:"6px 12px", borderRadius:20, border:"1px solid #f3f4f6", background:"none", color:"#d1d5db", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                clear ×
              </button>
            </div>
          </div>
        )}

      </div>
    );
  };
  const TrainingPage = () => (
    <div className="fade">
      <div style={{ maxWidth:760, margin:"0 auto", padding:"28px 20px" }}>
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Budtender Training</h1>
          <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Structured cannabis science education for retail staff. Complete modules to earn certification records.</p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:10, marginBottom:28 }}>
          {[["Modules",TRAINING_MODULES.length.toString()],["Completed",completedModules.length.toString()],["Questions",TRAINING_MODULES.reduce((s,m)=>s+m.quiz.length,0).toString()]].map(([l,v])=>(
            <div key={l} className="stat" style={{ textAlign:"center" }}>
              <div style={{ fontSize:26, fontWeight:400, color:"#1a2e1a", fontFamily:"'Fraunces',serif", marginBottom:4 }}>{v}</div>
              <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{l}</div>
            </div>
          ))}
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {TRAINING_MODULES.map(mod => {
            const done = completedModules.includes(mod.id);
            return (
              <div key={mod.id} className="card" style={{ padding:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:8, flexWrap:"wrap" }}>
                      <span className="pill">{mod.level}</span>
                      <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{mod.duration}</span>
                      <span style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>· {mod.lessons.length} lessons · {mod.quiz.length} questions</span>
                      {done && <span className="lab-badge lab-verified">✓ Completed</span>}
                    </div>
                    <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:400, color:"#1a2e1a", marginBottom:6 }}>{mod.title}</h3>
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                      {mod.lessons.map(l=><span key={l.title} className="pill" style={{ fontSize:9 }}>{l.title}</span>)}
                    </div>
                  </div>
                  <button className={done?"btn-ghost":"btn-primary"} style={{ flexShrink:0 }} onClick={() => { setTrainingModule(mod.id); setLessonIdx(0); setQuizMode(false); setQuizIdx(0); setQuizAnswer(null); setQuizScore(0); setQuizDone(false); }}>
                    {done ? "Review" : "Start →"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Flash Quiz CTA */}
        <div style={{ marginTop:16, background:"linear-gradient(135deg,#1a2e1a,#2d4a2d)", borderRadius:12, padding:"18px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <div>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>READY TO TEST YOUR KNOWLEDGE?</div>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:17, color:"#fff", marginBottom:2 }}>Terpene Flash Quiz</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", fontFamily:"'DM Mono',monospace" }}>20 randomised questions from all modules</div>
          </div>
          <button onClick={()=>setPage("quiz")} className="btn-primary" style={{ flexShrink:0, background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", color:"#fff" }}>
            Start Flash Quiz →
          </button>
        </div>

        <div style={{ marginTop:16, background:"#f7f5f0", border:"1px solid #e8e4de", borderRadius:12, padding:16 }}>
          <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>ABOUT THIS TRAINING PROGRAM</div>
          <p style={{ fontSize:12, color:"#374151", lineHeight:1.6 }}>All training content is derived from peer-reviewed scientific literature. Modules cover terpene science, cannabinoid pharmacology, and cultivation methodology. Completion records support staff education documentation for compliance purposes. Content does not constitute medical advice.</p>
        </div>
      </div>
    </div>
  );

  // ── ALGORITHM PAGE ────────────────────────────────────────
  // ── CUSTOMERS PAGE (Staff) ────────────────────────────────────
  const CustomersPage = () => {
    const NOW = new Date("2026-03-09");
    const daysSince = (dateStr) => dateStr ? Math.floor((NOW - new Date(dateStr)) / 86400000) : 9999;

    const filtered = profiles.filter(p =>
      !customerSearch || p.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
      p.phone?.includes(customerSearch.replace(/\D/g,""))
    ).sort((a, b) => {
      if (customerSort === "recent")   return daysSince(a.updatedAt||a.createdAt) - daysSince(b.updatedAt||b.createdAt);
      if (customerSort === "products") return (b.productHistory||[]).length - (a.productHistory||[]).length;
      if (customerSort === "lapsed")   return daysSince(a.updatedAt||a.createdAt) - daysSince(b.updatedAt||b.createdAt); // most lapsed first (desc)
      return (a.name||"").localeCompare(b.name||"");
    });

    const lapsedFiltered = customerSort === "lapsed"
      ? filtered.filter(p => daysSince(p.updatedAt||p.createdAt) >= 30).reverse()
      : filtered;

    if (staffCustomerPage === "view" && selectedCustomer) {
      const profile = profiles.find(p=>p.phone===selectedCustomer.phone) || selectedCustomer;
      const fp = buildTerpeneFingerprint(profile);
      const answers = profile.finderAnswers || profile.consumerAnswers || {};
      const topTerpenes = Object.entries(fp).sort((ea,eb)=>eb[1]-ea[1]).slice(0,3);
      const history = profile.productHistory || [];
      const upRated = history.filter(h=>h.rating==="up");
      const downRated = history.filter(h=>h.rating==="down");
      const formatLabel = {flower:"Dried flower",preroll:"Pre-roll",vape:"Vape/cartridge",edible:"Edible",oil:"Oil/tincture",beverage:"Beverage",concentrate:"Concentrate",any:"No preference"};
      const potencyLabel = {mild:"Under 15% THC",moderate:"15–22% THC",strong:"22%+ THC",any:"No preference"};
      const feelLabel = {relax:"Earthy & musky",energy:"Citrusy & bright",focus:"Piney & herbal",body:"Gassy & pungent",sleep:"Floral & soft",social:"Fruity & candy",any:"No preference"};

      return (
        <div className="fade" style={{maxWidth:760,margin:"0 auto",padding:"28px 20px"}}>
          <button onClick={()=>setStaffCustomerPage("list")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:20}}>← All Customers</button>

          {/* Header */}
          <div className="card" style={{padding:24,marginBottom:16,background:"linear-gradient(135deg,#1a2e1a,#2d4a2d)",color:"#f0f4f0"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:16}}>
              <div>
                <div style={{fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:300,marginBottom:4}}>{profile.name}</div>
                <div style={{fontSize:12,opacity:0.7,fontFamily:"'DM Mono',monospace"}}>📱 ({profile.phone?.slice(0,3)}) {profile.phone?.slice(3,6)}-{profile.phone?.slice(6)}</div>
                {profile.city&&<div style={{fontSize:12,opacity:0.7,fontFamily:"'DM Mono',monospace",marginTop:2}}>📍 {profile.city}</div>}
                <div style={{fontSize:11,opacity:0.5,fontFamily:"'DM Mono',monospace",marginTop:6}}>
                  Member since {new Date(profile.createdAt).toLocaleDateString("en-CA")} · {history.length} products tried
                </div>
              </div>
              <div style={{textAlign:"center"}}>
                <TerpeneRadar fingerprint={fp} size={160}/>
                <div style={{fontSize:10,opacity:0.6,fontFamily:"'DM Mono',monospace",marginTop:4}}>Terpene Passport</div>
              </div>
            </div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
            {/* Preferences summary */}
            <div className="card" style={{padding:18}}>
              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>Preference Profile</div>
              {[
                ["Aroma profile", feelLabel[answers.feeling||answers.vibe] || "—"],
                ["Format", formatLabel[answers.format] || "—"],
                ["Potency", potencyLabel[answers.intensity||answers.strength] || "—"],
                ["Aroma family", answers.aroma ? answers.aroma : "—"],
              ].map(([l,v])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",marginBottom:8,paddingBottom:8,borderBottom:"1px solid #f3f4f6"}}>
                  <span style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{l}</span>
                  <span style={{fontSize:12,color:"#1a2e1a",fontWeight:500}}>{v}</span>
                </div>
              ))}
            </div>

            {/* Top terpenes */}
            <div className="card" style={{padding:18}}>
              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>Top Terpenes</div>
              {topTerpenes.map(([t,v])=>(
                <div key={t} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:11,color:"#1a2e1a"}}>{t}</span>
                    <span style={{fontSize:11,color:"#4a7c59",fontFamily:"'DM Mono',monospace"}}>{v}%</span>
                  </div>
                  <div style={{height:5,background:"#f3f4f6",borderRadius:3}}>
                    <div style={{height:5,background:"#4a7c59",borderRadius:3,width:(v+"%"),transition:"width 0.5s ease"}}/>
                  </div>
                </div>
              ))}
              {topTerpenes.length===0&&<div style={{fontSize:12,color:"#9ca3af"}}>No terpene data yet — run the finder first.</div>}
            </div>
          </div>

          {/* ── Profile action buttons ── */}
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16 }}>
            <button onClick={()=>{ setPage("finder"); }}
              style={{ flex:"1 1 140px", padding:"10px 0", borderRadius:10, border:"none", background:"#1a2e1a", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:600 }}>
              🌿 Run Finder
            </button>
            <button onClick={()=>{ setLogForCustModal({ product: null, preselectedCustomer: profile }); setLogForCustSearch(""); setLogForCustDone(null); }}
              style={{ flex:"1 1 140px", padding:"10px 0", borderRadius:10, border:"1px solid #86efac", background:"#f0fdf4", color:"#15803d", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:600 }}>
              📋 Log product
            </button>
            {history.length > 0 && (
              <button onClick={()=>setSmsModal({ products: history.slice(0,3).map(h=>ALL_PRODUCTS.find(p=>p.id===h.productId)).filter(Boolean), customerName: profile.name })}
                style={{ flex:"1 1 140px", padding:"10px 0", borderRadius:10, border:"1px solid #e8e4de", background:"#f9fafb", color:"#374151", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                📱 Send history
              </button>
            )}
          </div>

          {/* Sensitivity flags */}
          {(answers.avoid||[]).filter(a=>a!=="none").length>0&&(
            <div className="card" style={{padding:16,marginBottom:14,background:"#fffbeb",border:"1px solid #fcd34d"}}>
              <div style={{fontSize:10,color:"#92400e",fontFamily:"'DM Mono',monospace",marginBottom:8,textTransform:"uppercase"}}>⚠️ Sensitivity Flags</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {(answers.avoid||[]).filter(a=>a!=="none").map(a=>(
                  <span key={a} className="pill" style={{background:"#fef3c7",color:"#92400e",border:"1px solid #fcd34d"}}>{a.replace("_"," ")}</span>
                ))}
              </div>
            </div>
          )}

          {/* Shift Handoff Notes */}
          <div className="card" style={{padding:18,marginBottom:14}}>
            <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.08em"}}>
              📋 Shift Handoff Notes
              {profile.noteUpdatedAt&&<span style={{marginLeft:8,color:"#d1d5db"}}>Last updated {new Date(profile.noteUpdatedAt).toLocaleDateString("en-CA")}</span>}
            </div>
            <textarea value={staffNoteText||profile.staffNote||""} onChange={e=>{setStaffNoteText(e.target.value);setStaffNoteSaved(false);}}
              placeholder="e.g. Prefers indica-dominant, sensitive to high THC. Loved Wedding Cake last visit. Looking for something new with similar terpene profile..."
              style={{width:"100%",minHeight:80,padding:"10px 12px",border:"1px solid #e8e4de",borderRadius:10,fontSize:12,fontFamily:"'DM Mono',monospace",
                color:"#374151",background:"#fafaf7",resize:"vertical",outline:"none",lineHeight:1.6}}/>
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:8}}>
              <button onClick={()=>{saveNote(profile.phone,staffNoteText||profile.staffNote||"");setStaffNoteSaved(true);}}
                className="btn-primary" style={{fontSize:11,padding:"7px 16px",background:staffNoteSaved?"#4a7c59":"#1a2e1a"}}>
                {staffNoteSaved?"✓ Saved":"Save Note"}
              </button>
            </div>
          </div>

          {/* Recommend button */}
          <div className="card" style={{padding:16,marginBottom:14,background:"#f0fdf4",border:"1px solid #bbf7d0"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
              <div>
                <div style={{fontSize:13,fontWeight:500,color:"#1a2e1a",marginBottom:2}}>Recommend for this customer</div>
                <div style={{fontSize:11,color:"#6b7280",fontFamily:"'DM Mono',monospace"}}>Pre-fills the finder with their saved preferences</div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{ setBatchCustomers([profile]); setShowBatchFinder(true); }} className="btn-ghost" style={{fontSize:11,whiteSpace:"nowrap"}}>👥 Add to Batch</button>
                <button onClick={()=>{
                  const a = profile.finderAnswers||{};
                  setFAnswers(a); setFStep(0); setFResults(null); setFAvoidSelected(a.avoid||[]); setPage("finder");
                }} className="btn-primary" style={{whiteSpace:"nowrap"}}>Open Finder →</button>
              </div>
            </div>
          </div>

          {/* Wishlist */}
          {(profile.wishlist||[]).length>0&&(
            <div className="card" style={{padding:18,marginBottom:14,background:"#fffbeb",border:"1px solid #fcd34d"}}>
              <div style={{fontSize:10,color:"#92400e",fontFamily:"'DM Mono',monospace",marginBottom:10,textTransform:"uppercase",letterSpacing:"0.08em"}}>
                ⭐ Wishlist ({profile.wishlist.length})
              </div>
              {(profile.wishlist||[]).map((w,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:i<profile.wishlist.length-1?"1px solid #fef3c7":"none"}}>
                  <div>
                    <div style={{fontSize:12,color:"#1a2e1a",fontWeight:500}}>{w.name}</div>
                    <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{w.brand} · Added {new Date(w.addedAt).toLocaleDateString("en-CA")}</div>
                  </div>
                  {ALL_PRODUCTS.find(p=>p.id===w.productId)?.in_stock
                    ?<span style={{fontSize:10,background:"#d1fae5",color:"#065f46",border:"1px solid #6ee7b7",padding:"2px 8px",borderRadius:20,fontFamily:"'DM Mono',monospace"}}>✓ Now In Stock</span>
                    :<span style={{fontSize:10,background:"#fef3c7",color:"#92400e",padding:"2px 8px",borderRadius:20,fontFamily:"'DM Mono',monospace"}}>Out of Stock</span>
                  }
                </div>
              ))}
            </div>
          )}

          {/* Product history */}
          <div className="card" style={{padding:18}}>
            <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>
              Product History ({history.length})
            </div>
            {history.length===0&&<div style={{fontSize:12,color:"#9ca3af",textAlign:"center",padding:"20px 0"}}>No products logged yet.</div>}
            {history.slice(0,10).map((h,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:i<history.length-1?"1px solid #f3f4f6":"none"}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:"#1a2e1a",fontWeight:500}}>{h.name}</div>
                  <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{h.brand} · {h.category} · {h.thc}% THC · {new Date(h.triedAt).toLocaleDateString("en-CA")}</div>
                  {h.triedCount>1&&<div style={{fontSize:10,color:"#4a7c59",fontFamily:"'DM Mono',monospace"}}>Tried {h.triedCount}×</div>}
                </div>
                <div style={{fontSize:18}}>{h.rating==="up"?"👍":h.rating==="down"?"👎":h.rating==="noted"?"📋":"—"}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="fade" style={{maxWidth:760,margin:"0 auto",padding:"28px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
          <div>
            <h1 style={{fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:300,color:"#1a2e1a"}}>Customer Profiles</h1>
            <p style={{fontSize:12,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginTop:4}}>{profiles.length} passport{profiles.length!==1?"s":""} on file</p>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={()=>setPage("dashboard")} className="btn-ghost" style={{fontSize:12}}>📊 Dashboard</button>
            <button onClick={()=>setPage("data")} className="btn-ghost" style={{fontSize:12}}>📋 Export Data</button>
            <button onClick={()=>{ setBatchCustomers([]); setShowBatchFinder(true); }} className="btn-ghost" style={{fontSize:12}}>👥 Batch Finder</button>
            <button onClick={()=>{ setRegisterFor("staff"); setShowRegisterModal(true); }} className="btn-primary" style={{fontSize:12}}>+ New Profile</button>
          </div>
        </div>

        <div style={{display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center"}}>
          <input className="inp" value={customerSearch} onChange={e=>setCustomerSearch(e.target.value)}
            placeholder="Search by name or phone number..." style={{flex:"1 1 200px", marginBottom:0}}/>
          <div style={{display:"flex", gap:4}}>
            {[["name","A–Z"],["recent","Recent"],["products","Most Active"],["lapsed","Lapsed 30d+"]].map(([val,label])=>(
              <button key={val} onClick={()=>setCustomerSort(val)}
                style={{padding:"7px 11px", borderRadius:8, border:"1px solid " + (customerSort===val?"#4a7c59":"#e8e4de"),
                  background:customerSort===val?"#f0fdf4":"transparent", cursor:"pointer", fontSize:10,
                  fontFamily:"'DM Mono',monospace", color:customerSort===val?"#166534":"#9ca3af", whiteSpace:"nowrap", fontWeight:customerSort===val?600:400}}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {customerSort === "lapsed" && lapsedFiltered.length > 0 && (
          <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:11,color:"#92400e",fontFamily:"'DM Mono',monospace"}}>
            ⚠️ {lapsedFiltered.length} customer{lapsedFiltered.length!==1?"s":""} haven&#39;t been seen in 30+ days
          </div>
        )}
        {customerSort === "lapsed" && lapsedFiltered.length === 0 && profiles.length > 0 && (
          <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:11,color:"#166534",fontFamily:"'DM Mono',monospace"}}>
            ✓ No lapsed customers — everyone seen within 30 days
          </div>
        )}

        {showBatchFinder&&<BatchFinderModal onClose={()=>{ setShowBatchFinder(false); setBfResults(null); setBfSearch(""); }}/>}
        {newArrivalMatches&&(
          <div className="overlay" onClick={e=>e.target===e.currentTarget&&setNewArrivalMatches(null)}>
            <div className="modal">
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <div>
                  <div style={{fontFamily:"'Fraunces',serif",fontSize:18,color:"#1a2e1a"}}>New Arrival Match</div>
                  <div style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginTop:2}}>{newArrivalMatches.product.name}</div>
                </div>
                <button onClick={()=>setNewArrivalMatches(null)} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#9ca3af"}}>✕</button>
              </div>
              {newArrivalMatches.matches.length===0&&<div style={{textAlign:"center",color:"#9ca3af",padding:"24px 0",fontSize:12}}>No strong matches in current profiles.</div>}
              {newArrivalMatches.matches.map(({profile:p,pct},i)=>(
                <div key={p.phone} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid #f3f4f6"}}>
                  <div>
                    <div style={{fontSize:13,color:"#1a2e1a",fontWeight:500}}>{p.name}</div>
                    <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{p.city||"BC"} · {(p.productHistory||[]).length} products tried</div>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:16,fontWeight:700,color:pct>=70?"#22c55e":pct>=50?"#f59e0b":"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{pct}%</div>
                    <div style={{fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>match</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {showRegisterModal && (
          <RegisterModal mode="staff" finderAnswers={{}}
            onSave={(profile)=>{ setShowRegisterModal(false); setRmName(""); setRmPhone(""); setRmCity(""); setRmConsent(false); setRmAgeConsent(false); setRmError(""); setRmStep("form"); setSelectedCustomer(profile); setStaffCustomerPage("view"); }}
            onClose={closeRegModal}/>
        )}

        {filtered.length===0&&(
          <div style={{textAlign:"center",padding:"48px 0",color:"#9ca3af"}}>
            <div style={{fontSize:36,marginBottom:12}}>🪪</div>
            <div style={{fontFamily:"'Fraunces',serif",fontSize:18,marginBottom:6,color:"#374151"}}>No profiles yet</div>
            <div style={{fontSize:12,fontFamily:"'DM Mono',monospace"}}>Run the finder with a customer and save their terpene passport.</div>
          </div>
        )}

        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {(customerSort==="lapsed"?lapsedFiltered:filtered).map(profile=>{
            const fp = buildTerpeneFingerprint(profile);
            const top = Object.entries(fp).sort((ea,eb)=>eb[1]-ea[1])[0];
            const history = profile.productHistory||[];
            const dAgo = daysSince(profile.updatedAt||profile.createdAt);
            const isLapsed = dAgo >= 30;
            return (
              <div key={profile.phone} className="card card-click" style={{padding:18,display:"flex",justifyContent:"space-between",alignItems:"center",gap:16,borderLeft:isLapsed&&customerSort==="lapsed"?"3px solid #f59e0b":"none"}}
                onClick={()=>{ setSelectedCustomer(profile); setStaffCustomerPage("view"); }}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4,flexWrap:"wrap"}}>
                    <div style={{fontFamily:"'Fraunces',serif",fontSize:17,color:"#1a2e1a"}}>{profile.name}</div>
                    {profile.city&&<span className="pill">{profile.city}</span>}
                    {dAgo < 9999 && (
                      <span style={{fontSize:9,fontFamily:"'DM Mono',monospace",color:isLapsed?"#ea580c":"#6b7280",
                        background:isLapsed?"#fff7ed":"#f9fafb",border:"1px solid " + (isLapsed?"#fed7aa":"#e5e7eb"),
                        padding:"2px 7px",borderRadius:20}}>
                        {isLapsed ? "⚠ " + (dAgo) + "d ago" : "✓ " + (dAgo) + "d ago"}
                      </span>
                    )}
                    {profile.staffNote && <span style={{fontSize:9,fontFamily:"'DM Mono',monospace",color:"#4a7c59",background:"#f0fdf4",border:"1px solid #bbf7d0",padding:"2px 7px",borderRadius:20}}>📝 note</span>}
                  </div>
                  <div style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:6}}>
                    📱 ({profile.phone?.slice(0,3)}) {profile.phone?.slice(3,6)}-{profile.phone?.slice(6)} · {history.length} products tried
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {top&&<span className="pill" style={{background:"#f0fdf4",color:"#065f46",border:"1px solid #bbf7d0"}}>🌿 {top[0]}</span>}
                    {(profile.finderAnswers||profile.consumerAnswers||{}).format&&(
                      <span className="pill">{(profile.finderAnswers||profile.consumerAnswers||{}).format}</span>
                    )}
                    {history.filter(h=>h.rating==="up").length>0&&(
                      <span className="pill">👍 {history.filter(h=>h.rating==="up").length} liked</span>
                    )}
                  </div>
                </div>
                <div style={{flexShrink:0}}>
                  <TerpeneRadar fingerprint={fp} size={80}/>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── DATA EXPORT PAGE ──────────────────────────────────────────
  const DataExportPage = () => {
    return (<div className="fade"><h1>Data Export</h1></div>);
  };

  // ── DISPENSARY DASHBOARD ──────────────────────────────────────
  const AlgorithmPage = () => {
    const selectedA = algoA || ALL_PRODUCTS[0].id, setSelectedA = setAlgoA;
    const selectedB = algoB || ALL_PRODUCTS[1].id, setSelectedB = setAlgoB;
    const prodA = ALL_PRODUCTS.find(p=>p.id===selectedA);
    const prodB = ALL_PRODUCTS.find(p=>p.id===selectedB);
    const sim = prodA && prodB ? cosineSimilarity(prodA.terpenes, prodB.terpenes) : 0;
    const allKeys = prodA && prodB ? [...new Set([...Object.keys(prodA.terpenes),...Object.keys(prodB.terpenes)])] : [];

    return (
      <div className="fade">
        <div style={{ maxWidth:820, margin:"0 auto", padding:"28px 20px" }}>
          <button onClick={()=>setPage("dashboard")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:20}}>← Dashboard</button>
        <div style={{ marginBottom:24 }}>
            <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Terpene Similarity Algorithm</h1>
            <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Chemical profile matching via cosine similarity. Compare terpene vectors across products.</p>
          </div>

          <div style={{ display:"flex", gap:8, marginBottom:24 }}>
            {[["explorer","Compare Products"],["methodology","Methodology"]].map(([k,l])=>(
              <button key={k} className={"tab " + (algoTab===k?"on":"off")} onClick={()=>setAlgoTab(k)}>{l}</button>
            ))}
          </div>

          {algoTab === "explorer" && (
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
                {[["Product A", selectedA, setSelectedA],["Product B", selectedB, setSelectedB]].map(([label,val,setter])=>(
                  <div key={label} className="card" style={{ padding:16 }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>{label.toUpperCase()}</div>
                    <select className="sel" style={{ width:"100%", marginBottom:10 }} value={val} onChange={e=>setter(e.target.value)}>
                      {ALL_PRODUCTS.map(p=><option key={p.id} value={p.id}>{p.name} — {p.brand}</option>)}
                    </select>
                    {ALL_PRODUCTS.find(p=>p.id===val) && (
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                        {Object.entries(ALL_PRODUCTS.find(p=>p.id===val).terpenes).sort((ea,eb)=>eb[1]-ea[1]).map(([t,v])=>{
                          const ti=TERPENES[t];
                          return <span key={t} className="chip" style={{ background:(ti?.color||"#6b7280") + "15",color:ti?.color||"#6b7280",border:"1px solid " + (ti?.color||"#6b7280") + "25",fontSize:10 }}>{t} {(v*100).toFixed(1)}%</span>;
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Similarity Score */}
              <div className="card" style={{ padding:24, marginBottom:16, textAlign:"center" }}>
                <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>COSINE SIMILARITY SCORE</div>
                <div style={{ fontSize:52, fontWeight:300, color:"#1a2e1a", fontFamily:"'Fraunces',serif", marginBottom:8 }}>{Math.round(sim * 100)}%</div>
                <div style={{ maxWidth:400, margin:"0 auto 12px" }}>
                  <div className="bar-track" style={{ height:10 }}>
                    <div className="bar-fill" style={{ width:(Math.round(sim*100)) + "%", background:"linear-gradient(90deg, " + (sim>0.7?"#22c55e":sim>0.4?"#f59e0b":"#ef4444") + ", " + (sim>0.7?"#3b82f6":"#f59e0b") + ")" }} />
                  </div>
                </div>
                <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>
                  {sim > 0.8 ? "Very similar terpene profiles" : sim > 0.6 ? "Moderately similar terpene profiles" : sim > 0.4 ? "Some terpene overlap" : "Distinct terpene profiles"}
                </p>
              </div>

              {/* Vector comparison */}
              {prodA && prodB && (
                <div className="card" style={{ padding:20 }}>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:14 }}>TERPENE VECTOR COMPARISON</div>
                  {allKeys.map(t => {
                    const ti = TERPENES[t];
                    const vA = prodA.terpenes[t] || 0;
                    const vB = prodB.terpenes[t] || 0;
                    const max = Math.max(vA, vB, 0.01);
                    return (
                      <div key={t} style={{ marginBottom:14 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <button onClick={()=>{setCompound(t);setCompoundType("terpene");}} style={{ background:"none",border:"none",cursor:"pointer" }}>
                            <span className="chip" style={{ background:(ti?.color||"#6b7280") + "15",color:ti?.color||"#6b7280",border:"1px solid " + (ti?.color||"#6b7280") + "25",fontSize:10 }}>{t}</span>
                          </button>
                          <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{(vA*100).toFixed(2)}% vs {(vB*100).toFixed(2)}%</span>
                        </div>
                        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <span style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace", width:16 }}>A</span>
                            <div className="bar-track" style={{ flex:1 }}><div className="bar-fill" style={{ width:(vA/max*100) + "%", background:ti?.color||"#6b7280" }} /></div>
                          </div>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <span style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace", width:16 }}>B</span>
                            <div className="bar-track" style={{ flex:1 }}><div className="bar-fill" style={{ width:(vB/max*100) + "%", background:(ti?.color||"#6b7280") + "88" }} /></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {algoTab === "methodology" && (
            <div>
              <div className="card" style={{ padding:22, marginBottom:14 }}>
                <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:400, color:"#1a2e1a", marginBottom:14 }}>Cosine Similarity</h3>
                <p style={{ fontSize:13, color:"#374151", lineHeight:1.7, marginBottom:14 }}>The iTerps similarity algorithm represents each product&#39;s terpene profile as a mathematical vector in n-dimensional space, where n is the number of unique terpene compounds across the dataset. Each dimension&#39;s value is the terpene concentration as a percentage of sample weight.</p>
                <div style={{ background:"#1a2e1a", borderRadius:10, padding:16, marginBottom:14 }}>
                  <div style={{ fontSize:9, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>FORMULA</div>
                  <p style={{ fontSize:13, color:"#4ade80", fontFamily:"'DM Mono',monospace", lineHeight:1.8 }}>
                    similarity(A, B) = (A · B) / (|A| × |B|)<br />
                    where A · B = Σ(Aᵢ × Bᵢ)<br />
                    and |A| = √(Σ Aᵢ²)
                  </p>
                </div>
                <p style={{ fontSize:12, color:"#6b7280", lineHeight:1.6 }}>A score of 1.0 indicates identical terpene profiles. A score of 0.0 indicates no terpene overlap. The algorithm is magnitude-independent — it measures angular distance between vectors, meaning a product with high terpene concentration and a product with low concentration but identical ratios would score highly similar.</p>
              </div>

              <div className="card" style={{ padding:22, marginBottom:14 }}>
                <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:400, color:"#1a2e1a", marginBottom:14 }}>Data Standardization</h3>
                <p style={{ fontSize:13, color:"#374151", lineHeight:1.7, marginBottom:12 }}>All compound identifiers use IUPAC systematic nomenclature to ensure global interoperability. This means terpene data from different labs, different provinces, and different countries can be compared when they report using the same systematic names.</p>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {[["β-Myrcene","Not 'Myrcene' — systematic name specifies the beta isomer"],["Δ9-THC","Not 'THC' — specifies the delta-9 isomer vs delta-8"],["(+)-Limonene","Stereochemical designation specifies the R enantiomer"]].map(([name,note])=>(
                    <div key={name} style={{ display:"flex", gap:10, padding:"10px 12px", background:"#f7f5f0", borderRadius:8 }}>
                      <span className="chip" style={{ background:"#1a2e1a20", color:"#1a2e1a", fontSize:10, flexShrink:0 }}>{name}</span>
                      <span style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{note}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="info-box">
                <span>🌐</span>
                <p style={{ fontSize:12, color:"#065f46", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>iTerps data schema is designed for global interoperability. IUPAC nomenclature allows future integration with international cannabis data systems as legal markets develop worldwide.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── LP PORTAL ─────────────────────────────────────────────

  const LPPortal = () => {
    const lpTab=lpTabState, setLpTab=setLpTabState;
    const coaStep=coaStepState, setCoaStep=setCoaStepState;
    const coaSubmitted=coaSubmittedState, setCoaSubmitted=setCoaSubmittedState;
    const coaForm=coaFormState, setCoaForm=setCoaFormState;
    // Scope all data to the logged-in LP — never expose competitor product details
    const myProducts = activeLp ? ALL_PRODUCTS.filter(p=>p.cultivation?.lp===activeLp) : [];
    const verifiedLPs = [...new Set(ALL_PRODUCTS.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").map(p=>p.cultivation.lp))];
    const totalVerified = myProducts.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").length;
    const totalEstimated = myProducts.filter(p=>p.terpene_source==="estimated").length;
    const networkVerified = ALL_PRODUCTS.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").length;
    const coaSteps = ["LP & Product","Cannabinoids & Terpenes","Cultivation & Genetics","Lab & Contact"];

    const Field = ({ label, k, placeholder, half }) => (
      <div style={{ marginBottom:12, flex: half ? "1 1 45%" : "1 1 100%" }}>
        <label style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", display:"block", marginBottom:4 }}>{label}</label>
        <input value={coaForm[k]} onChange={e => setCoaForm(f => ({...f,[k]:e.target.value}))}
          placeholder={placeholder||""}
          style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", boxSizing:"border-box", outline:"none" }} />
      </div>
    );

    return (
      <div className="fade">
        <div style={{ maxWidth:820, margin:"0 auto", padding:"28px 20px" }}>
          <div style={{ marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:10 }}>
            <div>
              <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Licensed Producer Portal</h1>
              <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Submit verified lab data to replace estimated terpene profiles. Data partnership, not advertising.</p>
            </div>
            <button onClick={()=>{ setDemoRole("lp"); setDemoActive(true); setDemoStep(0); setPage("lp"); setLpTabState("overview"); }}
              style={{ display:"flex", alignItems:"center", gap:5, padding:"7px 14px", borderRadius:8,
                border:"1px solid #93c5fd", background:"#eff6ff", cursor:"pointer",
                fontSize:11, fontFamily:"'DM Mono',monospace", color:"#1d4ed8", flexShrink:0, fontWeight:600 }}
              onMouseEnter={e=>{ e.currentTarget.style.background="#1d4ed8"; e.currentTarget.style.color="#fff"; }}
              onMouseLeave={e=>{ e.currentTarget.style.background="#eff6ff"; e.currentTarget.style.color="#1d4ed8"; }}>
              ▶ LP Tour
            </button>
          </div>

          <div style={{ display:"flex", gap:4, marginBottom:24, borderBottom:"2px solid #f3f4f6" }}>
            {[["overview","Overview"],["data","Data Status"],["upload","⚡ Upload COA"],["submit","Manual Submit"],["analytics","📈 Analytics"],["index","📊 Demand Index"],["demo","Get a Demo"]].map(([k,l]) => (
              <button key={k} onClick={() => setLpTab(k)}
                style={{ padding:"9px 18px", background:"none", border:"none", borderBottom:lpTab===k?"2px solid #4a7c59":"2px solid transparent", marginBottom:-2, cursor:"pointer", fontSize:12, fontFamily:"'DM Mono',monospace", color:lpTab===k?"#1a2e1a":"#9ca3af", fontWeight:lpTab===k?600:400 }}>
                {l}
              </button>
            ))}
          </div>

          {lpTab === "overview" && (
            <div>
              {activeLp && (
                <div style={{ padding:"10px 16px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:18 }}>🏭</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, fontFamily:"'DM Mono',monospace", color:"#1a2e1a" }}>{activeLp}</div>
                    <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{myProducts.length} products in network · <button onClick={()=>{ setActiveLp(""); setOnboardDone(false); setOnboardStep(2); }} style={{ background:"none", border:"none", color:"#4a7c59", cursor:"pointer", fontFamily:"'DM Mono',monospace", fontSize:11, padding:0 }}>Switch LP ↗</button></div>
                  </div>
                </div>
              )}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:12, marginBottom:20 }}>
                {[
                  { label:"Your Verified SKUs", value:totalVerified, color:"#4a7c59" },
                  { label:"Your Estimated SKUs", value:totalEstimated, color:"#f59e0b" },
                  { label:"Network Verified SKUs", value:networkVerified, color:"#3b82f6" },
                  { label:"Your Total SKUs", value:myProducts.length, color:"#1a2e1a" },
                ].map(kpi => (
                  <div key={kpi.label} className="card" style={{ padding:"16px 18px" }}>
                    <div style={{ fontSize:28, fontWeight:300, fontFamily:"'Fraunces',serif", color:kpi.color }}>{kpi.value}</div>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{kpi.label}</div>
                  </div>
                ))}
              </div>
              <div className="card" style={{ padding:20, marginBottom:14 }}>
                <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a", marginBottom:12 }}>How It Works</h3>
                {[
                  ["1","Submit your COA","Upload terpene data from a Health Canada licensed testing facility via the Submit COA tab."],
                  ["2","Verification","iTerps cross-references your batch number and lab data. Estimated profiles are upgraded to Lab-Verified status."],
                  ["3","Display","Your verified data appears across all retailer dashboards carrying your product, with a ✓ Lab badge."],
                ].map(([n,title,desc]) => (
                  <div key={n} style={{ display:"flex", gap:14, marginBottom:14 }}>
                    <div style={{ width:28, height:28, borderRadius:"50%", background:"#f0fdf4", border:"1px solid #86efac", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:600, color:"#4a7c59", flexShrink:0 }}>{n}</div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:500, color:"#1a2e1a", marginBottom:3 }}>{title}</div>
                      <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="card" style={{ padding:20, borderLeft:"3px solid #4a7c59" }}>
                <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a", marginBottom:10 }}>LPs with Verified Data</h3>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {activeLp
                    ? myProducts.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").slice(0,8).map(p =>
                        <span key={p.id} className="chip" style={{ background:"#f0fdf4", color:"#166534", border:"1px solid #86efac" }}>✓ {p.name}</span>)
                    : verifiedLPs.map(lp => <span key={lp} className="chip" style={{ background:"#f0fdf4", color:"#166534", border:"1px solid #86efac" }}>✓ {lp}</span>)
                  }
                </div>
              </div>
            </div>
          )}

          {lpTab === "data" && (
            <div>
              <div className="card" style={{ padding:20 }}>
                <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a", marginBottom:14 }}>Data Status by Brand</h3>
                {Object.entries(
                  ALL_PRODUCTS.reduce((acc, p) => {
                    const b = p.brand;
                    if (!acc[b]) acc[b] = { lab:0, est:0 };
                    if (p.terpene_source === "lab_tested" || p.terpene_source === "lp_provided") acc[b].lab++;
                    else acc[b].est++;
                    return acc;
                  }, {})
                ).sort(([,a],[,b]) => (b.lab+b.est)-(a.lab+a.est)).map(([brand, counts]) => (
                  <div key={brand} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                    <div style={{ flex:1, fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{brand}</div>
                    <div style={{ display:"flex", gap:4 }}>
                      {counts.lab > 0 && <span className="chip" style={{ background:"#f0fdf4", color:"#166534", border:"1px solid #86efac", fontSize:9 }}>✓ {counts.lab} lab</span>}
                      {counts.est > 0 && <span className="chip" style={{ background:"#fefce8", color:"#854d0e", border:"1px solid #fde047", fontSize:9 }}>~ {counts.est} est.</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {lpTab === "submit" && (
            <div className="fade">
              {coaSubmitted ? (
                <div className="card" style={{ padding:32, textAlign:"center" }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
                  <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>Submission Received</h3>
                  <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>
                    COA data for <strong>{coaForm.productName}</strong> has been queued for verification. Confirmation will be sent to {coaForm.contactEmail}.
                  </p>
                  <button className="btn-primary" onClick={() => { setCoaSubmitted(false); setCoaStep(0); }}>Submit Another SKU</button>
                </div>
              ) : (
                <div>
                  <div style={{ display:"flex", gap:0, marginBottom:24 }}>
                    {coaSteps.map((s, i) => (
                      <div key={s} style={{ flex:1, textAlign:"center" }}>
                        <div style={{ height:3, background:i<=coaStep?"#4a7c59":"#e8e4de", marginBottom:6 }} />
                        <div style={{ fontSize:9, color:i<=coaStep?"#4a7c59":"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{s}</div>
                      </div>
                    ))}
                  </div>

                  <div className="card" style={{ padding:24 }}>
                    <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a", marginBottom:4 }}>{coaSteps[coaStep]}</h3>
                    <p style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>Step {coaStep+1} of {coaSteps.length}</p>

                    {coaStep === 0 && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:"0 12px" }}>
                        <Field label="Licensed Producer Name *" k="lpName" placeholder="e.g. Westleaf Labs Inc." />
                        <Field label="Brand Name *" k="brand" placeholder="e.g. Qwest, Carmel, Simply Bare" />
                        <Field label="Product / Cultivar Name *" k="productName" placeholder="e.g. Jealousy, Coastal Sage" />
                        <Field label="Batch Number *" k="batchNumber" placeholder="e.g. QW-JEA-2024-A014" half />
                        <Field label="Lot Number" k="lotNumber" placeholder="e.g. LOT-20241201" half />
                      </div>
                    )}

                    {coaStep === 1 && (
                      <div>
                        <p style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Enter as percentages (e.g. 24.8 for 24.8% THC, 0.54 for 0.54% terpene)</p>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:"0 12px" }}>
                          <Field label="Δ9-THC % *" k="thc" placeholder="e.g. 24.8" half />
                          <Field label="CBD %" k="cbd" placeholder="e.g. 0.1" half />
                          <Field label="Total Terpenes %" k="totalTerpenes" placeholder="e.g. 1.31" half />
                          <div style={{ flex:"1 1 100%", borderTop:"1px solid #f3f4f6", margin:"8px 0 14px" }} />
                          {[["β-Myrcene","myrcene"],["β-Caryophyllene","caryophyllene"],["(+)-Limonene","limonene"],["α-Pinene","pinene"],["Terpinolene","terpinolene"],["Linalool","linalool"],["β-Ocimene","ocimene"],["α-Humulene","humulene"],["α-Bisabolol","bisabolol"]].map(([label,k]) => (
                            <Field key={k} label={label} k={k} placeholder="e.g. 0.44" half />
                          ))}
                        </div>
                      </div>
                    )}

                    {coaStep === 2 && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:"0 12px" }}>
                        <div style={{ marginBottom:12, flex:"1 1 45%" }}>
                          <label style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", display:"block", marginBottom:4 }}>Cultivation Method *</label>
                          <select value={coaForm.cultivationMethod} onChange={e => setCoaForm(f=>({...f,cultivationMethod:e.target.value}))} className="sel" style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", boxSizing:"border-box" }}>
                            {["Indoor","Greenhouse","Outdoor"].map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <Field label="Growing Medium" k="medium" placeholder="e.g. Coco Coir, Living Soil" half />
                        <Field label="Cure Method" k="cure" placeholder="e.g. Slow Cure (Boveda)" half />
                        <Field label="Harvest Method" k="harvest" placeholder="e.g. Hand Trimmed" half />
                        <Field label="Cycle Days" k="cycledays" placeholder="e.g. 63" half />
                        <div style={{ flex:"1 1 100%", borderTop:"1px solid #f3f4f6", margin:"8px 0 14px" }} />
                        <Field label="Genetic Lineage" k="lineage" placeholder="e.g. Sherbert Bx1 × Gelato 41" />
                        <Field label="Breeder" k="breeder" placeholder="e.g. Seed Junky Genetics" half />
                      </div>
                    )}

                    {coaStep === 3 && (
                      <div style={{ display:"flex", flexWrap:"wrap", gap:"0 12px" }}>
                        <Field label="Lab Name *" k="labName" placeholder="e.g. Aris Global Canada" half />
                        <Field label="Lab Method" k="labMethod" placeholder="e.g. HPLC-UV / GC-MS" half />
                        <Field label="Test Date" k="testDate" placeholder="YYYY-MM-DD" half />
                        <div style={{ flex:"1 1 100%", borderTop:"1px solid #f3f4f6", margin:"8px 0 14px" }} />
                        <Field label="Contact Name *" k="contactName" placeholder="Your name" half />
                        <Field label="Contact Email *" k="contactEmail" placeholder="you@company.com" half />
                        <Field label="Additional Notes" k="notes" placeholder="Any context or questions for the iTerps team" />
                        <div style={{ flex:"1 1 100%", padding:14, background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8, marginTop:4 }}>
                          <div style={{ fontSize:10, color:"#166534", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>
                            By submitting you confirm this data is from a Health Canada licensed testing facility and you are authorised to share it. iTerps uses this data for educational, non-promotional display only.
                          </div>
                        </div>
                      </div>
                    )}

                    <div style={{ display:"flex", gap:10, marginTop:20 }}>
                      {coaStep > 0 && (
                        <button onClick={() => setCoaStep(s => s-1)} style={{ padding:"10px 20px", background:"none", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace", color:"#6b7280" }}>← Back</button>
                      )}
                      {coaStep < coaSteps.length - 1 ? (
                        <button onClick={() => setCoaStep(s => s+1)} className="btn-primary" style={{ flex:1, padding:"10px 0", fontSize:12 }}>
                          Continue: {coaSteps[coaStep+1]} →
                        </button>
                      ) : (
                        <button onClick={() => { if(coaForm.lpName && coaForm.productName && coaForm.thc && coaForm.contactEmail) { setCoaSubmitted(true); fireEmailTrigger('coa_manual_submitted', { product: coaForm.productName, lp: coaForm.lpName, email: coaForm.contactEmail }); } }} className="btn-primary" style={{ flex:1, padding:"10px 0", fontSize:12 }}>
                          Submit COA Data ✓
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {lpTab === "upload" && (
            <div className="fade">
              {/* ── COA AI PARSER ─────────────────────────────── */}
              {coaParseStatus === "idle" && (
                <div>
                  <div style={{ marginBottom:20 }}>
                    <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#1a2e1a", marginBottom:4 }}>AI COA Parser</h2>
                    <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>
                      Upload a PDF Certificate of Analysis from any Health Canada accredited lab. Our AI extracts terpene values, cannabinoids, batch data, and lab metadata — you review and confirm before anything is saved.
                    </p>
                  </div>

                  {/* Supported labs */}
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
                    {["Aris Global Canada","Keystone Labs","CannSafe","Agricore","MedReleaf Lab"].map(lab => (
                      <span key={lab} style={{ fontSize:10, background:"#f0fdf4", border:"1px solid #86efac", color:"#166534", padding:"3px 10px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>
                        ✓ {lab}
                      </span>
                    ))}
                    <span style={{ fontSize:10, background:"#f7f5f0", border:"1px solid #e8e4de", color:"#9ca3af", padding:"3px 10px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>
                      + any accredited lab
                    </span>
                  </div>

                  {/* Upload zone */}
                  <div
                    onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor="#4a7c59"; e.currentTarget.style.background="#f0fdf4"; }}
                    onDragLeave={e => { e.currentTarget.style.borderColor="#e8e4de"; e.currentTarget.style.background="#fff"; }}
                    onDrop={e => {
                      e.preventDefault();
                      e.currentTarget.style.borderColor="#e8e4de";
                      e.currentTarget.style.background="#fff";
                      const file = e.dataTransfer.files[0];
                      if (file && file.type === "application/pdf") {
                        setCoaUploadFile(file);
                        const reader = new FileReader();
                        reader.onload = ev => setCoaUploadB64(ev.target.result.split(",")[1]);
                        reader.readAsDataURL(file);
                      }
                    }}
                    style={{ border:"2px dashed #e8e4de", borderRadius:16, padding:"40px 24px", textAlign:"center", background:"#fff", cursor:"pointer", transition:"all 0.2s", marginBottom:16 }}
                    onClick={() => document.getElementById("coaPdfInput").click()}>
                    <input id="coaPdfInput" type="file" accept="application/pdf" style={{ display:"none" }}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file) {
                          setCoaUploadFile(file);
                          const reader = new FileReader();
                          reader.onload = ev => setCoaUploadB64(ev.target.result.split(",")[1]);
                          reader.readAsDataURL(file);
                        }
                      }} />
                    <div style={{ fontSize:36, marginBottom:12 }}>📄</div>
                    <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, color:"#1a2e1a", marginBottom:4 }}>
                      {coaUploadFile ? coaUploadFile.name : "Drop your COA PDF here"}
                    </div>
                    <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
                      {coaUploadFile ? ((coaUploadFile.size/1024).toFixed(0)) + " KB · Click to change" : "or click to browse · PDF only · max 10MB"}
                    </div>
                  </div>

                  {coaUploadFile && (
                    <div className="fade" style={{ display:"flex", gap:10 }}>
                      <button className="btn-ghost" onClick={() => { setCoaUploadFile(null); setCoaUploadB64(null); }}>Clear</button>
                      <button className="btn-primary" style={{ flex:1, padding:"12px 0", fontSize:13 }}
                        onClick={async () => {
                          if (!coaUploadB64) return;
                          setCoaParseStatus("parsing");
                          setCoaParseError("");
                          try {
                            const response = await fetch("https://api.anthropic.com/v1/messages", {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                model: "claude-sonnet-4-20250514",
                                max_tokens: 1000,
                                messages: [{
                                  role: "user",
                                  content: [
                                    {
                                      type: "document",
                                      source: { type: "base64", media_type: "application/pdf", data: coaUploadB64 }
                                    },
                                    {
                                      type: "text",
                                      text: "You are a cannabis lab data extraction specialist for Canadian licensed cannabis. Extract ALL data from this Certificate of Analysis (COA) and return ONLY valid JSON with no preamble or markdown.\n\nReturn exactly this structure:\n{\n  \"productName\": \"\",\n  \"brand\": \"\",\n  \"lpName\": \"\",\n  \"batchNumber\": \"\",\n  \"lotNumber\": \"\",\n  \"testDate\": \"\",\n  \"packagedDate\": \"\",\n  \"labName\": \"\",\n  \"labMethod\": \"\",\n  \"labAccreditation\": \"\",\n  \"thc\": 0,\n  \"thca\": 0,\n  \"total_thc\": 0,\n  \"cbd\": 0,\n  \"cbda\": 0,\n  \"cbg\": 0,\n  \"cbga\": 0,\n  \"cbn\": 0,\n  \"cbc\": 0,\n  \"cbca\": 0,\n  \"thcv\": 0,\n  \"cbdv\": 0,\n  \"d8thc\": 0,\n  \"totalCannabinoids\": 0,\n  \"moistureContent\": 0,\n  \"waterActivity\": 0,\n  \"totalTerpenes\": 0,\n  \"terpenes\": {\n    \"β-Myrcene\": 0,\n    \"β-Caryophyllene\": 0,\n    \"(+)-Limonene\": 0,\n    \"α-Pinene\": 0,\n    \"Terpinolene\": 0,\n    \"Linalool\": 0,\n    \"β-Ocimene\": 0,\n    \"α-Humulene\": 0,\n    \"α-Bisabolol\": 0,\n    \"Fenchol\": 0,\n    \"Camphene\": 0,\n    \"δ-3-Carene\": 0,\n    \"Borneol\": 0,\n    \"Geraniol\": 0,\n    \"Nerolidol\": 0,\n    \"Guaiol\": 0,\n    \"Valencene\": 0,\n    \"Sabinene\": 0,\n    \"p-Cymene\": 0,\n    \"Caryophyllene Oxide\": 0,\n    \"α-Terpineol\": 0\n  },\n  \"otherTerpenes\": {},\n  \"cultivationMethod\": \"\",\n  \"plantType\": \"\",\n  \"genetics\": \"\",\n  \"notes\": \"\",\n  \"qualityFlags\": [],\n  \"confidence\": \"high|medium|low\",\n  \"warnings\": []\n}\n\nRules:\n- All terpene and cannabinoid values must be in % w/w. Convert: mg/g ÷ 10 = %, ppm ÷ 10000 = %\n- total_thc: calculate as (thca × 0.877) + thc. This is the Health Canada label potency value.\n- If a terpene is listed on the COA but not in the terpenes object above, add it to otherTerpenes with its exact name as listed on the COA\n- Only include terpenes with detected values (>0) in both terpenes and otherTerpenes\n- packagedDate: look for packaged date, packaging date, or best before fields. Use ISO YYYY-MM-DD format.\n- labAccreditation: extract ISO 17025 accreditation number if shown (e.g. A2LA #4713.01)\n- qualityFlags: include any of: elevated_caryophyllene_oxide (>0.05%), high_cbn (age indicator), low_moisture (<8%), high_moisture (>13%), terpene_sum_mismatch\n- Set confidence to low if the PDF appears scanned, blurry, or values are unclear\n- Add warnings for: THC >35%, terpene sum differs from declared total by >0.1%, missing batch or lot number\n- Do not invent values — only extract what is explicitly stated in the document\n- Canadian labs to recognize: Aris Global Canada, Keystone Labs, CannSafe Analytics, Agricore, Almac Group"
                                    }
                                  ]
                                }]
                              })
                            });
                            const data = await response.json();
                            const text = (data.content||[]).map(c => c.text||"").join("");
                            const clean = text.replace(/"""json|"""/g,"").trim();
                            const parsed = JSON.parse(clean);
                            setCoaParseResult(parsed);
                            setCoaParseEdits(parsed);
                            setCoaParseStatus("review");
                          } catch(err) {
                            setCoaParseStatus("error");
                            setCoaParseError("Extraction failed — " + err.message + ". Try the manual submission form if this PDF is scanned or low quality.");
                          }
                        }}>
                        ⚡ Extract COA Data with AI →
                      </button>
                    </div>
                  )}

                  <div style={{ marginTop:20, background:"#f7f5f0", borderRadius:10, padding:"12px 16px", fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>
                    <strong style={{ color:"#374151" }}>How it works:</strong> Your PDF is sent to Claude AI which reads the document and extracts structured data. You review every field before confirming. Nothing is saved without your approval. All data is processed under our standard data agreement — your COA data belongs to you.
                  </div>
                </div>
              )}

              {/* Parsing state */}
              {coaParseStatus === "parsing" && (
                <div className="fade" style={{ textAlign:"center", padding:"60px 20px" }}>
                  <div style={{ fontSize:40, marginBottom:16 }}>🔬</div>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, color:"#1a2e1a", marginBottom:8 }}>Reading your COA...</div>
                  <div style={{ fontSize:12, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:24 }}>Extracting terpene values, cannabinoids, batch data, and lab metadata</div>
                  <div style={{ display:"flex", justifyContent:"center", gap:6 }}>
                    {["β-Myrcene","β-Caryophyllene","(+)-Limonene","α-Pinene","Linalool"].map((t,i) => (
                      <div key={t} style={{ width:8, height:8, borderRadius:"50%", background:"#4a7c59", opacity:0.3, animation:"pulse " + (0.6+i*0.15) + "s ease-in-out infinite alternate" }}/>
                    ))}
                  </div>
                </div>
              )}

              {/* Error state */}
              {coaParseStatus === "error" && (
                <div className="fade" style={{ textAlign:"center", padding:"40px 20px" }}>
                  <div style={{ fontSize:40, marginBottom:12 }}>⚠️</div>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, color:"#1a2e1a", marginBottom:8 }}>Extraction issue</div>
                  <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:20, maxWidth:400, margin:"0 auto 20px" }}>{coaParseError}</div>
                  <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
                    <button className="btn-ghost" onClick={() => { setCoaParseStatus("idle"); setCoaUploadFile(null); setCoaUploadB64(null); }}>Try Another PDF</button>
                    <button className="btn-primary" onClick={() => setLpTab("submit")}>Use Manual Form →</button>
                  </div>
                </div>
              )}

              {/* Review state — show extracted data for confirmation */}
              {coaParseStatus === "review" && coaParseEdits && (
                <div className="fade">
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:8 }}>
                    <div>
                      <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:300, color:"#1a2e1a", marginBottom:2 }}>Review Extracted Data</h2>
                      <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Edit any field before confirming. All values in % w/w.</div>
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%",
                        background: coaParseEdits.confidence==="high"?"#22c55e":coaParseEdits.confidence==="medium"?"#f59e0b":"#ef4444" }}/>
                      <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#6b7280" }}>
                        {coaParseEdits.confidence||"medium"} confidence extraction
                      </span>
                    </div>
                  </div>

                  {/* Warnings */}
                  {(coaParseEdits.warnings||[]).length > 0 && (
                    <div style={{ background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:10, padding:"10px 14px", marginBottom:14 }}>
                      <div style={{ fontSize:10, color:"#92400e", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:4 }}>EXTRACTION WARNINGS</div>
                      {coaParseEdits.warnings.map((w,i) => (
                        <div key={i} style={{ fontSize:11, color:"#92400e", fontFamily:"'DM Mono',monospace" }}>· {w}</div>
                      ))}
                    </div>
                  )}

                  {/* Quality Flags */}
                  {(coaParseEdits.qualityFlags||[]).length > 0 && (
                    <div style={{ background:"#f0f9ff", border:"1px solid #7dd3fc", borderRadius:10, padding:"10px 14px", marginBottom:14 }}>
                      <div style={{ fontSize:10, color:"#0369a1", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:6 }}>QUALITY FLAGS DETECTED</div>
                      {coaParseEdits.qualityFlags.map((flag,i) => {
                        const flagInfo = {
                          elevated_caryophyllene_oxide: { label:"Elevated Caryophyllene Oxide", note:"May indicate oxidation / aging", icon:"📊" },
                          high_cbn: { label:"Elevated CBN", note:"CBN increases with THC degradation — age/heat/light indicator", icon:"⏳" },
                          low_moisture: { label:"Low Moisture (<8%)", note:"Very dry — increased harshness possible", icon:"🌵" },
                          high_moisture: { label:"High Moisture (>13%)", note:"Mould risk — check storage and cure", icon:"💧" },
                          terpene_sum_mismatch: { label:"Terpene Sum Mismatch", note:"Individual terpene sum does not match declared total", icon:"⚠" },
                        }[flag] || { label:flag, note:"", icon:"🔍" };
                        return (
                          <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:6 }}>
                            <span>{flagInfo.icon}</span>
                            <div>
                              <div style={{ fontSize:11, color:"#0369a1", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{flagInfo.label}</div>
                              {flagInfo.note && <div style={{ fontSize:10, color:"#0284c7", fontFamily:"'DM Mono',monospace" }}>{flagInfo.note}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Total THC auto-calc notice */}
                  {coaParseEdits.thca > 0 && coaParseEdits.total_thc === 0 && (
                    <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"10px 14px", marginBottom:14, fontSize:11, fontFamily:"'DM Mono',monospace", color:"#166534" }}>
                      💡 Total THC not found on COA — calculated from THCA × 0.877 + Δ9-THC = <strong>{((coaParseEdits.thca||0)*0.877 + (coaParseEdits.thc||0)).toFixed(1)}%</strong>
                    </div>
                  )}

                  {/* Identity fields */}
                  <div className="card" style={{ padding:18, marginBottom:12 }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.08em" }}>Product Identity</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      {[
                        ["Product Name","productName"],["Brand","brand"],["LP Name","lpName"],
                        ["Batch #","batchNumber"],["Lot #","lotNumber"],["Test Date","testDate"],
                        ["Lab","labName"],["Lab Method","labMethod"],
                      ].map(([label, key]) => (
                        <div key={key}>
                          <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{label}</div>
                          <input value={coaParseEdits[key]||""} onChange={e => setCoaParseEdits(v=>({...v,[key]:e.target.value}))}
                            className="inp" style={{ width:"100%", padding:"7px 10px", fontSize:12, boxSizing:"border-box" }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Cannabinoids */}
                  <div className="card" style={{ padding:18, marginBottom:12 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                      <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em" }}>Cannabinoids (%)</div>
                      <button onClick={()=>setCoaShowAllFields(v=>!v)} style={{ fontSize:9, color:"#4a7c59", fontFamily:"'DM Mono',monospace", background:"none", border:"none", cursor:"pointer" }}>
                        {coaShowAllFields ? "▲ Show key fields" : "▼ Show all fields"}
                      </button>
                    </div>
                    {/* Primary fields — always visible */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10, marginBottom: coaShowAllFields ? 10 : 0 }}>
                      {[["Total THC (Label)","total_thc"],["THC (Δ9)","thc"],["THCA","thca"],["CBD","cbd"],["Total Terpenes","totalTerpenes"],["Total Cannabinoids","totalCannabinoids"]].map(([label,key]) => (
                        <div key={key}>
                          <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{label}</div>
                          <input type="number" step="0.01" value={coaParseEdits[key]||""} onChange={e => setCoaParseEdits(v=>({...v,[key]:parseFloat(e.target.value)||0}))}
                            className="inp" style={{ width:"100%", padding:"7px 10px", fontSize:12, boxSizing:"border-box" }} />
                        </div>
                      ))}
                    </div>
                    {/* Secondary fields — progressive disclosure */}
                    {coaShowAllFields && (
                      <div>
                        <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, paddingTop:8, borderTop:"1px solid #f3f4f6" }}>MINOR CANNABINOIDS & QUALITY METRICS</div>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10 }}>
                          {[["CBG","cbg"],["CBGa","cbga"],["CBN","cbn"],["CBC","cbc"],["CBCa","cbca"],["CBDA","cbda"],["THCV","thcv"],["CBDV","cbdv"],["Δ8-THC","d8thc"],["Moisture %","moistureContent"],["Water Activity","waterActivity"]].map(([label,key]) => (
                            <div key={key}>
                              <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{label}</div>
                              <input type="number" step="0.01" value={coaParseEdits[key]||""} onChange={e => setCoaParseEdits(v=>({...v,[key]:parseFloat(e.target.value)||0}))}
                                className="inp" style={{ width:"100%", padding:"7px 10px", fontSize:12, boxSizing:"border-box" }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Terpenes */}
                  <div className="card" style={{ padding:18, marginBottom:12 }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.08em" }}>Terpene Profile (%)</div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10 }}>
                      {Object.entries(coaParseEdits.terpenes||{}).map(([terpene, val]) => {
                        const info = TERPENES[terpene];
                        return (
                          <div key={terpene}>
                            <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:3 }}>
                              {info && <div style={{ width:6,height:6,borderRadius:"50%",background:info.color,flexShrink:0 }}/>}
                              <div style={{ fontSize:9, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{terpene}</div>
                            </div>
                            <input type="number" step="0.001" value={val||""} onChange={e => setCoaParseEdits(v=>({...v,terpenes:{...v.terpenes,[terpene]:parseFloat(e.target.value)||0}}))}
                              className="inp" style={{ width:"100%", padding:"7px 10px", fontSize:12, boxSizing:"border-box",
                                borderColor: val > 0 ? "#86efac" : "#e8e4de",
                                background: val > 0 ? "#f0fdf4" : "#fff" }} />
                          </div>
                        );
                      })}
                    </div>
                    {/* Terpene total validation */}
                    {(() => {
                      const sum = Object.values(coaParseEdits.terpenes||{}).reduce((a,b)=>a+(b||0),0);
                      const declared = coaParseEdits.totalTerpenes||0;
                      const diff = Math.abs(sum - declared);
                      if (diff > 0.05 && declared > 0) return (
                        <div style={{ marginTop:10, padding:"8px 12px", background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:8, fontSize:11, fontFamily:"'DM Mono',monospace", color:"#92400e" }}>
                          ⚠ Sum of individual terpenes ({sum.toFixed(3)}%) differs from declared total ({declared}%) by {diff.toFixed(3)}% — verify values before confirming
                        </div>
                      );
                      return (
                        <div style={{ marginTop:10, padding:"8px 12px", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8, fontSize:11, fontFamily:"'DM Mono',monospace", color:"#166534" }}>
                          ✓ Terpene sum ({sum.toFixed(3)}%) matches declared total
                        </div>
                      );
                    })()}
                  </div>

                  {/* Cultivation */}
                  <div className="card" style={{ padding:18, marginBottom:16 }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.08em" }}>Cultivation & Genetics</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                      {[["Cultivation Method","cultivationMethod"],["Plant Type","plantType"]].map(([label,key]) => (
                        <div key={key}>
                          <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{label}</div>
                          <input value={coaParseEdits[key]||""} onChange={e => setCoaParseEdits(v=>({...v,[key]:e.target.value}))}
                            className="inp" style={{ width:"100%", padding:"7px 10px", fontSize:12, boxSizing:"border-box" }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Compliance notice */}
                  <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"12px 16px", marginBottom:16, fontSize:11, color:"#166534", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>
                    By confirming you attest this data originates from a Health Canada accredited testing facility and you are authorised to submit it. Data will be flagged as <strong>LP-submitted · AI-extracted · Pending human review</strong> until an iTerps team member verifies it against the original COA. This typically takes 1–2 business days.
                  </div>

                  <div style={{ display:"flex", gap:10 }}>
                    <button className="btn-ghost" onClick={() => { setCoaParseStatus("idle"); setCoaUploadFile(null); setCoaUploadB64(null); }}>← Start Over</button>
                    <button className="btn-primary" style={{ flex:1, padding:"12px 0", fontSize:13 }}
                      onClick={() => { setCoaParseStatus("confirmed"); logCOASubmission({ ...coaParseEdits, status:"confirmed", parseMethod:"ai_coa_parser" }); fireEmailTrigger("coa_confirmed", { lp: coaParseEdits.lpName, product: coaParseEdits.productName }); }}>
                      ✓ Confirm &amp; Submit for Verification
                    </button>
                  </div>
                </div>
              )}

              {/* Confirmed */}
              {coaParseStatus === "confirmed" && (
                <div className="fade" style={{ textAlign:"center", padding:"48px 20px" }}>
                  <div style={{ fontSize:48, marginBottom:16 }}>✅</div>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>
                    {coaParseEdits.productName || "Product"} submitted
                  </div>
                  <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>
                    Batch: {coaParseEdits.batchNumber||"—"} · Lab: {coaParseEdits.labName||"—"}
                  </div>
                  <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:24 }}>
                    Status: <span style={{ color:"#f59e0b" }}>LP-submitted · Pending human verification</span>
                  </div>

                  {/* Quick summary of what was captured */}
                  <div style={{ maxWidth:360, margin:"0 auto 24px", background:"#f7f5f0", borderRadius:12, padding:"16px 20px", textAlign:"left" }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:10, textTransform:"uppercase" }}>Terpene Summary</div>
                    {Object.entries(coaParseEdits.terpenes||{}).filter(([,v])=>v>0).sort((ea,eb)=>eb[1]-ea[1]).slice(0,5).map(([t,v]) => {
                      const info = TERPENES[t];
                      return (
                        <div key={t} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            {info && <div style={{ width:7,height:7,borderRadius:"50%",background:info.color }}/>}
                            <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                          </div>
                          <span style={{ fontSize:11, color:"#4a7c59", fontFamily:"'DM Mono',monospace" }}>{(v*10).toFixed(1)} mg/g</span>
                        </div>
                      );
                    })}
                  </div>

                  <button className="btn-primary" onClick={() => { setCoaParseStatus("idle"); setCoaUploadFile(null); setCoaUploadB64(null); setCoaParseResult(null); setCoaParseEdits({}); }}>
                    Submit Another COA →
                  </button>
                </div>
              )}
            </div>
          )}

          {lpTab === "analytics" && (
            <div className="fade">
              <h2 style={{fontFamily:"'Fraunces',serif",fontSize:22,fontWeight:300,color:"#1a2e1a",marginBottom:6}}>Product Analytics</h2>
              <p style={{fontSize:12,color:"#6b7280",fontFamily:"'DM Mono',monospace",marginBottom:20}}>How your products perform across the iTerps network — views, matches, and retailer coverage.</p>
              {myProducts.length === 0 ? (
                <div className="card" style={{padding:32,textAlign:"center"}}>
                  <div style={{fontSize:36,marginBottom:12}}>📈</div>
                  <p style={{fontSize:13,color:"#6b7280",fontFamily:"'DM Mono',monospace"}}>No products found for <strong>{activeLp}</strong>. Ensure your LP name matches the product database.</p>
                </div>
              ) : (
                <>
                  {/* KPI strip */}
                  <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
                    {[
                      {label:"Products in Network", value:myProducts.length, color:"#1a2e1a"},
                      {label:"Lab Verified", value:totalVerified, color:"#22c55e"},
                      {label:"Retailers Carrying", value:[...new Set(myProducts.map(p=>p.dispensary_id))].length, color:"#4a7c59"},
                      {label:"Avg THC", value:(myProducts.length ? (myProducts.reduce((s,p)=>s+(parseFloat(p.thc)||0),0)/myProducts.length).toFixed(1) : 0) + "%", color:"#f59e0b"},
                    ].map(kpi=>(
                      <div key={kpi.label} className="card" style={{padding:"14px 18px",flex:1,minWidth:120}}>
                        <div style={{fontSize:22,fontWeight:500,fontFamily:"'Fraunces',serif",color:kpi.color,marginBottom:4}}>{kpi.value}</div>
                        <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{kpi.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Terpene distribution */}
                  <div className="card" style={{padding:20,marginBottom:14}}>
                    <div style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.06em"}}>Terpene Signature — {activeLp}</div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {Object.entries(myProducts.reduce((acc,p)=>{
                        Object.entries(p.terpenes).forEach(([t,v])=>{ acc[t]=(acc[t]||0)+v; });
                        return acc;
                      },{})).sort((ea,eb)=>eb[1]-ea[1]).slice(0,8).map(([t,total])=>{
                        const avg = (total/myProducts.length).toFixed(2);
                        const pct = Math.min(100, (parseFloat(avg)/1.5)*100);
                        const color = TERPENES[t]?.color || "#4a7c59";
                        return (
                          <div key={t} style={{display:"flex",alignItems:"center",gap:10}}>
                            <div style={{width:130,fontSize:11,color:"#374151",fontFamily:"'DM Mono',monospace",flexShrink:0}}>{t}</div>
                            <div style={{flex:1,height:8,background:"#f3f4f6",borderRadius:4,overflow:"hidden"}}>
                              <div style={{height:"100%",width:(pct) + "%",background:color,borderRadius:4,transition:"width 0.4s"}}/>
                            </div>
                            <div style={{fontSize:10,color:color,fontFamily:"'DM Mono',monospace",width:50,textAlign:"right",flexShrink:0}}>{avg}% avg</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Retailer coverage */}
                  <div className="card" style={{padding:20,marginBottom:14}}>
                    <div style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.06em"}}>Retailer Coverage</div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {[...new Set(myProducts.map(p=>p.dispensary_id))].map(dId=>{
                        const disp = DB_DISPENSARIES.find(d=>d.id===dId);
                        const skus = myProducts.filter(p=>p.dispensary_id===dId);
                        return (
                          <div key={dId} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #f3f4f6"}}>
                            <div>
                              <div style={{fontSize:12,color:"#1a2e1a",fontWeight:500}}>{disp?.name||dId}</div>
                              <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{disp?.city} · {disp?.province}</div>
                            </div>
                            <div style={{fontSize:12,color:"#4a7c59",fontFamily:"'DM Mono',monospace",fontWeight:600}}>{skus.length} SKU{skus.length!==1?"s":""}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Product list with terpene data */}
                  <div className="card" style={{padding:0,overflow:"hidden"}}>
                    <div style={{padding:"14px 18px",borderBottom:"1px solid #f3f4f6",fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:"0.06em"}}>Your Products</div>
                    {myProducts.map((p,i)=>{
                      const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
                      const disp = DB_DISPENSARIES.find(d=>d.id===p.dispensary_id);
                      return (
                        <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 18px",borderBottom:i<myProducts.length-1?"1px solid #f9fafb":"none"}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,color:"#1a2e1a",fontWeight:500,marginBottom:2}}>{p.name}</div>
                            <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{p.category} · {disp?.name} · {p.thc}% THC</div>
                          </div>
                          <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                            {topT && <span style={{fontSize:10,background:(TERPENES[topT[0]]?.color||"#4a7c59") + "15",color:TERPENES[topT[0]]?.color||"#4a7c59",padding:"2px 8px",borderRadius:12,fontFamily:"'DM Mono',monospace"}}>{topT[0]}</span>}
                            <span className={"badge " + (p.terpene_source==="lab_tested"?"verified":p.terpene_source==="lp_provided"?"lp":"estimated")} style={{fontSize:9}}>
                              {p.terpene_source==="lab_tested"?"✓ Lab":p.terpene_source==="lp_provided"?"✓ LP":"~ Est."}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {lpTab === "index" && (
            <div className="fade">
              <BCDemandIndexSection />
            </div>
          )}

          {lpTab === "demo" && (
            <div className="fade">
              {lpDemoSent ? (
                <div style={{ textAlign:"center", padding:"48px 20px" }}>
                  <div style={{ fontSize:52, marginBottom:16 }}>✅</div>
                  <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:10 }}>Request received</h2>
                  <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.7, maxWidth:380, margin:"0 auto 24px" }}>
                    We&#39;ll be in touch within one business day to schedule a walkthrough tailored to your catalogue and retail network.
                  </p>
                  <button onClick={() => { setLpDemoSent(false); setLpDemoForm({ name:"", company:"", email:"", phone:"", skus:"", message:"" }); }}
                    style={{ padding:"10px 24px", borderRadius:8, border:"1px solid #e8e4de", background:"#fff", color:"#6b7280", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    Send another request
                  </button>
                </div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:32 }}>
                  <div>
                    <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>See iTerps for your catalogue</h2>
                    <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.7, marginBottom:20 }}>
                      We&#39;ll walk you through how your SKUs appear on the platform, show you the terpene gap analysis for your category, and outline the data partnership structure.
                    </p>
                    {[
                      ["🔬","Your COA data, visualised","See exactly how your terpene profiles are displayed to budtenders and consumers across the network"],
                      ["📊","Gap analysis for your category","Where is consumer demand going unmet? Which terpene profiles are underrepresented in your segment?"],
                      ["🤝","Partnership structure","No advertising. No pay-to-play placement. Data accuracy earns visibility."],
                      ["🌐","Retail network reach","How many dispensaries and consumers your verified data would reach from day one"],
                    ].map(([icon, title, desc]) => (
                      <div key={title} style={{ display:"flex", gap:12, marginBottom:16, alignItems:"flex-start" }}>
                        <span style={{ fontSize:20, flexShrink:0, marginTop:1 }}>{icon}</span>
                        <div>
                          <div style={{ fontSize:12, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{title}</div>
                          <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="card" style={{ padding:24 }}>
                      <div style={{ fontSize:11, color:"#4a7c59", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:16, textTransform:"uppercase", letterSpacing:"0.06em" }}>Request a Demo</div>
                      {[
                        ["name","Full Name","Your name",""],
                        ["company","Licensed Producer / Company","Company name",""],
                        ["email","Email *","you@company.com",""],
                        ["phone","Phone (optional)","",""],
                        ["skus","Approx. SKU Count","How many products in your catalogue?",""],
                      ].map(([k, label, ph]) => (
                        <div key={k} style={{ marginBottom:12 }}>
                          <label style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", display:"block", marginBottom:4 }}>{label}</label>
                          <input value={lpDemoForm[k]} onChange={e => setLpDemoForm(f=>({...f,[k]:e.target.value}))}
                            placeholder={ph}
                            style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", boxSizing:"border-box", outline:"none" }} />
                        </div>
                      ))}
                      <div style={{ marginBottom:16 }}>
                        <label style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", display:"block", marginBottom:4 }}>Anything specific you&#39;d like to cover?</label>
                        <textarea value={lpDemoForm.message} onChange={e => setLpDemoForm(f=>({...f,message:e.target.value}))}
                          rows={3} placeholder="e.g. interested in gap analysis for our indica-dominant line…"
                          style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", boxSizing:"border-box", outline:"none", resize:"vertical" }} />
                      </div>
                      <button onClick={() => { if (lpDemoForm.email) setLpDemoSent(true); }}
                        disabled={!lpDemoForm.email}
                        className="btn-primary" style={{ width:"100%", padding:"11px 0", fontSize:12, opacity: lpDemoForm.email ? 1 : 0.5 }}>
                        Request Demo →
                      </button>
                      <p style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:10, textAlign:"center", lineHeight:1.5 }}>
                        No sales pressure. We&#39;ll only reach out to schedule the walkthrough you requested.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const RetailerPage = () => {
    // Default to logged-in store if not manually overridden
    const effectiveDisp = retSelDisp || activeDispensary;
    const selDisp=effectiveDisp, setSelDisp=setRetSelDisp;
    const retailTab=retTab, setRetailTab=setRetTab;

    const dp = ALL_PRODUCTS.filter(p => p.dispensary_id === selDisp);
    const tf = {};
    dp.forEach(p => Object.keys(p.terpenes).forEach(t => { tf[t] = (tf[t]||0)+1; }));
    const topT = Object.entries(tf).sort(([,a],[,b]) => b-a);

    const allProds = ALL_PRODUCTS;
    const networkTf = {};
    allProds.forEach(p => Object.keys(p.terpenes).forEach(t => { networkTf[t]=(networkTf[t]||0)+1; }));
    const networkTopTerps = Object.entries(networkTf).sort(([,a],[,b]) => b-a).slice(0,6);

    const byType = {};
    allProds.forEach(p => { if(!byType[p.plant_type]) byType[p.plant_type]={sum:0,n:0}; byType[p.plant_type].sum+=p.thc; byType[p.plant_type].n++; });
    const avgThcByType = Object.entries(byType).map(([type,{sum,n}]) => ({ type, avg:(sum/n).toFixed(1) })).sort((a,b) => b.avg-a.avg);

    const brandCount = {};
    allProds.forEach(p => { brandCount[p.brand]=(brandCount[p.brand]||0)+1; });

    const cultivationCount = {};
    allProds.forEach(p => { const m=p.cultivation?.method||"Unknown"; cultivationCount[m]=(cultivationCount[m]||0)+1; });

    const brandTerps = {};
    allProds.filter(p => p.total_terpenes).forEach(p => { if(!brandTerps[p.brand]) brandTerps[p.brand]={sum:0,n:0}; brandTerps[p.brand].sum+=p.total_terpenes; brandTerps[p.brand].n++; });
    const topTerpBrands = Object.entries(brandTerps).map(([b,{sum,n}]) => ({ brand:b, avg:sum/n })).sort((a,b) => b.avg-a.avg).slice(0,8);

    const dispAvgThc = dp.length ? (dp.reduce((s,p) => s+p.thc,0)/dp.length).toFixed(1) : 0;
    const dpWithTerps = dp.filter(p => p.total_terpenes);
    const dispAvgTerp = dpWithTerps.length ? (dpWithTerps.reduce((s,p) => s+p.total_terpenes,0)/dpWithTerps.length).toFixed(2) : null;
    const dispTopTerp = topT[0]?.[0];

    return (
      <div className="fade">
        <div style={{ maxWidth:980, margin:"0 auto", padding:"28px 20px" }}>
          <div style={{ marginBottom:20 }}>
            <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Retailer Dashboard</h1>
            <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Staff education tool. Factual inventory chemical composition. Non-promotional.</p>
          </div>

          <div style={{ display:"flex", gap:4, marginBottom:24, borderBottom:"2px solid #f3f4f6" }}>
            {[["inventory","📋 Inventory"],["reorder","⚠ Batch Drift"],["analytics","📊 Analytics"],["import","📥 Import Inventory"]].map(([t,l]) => (
              <button key={t} onClick={() => setRetailTab(t)}
                style={{ padding:"9px 18px", background:"none", border:"none", borderBottom:retailTab===t?"2px solid #4a7c59":"2px solid transparent", marginBottom:-2, cursor:"pointer", fontSize:12, fontFamily:"'DM Mono',monospace", color:retailTab===t?"#1a2e1a":"#9ca3af", fontWeight:retailTab===t?600:400 }}>
                {l}
              </button>
            ))}
          </div>

          {retailTab === "analytics" && (
            <div className="fade">
              {/* Dispensary selector for store comparison */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
                <span style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",flexShrink:0}}>Compare store:</span>
                <select value={selDisp} onChange={e=>setSelDisp(e.target.value)} className="sel" style={{flex:"1 1 200px",maxWidth:280}}>
                  <option value="">— Select a dispensary —</option>
                  {DB_DISPENSARIES.map(d=><option key={d.id} value={d.id}>{d.name} · {d.city}</option>)}
                </select>
              </div>

              {/* KPI comparison row — store vs network */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:20 }}>
                {[
                  { label:"Network SKUs",  value:allProds.length, store: dp.length, color:"#1a2e1a" },
                  { label:"Avg THC %",     value:((allProds.reduce((s,p)=>s+p.thc,0)/allProds.length).toFixed(1)) + "%", store:dp.length?(dispAvgThc) + "%":"—", color:"#22c55e" },
                  { label:"Avg Terpenes",  value:((allProds.filter(p=>p.total_terpenes).reduce((s,p)=>s+p.total_terpenes,0)/allProds.filter(p=>p.total_terpenes).length).toFixed(2)) + "%", store:dispAvgTerp?(dispAvgTerp) + "%":"—", color:"#4a7c59" },
                  { label:"Lab-Verified",  value:(Math.round(allProds.filter(p=>p.terpene_source==="lab_tested").length/allProds.length*100)) + "%", store:dp.length?(Math.round(dp.filter(p=>p.terpene_source==="lab_tested").length/dp.length*100)) + "%":"—", color:"#3b82f6" },
                  { label:"Brands",        value:Object.keys(brandCount).length, store:[...new Set(dp.map(p=>p.brand))].length||"—", color:"#8b5cf6" },
                  { label:"Unique Terp.",  value:[...new Set(allProds.flatMap(p=>Object.keys(p.terpenes)))].length, store:[...new Set(dp.flatMap(p=>Object.keys(p.terpenes)))].length||"—", color:"#f59e0b" },
                ].map(kpi => (
                  <div key={kpi.label} className="card" style={{ padding:"16px 18px" }}>
                    <div style={{fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>{kpi.label}</div>
                    <div style={{display:"flex",alignItems:"flex-end",gap:8,flexWrap:"wrap"}}>
                      <div>
                        <div style={{ fontSize:20, fontWeight:500, fontFamily:"'Fraunces',serif", color:kpi.color }}>{kpi.value}</div>
                        <div style={{fontSize:8,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>Network</div>
                      </div>
                      {selDisp && kpi.store !== "—" && (
                        <>
                          <div style={{fontSize:12,color:"#e8e4de",marginBottom:4}}>vs</div>
                          <div>
                            <div style={{fontSize:18,fontWeight:500,fontFamily:"'Fraunces',serif",color:kpi.color}}>{kpi.store}</div>
                            <div style={{fontSize:8,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>This store</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Terpene profile comparison: this store vs network */}
              <div style={{ display:"grid", gridTemplateColumns: selDisp ? "1fr 1fr" : "1fr", gap:14, marginBottom:14 }}>
                <div className="card" style={{ padding:20 }}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a", marginBottom:14 }}>Top Terpenes — Network</div>
                  {networkTopTerps.map(([t,count]) => {
                    const ti = TERPENES[t];
                    const max = networkTopTerps[0][1];
                    return (
                      <div key={t} style={{ marginBottom:10 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <span style={{ width:8,height:8,borderRadius:"50%",background:ti?.color||"#6b7280",display:"inline-block" }} />
                            <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#374151" }}>{t}</span>
                          </div>
                          <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#6b7280" }}>{count} SKUs</span>
                        </div>
                        <div style={{ height:6, background:"#f3f4f6", borderRadius:3 }}>
                          <div style={{ height:6, background:ti?.color||"#4a7c59", borderRadius:3, width:((count/max)*100) + "%" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* This store terpene profile — only shown when a store is selected */}
                {selDisp && dp.length > 0 && (() => {
                  const storeTf = {};
                  dp.forEach(p => Object.keys(p.terpenes).forEach(t => { storeTf[t]=(storeTf[t]||0)+1; }));
                  const storeTopTerps = Object.entries(storeTf).sort((ea,eb)=>eb[1]-ea[1]).slice(0,6);
                  const storeMax = storeTopTerps[0]?.[1]||1;
                  const networkMap = Object.fromEntries(networkTopTerps);
                  const netMax = networkTopTerps[0]?.[1]||1;
                  return (
                    <div className="card" style={{ padding:20 }}>
                      <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a", marginBottom:4 }}>This Store — {DB_DISPENSARIES.find(d=>d.id===selDisp)?.name}</div>
                      <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14}}>{dp.length} SKUs · dark bar = this store · light bar = network avg</div>
                      {storeTopTerps.map(([t,count]) => {
                        const ti = TERPENES[t];
                        const networkCount = networkMap[t]||0;
                        const isGap = (networkCount/netMax) > (count/storeMax) * 1.4;
                        return (
                          <div key={t} style={{ marginBottom:10 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                <span style={{ width:8,height:8,borderRadius:"50%",background:ti?.color||"#6b7280",display:"inline-block" }} />
                                <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#374151" }}>{t}</span>
                                {isGap && <span style={{fontSize:8,color:"#ea580c",background:"#fff7ed",border:"1px solid #fed7aa",padding:"1px 5px",borderRadius:10,fontFamily:"'DM Mono',monospace"}}>gap</span>}
                              </div>
                              <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#6b7280" }}>{count} / {networkCount} net</span>
                            </div>
                            <div style={{ position:"relative", height:8, background:"#f3f4f6", borderRadius:3 }}>
                              <div style={{ position:"absolute", height:8, background:(ti?.color||"#6b7280") + "40", borderRadius:3, width:((networkCount/netMax)*100) + "%" }} />
                              <div style={{ position:"absolute", height:8, background:ti?.color||"#4a7c59", borderRadius:3, width:((count/storeMax)*100) + "%" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                <div className="card" style={{ padding:20 }}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a", marginBottom:14 }}>Avg Terpenes by Brand</div>
                  {topTerpBrands.map(({brand, avg}) => {
                    const max = topTerpBrands[0].avg;
                    return (
                      <div key={brand} style={{ marginBottom:10 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                          <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#374151" }}>{brand.split(' ')[0]}</span>
                          <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#4a7c59", fontWeight:600 }}>{avg.toFixed(2)}%</span>
                        </div>
                        <div style={{ height:6, background:"#f3f4f6", borderRadius:3 }}>
                          <div style={{ height:6, background:"#4a7c59", borderRadius:3, width:((avg/max)*100) + "%", opacity:0.8 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="card" style={{ padding:20 }}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a", marginBottom:14 }}>Avg THC by Plant Type</div>
                  {avgThcByType.map(({type, avg}) => (
                    <div key={type} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid #f3f4f6" }}>
                      <span style={{ fontSize:12, fontFamily:"'DM Mono',monospace", color:"#374151" }}>{type}</span>
                      <span style={{ fontSize:16, fontWeight:600, fontFamily:"'Fraunces',serif", color:"#22c55e" }}>{avg}%</span>
                    </div>
                  ))}
                </div>

                <div className="card" style={{ padding:20 }}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a", marginBottom:14 }}>Cultivation Mix</div>
                  {Object.entries(cultivationCount).sort(([,a],[,b]) => b-a).map(([method, count]) => {
                    const pct = Math.round((count/allProds.length)*100);
                    const colors = { "Indoor":"#4a7c59","Greenhouse":"#22c55e","Outdoor":"#86efac" };
                    return (
                      <div key={method} style={{ marginBottom:10 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                          <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#374151" }}>{method}</span>
                          <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#6b7280" }}>{count} · {pct}%</span>
                        </div>
                        <div style={{ height:6, background:"#f3f4f6", borderRadius:3 }}>
                          <div style={{ height:6, background:colors[method]||"#9ca3af", borderRadius:3, width:(pct) + "%" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Store vs Network Comparison */}
              {(() => {
                const dispInfo = DB_DISPENSARIES.find(d => d.id===selDisp);
                const networkAllProds = ALL_PRODUCTS;
                const networkWithT = networkAllProds.filter(p=>p.total_terpenes);
                const networkAvgThc = (networkAllProds.reduce((s,p)=>s+p.thc,0)/networkAllProds.length).toFixed(1);
                const networkAvgTerp = networkWithT.length ? (networkWithT.reduce((s,p)=>s+p.total_terpenes,0)/networkWithT.length).toFixed(2) : null;
                const networkAvgSkus = (networkAllProds.length / DB_DISPENSARIES.length).toFixed(0);
                const networkLabPct = Math.round(networkAllProds.filter(p=>p.terpene_source==="lab_tested").length/networkAllProds.length*100);
                const storeLabPct = dp.length ? Math.round(dp.filter(p=>p.terpene_source==="lab_tested").length/dp.length*100) : 0;
                const storeInTerps = dp.filter(p=>p.terpenes);
                const storeTerpCount = {}; storeInTerps.forEach(p=>Object.keys(p.terpenes).forEach(t=>{ storeTerpCount[t]=(storeTerpCount[t]||0)+1; }));
                const storeTopTerps = Object.entries(storeTerpCount).sort((ea,eb)=>eb[1]-ea[1]).slice(0,5);
                const netTerpCount = {}; networkAllProds.forEach(p=>Object.keys(p.terpenes).forEach(t=>{ netTerpCount[t]=(netTerpCount[t]||0)+1; }));
                const netTopTerps = Object.entries(netTerpCount).sort((ea,eb)=>eb[1]-ea[1]).slice(0,5);

                const metrics = [
                  { label:"SKUs", store:dp.length, network:Number(networkAvgSkus), unit:"", higherIsBetter:true },
                  { label:"Avg THC", store:Number(dispAvgThc), network:Number(networkAvgThc), unit:"%", higherIsBetter:false },
                  { label:"Avg Terpenes", store:Number(dispAvgTerp||0), network:Number(networkAvgTerp||0), unit:"%", higherIsBetter:true },
                  { label:"Lab-Verified", store:storeLabPct, network:networkLabPct, unit:"%", higherIsBetter:true },
                ];

                return (
                  <div className="card" style={{ padding:20 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8, marginBottom:16 }}>
                      <div>
                        <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a", marginBottom:2 }}>Your Store vs BC Network</div>
                        <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{dispInfo?.name} · {dispInfo?.city}</div>
                      </div>
                      <select className="sel" value={selDisp} onChange={e=>setSelDisp(e.target.value)} style={{ fontSize:11 }}>
                        {DB_DISPENSARIES.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>

                    {/* KPI comparison table */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 80px", gap:0, marginBottom:16, border:"1px solid #f3f4f6", borderRadius:10, overflow:"hidden" }}>
                      <div style={{ padding:"8px 12px", background:"#f9fafb", fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em" }}>Metric</div>
                      <div style={{ padding:"8px 12px", background:"#f0fdf4", fontSize:9, color:"#166534", fontFamily:"'DM Mono',monospace", textTransform:"uppercase" }}>This Store</div>
                      <div style={{ padding:"8px 12px", background:"#f9fafb", fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase" }}>BC Network Avg</div>
                      <div style={{ padding:"8px 12px", background:"#f9fafb", fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase" }}>vs Avg</div>
                      {metrics.map(({label,store,network,unit,higherIsBetter}) => {
                        const diff = store - network;
                        const ahead = higherIsBetter ? diff > 0 : diff < 0;
                        const behind = higherIsBetter ? diff < -0.5 : diff > 0.5;
                        const diffColor = ahead ? "#166534" : behind ? "#991b1b" : "#9ca3af";
                        return [
                          <div key={(label) + "-label"} style={{ padding:"10px 12px", borderTop:"1px solid #f3f4f6", fontSize:12, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{label}</div>,
                          <div key={(label) + "-store"} style={{ padding:"10px 12px", borderTop:"1px solid #f3f4f6", background:"#f0fdf4", fontSize:13, fontWeight:600, fontFamily:"'Fraunces',serif", color:"#1a2e1a" }}>{store}{unit}</div>,
                          <div key={(label) + "-net"} style={{ padding:"10px 12px", borderTop:"1px solid #f3f4f6", fontSize:13, fontFamily:"'Fraunces',serif", color:"#6b7280" }}>{network}{unit}</div>,
                          <div key={(label) + "-diff"} style={{ padding:"10px 12px", borderTop:"1px solid #f3f4f6", fontSize:11, fontFamily:"'DM Mono',monospace", color:diffColor, fontWeight:600 }}>
                            {Math.abs(diff) < 0.05 ? "—" : (diff>0?"+":"") + (diff.toFixed(label==="SKUs"?0:2)) + (unit)}
                          </div>
                        ];
                      })}
                    </div>

                    {/* Terpene profile comparison */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                      <div>
                        <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", marginBottom:8, letterSpacing:"0.08em" }}>This Store — Top Terpenes</div>
                        {storeTopTerps.map(([t,count]) => {
                          const ti = TERPENES[t]; const max = storeTopTerps[0][1];
                          return (
                            <div key={t} style={{ marginBottom:7 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                                  <span style={{ width:6,height:6,borderRadius:"50%",background:ti?.color||"#4a7c59",display:"inline-block" }}/>
                                  <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#374151" }}>{t}</span>
                                </div>
                                <span style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{count}</span>
                              </div>
                              <div style={{ height:5, background:"#f3f4f6", borderRadius:3 }}>
                                <div style={{ height:5, background:ti?.color||"#4a7c59", borderRadius:3, width:((count/max)*100) + "%" }}/>
                              </div>
                            </div>
                          );
                        })}
                        {storeTopTerps.length === 0 && <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>No inventory selected</div>}
                      </div>
                      <div>
                        <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", marginBottom:8, letterSpacing:"0.08em" }}>BC Network — Top Terpenes</div>
                        {netTopTerps.map(([t,count]) => {
                          const ti = TERPENES[t]; const max = netTopTerps[0][1];
                          const storeHas = storeTerpCount[t];
                          return (
                            <div key={t} style={{ marginBottom:7 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                                  <span style={{ width:6,height:6,borderRadius:"50%",background:ti?.color||"#4a7c59",display:"inline-block" }}/>
                                  <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#374151" }}>{t}</span>
                                </div>
                                {!storeHas && <span style={{ fontSize:8, color:"#dc2626", fontFamily:"'DM Mono',monospace", background:"#fef2f2", border:"1px solid #fca5a5", padding:"0px 5px", borderRadius:4 }}>gap</span>}
                              </div>
                              <div style={{ height:5, background:"#f3f4f6", borderRadius:3 }}>
                                <div style={{ height:5, background:ti?.color||"#9ca3af", borderRadius:3, width:((count/max)*100) + "%", opacity:0.5 }}/>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {retailTab === "inventory" && (
            <div>
              {/* Controls row */}
              <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
                <select className="sel" value={selDisp} onChange={e => setSelDisp(e.target.value)} style={{ flex:"1 1 200px", maxWidth:280 }}>
                  {DB_DISPENSARIES.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <input className="inp" placeholder="Search product or brand…" value={retSearch} onChange={e=>setRetSearch(e.target.value)}
                  style={{ flex:"1 1 180px", maxWidth:260 }} />
                <div style={{ display:"flex", gap:4 }}>
                  {[["all","All"],["in","✓ In"],["out","✗ Out"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setRetStockFilter(v)}
                      style={{ padding:"6px 10px", borderRadius:8, border:"1px solid " + (retStockFilter===v?"#4a7c59":"#e8e4de"),
                        background:retStockFilter===v?"#f0fdf4":"transparent", color:retStockFilter===v?"#15803d":"#9ca3af",
                        fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer", whiteSpace:"nowrap" }}>{l}</button>
                  ))}
                </div>
              </div>

              <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:20 }}>
                {[
                  { label:"Avg THC", value:(dispAvgThc) + "%", color:"#22c55e" },
                  { label:"Top Terpene", value:dispTopTerp||"—", color:TERPENES[dispTopTerp]?.color||"#4a7c59" },
                  { label:"SKUs", value:dp.length, color:"#1a2e1a" },
                  { label:"In Stock", value:dp.filter(p=>(stockOverrides[p.id]!==undefined?stockOverrides[p.id]:p.in_stock)).length, color:"#22c55e" },
                ].map(s => (
                  <div key={s.label} className="card" style={{ padding:"12px 16px", flex:1, minWidth:100 }}>
                    <div style={{ fontSize:18, fontWeight:500, fontFamily:"'Fraunces',serif", color:s.color }}>{s.value}</div>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="card" style={{ padding:0, overflow:"hidden" }}>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                    <thead>
                      <tr>{["Product","THC","CBD","Total Terp%","Primary Terpene","Method","Data","Stock"].map(h => (
                        <th key={h} style={{ textAlign:"left", padding:"10px 12px", borderBottom:"2px solid #f3f4f6", color:"#9ca3af", fontSize:9, letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {dp.filter(p => {
                        const stock = stockOverrides[p.id] !== undefined ? stockOverrides[p.id] : p.in_stock;
                        const s = retSearch.toLowerCase();
                        if (retStockFilter==="in" && !stock) return false;
                        if (retStockFilter==="out" && stock) return false;
                        if (s && !p.name.toLowerCase().includes(s) && !(p.brand||"").toLowerCase().includes(s)) return false;
                        return true;
                      }).map(p => {
                        const topTerp = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
                        const ti = TERPENES[topTerp?.[0]];
                        const inStock = stockOverrides[p.id] !== undefined ? stockOverrides[p.id] : p.in_stock;
                        return (
                          <tr key={p.id} style={{ borderBottom:"1px solid #f9fafb" }}>
                            <td style={{ padding:"10px 12px", cursor:"pointer" }}
                              onClick={() => { viewProduct(p) }}>
                              <div style={{ fontWeight:500, color:"#1a2e1a" }}>{p.name}</div>
                              <div style={{ fontSize:9, color:"#9ca3af" }}>{p.brand}</div>
                            </td>
                            <td style={{ padding:"10px 12px", color:"#22c55e", fontWeight:600 }}>{p.thc}%</td>
                            <td style={{ padding:"10px 12px", color:"#3b82f6" }}>{p.cbd}%</td>
                            <td style={{ padding:"10px 12px", color:"#4a7c59", fontWeight:600 }}>{p.total_terpenes ? (p.total_terpenes.toFixed(2)) + "%" : "—"}</td>
                            <td style={{ padding:"10px 12px" }}>
                              {topTerp && (
                                <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                                  <span style={{ width:7,height:7,borderRadius:"50%",background:ti?.color||"#6b7280",display:"inline-block" }} />
                                  {topTerp[0]}
                                </span>
                              )}
                            </td>
                            <td style={{ padding:"10px 12px", color:"#6b7280" }}>{p.cultivation?.method||"—"}</td>
                            <td style={{ padding:"10px 12px" }}>
                              <span className={"badge " + (p.terpene_source==="lab_tested"?"verified":p.terpene_source==="lp_provided"?"lp":"estimated")} style={{ fontSize:9 }}>
                                {p.terpene_source==="lab_tested"?"✓ Lab":p.terpene_source==="lp_provided"?"✓ LP":"~ Est."}
                              </span>
                            </td>
                            <td style={{ padding:"10px 12px" }}>
                              <button
                                onClick={e=>{ e.stopPropagation();
                                  const next = { ...stockOverrides, [p.id]: !inStock };
                                  setStockOverrides(next);
                                  try { localStorage.setItem("iterps_stock_overrides", JSON.stringify(next)); } catch (_e) {}
                                  // Sync to Supabase
                                  const dispObj = DB_DISPENSARIES.find(d => d.id === activeDispensary || d.name === activeDispensary);
                                  if (dispObj) db.setStockOverride(dispObj.id, p.id, !inStock).catch(() => {});
                                  showToast(inStock ? (p.name) + " marked out of stock" : (p.name) + " back in stock", inStock?"warning":"success");
                                }}
                                style={{ padding:"4px 10px", borderRadius:6, border:"1px solid " + (inStock?"#86efac":"#fca5a5"),
                                  background:inStock?"#f0fdf4":"#fef2f2", color:inStock?"#15803d":"#991b1b",
                                  fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer", whiteSpace:"nowrap", fontWeight:600 }}>
                                {inStock ? "✓ In" : "✗ Out"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {retailTab === "import" && (
            <div className="fade">
              {csvImported ? (
                <div style={{ textAlign:"center", padding:"40px 20px" }}>
                  <div style={{ fontSize:52, marginBottom:12 }}>✅</div>
                  <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>Inventory imported</h2>
                  <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.7, maxWidth:380, margin:"0 auto 20px" }}>
                    {csvPreviewRows.length} products loaded. Switch to Inventory to browse your menu.
                  </p>
                  <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
                    <button onClick={() => setRetailTab("inventory")} className="btn-primary" style={{ fontSize:12, padding:"10px 22px" }}>View Inventory →</button>
                    <button onClick={() => { setCsvImported(false); setCsvPreviewRows([]); setCsvUploadError(""); setCsvUploadData(null); }}
                      style={{ padding:"10px 18px", borderRadius:8, border:"1px solid #e8e4de", background:"#fff", color:"#6b7280", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                      Import another file
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:28 }}>
                  {/* Left — instructions */}
                  <div>
                    <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>Import your menu</h2>
                    <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.7, marginBottom:20 }}>
                      Upload a CSV from your POS or inventory system. iTerps will parse your product names, THC values, and any available terpene data, then layer in its compound database to fill gaps.
                    </p>
                    <div style={{ marginBottom:20 }}>
                      <div style={{ fontSize:10, color:"#4a7c59", fontFamily:"'DM Mono',monospace", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10 }}>Expected CSV columns</div>
                      {[
                        ["name","Product Name","Required"],
                        ["brand","Brand / LP","Required"],
                        ["thc","THC %","Required"],
                        ["cbd","CBD %","Optional"],
                        ["category","Category (e.g. Dried Flower)","Optional"],
                        ["total_terpenes","Total Terpenes %","Optional"],
                        ["myrcene","β-Myrcene %","Optional"],
                        ["caryophyllene","β-Caryophyllene %","Optional"],
                        ["limonene","(+)-Limonene %","Optional"],
                        ["price","Price ($)","Optional"],
                        ["sku","SKU / Barcode","Optional"],
                      ].map(([col, desc, req]) => (
                        <div key={col} style={{ display:"flex", alignItems:"baseline", gap:10, padding:"5px 0", borderBottom:"1px solid #f3f4f6" }}>
                          <code style={{ fontSize:11, color:"#1a2e1a", fontFamily:"'DM Mono',monospace", minWidth:140 }}>{col}</code>
                          <span style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", flex:1 }}>{desc}</span>
                          <span style={{ fontSize:9, color: req==="Required"?"#dc2626":"#9ca3af", fontFamily:"'DM Mono',monospace", fontWeight:600, flexShrink:0 }}>{req}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ background:"#fefce8", border:"1px solid #fde68a", borderRadius:10, padding:"12px 14px" }}>
                      <div style={{ fontSize:11, color:"#92400e", fontFamily:"'DM Mono',monospace", lineHeight:1.6 }}>
                        💡 Most POS systems (Cova, Greenline, Dutchie) can export a product CSV from the inventory or reporting section. Column names do not need to match exactly — iTerps will attempt to auto-map common variations.
                      </div>
                    </div>
                  </div>

                  {/* Right — upload zone */}
                  <div>
                    <div className="card" style={{ padding:24 }}>
                      <div style={{ fontSize:11, color:"#4a7c59", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:16, textTransform:"uppercase", letterSpacing:"0.06em" }}>Upload CSV</div>

                      {/* Drop zone */}
                      <label style={{ display:"block", border:"2px dashed #e8e4de", borderRadius:12, padding:"32px 20px", textAlign:"center", cursor:"pointer",
                        background: csvUploadData ? "#f0fdf4" : "#fafaf7", transition:"all 0.2s" }}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                          e.preventDefault();
                          const file = e.dataTransfer.files[0];
                          if (!file) return;
                          if (!file.name.endsWith('.csv')) { setCsvUploadError("Please upload a .csv file"); return; }
                          const reader = new FileReader();
                          reader.onload = ev => {
                            const text = ev.target.result;
                            const lines = text.trim().split('\n');
                            if (lines.length < 2) { setCsvUploadError("CSV appears empty"); return; }
                            const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[\x27\x22]/g,''));
                            const rows = lines.slice(1).map(l => {
                              const vals = l.split(',').map(v => v.trim().replace(/[\x27\x22]/g,''));
                              const row = {};
                              headers.forEach((h, i) => { row[h] = vals[i] || ""; });
                              return row;
                            }).filter(r => r[headers[0]]);
                            setCsvUploadData({ headers, rows, filename: file.name });
                            setCsvPreviewRows(rows.slice(0, 5));
                            setCsvUploadError("");
                          };
                          reader.readAsText(file);
                        }}>
                        <input type="file" accept=".csv" style={{ display:"none" }} onChange={e => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = ev => {
                            const text = ev.target.result;
                            const lines = text.trim().split('\n');
                            if (lines.length < 2) { setCsvUploadError("CSV appears empty"); return; }
                            const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[\x27\x22]/g,''));
                            const rows = lines.slice(1).map(l => {
                              const vals = l.split(',').map(v => v.trim().replace(/[\x27\x22]/g,''));
                              const row = {};
                              headers.forEach((h, i) => { row[h] = vals[i] || ""; });
                              return row;
                            }).filter(r => r[headers[0]]);
                            setCsvUploadData({ headers, rows, filename: file.name });
                            setCsvPreviewRows(rows.slice(0, 5));
                            setCsvUploadError("");
                          };
                          reader.readAsText(file);
                        }} />
                        {csvUploadData ? (
                          <>
                            <div style={{ fontSize:28, marginBottom:8 }}>📄</div>
                            <div style={{ fontSize:13, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{csvUploadData.filename}</div>
                            <div style={{ fontSize:11, color:"#4a7c59", fontFamily:"'DM Mono',monospace" }}>{csvUploadData.rows.length} products detected</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize:32, marginBottom:10 }}>📥</div>
                            <div style={{ fontSize:13, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>Drop your CSV here</div>
                            <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>or click to browse</div>
                          </>
                        )}
                      </label>

                      {csvUploadError && (
                        <div style={{ marginTop:10, padding:"8px 12px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, fontSize:11, color:"#dc2626", fontFamily:"'DM Mono',monospace" }}>
                          {csvUploadError}
                        </div>
                      )}

                      {/* Preview table */}
                      {csvPreviewRows.length > 0 && (
                        <div style={{ marginTop:16 }}>
                          <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>Preview (first 5 rows)</div>
                          <div style={{ overflowX:"auto", borderRadius:8, border:"1px solid #e8e4de" }}>
                            <table style={{ fontSize:10, fontFamily:"'DM Mono',monospace", borderCollapse:"collapse", width:"100%" }}>
                              <thead>
                                <tr>{csvUploadData.headers.slice(0,6).map(h => (
                                  <th key={h} style={{ padding:"6px 10px", background:"#f7f5f0", borderBottom:"1px solid #e8e4de", textAlign:"left", color:"#6b7280", whiteSpace:"nowrap" }}>{h}</th>
                                ))}</tr>
                              </thead>
                              <tbody>
                                {csvPreviewRows.map((row, i) => (
                                  <tr key={i} style={{ borderBottom:"1px solid #f3f4f6" }}>
                                    {csvUploadData.headers.slice(0,6).map(h => (
                                      <td key={h} style={{ padding:"6px 10px", color:"#374151", maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row[h]||"—"}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {csvUploadData && (
                        <button onClick={() => {
                          const parsed = parseCSVToProducts(csvUploadData.rows, retSelDisp);
                          setImportedProducts(prev => {
                            // Remove any previous imports for this dispensary, add new ones
                            const kept = prev.filter(p => p.dispensary_id !== retSelDisp);
                            return [...kept, ...parsed];
                          });
                          setCsvImported(true);
                          setCsvPreviewRows(parsed.slice(0, 5));
                          showToast((parsed.length) + " products imported successfully", "success");
                        }} className="btn-primary" style={{ width:"100%", padding:"11px 0", fontSize:12, marginTop:16 }}>
                          Import {csvUploadData.rows.length} Products →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {retailTab === "reorder" && (
            <div className="fade">
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:"0.08em",display:"block",marginBottom:5 }}>Filter by Dispensary</label>
                <select value={reorderDisp} onChange={e=>setReorderDisp(e.target.value)}
                  style={{ padding:"8px 12px",border:"1px solid #e8e4de",borderRadius:8,fontSize:12,fontFamily:"'DM Mono',monospace",background:"#fff",outline:"none",cursor:"pointer",minWidth:220 }}>
                  <option value="all">All Dispensaries</option>
                  {DB_DISPENSARIES.map(d=><option key={d.id} value={d.id}>{d.name} · {d.city}</option>)}
                </select>
              </div>
              <ReorderIntelSection dispId={reorderDisp} />
            </div>
          )}
        </div>
      </div>
    );
  };
  const CONSUMER_QUESTIONS = [
    // Q1 — Location first (city/dispensary — most important for product availability)
    { id:"location", question:"Which store are you shopping at?", emoji:"📍", subtitle:"Only show products available at your location", locationSelect:true },
    // Q2 — Aroma preference
    { id:"vibe", question:"What kind of aroma are you drawn to?", emoji:"👃",
      subtitle:"We use your aroma preference to match terpene profiles",
      options:[
        { label:"Earthy & musky",        value:"relax",   feeling:"relax",  aromas:["Earthy","Woody","Hash","Sandalwood","Spice"],           desc:"Deep, damp earth — myrcene and caryophyllene-heavy" },
        { label:"Citrusy & bright",      value:"energy",  feeling:"energy", aromas:["Citrus","Lemon","Orange","Tropical","Mango","Lime"],     desc:"Zesty lemons, tropical fruit — limonene and terpinolene forward" },
        { label:"Piney & herbal",        value:"focus",   feeling:"focus",  aromas:["Pine","Herbal","Fresh","Cedar","Mint"],                  desc:"Crisp pine and fresh herbs — α-pinene forward" },
        { label:"Gassy & pungent",       value:"body",    feeling:"body",   aromas:["Gas","Diesel","Fuel","Skunk","Pungent","Chemical"],      desc:"Bold, diesel, funky — classic cannabis aroma" },
        { label:"Floral & soft",         value:"sleep",   feeling:"sleep",  aromas:["Floral","Lavender","Chamomile","Rose","Sweet","Vanilla"],desc:"Lavender, chamomile, gentle sweetness — linalool and bisabolol" },
        { label:"Fruity & candy",        value:"social",  feeling:"social", aromas:["Berry","Grape","Candy","Fruity","Banana","Cherry"],      desc:"Gummy bears, grape, tropical candy — limonene and ocimene" },
        { label:"No preference",         value:"any",     feeling:"any",    aromas:[],                                                        desc:"Match on format and potency instead" },
      ]},
    // Q3 — Format
    { id:"format", question:"How do you want to use it?", emoji:"🌿",
      options:[
        { label:"Dried flower",             value:"flower",      categories:["Dried Flower"],               desc:"Smoked or vaporized — widest terpene variety, most COA data available" },
        { label:"Pre-roll",                 value:"preroll",     categories:["Pre-Roll"],                   desc:"Ready to use straight from the package — whole flower or infused" },
        { label:"Vape pen or cartridge",    value:"vape",        categories:["Vape Cart","Disposable Vape"],desc:"Inhaled concentrate — faster onset, more discreet than flower" },
        { label:"Edible or capsule",        value:"edible",      categories:["Edible"],                     desc:"Ingested — onset 30 min to 2 hours, longer duration, no inhalation" },
        { label:"Oil or tincture",          value:"oil",         categories:["Oil"],                        desc:"Sublingual or added to food — precise dosing, flexible onset time" },
        { label:"Beverage",                 value:"beverage",    categories:["Beverage"],                   desc:"Infused drinks — typically faster onset than solid edibles" },
        { label:"Concentrate",              value:"concentrate", categories:["Concentrate"],                desc:"Hash, rosin, live resin — highest terpene concentration of any format" },
        { label:"No preference",            value:"any",         categories:null,                           desc:"Show all product types" },
      ]},
    // Q4 — Grow method
    { id:"grow", question:"How was it grown?", emoji:"🌱",
      subtitle:"Grow method affects terpene density and flavour complexity",
      options:[
        { label:"Indoor",        value:"indoor",      methods:["Indoor"],              desc:"Controlled environment — typically the highest terpene concentration and most consistent flavour" },
        { label:"Greenhouse",    value:"greenhouse",  methods:["Greenhouse"],          desc:"Sun-grown with climate control — good terpene expression, lighter environmental footprint" },
        { label:"Outdoor",       value:"outdoor",     methods:["Outdoor","Sun-Grown"], desc:"Grown in natural sunlight — robust terpene profiles shaped by real growing conditions" },
        { label:"No preference", value:"any",         methods:null,                    desc:"Match on aroma and potency instead" },
      ]},
    // Q5 — Potency
    { id:"strength", question:"What potency level are you looking for?", emoji:"📊",
      options:[
        { label:"Lower (under 15% THC)",   value:"mild",     thcMax:15,  thcMin:0,    desc:"A common starting point — lower THC, often easier to gauge your response" },
        { label:"Mid-range (15–22% THC)",  value:"moderate", thcMax:22,  thcMin:15,   desc:"The most common range in Canadian stores" },
        { label:"Higher (22%+ THC)",       value:"strong",   thcMax:100, thcMin:22,   desc:"Higher concentration — Health Canada advises starting low regardless of experience" },
        { label:"No preference",           value:"any",       thcMax:null,thcMin:null, desc:"Match on aroma and format instead" },
      ]},
    ];

  const scoreConsumer = (p, answers) => {
    let score = 50;
    const isConcentrate = ["Vape Cart","Disposable Vape","Concentrate"].includes(p.category);

    // Format hard filter — CONSUMER_QUESTIONS[2] is the format question (index 0 = location)
    const formatAnswer = answers.format;
    if (formatAnswer && formatAnswer !== "any") {
      const fmtQ = CONSUMER_QUESTIONS[2].options.find(o => o.value === formatAnswer);
      if (fmtQ?.categories && !fmtQ.categories.includes(p.category)) {
        return { score: -999, pct: 0 };
      }
    }

    // Vibe → terpene scoring
    const feelMap = {
      relax: ["β-Myrcene","Linalool","α-Bisabolol"],
      energy:["Terpinolene","(+)-Limonene","β-Ocimene"],
      focus: ["α-Pinene","(+)-Limonene","Terpinolene"],
      social:["(+)-Limonene","β-Caryophyllene","Linalool"],
      body:  ["β-Myrcene","β-Caryophyllene","α-Humulene"],
      sleep: ["β-Myrcene","Linalool","α-Bisabolol"],
    };
    const typeMap = { relax:"indica", energy:"sativa", focus:"sativa", social:"balanced", body:"indica", sleep:"indica" };
    const vibeVal = answers.vibe;
    if (vibeVal && vibeVal !== "any" && feelMap[vibeVal]) {
      feelMap[vibeVal].forEach((t,i) => { if (p.terpenes[t]) score += (3-i) * p.terpenes[t] * 12; });
      const tp = typeMap[vibeVal];
      if (tp === "indica" && p.plant_type?.toLowerCase().includes("indica")) score += 18;
      if (tp === "sativa" && p.plant_type?.toLowerCase().includes("sativa")) score += 18;
    }


    // Aroma scoring — pulled from vibe question aromas array (Q2, index 1)
    const vibeQ = CONSUMER_QUESTIONS[1].options.find(o => o.value === vibeVal);
    if (vibeQ?.aromas?.length) {
      const prodAroma = (p.aroma||[]).map(a => a.toLowerCase());
      const matched = vibeQ.aromas.filter(a => prodAroma.some(pa => pa.includes(a.toLowerCase())));
      score += Math.min(24, matched.length * 10);
    }

    // Grow method scoring — CONSUMER_QUESTIONS[3] (index 0 = location)
    const growAnswer = answers.grow;
    if (growAnswer && growAnswer !== "any") {
      const growQ = CONSUMER_QUESTIONS[3].options.find(o => o.value === growAnswer);
      if (growQ?.methods?.length) {
        if (growQ.methods.includes(p.cultivation?.method)) score += 18;
      }
    }

    // Strength — CONSUMER_QUESTIONS[4]
    const strAnswer = answers.strength;
    if (!isConcentrate && strAnswer && strAnswer !== "any") {
      const strQ = CONSUMER_QUESTIONS[4].options.find(o => o.value === strAnswer);
      if (strQ && strQ.thcMax !== null) {
        if (p.thc >= strQ.thcMin && p.thc <= strQ.thcMax) score += 22;
        else if (p.thc > strQ.thcMax) score -= Math.min(25, (p.thc - strQ.thcMax) * 2);
        else score -= Math.min(12, (strQ.thcMin - p.thc) * 1.5);
      }
    }

    // Terpene richness bonus
    if (p.total_terpenes) {
      const normVal = isConcentrate ? p.total_terpenes / 9 : p.total_terpenes / 2;
      score += Math.min(8, normVal * 8);
    }
    if (p.terpene_source === "lab_tested") score += 6;

    // Location hard filter
    if (answers.locationDispensary && answers.locationDispensary !== "any") {
      if (p.dispensary_id !== answers.locationDispensary) return { score: -999, pct: 0 };
    } else if (answers.locationCity && answers.locationCity !== "any") {
      const disp = DB_DISPENSARIES.find(d => d.id === p.dispensary_id);
      if (disp?.city !== answers.locationCity) return { score: -999, pct: 0 };
    }

    const maxPossible = 50 + 36 + 36 + 22 + 8 + 6;
    const pct = Math.max(0, Math.min(100, Math.round((score / maxPossible) * 100)));
    return { score, pct };
  };

  // Plain-English terpene effect tags
  const TERPENE_EFFECT_TAGS = {
    "β-Myrcene":        { tag:"Earthy · Musky",     color:"#6366f1" },
    "β-Caryophyllene":  { tag:"Pepper · Spice",     color:"#92400e" },
    "(+)-Limonene":     { tag:"Citrus · Lemon",     color:"#f59e0b" },
    "α-Pinene":         { tag:"Pine · Fresh",        color:"#059669" },
    "Terpinolene":      { tag:"Herbal · Woody",      color:"#0ea5e9" },
    "Linalool":         { tag:"Floral · Herbal",     color:"#8b5cf6" },
    "β-Ocimene":        { tag:"Sweet · Bright",      color:"#ec4899" },
    "α-Humulene":       { tag:"Hoppy · Earthy",      color:"#78716c" },
    "α-Bisabolol":      { tag:"Chamomile · Delicate",color:"#f472b6" },
  };

  // ── KIOSK INACTIVITY RESET ──────────────────────────────────
  const resetKioskSession = () => {
    setConsumerPhoneInput("");
    setConsumerPhoneStep("idle");
    setCStep(0);
    setCAnswers({});
    setCResults([]);
    setCSelectedProduct(null);
    setConsumerTab("find");
    setKioskWelcome(true);
  };

  const touchKioskTimer = () => {
    if (!kioskMode) return;
    if (kioskIdleTimerRef.current) clearTimeout(kioskIdleTimerRef.current);
    kioskIdleTimerRef.current = setTimeout(() => {
      resetKioskSession();
    }, 60000);
  };

  // ── KIOSK WELCOME SCREEN ─────────────────────────────────────
  const KioskWelcomeScreen = () => {
    const disp = DB_DISPENSARIES.find(d => d.id === kioskDispensary);
    const dispName = disp ? disp.name : kioskStoreName || "This Store";
    const city = disp ? disp.city : "";
    return (
      <div
        style={{ minHeight:"100vh", background:"#0d1a0d", display:"flex", flexDirection:"column",
          alignItems:"center", justifyContent:"center", padding:"40px 24px", position:"relative",
          cursor:"pointer" }}
        onClick={() => { setKioskWelcome(false); touchKioskTimer(); }}
      >
        {/* iTerps watermark */}
        <div style={{ position:"absolute", top:20, right:24, display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:22, height:22, borderRadius:6, background:"rgba(74,124,89,0.3)",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:12 }}>🌿</div>
          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:"rgba(240,244,240,0.3)",
            letterSpacing:"0.08em" }}>POWERED BY iTERPS</span>
        </div>

        {/* Exit kiosk button - small, top left */}
        <button
          onClick={e => { e.stopPropagation(); setKioskShowExit(true); }}
          style={{ position:"absolute", top:20, left:24, background:"none", border:"none",
            color:"rgba(240,244,240,0.15)", fontSize:10, fontFamily:"'DM Mono',monospace",
            cursor:"pointer", padding:4 }}>
          ⬡ exit kiosk
        </button>

        {/* Store branding */}
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <div style={{ width:80, height:80, borderRadius:20, background:"rgba(74,124,89,0.2)",
            border:"1px solid rgba(74,124,89,0.4)", display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:36, margin:"0 auto 20px" }}>🌿</div>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:36, fontWeight:300,
            color:"#f0f4f0", marginBottom:6, letterSpacing:"-0.02em" }}>{dispName}</h1>
          {city && <p style={{ fontFamily:"'DM Mono',monospace", fontSize:12,
            color:"rgba(240,244,240,0.4)", letterSpacing:"0.06em" }}>{city.toUpperCase()}</p>}
        </div>

        {/* Main CTA */}
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300,
            color:"rgba(240,244,240,0.9)", marginBottom:12 }}>
            Discover your terpene profile
          </div>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:12,
            color:"rgba(240,244,240,0.4)", marginBottom:32 }}>
            Find cannabis products matched to your preferences
          </div>
          <div style={{ display:"inline-flex", alignItems:"center", gap:10,
            background:"#4a7c59", color:"#fff", padding:"16px 40px", borderRadius:50,
            fontSize:14, fontFamily:"'DM Mono',monospace", letterSpacing:"0.04em",
            boxShadow:"0 8px 32px rgba(74,124,89,0.4)", animation:"pulse 2s infinite" }}>
            Tap anywhere to start →
          </div>
        </div>

        {/* Rotating terpene fact */}
        <div style={{ position:"absolute", bottom:32, left:0, right:0, textAlign:"center",
          padding:"0 40px" }}>
          <div style={{ fontFamily:"'DM Mono',monospace", fontSize:10,
            color:"rgba(240,244,240,0.25)", letterSpacing:"0.04em", marginBottom:6 }}>
            DID YOU KNOW
          </div>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:13, fontWeight:300,
            color:"rgba(240,244,240,0.45)", fontStyle:"italic" }}>
            Cannabis contains over 200 terpenes — the same aromatic compounds found in citrus, lavender, and pine
          </div>
        </div>

        {/* Exit PIN modal */}
        {kioskShowExit && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:999,
            display:"flex", alignItems:"center", justifyContent:"center" }}
            onClick={e => { if(e.target===e.currentTarget){ setKioskShowExit(false); setKioskExitEntry(""); } }}>
            <div style={{ background:"#1a2e1a", border:"1px solid rgba(74,124,89,0.4)",
              borderRadius:16, padding:"32px 28px", width:280, textAlign:"center" }}>
              <div style={{ fontSize:24, marginBottom:12 }}>🔐</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:300,
                color:"#f0f4f0", marginBottom:8 }}>Exit Kiosk Mode</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11,
                color:"rgba(240,244,240,0.4)", marginBottom:20 }}>Enter manager PIN to exit</div>
              <input
                type="password"
                value={kioskExitEntry}
                onChange={e => setKioskExitEntry(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    if (kioskExitEntry === kioskPin) {
                      setKioskMode(false); setKioskDispensary(""); setKioskPin("");
                      setKioskWelcome(true); setKioskShowExit(false); setKioskExitEntry("");
                      setPage("home"); setOnboardDone(false);
                    } else {
                      setKioskExitEntry("");
                    }
                  }
                }}
                placeholder="PIN"
                style={{ width:"100%", padding:"10px 14px", borderRadius:10, border:"1px solid rgba(74,124,89,0.4)",
                  background:"rgba(255,255,255,0.05)", color:"#f0f4f0", fontFamily:"'DM Mono',monospace",
                  fontSize:16, textAlign:"center", letterSpacing:"0.2em", marginBottom:12 }}
                autoFocus
              />
              <button
                onClick={() => {
                  if (kioskExitEntry === kioskPin) {
                    setKioskMode(false); setKioskDispensary(""); setKioskPin("");
                    setKioskWelcome(true); setKioskShowExit(false); setKioskExitEntry("");
                    setPage("home"); setOnboardDone(false);
                  } else {
                    setKioskExitEntry("");
                  }
                }}
                style={{ width:"100%", padding:"11px 0", borderRadius:10, border:"none",
                  background:"#4a7c59", color:"#fff", fontFamily:"'DM Mono',monospace",
                  fontSize:13, cursor:"pointer", marginBottom:8 }}>
                Confirm Exit
              </button>
              <button
                onClick={() => { setKioskShowExit(false); setKioskExitEntry(""); }}
                style={{ background:"none", border:"none", color:"rgba(240,244,240,0.3)",
                  fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };



  const ConsumerMode = () => {
    // All state lives in parent to survive re-renders caused by setTasteProfile etc.

    const handleCAnswer = (qId, value) => {
      const next = { ...cAnswers, [qId]: value };
      setCAnswers(next);
      if (cStep < CONSUMER_QUESTIONS.length - 1) {
        setCStep(cStep + 1);
      } else {
        const productPool = kioskMode && kioskDispensary
          ? ALL_PRODUCTS.filter(p => p.dispensary_id === kioskDispensary)
          : ALL_PRODUCTS;
        const scored = productPool.map(p => ({ p, ...scoreConsumer(p, next) }))
          .filter(x => x.score > -999)
          .sort((a,b) => b.score - a.score)
          .slice(0, 5);
        setCResults(scored);
        // Seed Browse tab with the top terpene from best match
        if (scored[0]) {
          const topT = Object.entries(scored[0].p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0]?.[0];
          if (topT) setFinderSeedTerpene(topT);
        }
        setTasteProfile({ answers: next, timestamp: new Date().toLocaleDateString("en-CA") });
        trackEvent("finder_completed", {
          mode: "consumer",
          answers: next,
          resultCount: scored.length,
          topMatch: scored[0] ? { name: scored[0].p.name, brand: scored[0].p.brand, score: scored[0].score } : null,
        });
        fireEmailTrigger("passport_created", { role: "consumer_finder", ts: new Date().toISOString() });
        trackEvent("passport_created", { answers: next, topResult: scored[0] ? { name: scored[0].p.name, score: scored[0].score } : null });
      }
    };

    // In kiosk mode, pre-fill location answer and skip Q0
    const kioskStartStep = (kioskMode && kioskDispensary) ? 1 : 0;
    const kioskLocationAnswer = (kioskMode && kioskDispensary) ? { location: kioskDispensary } : {};
    const resetConsumer = () => { setCStep(kioskStartStep); setCAnswers(kioskLocationAnswer); setCResults(null); setCSelectedProduct(null); setPickerCity("any"); setPickerDisp("any"); };

    // Product detail view within consumer mode
    if (cSelectedProduct) {
      const p = cSelectedProduct;
      const disp = DB_DISPENSARIES.find(d => d.id === p.dispensary_id);
      const topTerps = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1]).slice(0,4);
      const effectTags = topTerps.map(([t]) => TERPENE_EFFECT_TAGS[t]).filter(Boolean);
      const productTypeInfo = PRODUCT_TYPES[p.category];
      return (
        <div className="fade" style={{ maxWidth:520, margin:"0 auto", padding:"28px 20px" }}>
          <button onClick={()=>setCSelectedProduct(null)} style={{ background:"none", border:"none", color:"#6b7280", fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>← Back to results</button>
          <div className="card" style={{ padding:0, overflow:"hidden", marginBottom:16 }}>
            <div style={{ background:"linear-gradient(135deg, #1a2e1a 0%, #2d4a2d 100%)", padding:"28px 24px", position:"relative" }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.5)", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{p.brand} · {p.category}</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:300, color:"#fff", marginBottom:12 }}>{p.name}</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {effectTags.map((et,i) => (
                  <span key={i} style={{ background:(et.color) + "30", border:"1px solid " + (et.color) + "60", color:"#fff", fontSize:11, padding:"3px 10px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>{et.tag}</span>
                ))}
                {p.in_stock && <span style={{ background:"#22c55e30", border:"1px solid #22c55e60", color:"#fff", fontSize:11, padding:"3px 10px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>✓ In Stock</span>}
              </div>
            </div>
            <div style={{ padding:"20px 24px" }}>
              {/* Plain English aroma */}
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.08em" }}>How it smells</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {(p.aroma||[]).map(a => <span key={a} className="pill">{a}</span>)}
                </div>
              </div>
              {/* Top terpenes — plain English */}
              <div style={{ marginBottom:18 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.08em" }}>Primary terpenes</div>
                {topTerps.map(([t, v]) => {
                  const et = TERPENE_EFFECT_TAGS[t];
                  const ti = TERPENES[t];
                  if (!ti) return null;
                  return (
                    <div key={t} style={{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:10, padding:"10px 12px", background:"#f7f5f0", borderRadius:8 }}>
                      <div style={{ width:10, height:10, borderRadius:"50%", background:ti.color, flexShrink:0, marginTop:3 }}/>
                      <div>
                        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                          <span style={{ fontSize:12, fontWeight:500, color:"#1a2e1a" }}>{et?.tag || t}</span>
                          {et && <span style={{ fontSize:9, background:(et.color) + "20", color:et.color, padding:"1px 6px", borderRadius:10, fontFamily:"'DM Mono',monospace" }}>{t}</span>}
                        </div>
                        <div style={{ fontSize:11, color:"#6b7280", lineHeight:1.5 }}>{ti.plain?.split('.')[0]}.</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Strength & grow — plain */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:18 }}>
                {[
                  ["Strength", p.total_thc ? (p.total_thc) + "% THC" : p.category === "Edible" ? (p.thc) + "mg THC" : (p.thc) + "% THC", p.thc > 25 ? "Strong" : p.thc > 18 ? "Moderate" : "Mild"],
                  ["Grown", p.cultivation?.method || p.category, p.cultivation?.lp || "Licensed Producer"],
                ].map(([label, val, sub]) => (
                  <div key={label} style={{ background:"#f7f5f0", borderRadius:8, padding:"12px 14px" }}>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:2 }}>{label}</div>
                    <div style={{ fontSize:15, fontWeight:500, fontFamily:"'Fraunces',serif", color:"#1a2e1a" }}>{val}</div>
                    <div style={{ fontSize:10, color:"#6b7280" }}>{sub}</div>
                  </div>
                ))}
              </div>

              {/* Freshness — consumer plain language */}
              {(() => { const f = getFreshness(p); if (!f) return null; return (
                <div style={{ background:(f.color) + "10", border:"1px solid " + (f.color) + "40", borderRadius:8, padding:"12px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:20 }}>{f.score >= 85 ? "🌿" : f.score >= 65 ? "🟡" : "⚠️"}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:500, color:"#1a2e1a" }}>Packaged {f.daysSince} days ago — {f.label}</div>
                    <div style={{ fontSize:11, color:"#6b7280" }}>{f.score >= 85 ? "Terpene profile at its best." : f.score >= 65 ? "Good condition — some terpene fade is normal." : "Older stock. Terpene intensity may be reduced."}</div>
                  </div>
                </div>
              ); })()}

              {/* LP Verified badge — consumer friendly */}
              {p.cultivation?.lp_verified && (
                <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:8, padding:"10px 14px", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:16 }}>🏷</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:500, color:"#15803d" }}>Licensed Producer Verified</div>
                    <div style={{ fontSize:10, color:"#6b7280" }}>The grower ({p.cultivation.lp}) has submitted and verified the terpene data on this product.</div>
                  </div>
                </div>
              )}

              {/* Rare / minor terpenes — consumer plain language */}
              {p.otherTerpenes && Object.keys(p.otherTerpenes).length > 0 && (() => {
                const rareSorted = Object.entries(p.otherTerpenes).sort((ea,eb)=>eb[1]-ea[1]);
                return (
                  <div style={{ marginBottom:18 }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.08em" }}>Also detected — trace compounds</div>
                    <div style={{ background:"#faf5ff", border:"1px solid #ede9fe", borderRadius:8, padding:"12px 14px" }}>
                      <div style={{ fontSize:11, color:"#6b7280", marginBottom:10, lineHeight:1.5 }}>This product has a richer terpene fingerprint than most — the lab detected these additional compounds in small amounts:</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {rareSorted.map(([t, v]) => {
                          const info = TERPENES[t];
                          return (
                            <div key={t} style={{ background:"#fff", border:"1px solid #ddd6fe", borderRadius:8, padding:"6px 10px" }}>
                              <div style={{ fontSize:11, color:"#5b21b6", fontWeight:500, marginBottom:2 }}>{info?.commonName || t}</div>
                              <div style={{ fontSize:9, color:"#9ca3af" }}>{info?.aroma?.[0] || "—"} · {(v*10).toFixed(1)} mg/g</div>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ fontSize:9, color:"#9ca3af", marginTop:8 }}>More terpene compounds = more aromatic complexity. Think of it like a wine with more layers of flavour.</div>
                    </div>
                  </div>
                );
              })()}
              {/* Where to get it */}
              <div style={{ background:"#f0fdf4", borderRadius:8, padding:"12px 14px", marginBottom:18, border:"1px solid #bbf7d0" }}>
                <div style={{ fontSize:10, color:"#065f46", fontFamily:"'DM Mono',monospace", marginBottom:2 }}>📍 Available at</div>
                <div style={{ fontSize:14, fontWeight:500, color:"#1a2e1a" }}>{disp?.name}</div>
                <div style={{ fontSize:11, color:"#6b7280" }}>{disp?.city}{disp?.address ? " · " + (disp.address) : ""}</div>
                {disp?.hours && <div style={{ fontSize:10, color:"#6b7280", marginTop:2 }}>🕐 {disp.hours}</div>}
              </div>
              {/* Product type plain explainer */}
              {productTypeInfo && (
                <div style={{ background:"#f7f5f0", borderRadius:8, padding:"12px 14px" }}>
                  <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>About {p.category}s</div>
                  <div style={{ fontSize:11, color:"#374151", lineHeight:1.6 }}>{productTypeInfo.description.slice(0,160)}…</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:8 }}>
                    <span style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>⚡ {productTypeInfo.onset}</span>
                    <span style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>· ⏱ {productTypeInfo.duration}</span>
                  </div>
                </div>
              )}

              {/* Irradiation — consumer plain language */}
              {(() => {
                const irr = p.cultivation?.irradiation || "not_disclosed";
                const consumerText = {
                  non_irradiated: { icon:"✓", color:"#15803d", bg:"#f0fdf4", border:"#86efac", title:"Not Irradiated", body:"This product was not treated with radiation after harvest. The plant natural aroma compounds are in their original form." },
                  gamma:          { icon:"⚠", color:"#92400e", bg:"#fffbeb", border:"#fcd34d", title:"Gamma Irradiated", body:"Radiation (gamma rays) was used after harvest to kill bacteria and mould. This is legal and safe to consume — but it can reduce the intensity of natural aromas. If terpene character matters to you, this is worth knowing." },
                  e_beam:         { icon:"⚡", color:"#6d28d9", bg:"#faf5ff", border:"#ddd6fe", title:"E-Beam Irradiated", body:"Electron beam processing was used for safety compliance. Similar to gamma but slightly gentler on aroma compounds. Legal and safe — some aroma reduction may occur." },
                  x_ray:          { icon:"📡", color:"#6d28d9", bg:"#faf5ff", border:"#ddd6fe", title:"X-Ray Irradiated", body:"X-ray processing used for safety compliance. Similar effect to e-beam on aroma compounds. Legal and safe." },
                  not_disclosed:  { icon:"?", color:"#6b7280", bg:"#f9fafb", border:"#e5e7eb", title:"Irradiation — Not Confirmed", body:"We don't have confirmed information on whether this product was irradiated. The grower hasn't publicly disclosed this. You can ask the budtender or contact the brand directly." },
                };
                const cfg = consumerText[irr] || consumerText.not_disclosed;
                return (
                  <div style={{ background:cfg.bg, border:"1px solid " + (cfg.border), borderRadius:8, padding:"12px 14px", marginTop:12 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                      <span style={{ fontSize:15 }}>{cfg.icon}</span>
                      <div style={{ fontSize:12, fontWeight:600, color:cfg.color }}>{cfg.title}</div>
                    </div>
                    <div style={{ fontSize:11, color:"#374151", lineHeight:1.6 }}>{cfg.body}</div>
                  </div>
                );
              })()}
            </div>
          </div>
          <button onClick={resetConsumer} style={{ width:"100%", background:"none", border:"1px solid #e8e4de", borderRadius:10, padding:"11px 0", fontSize:12, cursor:"pointer", color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>← Find something else</button>
        </div>
      );
    }

    // Results view
    if (cResults) return (
      <div className="fade" style={{ maxWidth:580, margin:"0 auto", padding:"28px 20px" }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>🌿</div>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>Terpene Matches</h1>
          <p style={{ fontSize:13, color:"#6b7280" }}>Ranked by terpene profile compatibility with your selections</p>
          {(cAnswers.locationDispensary && cAnswers.locationDispensary !== "any") ? (
            <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:8, padding:"4px 12px", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.3)", borderRadius:20 }}>
              <span style={{ fontSize:11 }}>📍</span>
              <span style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace", background:"#f0fdf4", padding:"2px 8px", borderRadius:20, border:"1px solid #bbf7d0" }}>{DB_DISPENSARIES.find(d=>d.id===cAnswers.locationDispensary)?.name}</span>
            </div>
          ) : (cAnswers.locationCity && cAnswers.locationCity !== "any") ? (
            <div style={{ display:"inline-flex", alignItems:"center", gap:6, marginTop:8 }}>
              <span style={{ fontSize:11, color:"#065f46", fontFamily:"'DM Mono',monospace", background:"#f0fdf4", padding:"3px 10px", borderRadius:20, border:"1px solid #bbf7d0" }}>📍 {cAnswers.locationCity}</span>
            </div>
          ) : null}
        </div>
        {cResults.length === 0 ? (
          <div className="card" style={{ padding:28, textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>🔍</div>
            <p style={{ fontFamily:"'Fraunces',serif", fontSize:16, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>No exact matches found</p>
            <p style={{ fontSize:12, color:"#6b7280", marginBottom:20 }}>Try selecting "No preference" for format or strength — or browse all in-stock products.</p>
            <button onClick={resetConsumer} className="btn-primary" style={{ padding:"10px 24px", fontSize:12 }}>Try again</button>
          </div>
        ) : cResults.map(({ p, pct }, i) => {
          const topTerps = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1]).slice(0,3);
          const effectTags = topTerps.map(([t]) => TERPENE_EFFECT_TAGS[t]).filter(Boolean);
          const disp = DB_DISPENSARIES.find(d => d.id === p.dispensary_id);
          const pctColor = pct >= 75 ? "#22c55e" : pct >= 55 ? "#f59e0b" : "#9ca3af";
          return (
            <div key={p.id} className="card" style={{ padding:20, marginBottom:12, border: i===0 ? "2px solid #4a7c59" : "1px solid #e8e4de", position:"relative", cursor:"pointer" }}
              onClick={()=>setCSelectedProduct(p)}>
              {i===0 && <div style={{ position:"absolute", top:-10, left:16, background:"#4a7c59", color:"#fff", fontSize:9, fontFamily:"'DM Mono',monospace", padding:"3px 10px", borderRadius:20 }}>🌿 Top Pick</div>}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:10, color:"#9ca3af", marginBottom:2 }}>{p.brand} · {p.category}</div>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:400, color:"#1a2e1a", marginBottom:8 }}>{p.name}</div>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:8 }}>
                    {effectTags.map((et,ei) => (
                      <span key={ei} style={{ fontSize:10, background:(et.color) + "15", color:et.color, border:"1px solid " + (et.color) + "30", padding:"2px 8px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>{et.tag}</span>
                    ))}
                    {(p.aroma||[]).slice(0,2).map(a => <span key={a} className="pill" style={{ fontSize:10 }}>{a}</span>)}
                  </div>
                  <div style={{ fontSize:11, color:"#6b7280" }}>📍 {disp?.name} · {disp?.city}</div>
                  {p.in_stock
                    ? <span style={{ display:"inline-block", marginTop:6, fontSize:10, background:"#d1fae5", color:"#065f46", border:"1px solid #6ee7b7", padding:"2px 8px", borderRadius:20 }}>✓ In Stock</span>
                    : <span style={{ display:"inline-block", marginTop:6, fontSize:10, background:"#fef2f2", color:"#991b1b", border:"1px solid #fca5a5", padding:"2px 8px", borderRadius:20 }}>Out of Stock</span>}
                </div>
                <div style={{ textAlign:"center", flexShrink:0 }}>
                  <div style={{ position:"relative", width:54, height:54, margin:"0 auto 4px" }}>
                    <svg width="54" height="54" style={{ transform:"rotate(-90deg)" }}>
                      <circle cx="27" cy="27" r="22" fill="none" stroke="#e8e4de" strokeWidth="4"/>
                      <circle cx="27" cy="27" r="22" fill="none" stroke={pctColor} strokeWidth="4"
                        strokeDasharray={2*Math.PI*22}
                        strokeDashoffset={2*Math.PI*22*(1-pct/100)}
                        strokeLinecap="round"/>
                    </svg>
                    <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <span style={{ fontSize:11, fontWeight:600, color:pctColor, fontFamily:"'DM Mono',monospace" }}>{pct}%</span>
                    </div>
                  </div>
                  <div style={{ fontSize:8, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>match</div>
                </div>
              </div>
              <div style={{ marginTop:10, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div style={{ fontSize:11, color:"#4a7c59", fontFamily:"'DM Mono',monospace" }}>Tap to see more →</div>
                <FlagButton context="Consumer Finder Result" productId={p.id} productName={p.name}
                  style={{ fontSize:9 }} />
              </div>
            </div>
          );
        })}
        <div style={{ display:"flex", gap:10, marginTop:8 }}>
          {kioskMode
            ? <button onClick={resetConsumer} style={{ flex:1, padding:"11px 0", borderRadius:10, border:"none", background:"#1a2e1a", color:"#fff", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:500 }}>✓ Done — Next Customer →</button>
            : <button onClick={resetConsumer} style={{ flex:1, background:"none", border:"1px solid #e8e4de", borderRadius:10, padding:"11px 0", fontSize:12, cursor:"pointer", color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>↺ Start over</button>
          }
          <button onClick={()=>{ setRegisterFor("consumer"); setShowRegisterModal(true); }} className="btn-primary" style={{ flex:2, padding:"11px 0", fontSize:12 }}>
            🪪 Save My Passport
          </button>
        </div>
        {showRegisterModal && registerFor==="consumer" && (
          <RegisterModal mode="consumer" finderAnswers={cAnswers}
            onSave={(profile)=>{ setShowRegisterModal(false); setRmName(""); setRmPhone(""); setRmCity(""); setRmConsent(false); setRmAgeConsent(false); setRmError(""); setRmStep("form"); setTasteProfile({...tasteProfile, phone:profile.phone, name:profile.name}); setConsumerTab("profile"); }}
            onClose={closeRegModal}/>
        )}
      </div>
    );

    // Question flow — in kiosk mode step 0 (location) is auto-skipped
    const effectiveStep = (kioskMode && kioskDispensary) ? Math.max(cStep, 1) : cStep;
    const q = CONSUMER_QUESTIONS[effectiveStep];
    const visibleTotal = (kioskMode && kioskDispensary) ? CONSUMER_QUESTIONS.length - 1 : CONSUMER_QUESTIONS.length;
    const visibleStep = (kioskMode && kioskDispensary) ? effectiveStep : cStep;
    const progress = (visibleStep / visibleTotal) * 100;

    // Location picker step — skip entirely in kiosk mode (store is pre-set)
    if (q?.locationSelect) {
      if (kioskMode && kioskDispensary) {
        // Auto-advance past location question with kiosk store pre-filled
        if (!cAnswers.location) {
          setCAnswers(a => ({...a, location: kioskDispensary}));
          setCStep(cStep + 1);
        }
        return null;
      }
      return (
        <LocationPickerStep
          compact={true}
          onConfirm={(loc) => {
            const next = { ...cAnswers, ...loc };
            setCAnswers(next);
            setCStep(cStep + 1);
          }}
          onBack={cStep > 0 ? () => setCStep(cStep - 1) : null}
        />
      );
    }

    return (
      <div className="fade" style={{ maxWidth:520, margin:"0 auto", padding:"28px 20px" }}>
        <div style={{ marginBottom:28 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
            <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{visibleStep} of {visibleTotal}</span>
            <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{Math.round(progress)}%</span>
          </div>
          <div style={{ height:4, background:"#e8e4de", borderRadius:2 }}>
            <div style={{ height:4, background:"linear-gradient(90deg, #4a7c59, #22c55e)", borderRadius:2, width:(progress) + "%", transition:"width 0.4s ease" }}/>
          </div>
        </div>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          <div style={{ fontSize:44, marginBottom:10 }}>{q.emoji}</div>
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#1a2e1a", marginBottom:6 }}>{q.question}</h2>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {q.options.map(opt => (
            <button key={opt.value} onClick={()=>handleCAnswer(q.id, opt.value)}
              style={{ padding:"16px 20px", background:"#fff", border:"1px solid #e8e4de", borderRadius:14, cursor:"pointer", textAlign:"left", transition:"all 0.15s" }}
              onMouseEnter={e=>{ e.currentTarget.style.borderColor="#4a7c59"; e.currentTarget.style.background="#f0fdf4"; e.currentTarget.style.transform="translateY(-1px)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.borderColor="#e8e4de"; e.currentTarget.style.background="#fff"; e.currentTarget.style.transform="translateY(0)"; }}>
              <div style={{ fontSize:15, fontWeight:500, color:"#1a2e1a", fontFamily:"'Fraunces',serif", marginBottom:3 }}>{opt.label}</div>
              <div style={{ fontSize:11, color:"#9ca3af" }}>{opt.desc}</div>
            </button>
          ))}
        </div>
        {((kioskMode && kioskDispensary) ? cStep > 1 : cStep > 0) && (
          <div style={{ textAlign:"center", marginTop:14 }}>
            <button onClick={()=>setCStep(cStep-1)} style={{ background:"none", border:"none", color:"#9ca3af", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>← Back</button>
          </div>
        )}
        {cStep === 0 && !kioskMode && (
          <div style={{ textAlign:"center", marginTop:20, paddingTop:16, borderTop:"1px solid #f3f4f6" }}>
            <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Not sure where to start?</div>
            <button onClick={()=>{ setNewbieModeActive(true); setConsumerMode(false); setPage("newbie"); }}
              style={{ background:"none", border:"1px solid #e8e4de", borderRadius:20, padding:"6px 18px", fontSize:11, color:"#4a7c59", fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              🌱 New to cannabis — simpler guide
            </button>
          </div>
        )}
      </div>
    );
  };

  // Consumer Browse tab — visual card grid, plain English
  const ConsumerBrowse = () => {
    const browseSearch=cbSearch, setBrowseSearch=setCbSearch;
    const browseCategory=cbCategory, setBrowseCategory=setCbCategory;
    const cViewProduct=cbViewProd, setCViewProduct=setCbViewProd;
    const browseSort=cbSort, setBrowseSort=setCbSort;
    const browseCity=cbBrowseCity, setBrowseCity=setCbBrowseCity;
    const cities = ["any", ...new Set(DB_DISPENSARIES.map(d=>d.city)).values()].sort((a,b)=>a==="any"?-1:b==="any"?1:a.localeCompare(b));

    // Sensory-only terpene descriptors — no health/lifestyle/effect claims (Cannabis Act compliant)
    const EFFECT_FILTERS = [
      { label:"Floral & Herbal",  terp:"Linalool",           color:"#8b5cf6" },
      { label:"Citrus & Lemon",   terp:"(+)-Limonene",       color:"#f59e0b" },
      { label:"Earthy & Musky",   terp:"β-Myrcene",          color:"#6366f1" },
      { label:"Pepper & Spice",   terp:"β-Caryophyllene",    color:"#92400e" },
      { label:"Pine & Fresh",     terp:"α-Pinene",           color:"#059669" },
      { label:"Chamomile & Sweet",terp:"α-Bisabolol",        color:"#f472b6" },
      { label:"Herbal & Woody",   terp:"Terpinolene",        color:"#0ea5e9" },
      { label:"Sweet & Bright",   terp:"β-Ocimene",          color:"#ec4899" },
      { label:"Hoppy & Earthy",   terp:"α-Humulene",         color:"#78716c" },
    ];

    // Aroma families grouped by vibe
    const AROMA_GROUPS = [
      { label:"Citrus & Fruit",   aromas:["Lemon","Citrus","Orange","Lime","Tangerine","Mango","Pineapple","Tropical","Strawberry","Banana","Blueberry","Grape","Berry","Cherry","Watermelon","Papaya"] },
      { label:"Earthy & Wood",    aromas:["Earthy","Woody","Sandalwood","Pine","Forest","Soil","Hash","Cedar"] },
      { label:"Sweet & Dessert",  aromas:["Sweet","Vanilla","Cookie","Cake","Baked","Candy","Cereal","Cream","Sherbet","Chocolate","Dessert"] },
      { label:"Floral & Herbal",  aromas:["Floral","Lavender","Herbal","Chamomile","Rose","Fresh","Spice","Mint"] },
      { label:"Gas & Fuel",       aromas:["Gas","Diesel","Fuel","Chemical","Skunk","Sharp"] },
    ];

    // In kiosk mode, filter brands and categories to this store only
    const browsePool2 = kioskMode && kioskDispensary ? ALL_PRODUCTS.filter(p=>p.dispensary_id===kioskDispensary) : ALL_PRODUCTS;
    const categories = [...new Set(browsePool2.map(p=>p.category))].sort();
    const brands = ["", ...new Set(browsePool2.map(p=>p.brand)).values()].sort();

    // Parse current filter state out of browseSearch
    // browseSearch format: "brand:X" | "aroma:X" | "effect:X" | ""
    let filterBrand = "", filterAroma = "", filterEffect = "";
    if (browseSearch.startsWith("brand:")) filterBrand = browseSearch.slice(6);
    else if (browseSearch.startsWith("aroma:")) filterAroma = browseSearch.slice(6);
    else if (browseSearch.startsWith("effect:")) filterEffect = browseSearch.slice(7);

    // Sort: "thc_asc" | "thc_desc" | "terp_asc" | "terp_desc" | "name" | terpene key + "_asc"/"_desc"
    const TERPENE_KEYS = ["β-Myrcene","β-Caryophyllene","(+)-Limonene","α-Pinene","Terpinolene","Linalool","β-Ocimene","α-Humulene","α-Bisabolol"];

    // In kiosk mode, lock to the configured dispensary
    const browsePool = kioskMode && kioskDispensary
      ? ALL_PRODUCTS.filter(p => p.dispensary_id === kioskDispensary)
      : ALL_PRODUCTS;
    const browsed = browsePool.filter(p => {
      if (!p.in_stock) return false;
      if (browseCategory && p.category !== browseCategory) return false;
      if (!kioskMode && browseCity !== "any") {
        const disp = DB_DISPENSARIES.find(d=>d.id===p.dispensary_id);
        if (disp?.city !== browseCity) return false;
      }
      if (filterBrand && p.brand !== filterBrand) return false;
      if (filterAroma) {
        const group = AROMA_GROUPS.find(g => g.label === filterAroma);
        if (group) {
          const matches = (p.aroma||[]).some(a => group.aromas.some(ga => a.toLowerCase().includes(ga.toLowerCase())));
          if (!matches) return false;
        }
      }
      if (filterEffect) {
        const ef = EFFECT_FILTERS.find(e => e.label === filterEffect);
        if (ef && !p.terpenes[ef.terp]) return false;
      }
      // Finder seed terpene — filter to products that contain it (if no other search active)
      if (finderSeedTerpene && !browseSearch && !filterEffect) {
        if (!(p.terpenes[finderSeedTerpene] > 0)) return false;
      }
      return true;
    }).sort((a,b) => {
      if (!browseSort || browseSort === "name") return a.name.localeCompare(b.name);
      if (browseSort === "thc_desc") return b.thc - a.thc;
      if (browseSort === "thc_asc")  return a.thc - b.thc;
      if (browseSort === "terp_desc") return (b.total_terpenes||0) - (a.total_terpenes||0);
      if (browseSort === "terp_asc")  return (a.total_terpenes||0) - (b.total_terpenes||0);
      // Specific terpene sort
      const [tKey, dir] = browseSort.split("|");
      if (TERPENE_KEYS.includes(tKey)) return dir === "asc" ? (a.terpenes[tKey]||0) - (b.terpenes[tKey]||0) : (b.terpenes[tKey]||0) - (a.terpenes[tKey]||0);
      return a.name.localeCompare(b.name);
    }).slice(0,60);

    if (cViewProduct) {
      const p = cViewProduct;
      const disp = DB_DISPENSARIES.find(d => d.id === p.dispensary_id);
      const topTerps = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1]).slice(0,4);
      const effectTags = topTerps.map(([t]) => TERPENE_EFFECT_TAGS[t]).filter(Boolean);
      const productTypeInfo = PRODUCT_TYPES[p.category];
      return (
        <div className="fade" style={{ maxWidth:520, margin:"0 auto", padding:"28px 20px" }}>
          <button onClick={()=>setCViewProduct(null)} style={{ background:"none", border:"none", color:"#6b7280", fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>← Back to browse</button>
          <div className="card" style={{ padding:0, overflow:"hidden" }}>
            <div style={{ background:"linear-gradient(135deg, #1a2e1a 0%, #2d4a2d 100%)", padding:"28px 24px" }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.5)", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{p.brand} · {p.category}</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:300, color:"#fff", marginBottom:12 }}>{p.name}</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {effectTags.map((et,i) => (
                  <span key={i} style={{ background:(et.color) + "30", border:"1px solid " + (et.color) + "60", color:"#fff", fontSize:11, padding:"3px 10px", borderRadius:20 }}>{et.tag}</span>
                ))}
              </div>
            </div>
            <div style={{ padding:"20px 24px" }}>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:6, textTransform:"uppercase" }}>Aroma</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>{(p.aroma||[]).map(a=><span key={a} className="pill">{a}</span>)}</div>
              </div>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, textTransform:"uppercase" }}>Primary terpenes</div>
                {topTerps.map(([t]) => {
                  const et = TERPENE_EFFECT_TAGS[t]; const ti = TERPENES[t];
                  if (!ti) return null;
                  return (
                    <div key={t} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:8, padding:"8px 10px", background:"#f7f5f0", borderRadius:8 }}>
                      <div style={{ width:9, height:9, borderRadius:"50%", background:ti.color, flexShrink:0, marginTop:3 }}/>
                      <div>
                        <div style={{ fontSize:12, fontWeight:500, color:"#1a2e1a", marginBottom:1 }}>{et?.tag || t}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{ti.plain?.split('.')[0]}.</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
                <div style={{ background:"#f7f5f0", borderRadius:8, padding:"10px 12px" }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Strength</div>
                  <div style={{ fontSize:16, fontWeight:500, fontFamily:"'Fraunces',serif", color:"#22c55e" }}>{p.thc}% THC</div>
                  <div style={{ fontSize:10, color:"#6b7280" }}>{p.thc > 25 ? "Strong" : p.thc > 18 ? "Moderate" : "Mild"}</div>
                </div>
                <div style={{ background:"#f7f5f0", borderRadius:8, padding:"10px 12px" }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Grown</div>
                  <div style={{ fontSize:13, fontWeight:500, fontFamily:"'Fraunces',serif", color:"#1a2e1a" }}>{p.cultivation?.method}</div>
                  <div style={{ fontSize:10, color:"#6b7280" }}>{p.cultivation?.lp}</div>
                </div>
              </div>
              <div style={{ background:"#f0fdf4", borderRadius:8, padding:"12px 14px", marginBottom:16, border:"1px solid #bbf7d0" }}>
                <div style={{ fontSize:10, color:"#065f46", fontFamily:"'DM Mono',monospace", marginBottom:2 }}>📍 Where to get it</div>
                <div style={{ fontSize:14, fontWeight:500, color:"#1a2e1a" }}>{disp?.name}</div>
                <div style={{ fontSize:11, color:"#6b7280" }}>{disp?.city}{disp?.address ? " · " + (disp.address) : ""}</div>
                {disp?.hours && <div style={{ fontSize:10, color:"#6b7280", marginTop:2 }}>🕐 {disp.hours}</div>}
              </div>
              {p.genetics?.lineage && p.genetics.lineage !== "Unknown" && (
                <div style={{ background:"#f7f5f0", borderRadius:8, padding:"10px 12px", marginBottom:10 }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3, textTransform:"uppercase", letterSpacing:"0.06em" }}>Genetic Lineage</div>
                  <div style={{ fontSize:11, color:"#374151", lineHeight:1.5, marginBottom:p.genetics.breeder?4:0 }}>{p.genetics.lineage}</div>
                  {p.genetics.breeder && <div style={{ fontSize:10, color:"#6b7280" }}>Breeder: {p.genetics.breeder}</div>}
                </div>
              )}
              {productTypeInfo && (
                <div style={{ background:"#f7f5f0", borderRadius:8, padding:"10px 12px" }}>
                  <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>About {p.category}s · Onset: {productTypeInfo.onset}</div>
                  <div style={{ fontSize:11, color:"#374151", lineHeight:1.5 }}>{productTypeInfo.description.slice(0,140)}…</div>
                </div>
              )}

              {/* Rate & log to passport */}
              {tasteProfile?.phone && (() => {
                const phone = tasteProfile.phone;
                const prof = profiles.find(pr=>pr.phone===phone);
                const existing = prof?.productHistory?.find(h=>h.productId===p.id);
                return (
                  <div style={{ marginTop:14, padding:"12px 14px", background:"#f0fdf4", borderRadius:10, border:"1px solid #bbf7d0" }}>
                    <div style={{ fontSize:10, color:"#065f46", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>
                      {existing ? "Logged to your passport " + (existing.triedCount > 1 ? "(tried " + (existing.triedCount) + "×)" : "") : "Log to your terpene passport"}
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <div style={{ fontSize:12, color:"#374151", flex:1 }}>Rate this product:</div>
                      {[["up","👍 Liked it"],["down","👎 Not for me"]].map(([r,label])=>(
                        <button key={r} onClick={()=>addProductToHistory(phone, p, r)}
                          style={{ padding:"6px 14px", fontSize:12, border:"1px solid " + (existing?.rating===r?"#4a7c59":"#e8e4de"),
                            background: existing?.rating===r?"#4a7c59":"#fff",
                            color: existing?.rating===r?"#fff":"#374151",
                            borderRadius:8, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {!tasteProfile?.phone && (
                <div style={{ marginTop:14, padding:"10px 14px", background:"#f7f5f0", borderRadius:10, border:"1px solid #e8e4de" }}>
                  <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>
                    💡 Save a terpene passport from the Match tab to log products and track your preferences.
                  </div>
                </div>
              )}

              {/* Wishlist button */}
              {tasteProfile?.phone && (() => {
                const prof = profiles.find(pr=>pr.phone===tasteProfile.phone);
                const onWishlist = (prof?.wishlist||[]).find(w=>w.productId===p.id);
                return (
                  <button onClick={()=>toggleWishlist(tasteProfile.phone, p)}
                    style={{marginTop:10,width:"100%",padding:"9px 0",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:"pointer",
                      background:onWishlist?"#fffbeb":"transparent",
                      border:"1px solid " + (onWishlist?"#fcd34d":"#e8e4de"),
                      color:onWishlist?"#92400e":"#6b7280",borderRadius:10,transition:"all 0.15s"}}>
                    {onWishlist?"⭐ On Wishlist — tap to remove":"☆ Add to Wishlist"}
                  </button>
                );
              })()}
            </div>
            {/* Similar to this */}
            <button onClick={()=>setSimilarModal(p)}
              style={{marginTop:10,width:"100%",padding:"9px 0",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:"pointer",
                background:"#f7f5f0",border:"1px solid #e8e4de",color:"#374151",borderRadius:10,transition:"all 0.15s"}}>
              🔍 Find Similar Products
            </button>
          </div>
        </div>
      );
    }

    // Active filter label for display
    const activeFilterLabel = filterBrand ? "Brand: " + (filterBrand) : filterAroma ? "Vibe: " + (filterAroma) : filterEffect || "";
    const activeSortLabel = browseSort === "thc_desc" ? "THC: High → Low" : browseSort === "thc_asc" ? "THC: Low → High" : browseSort === "terp_desc" ? "Terpenes: High → Low" : browseSort === "terp_asc" ? "Terpenes: Low → High" : browseSort.includes("|") ? (() => { const [tKey, dir] = browseSort.split("|"); const ef = EFFECT_FILTERS.find(e=>e.terp===tKey); return (ef?.label||tKey) + ": " + (dir==="desc"?"High → Low":"Low → High"); })() : "";

    return (
      <div className="fade" style={{ maxWidth:660, margin:"0 auto", padding:"20px 16px" }}>

        {/* From-finder seed banner */}
        {finderSeedTerpene && !browseSearch && (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, marginBottom:12, gap:10 }}>
            <div style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#15803d" }}>
              🌿 Showing products rich in <strong>{finderSeedTerpene}</strong> — based on your finder results
            </div>
            <button onClick={()=>setFinderSeedTerpene(null)}
              style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#15803d", background:"none", border:"1px solid #86efac", borderRadius:6, padding:"3px 8px", cursor:"pointer", flexShrink:0 }}>
              Clear ×
            </button>
          </div>
        )}
        <div style={{ display:"flex", gap:8, marginBottom:10, flexWrap:"wrap" }}>

          {/* Brand dropdown */}
          <select value={filterBrand} onChange={e=>{ setBrowseSearch(e.target.value ? "brand:" + (e.target.value) : ""); }}
            style={{ flex:"1 1 140px", padding:"9px 12px", border:"1px solid " + (filterBrand?"#4a7c59":"#e8e4de"), borderRadius:10, fontSize:12, fontFamily:"'DM Mono',monospace", background: filterBrand?"#f0fdf4":"#fff", color: filterBrand?"#166534":"#6b7280", cursor:"pointer", outline:"none" }}>
            <option value="">All brands</option>
            {brands.filter(Boolean).map(b=><option key={b} value={b}>{b}</option>)}
          </select>

          {/* Product type dropdown */}
          <select value={browseCategory} onChange={e=>setBrowseCategory(e.target.value)}
            style={{ flex:"1 1 130px", padding:"9px 12px", border:"1px solid " + (browseCategory?"#4a7c59":"#e8e4de"), borderRadius:10, fontSize:12, fontFamily:"'DM Mono',monospace", background: browseCategory?"#f0fdf4":"#fff", color: browseCategory?"#166534":"#6b7280", cursor:"pointer", outline:"none" }}>
            <option value="">All product types</option>
            {categories.map(c=><option key={c} value={c}>{c}</option>)}
          </select>

          {/* City filter — hidden in kiosk mode */}
          {!kioskMode && (
            <select value={browseCity} onChange={e=>setBrowseCity(e.target.value)}
              style={{ flex:"1 1 130px", padding:"9px 12px", border:"1px solid " + (browseCity!=="any"?"#4a7c59":"#e8e4de"), borderRadius:10, fontSize:12, fontFamily:"'DM Mono',monospace", background: browseCity!=="any"?"#f0fdf4":"#fff", color: browseCity!=="any"?"#166534":"#6b7280", cursor:"pointer", outline:"none" }}>
              <option value="any">All cities</option>
              {cities.filter(c=>c!=="any").map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {kioskMode && kioskDispensary && (
            <div style={{ flex:"1 1 130px", padding:"9px 12px", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, fontSize:11, fontFamily:"'DM Mono',monospace", color:"#166534", display:"flex", alignItems:"center", gap:6 }}>
              📍 {DB_DISPENSARIES.find(d=>d.id===kioskDispensary)?.name}
            </div>
          )}

          {/* Aroma / vibe dropdown */}
          <select value={filterAroma} onChange={e=>{ setBrowseSearch(e.target.value ? "aroma:" + (e.target.value) : ""); }}
            style={{ flex:"1 1 150px", padding:"9px 12px", border:"1px solid " + (filterAroma?"#4a7c59":"#e8e4de"), borderRadius:10, fontSize:12, fontFamily:"'DM Mono',monospace", background: filterAroma?"#f0fdf4":"#fff", color: filterAroma?"#166534":"#6b7280", cursor:"pointer", outline:"none" }}>
            <option value="">All aromas</option>
            {AROMA_GROUPS.map(g=><option key={g.label} value={g.label}>{g.label}</option>)}
          </select>

          {/* Clear filters */}
          {(filterBrand || filterAroma || filterEffect || browseCategory || browseCity!=="any") && (
            <button onClick={()=>{ setBrowseSearch(""); setBrowseCategory(""); if(!kioskMode) setBrowseCity("any"); }}
              style={{ padding:"9px 14px", borderRadius:10, border:"1px solid #fca5a5", background:"#fff", color:"#dc2626", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              ✕ Clear
            </button>
          )}
        </div>

        {/* Row 2: Effect chips — plain language terpene filter */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:7 }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Filter by aroma profile</div>
            {filterEffect && (
              <button onClick={()=>setBrowseSearch("")}
                style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#dc2626", background:"none", border:"none", cursor:"pointer", padding:0 }}>
                Clear filter ×
              </button>
            )}
          </div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {EFFECT_FILTERS.map(ef => {
              const isActive = filterEffect === ef.label;
              return (
                <button key={ef.label}
                  onClick={()=> setBrowseSearch(isActive ? "" : "effect:" + (ef.label)) }
                  style={{ padding:"5px 12px", borderRadius:20, border:"1px solid " + (isActive ? ef.color : "#e8e4de"),
                    background: isActive ? ef.color : "#fff",
                    color: isActive ? "#fff" : "#6b7280",
                    fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap",
                    display:"flex", alignItems:"center", gap:5 }}>
                  {isActive && <span style={{ fontSize:10, opacity:0.85 }}>×</span>}
                  {ef.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 3: Sort controls — bidirectional */}
        <div style={{ background:"#f7f5f0", borderRadius:12, padding:"10px 14px", marginBottom:14 }}>
          <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8 }}>Sort by</div>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {/* Name */}
            <button onClick={()=>setBrowseSort("name")}
              style={{ padding:"5px 11px", borderRadius:8, border:"1px solid " + ((!browseSort||browseSort==="name")?"#4a7c59":"#e8e4de"),
                background:(!browseSort||browseSort==="name")?"#4a7c59":"#fff",
                color:(!browseSort||browseSort==="name")?"#fff":"#6b7280",
                fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              A–Z
            </button>
            {/* THC bidirectional */}
            {[["thc_asc","THC ↑ Low first"],["thc_desc","THC ↓ High first"]].map(([val,label])=>(
              <button key={val} onClick={()=>setBrowseSort(val)}
                style={{ padding:"5px 11px", borderRadius:8, border:"1px solid " + (browseSort===val?"#f59e0b":"#e8e4de"),
                  background: browseSort===val?"#fef3c7":"#fff",
                  color: browseSort===val?"#92400e":"#6b7280",
                  fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                {label}
              </button>
            ))}
            {/* Terpenes bidirectional */}
            {[["terp_asc","Terpenes ↑"],["terp_desc","Terpenes ↓"]].map(([val,label])=>(
              <button key={val} onClick={()=>setBrowseSort(val)}
                style={{ padding:"5px 11px", borderRadius:8, border:"1px solid " + (browseSort===val?"#4a7c59":"#e8e4de"),
                  background: browseSort===val?"#f0fdf4":"#fff",
                  color: browseSort===val?"#166534":"#6b7280",
                  fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                {label}
              </button>
            ))}
            {/* Effect-specific terpene sort (shown when effect filter active) */}
            {filterEffect && (() => {
              const ef = EFFECT_FILTERS.find(e=>e.label===filterEffect);
              if (!ef) return null;
              return [["desc","Most → Least"],["asc","Least → Most"]].map(([dir, label]) => {
                const val = (ef.terp) + "|" + (dir);
                return (
                  <button key={val} onClick={()=>setBrowseSort(val)}
                    style={{ padding:"5px 11px", borderRadius:8, border:"1px solid " + (browseSort===val?ef.color:"#e8e4de"),
                      background: browseSort===val?(ef.color) + "20":"#fff",
                      color: browseSort===val?ef.color:"#6b7280",
                      fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    {ef.label.split(" ").pop()} {label}
                  </button>
                );
              });
            })()}
          </div>
        </div>

        {/* Active filters & result count */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:6 }}>
          <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
            {browsed.length} in-stock product{browsed.length!==1?"s":""}
            {activeFilterLabel && <span> · <span style={{ color:"#4a7c59" }}>{activeFilterLabel}</span></span>}
          </div>
          {activeSortLabel && (
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", background:"#f7f5f0", padding:"3px 9px", borderRadius:8 }}>
              {activeSortLabel}
            </div>
          )}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:12 }}>
          {browsed.map(p => {
            const topTerps = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1]).slice(0,2);
            const effectTags = topTerps.map(([t]) => TERPENE_EFFECT_TAGS[t]).filter(Boolean);
            // Show the active effect terpene value if filtering by effect
            const activeEf = filterEffect ? EFFECT_FILTERS.find(e=>e.label===filterEffect) : null;
            const highlightTerpVal = activeEf ? p.terpenes[activeEf.terp] : null;
            // Or show terpene-specific sort value
            const [sortTerpKey] = browseSort.includes("|") ? browseSort.split("|") : [null];
            const sortTerpVal = sortTerpKey && !activeEf ? p.terpenes[sortTerpKey] : null;
            return (
              <div key={p.id} className="card card-click" style={{ padding:16 }} onClick={()=>setCViewProduct(p)}>
                <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{p.brand} · {p.category}</div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:15, fontWeight:400, color:"#1a2e1a", marginBottom:8, lineHeight:1.3 }}>{p.name}</div>
                {/* Effect highlight badge */}
                {(highlightTerpVal != null || sortTerpVal != null) && (() => {
                  const val = highlightTerpVal ?? sortTerpVal;
                  const ef = activeEf || EFFECT_FILTERS.find(e=>e.terp===sortTerpKey);
                  return (
                    <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:(ef?.color||"#4a7c59") + "18", border:"1px solid " + (ef?.color||"#4a7c59") + "40", borderRadius:8, padding:"3px 9px", marginBottom:8 }}>
                      <div style={{ width:7, height:7, borderRadius:"50%", background:ef?.color||"#4a7c59" }}/>
                      <span style={{ fontSize:10, color:ef?.color||"#166534", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>
                        {ef ? ef.label.split(" ").pop() : "Terp"} · {val ? ((val*100).toFixed(0)) + "mg/g" : "trace"}
                      </span>
                    </div>
                  );
                })()}
                <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:8 }}>
                  {effectTags.map((et,i)=>(
                    <span key={i} style={{ fontSize:9, background:(et.color) + "15", color:et.color, border:"1px solid " + (et.color) + "25", padding:"2px 7px", borderRadius:20 }}>{et.tag}</span>
                  ))}
                </div>
                <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:10 }}>
                  {(p.aroma||[]).slice(0,3).map(a=><span key={a} className="pill" style={{ fontSize:9 }}>{a}</span>)}
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:11, color:"#22c55e", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{p.thc}% THC</span>
                  <span style={{ fontSize:9, color:"#4a7c59", fontFamily:"'DM Mono',monospace" }}>{p.cultivation?.method}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Consumer Profile tab
  const ConsumerProfile = () => {
    const lookupPhone=cpLookupPhone, setLookupPhone=setCpLookupPhone;
    const lookupError=cpLookupError, setLookupError=setCpLookupError;
    const lookupResult=cpLookupResult, setLookupResult=setCpLookupResult;
    const ratingProduct=null, setRatingProduct=()=>{};

    // If tasteProfile has a saved phone, auto-load
    const phone = tasteProfile?.phone || lookupResult?.phone;
    const profile = phone ? profiles.find(p=>p.phone===phone) : null;
    const fp = profile ? buildTerpeneFingerprint(profile) : null;
    const history = profile?.productHistory || [];
    const answers = profile?.consumerAnswers || profile?.finderAnswers || tasteProfile?.answers || {};

    const handleLookup = () => {
      const raw = lookupPhone.replace(/\D/g,"");
      if (raw.length!==10) return setLookupError("Enter a valid 10-digit phone number.");
      const found = profiles.find(p=>p.phone===raw);
      if (!found) return setLookupError("No profile found. Create one after completing the finder.");
      setLookupResult(found); setLookupError("");
    };

    if (!profile && !tasteProfile) return (
      <div className="fade" style={{maxWidth:480,margin:"0 auto",padding:"28px 20px"}}>
        <h2 style={{fontFamily:"'Fraunces',serif",fontSize:22,fontWeight:300,color:"#1a2e1a",marginBottom:20}}>My Terpene Passport</h2>
        <div className="card" style={{padding:28,textAlign:"center",marginBottom:16}}>
          <div style={{fontSize:40,marginBottom:12}}>🪪</div>
          <p style={{fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:8}}>No passport yet</p>
          <p style={{fontSize:12,color:"#6b7280",marginBottom:20}}>Complete the terpene matcher and save your profile to build your passport.</p>
          <button onClick={()=>setConsumerTab("find")} className="btn-primary" style={{padding:"10px 24px",fontSize:12}}>Start Matcher →</button>
        </div>
        <div className="card" style={{padding:18}}>
          <div style={{fontSize:11,color:"#374151",marginBottom:12,fontWeight:500}}>Already have a passport? Look it up:</div>
          <input className="inp" value={lookupPhone} onChange={e=>setLookupPhone(e.target.value)} placeholder="Phone number" style={{marginBottom:10}}/>
          {lookupError&&<div style={{fontSize:11,color:"#991b1b",fontFamily:"'DM Mono',monospace",marginBottom:8}}>{lookupError}</div>}
          <button onClick={handleLookup} className="btn-primary" style={{width:"100%",padding:"10px 0"}}>Look Up →</button>
        </div>
      </div>
    );

    const topTerpenes = fp ? Object.entries(fp).sort((ea,eb)=>eb[1]-ea[1]).slice(0,3) : [];
    const feelLabel = {relax:"Earthy & musky",energy:"Citrusy & bright",focus:"Piney & herbal",body:"Gassy & pungent",sleep:"Floral & soft",social:"Fruity & candy",any:"No preference"};
    const formatLabel = {flower:"Dried flower",preroll:"Pre-roll",vape:"Vape/cartridge",edible:"Edible",oil:"Oil/tincture",beverage:"Beverage",concentrate:"Concentrate",any:"No preference"};

    return (
      <div className="fade" style={{maxWidth:520,margin:"0 auto",padding:"28px 20px"}}>
        {/* Header card */}
        <div className="card" style={{padding:22,marginBottom:14,background:"linear-gradient(135deg,#1a2e1a,#2d4a2d)",color:"#f0f4f0"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
            <div style={{flex:1}}>
              <div style={{fontSize:10,opacity:0.6,fontFamily:"'DM Mono',monospace",marginBottom:4}}>TERPENE PASSPORT</div>
              <div style={{fontFamily:"'Fraunces',serif",fontSize:24,fontWeight:300,marginBottom:4}}>{profile?.name || "My Profile"}</div>
              {profile?.city&&<div style={{fontSize:12,opacity:0.7,fontFamily:"'DM Mono',monospace"}}>📍 {profile.city}</div>}
              <div style={{fontSize:10,opacity:0.5,fontFamily:"'DM Mono',monospace",marginTop:8}}>
                {history.length} products tried · {history.filter(h=>h.rating==="up").length} liked
              </div>
            </div>
            {fp && <TerpeneRadar fingerprint={fp} size={120}/>}
          </div>
          {topTerpenes.length>0&&(
            <div style={{marginTop:14,display:"flex",gap:6,flexWrap:"wrap"}}>
              {topTerpenes.map(([t,v])=>(
                <span key={t} style={{fontSize:10,background:"rgba(255,255,255,0.15)",color:"#f0f4f0",padding:"3px 10px",borderRadius:20,fontFamily:"'DM Mono',monospace"}}>
                  {t} {v}%
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Preferences */}

        {/* Profile actions */}
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
          <button onClick={()=>{
            const newName = prompt("Update your name:", profile?.name||"");
            if (newName && newName.trim() && phone) {
              upsertProfile({ ...profile, phone, name: newName.trim() });
            }
          }} style={{flex:1,padding:"9px 0",borderRadius:10,border:"1px solid #e8e4de",background:"#f9fafb",color:"#374151",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>
            ✏️ Edit name
          </button>
          <button onClick={()=>{
            if (phone && window.confirm("Delete your profile and all history? This cannot be undone.")) {
              setProfiles(prev => { const next = prev.filter(p=>p.phone!==phone); saveProfiles(next); return next; });
              setCpLookupResult(null); setCpLookupPhone(""); setTasteProfile(null);
              showToast("Profile deleted", "warning");
            }
          }} style={{flex:1,padding:"9px 0",borderRadius:10,border:"1px solid #fca5a5",background:"#fef2f2",color:"#991b1b",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:"pointer"}}>
            🗑 Delete profile
          </button>
        </div>

        {/* Preferences */}
        <div className="card" style={{padding:18,marginBottom:14}}>
          <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>Aroma Preferences</div>
          {[
            ["Terpene profile", feelLabel[answers.vibe||answers.feeling]||"—"],
            ["Format", formatLabel[answers.format]||"—"],
            ["Aroma preference", feelLabel[answers.vibe||answers.feeling]||answers.vibe||"—"],
            ["Potency", answers.strength ? answers.strength : "—"],
          ].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #f3f4f6"}}>
              <span style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{l}</span>
              <span style={{fontSize:12,color:"#1a2e1a",fontWeight:500}}>{v}</span>
            </div>
          ))}
        </div>

        {/* Product history */}
        <div className="card" style={{padding:18,marginBottom:14}}>
          <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>
            Products Tried ({history.length})
          </div>
          {history.length===0&&<div style={{fontSize:12,color:"#9ca3af",textAlign:"center",padding:"16px 0"}}>No products logged yet — rate products from the Browse tab.</div>}
          {history.map((h,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:i<history.length-1?"1px solid #f3f4f6":"none"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:13,color:"#1a2e1a",fontWeight:500}}>{h.name}</div>
                <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{h.brand} · {h.thc}% THC · {new Date(h.triedAt).toLocaleDateString("en-CA")}</div>
              </div>
              <div style={{display:"flex",gap:6,flexShrink:0}}>
                {[["up","👍"],["down","👎"]].map(([r,e])=>(
                  <button key={r} onClick={()=>{ if(phone) addProductToHistory(phone,{id:h.productId,...h},r); }}
                    style={{background:h.rating===r?"#f0fdf4":"none",border:h.rating===r?"1px solid #bbf7d0":"1px solid #e8e4de",
                      borderRadius:8,padding:"4px 10px",cursor:"pointer",fontSize:14}}>
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Wishlist */}
        {(profile?.wishlist||[]).length>0&&(
          <div className="card" style={{padding:18,marginBottom:14}}>
            <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:12,textTransform:"uppercase",letterSpacing:"0.08em"}}>
              ⭐ Wishlist ({(profile.wishlist||[]).length})
            </div>
            {(profile.wishlist||[]).map((w,i)=>{
              const inStock = ALL_PRODUCTS.find(p=>p.id===w.productId)?.in_stock;
              return (
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:i<(profile.wishlist||[]).length-1?"1px solid #f3f4f6":"none"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,color:"#1a2e1a",fontWeight:500}}>{w.name}</div>
                    <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>{w.brand}</div>
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    {inStock
                      ?<span style={{fontSize:10,background:"#d1fae5",color:"#065f46",border:"1px solid #6ee7b7",padding:"2px 8px",borderRadius:20}}>✓ In Stock</span>
                      :<span style={{fontSize:10,background:"#fef3c7",color:"#92400e",padding:"2px 8px",borderRadius:20}}>Waiting</span>}
                    <button onClick={()=>{ if(phone) toggleWishlist(phone,{id:w.productId,...w}); }}
                      style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",fontSize:14}}>✕</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Terpene Journey Timeline */}
        {(profile?.journey||[]).length>1&&(
          <div className="card" style={{padding:18,marginBottom:14}}>
            <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.08em"}}>
              🗺 Terpene Journey
            </div>
            <div style={{display:"flex",gap:12,overflowX:"auto",paddingBottom:8}}>
              {(profile.journey||[]).map((snap,i)=>(
                <div key={i} style={{flexShrink:0,textAlign:"center"}}>
                  <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:6}}>
                    {new Date(snap.date).toLocaleDateString("en-CA",{month:"short",day:"numeric"})}
                  </div>
                  <TerpeneRadar fingerprint={snap.fingerprint} size={90}/>
                  {snap.label&&<div style={{fontSize:9,color:"#6b7280",fontFamily:"'DM Mono',monospace",marginTop:4,maxWidth:90}}>{snap.label}</div>}
                </div>
              ))}
            </div>
            <div style={{fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginTop:8}}>
              {(profile.journey||[]).length} snapshots — your preferences evolve as you rate products
            </div>
          </div>
        )}

        <div style={{display:"flex",gap:10,marginBottom:10}}>
          <button onClick={()=>{
            const terpData = topTerpenes.map(([t,v]) => {
              const info = TERPENES[t];
              const tag = TERPENE_EFFECT_TAGS[t];
              return "<div style=\"display:flex;align-items:center;gap:10;margin-bottom:8px;padding:8px 10px;background:#f7f5f0;border-radius:8px;\">\n                <div style=\"width:10px;height:10px;border-radius:50%;background:" + (info?.color||'#4a7c59') + ";flex-shrink:0;\"></div>\n                <div>\n                  <div style=\"font-size:12px;font-weight:600;color:#1a2e1a;\">" + (t) + "</div>\n                  <div style=\"font-size:11px;color:#6b7280;\">" + (tag?.tag||'') + " · " + (v) + "% in profile</div>\n                  <div style=\"font-size:10px;color:#9ca3af;\">" + (info?.plain?.split('.')[0]||'') + ".</div>\n                </div>\n              </div>";
            }).join('');

            const historyRows = history.slice(0,8).map(h =>
              "<div style=\"display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #f3f4f6;font-size:11px;\">\n                <span style=\"color:#1a2e1a;font-weight:500;\">" + (h.name) + " <span style=\"color:#9ca3af;font-weight:400;\">· " + (h.brand) + "</span></span>\n                <span style=\"color:" + (h.rating==='up'?'#16a34a':'#9ca3af') + "\">" + (h.rating==='up'?'👍 Liked':h.rating==='down'?'👎 Passed':'—') + "</span>\n              </div>"
            ).join('');

            const wishlistRows = (profile?.wishlist||[]).slice(0,5).map(w =>
              "<div style=\"font-size:11px;color:#1a2e1a;padding:4px 0;border-bottom:1px solid #f3f4f6;\">" + (w.name) + " · <span style=\"color:#9ca3af;\">" + (w.brand) + "</span></div>"
            ).join('');

            const win = window.open('','_blank');
            win.document.write("<!DOCTYPE html><html><head>\n              <title>iTerps Terpene Passport — " + (profile?.name||'Consumer') + "</title>\n              <link href=\"https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Fraunces:ital,wght@0,300;0,400;1,300&display=swap\" rel=\"stylesheet\">\n              <style>\n                body{font-family:'DM Mono',monospace;background:#fff;color:#1a2e1a;margin:0;padding:0;}\n                @media print{body{margin:0;} .no-print{display:none;}}\n              </style>\n            </head><body>\n              <div style=\"max-width:540px;margin:0 auto;padding:32px 28px;\">\n                <!-- Header -->\n                <div style=\"background:#1a2e1a;border-radius:14px;padding:24px;color:#f0f4f0;margin-bottom:20px;\">\n                  <div style=\"font-size:9px;letter-spacing:0.1em;opacity:0.5;margin-bottom:4px;\">ITERPS · TERPENE PASSPORT</div>\n                  <div style=\"font-family:'Fraunces',serif;font-size:28px;font-weight:300;margin-bottom:4px;\">" + (profile?.name||'Consumer Profile') + "</div>\n                  " + (profile?.city?"<div style=\"font-size:12px;opacity:0.6;\">📍 " + (profile.city) + "</div>":'') + "\n                  <div style=\"font-size:10px;opacity:0.4;margin-top:8px;\">" + (history.length) + " products tried · " + (history.filter(h=>h.rating==='up').length) + " liked · Generated " + (new Date().toLocaleDateString('en-CA')) + "</div>\n                </div>\n\n                <!-- Top Terpenes -->\n                <div style=\"margin-bottom:20px;\">\n                  <div style=\"font-size:9px;color:#9ca3af;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:10px;\">Primary Terpene Profile</div>\n                  " + (terpData || '<div style="font-size:12px;color:#9ca3af;">Complete the finder to build your terpene profile.</div>') + "\n                </div>\n\n                <!-- Preferences -->\n                <div style=\"margin-bottom:20px;padding:16px;background:#f7f5f0;border-radius:10px;\">\n                  <div style=\"font-size:9px;color:#9ca3af;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:10px;\">Stated Preferences</div>\n                  " + ([
                    ['Aroma', {relax:"Earthy & musky",energy:"Citrusy & bright",focus:"Piney & herbal",social:"Spicy & complex",body:"Earthy & hoppy",sleep:"Floral & soft"}[answers.vibe||answers.feeling]],
                    ['Format', {flower:"Dried flower",preroll:"Pre-roll",vape:"Vape/cartridge",edible:"Edible",oil:"Oil/tincture",beverage:"Beverage",concentrate:"Concentrate"}[answers.format]||"—"],
                    ['Potency', answers.strength],
                  ].filter(([,v])=>v).map(([l,v])=>"\n                    <div style=\"display:flex;justify-content:space-between;font-size:11px;padding:4px 0;border-bottom:1px solid #e8e4de;\">\n                      <span style=\"color:#9ca3af;\">" + (l) + "</span><span style=\"font-weight:500;\">" + (v) + "</span>\n                    </div>").join('')) + "\n                </div>\n\n                <!-- Product History -->\n                " + (history.length>0?"\n                <div style=\"margin-bottom:20px;\">\n                  <div style=\"font-size:9px;color:#9ca3af;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:10px;\">Products Tried (" + (history.length) + ")</div>\n                  " + (historyRows) + "\n                </div>":'') + "\n\n                <!-- Wishlist -->\n                " + ((profile?.wishlist||[]).length>0?"\n                <div style=\"margin-bottom:20px;\">\n                  <div style=\"font-size:9px;color:#9ca3af;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:10px;\">⭐ Want to Try (" + (profile.wishlist.length) + ")</div>\n                  " + (wishlistRows) + "\n                </div>":'') + "\n\n                <!-- Footer -->\n                <div style=\"margin-top:28px;padding-top:16px;border-top:1px solid #e8e4de;font-size:9px;color:#d1d5db;text-align:center;line-height:1.7;\">\n                  <div style=\"font-family:'Fraunces',serif;font-size:13px;color:#1a2e1a;margin-bottom:4px;\">⚗️ iTerps</div>\n                  Educational cannabis compound information · Cannabis Act (S.C. 2018, c. 16) Compliant<br/>\n                  This document does not constitute medical advice or a product recommendation.\n                </div>\n              </div>\n              <div class=\"no-print\" style=\"text-align:center;padding:16px;\">\n                <button onclick=\"window.print()\" style=\"padding:10px 28px;background:#1a2e1a;color:#fff;border:none;border-radius:8px;font-size:13px;cursor:pointer;font-family:'DM Mono',monospace;\">Print / Save as PDF</button>\n              </div>\n            </body></html>");
            win.document.close();
          }} style={{flex:1,padding:"10px 0",borderRadius:10,border:"1px solid #e8e4de",background:"#fff",color:"#374151",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            🪪 Export Passport
          </button>
          <button onClick={()=>setPassportQR(v=>!v)}
            style={{flex:1,padding:"10px 0",borderRadius:10,border:"1px solid #e8e4de",background:passportQR?"#1a2e1a":"#fff",color:passportQR?"#fff":"#374151",fontSize:12,fontFamily:"'DM Mono',monospace",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            📱 {passportQR?"Hide QR":"Show QR"}
          </button>
          <button onClick={()=>{ setTasteProfile(null); setConsumerTab("find"); setPickerCity("any"); setPickerDisp("any"); setCStep(0); setCAnswers({}); setCResults(null); }}
            className="btn-ghost" style={{flex:1,fontSize:12}}>↺ Retake</button>
          <button onClick={()=>setConsumerTab("browse")} className="btn-primary" style={{flex:2,fontSize:12}}>Browse →</button>
        </div>
        {passportQR && profile && <PassportQRSection profile={profile} />}
      </div>
    );
  };

  // ── DISPENSARY DETAIL PAGE ─────────────────────────────────
  const DispensaryPage = ({ dispId }) => {
    const disp = DB_DISPENSARIES.find(d => d.id === dispId);
    if (!disp) return null;
    const prods = ALL_PRODUCTS.filter(p => p.dispensary_id === dispId && p.in_stock);
    const terpFreq = {};
    prods.forEach(p => Object.entries(p.terpenes).forEach(([t,v]) => {
      if (!terpFreq[t]) terpFreq[t] = { count:0, totalPct:0 };
      terpFreq[t].count++;
      terpFreq[t].totalPct += v;
    }));
    const topTerps = Object.entries(terpFreq).sort(([,a],[,b]) => b.count - a.count).slice(0,5);
    const avgThc = prods.length ? (prods.reduce((s,p)=>s+p.thc,0)/prods.length).toFixed(1) : 0;
    const categories = [...new Set(prods.map(p=>p.category))];
    const labVerified = prods.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").length;
    const verifiedPct = prods.length ? Math.round((labVerified/prods.length)*100) : 0;

    return (
      <div className="fade" style={{ maxWidth:780, margin:"0 auto", padding:"28px 20px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <button onClick={()=>setDispDetailId(null)} style={{ background:"none", border:"none", color:"#6b7280", fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace", display:"flex", alignItems:"center", gap:4 }}>
            ← Back
          </button>
          <button onClick={()=>{
            // Build gap data
            const networkTf = {}; const dispTf = {};
            ALL_PRODUCTS.forEach(p => Object.keys(p.terpenes).forEach(t => { networkTf[t]=(networkTf[t]||0)+1; }));
            prods.forEach(p => Object.keys(p.terpenes).forEach(t => { dispTf[t]=(dispTf[t]||0)+1; }));
            const terpKeys = Object.keys(TERPENES);
            const gapRows = terpKeys.map(t => {
              const netPct = Math.round((networkTf[t]||0)/ALL_PRODUCTS.length*100);
              const dPct = prods.length ? Math.round((dispTf[t]||0)/prods.length*100) : 0;
              const gap = netPct - dPct;
              return { t, netPct, dPct, gap, color: TERPENES[t]?.color||"#4a7c59" };
            }).sort((a,b) => b.gap - a.gap);

            const win = window.open('','_blank');
            win.document.write("<!DOCTYPE html><html><head>\n              <title>iTerps Terpene Gap Report — " + (disp.name) + "</title>\n              <link href=\"https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Fraunces:ital,wght@0,300;0,400&display=swap\" rel=\"stylesheet\">\n              <style>body{font-family:'DM Mono',monospace;background:#fff;color:#1a2e1a;margin:0;padding:0;} @media print{.no-print{display:none;}}</style>\n            </head><body>\n            <div style=\"max-width:620px;margin:0 auto;padding:36px 28px;\">\n              <div style=\"background:#1a2e1a;border-radius:14px;padding:24px 24px 20px;color:#f0f4f0;margin-bottom:24px;\">\n                <div style=\"font-size:9px;letter-spacing:0.1em;opacity:0.4;margin-bottom:4px;\">ITERPS · TERPENE GAP REPORT</div>\n                <div style=\"font-family:'Fraunces',serif;font-size:26px;font-weight:300;margin-bottom:4px;\">" + (disp.name) + "</div>\n                <div style=\"font-size:11px;opacity:0.6;\">📍 " + (disp.city) + " · " + (prods.length) + " in-stock SKUs · Generated " + (new Date().toLocaleDateString('en-CA')) + "</div>\n              </div>\n\n              <div style=\"margin-bottom:20px;\">\n                <div style=\"font-size:9px;color:#9ca3af;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:12px;\">Inventory Summary</div>\n                <div style=\"display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:16px;\">\n                  " + ([
                    [prods.length, "In-Stock SKUs"],
                    [(avgThc) + "%", "Avg THC"],
                    [(verifiedPct) + "%", "Lab Verified"],
                  ].map(([v,l])=>"<div style=\"background:#f7f5f0;border-radius:8px;padding:12px 14px;\">\n                    <div style=\"font-family:'Fraunces',serif;font-size:22px;font-weight:300;color:#4a7c59;\">" + (v) + "</div>\n                    <div style=\"font-size:10px;color:#9ca3af;\">" + (l) + "</div>\n                  </div>").join('')) + "\n                </div>\n              </div>\n\n              <div style=\"margin-bottom:24px;\">\n                <div style=\"font-size:9px;color:#9ca3af;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:12px;\">Terpene Coverage vs. BC Network Average</div>\n                <div style=\"font-size:10px;color:#6b7280;margin-bottom:12px;line-height:1.6;\">Each bar shows what % of your inventory carries this terpene vs. the network average across " + (ALL_PRODUCTS.length) + " BC SKUs. Gap = opportunity to fill with targeted LP sourcing.</div>\n                " + (gapRows.map(({t,netPct,dPct,gap,color})=>"\n                <div style=\"margin-bottom:10px;\">\n                  <div style=\"display:flex;justify-content:space-between;margin-bottom:3px;\">\n                    <div style=\"display:flex;align-items:center;gap:6px;\">\n                      <div style=\"width:8px;height:8px;border-radius:50%;background:" + (color) + ";flex-shrink:0;\"></div>\n                      <span style=\"font-size:11px;font-weight:500;\">" + (t) + "</span>\n                    </div>\n                    <span style=\"font-size:10px;color:" + (gap>15?"#dc2626":gap>5?"#f59e0b":"#16a34a") + ";font-weight:600;\">\n                      " + (gap>0?"Gap: +" + (gap) + "%":gap<0?"Surplus: " + (Math.abs(gap)) + "%":"On par") + "\n                    </span>\n                  </div>\n                  <div style=\"position:relative;height:8px;background:#f3f4f6;border-radius:4px;\">\n                    <div style=\"position:absolute;height:8px;background:" + (color) + ";opacity:0.35;border-radius:4px;width:" + (netPct) + "%;\"></div>\n                    <div style=\"position:absolute;height:8px;background:" + (color) + ";border-radius:4px;width:" + (dPct) + "%;\"></div>\n                  </div>\n                  <div style=\"display:flex;gap:12px;margin-top:3px;\">\n                    <span style=\"font-size:9px;color:rgba(0,0,0,0.3);\">■ Your store: " + (dPct) + "%</span>\n                    <span style=\"font-size:9px;color:rgba(0,0,0,0.2);\">■ Network avg: " + (netPct) + "%</span>\n                  </div>\n                </div>").join('')) + "\n              </div>\n\n              <div style=\"background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:16px;margin-bottom:24px;\">\n                <div style=\"font-size:10px;color:#166534;font-weight:600;margin-bottom:8px;\">Top sourcing priorities based on this gap analysis</div>\n                " + (gapRows.filter(g=>g.gap>10).slice(0,3).map(({t,gap})=>"\n                  <div style=\"font-size:11px;color:#374151;padding:4px 0;border-bottom:1px solid #d1fae5;\">\n                    " + (t) + " — " + (gap) + "% below network average. Consider sourcing 1–2 additional SKUs dominant in this compound.\n                  </div>").join('') || '<div style="font-size:11px;color:#6b7280;">Your inventory is well-balanced across all major terpenes.</div>') + "\n              </div>\n\n              <div style=\"margin-top:28px;padding-top:14px;border-top:1px solid #e8e4de;font-size:9px;color:#d1d5db;text-align:center;line-height:1.7;\">\n                <div style=\"font-family:'Fraunces',serif;font-size:13px;color:#1a2e1a;margin-bottom:4px;\">⚗️ iTerps · Terpene Intelligence Platform</div>\n                For LP sourcing conversations only. Not for consumer-facing use. Cannabis Act (S.C. 2018, c. 16) Compliant.\n              </div>\n            </div>\n            <div class=\"no-print\" style=\"text-align:center;padding:16px;\">\n              <button onclick=\"window.print()\" style=\"padding:10px 28px;background:#1a2e1a;color:#fff;border:none;border-radius:8px;font-size:13px;cursor:pointer;font-family:'DM Mono',monospace;\">Print / Save as PDF</button>\n            </div>\n            </body></html>");
            win.document.close();
          }} style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:10, border:"1px solid #e8e4de", background:"#fff", color:"#374151", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
            📄 Share Gap Report
          </button>
        </div>

        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,#1a2e1a,#2d4a2d)", borderRadius:16, padding:"28px 24px", color:"#f0f4f0", marginBottom:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ fontSize:10, opacity:0.5, fontFamily:"'DM Mono',monospace", marginBottom:4, letterSpacing:"0.08em" }}>LICENSED DISPENSARY · BC</div>
              <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:30, fontWeight:300, marginBottom:6, lineHeight:1.2 }}>{disp.name}</h1>
              <div style={{ fontSize:12, opacity:0.7, fontFamily:"'DM Mono',monospace", marginBottom:4 }}>📍 {disp.address || disp.city}</div>
              {disp.neighbourhood && <div style={{ fontSize:11, opacity:0.5, fontFamily:"'DM Mono',monospace" }}>{disp.neighbourhood} · {disp.city}</div>}
            </div>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
              {[
                [prods.length, "In-Stock SKUs"],
                [(avgThc) + "%", "Avg THC"],
                [(verifiedPct) + "%", "Lab Verified"],
              ].map(([val,lbl]) => (
                <div key={lbl} style={{ textAlign:"center" }}>
                  <div style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#4ade80" }}>{val}</div>
                  <div style={{ fontSize:9, opacity:0.6, fontFamily:"'DM Mono',monospace" }}>{lbl}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Terpene profile */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
          <div className="card" style={{ padding:18 }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.08em" }}>Top Terpenes in Inventory</div>
            {topTerps.map(([t, data]) => {
              const info = TERPENES[t];
              const tag = TERPENE_EFFECT_TAGS[t];
              const barW = Math.round((data.count / prods.length) * 100);
              return (
                <div key={t} style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:info?.color||"#4a7c59", flexShrink:0 }} />
                      <span style={{ fontSize:11, color:"#1a2e1a", fontWeight:500 }}>{t}</span>
                    </div>
                    <span style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{data.count} SKUs</span>
                  </div>
                  <div style={{ height:4, background:"#f3f4f6", borderRadius:2 }}>
                    <div style={{ height:4, background:info?.color||"#4a7c59", borderRadius:2, width:(barW) + "%", opacity:0.7 }} />
                  </div>
                  {tag && <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{tag.tag}</div>}
                </div>
              );
            })}
          </div>

          <div className="card" style={{ padding:18 }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.08em" }}>Inventory Snapshot</div>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Product Types</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {categories.map(c => {
                  const count = prods.filter(p=>p.category===c).length;
                  return <span key={c} style={{ fontSize:10, background:"#f0fdf4", border:"1px solid #bbf7d0", color:"#166534", padding:"3px 9px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>{c} ({count})</span>;
                })}
              </div>
            </div>
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>Data Verification</div>
              <div style={{ height:8, background:"#f3f4f6", borderRadius:4, marginBottom:4 }}>
                <div style={{ height:8, background:"#4a7c59", borderRadius:4, width:(verifiedPct) + "%" }} />
              </div>
              <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{labVerified} of {prods.length} SKUs have lab-verified terpene data</div>
            </div>
            {disp.bc_verified && (
              <div style={{ display:"inline-flex", alignItems:"center", gap:5, background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, padding:"5px 10px" }}>
                <span style={{ fontSize:11 }}>🏛️</span>
                <span style={{ fontSize:10, color:"#1d4ed8", fontFamily:"'DM Mono',monospace" }}>BC Licensed Retailer</span>
              </div>
            )}
          </div>
        </div>

        {/* Product list */}
        <div className="card" style={{ padding:0, overflow:"hidden" }}>
          <div style={{ padding:"14px 18px", borderBottom:"1px solid #f3f4f6", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:12, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace" }}>In-Stock Products</div>
            <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{prods.length} SKUs</div>
          </div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
              <thead>
                <tr>{["Product","THC","CBD","Top Terpene","Category","Data"].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"10px 14px", borderBottom:"2px solid #f3f4f6", color:"#9ca3af", fontSize:9, letterSpacing:"0.06em" }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {prods.map((p, i) => {
                  const topTerp = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
                  const ti = topTerp ? TERPENES[topTerp[0]] : null;
                  return (
                    <tr key={p.id} style={{ borderBottom: i < prods.length-1 ? "1px solid #f9fafb" : "none", cursor:"pointer" }}
                      onClick={()=>{ viewProduct(p) }}>
                      <td style={{ padding:"10px 14px" }}>
                        <div style={{ fontWeight:500, color:"#1a2e1a" }}>{p.name}</div>
                        <div style={{ fontSize:9, color:"#9ca3af" }}>{p.brand}</div>
                      </td>
                      <td style={{ padding:"10px 14px", color:"#22c55e", fontWeight:600 }}>{p.thc}%</td>
                      <td style={{ padding:"10px 14px", color:"#3b82f6" }}>{p.cbd}%</td>
                      <td style={{ padding:"10px 14px" }}>
                        {topTerp && (
                          <span style={{ display:"inline-flex", alignItems:"center", gap:5 }}>
                            <span style={{ width:7,height:7,borderRadius:"50%",background:ti?.color||"#6b7280",display:"inline-block" }} />
                            {topTerp[0]}
                          </span>
                        )}
                      </td>
                      <td style={{ padding:"10px 14px", color:"#6b7280" }}>{p.category}</td>
                      <td style={{ padding:"10px 14px" }}>
                        <span className={"badge " + (p.terpene_source==="lab_tested"?"verified":p.terpene_source==="lp_provided"?"lp":"estimated")} style={{ fontSize:9 }}>
                          {p.terpene_source==="lab_tested"?"✓ Lab":p.terpene_source==="lp_provided"?"✓ LP":"~ Est."}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ── PLAN MODAL ────────────────────────────────────────────
  const DISP_PLANS = [
    { id:"starter", name:"Starter", price:"$99", per:"/mo", color:"#4a7c59", badge:"",
      features:["Staff finder + explorer","Customer terpene profiles","Budtender training module","Basic inventory dashboard","Email support"] },
    { id:"pro", name:"Pro", price:"$199", per:"/mo", color:"#1d4ed8", badge:"Most Popular",
      features:["Everything in Starter","CSV inventory import + live sync","Branded QR shelf cards (PDF export)","Dispensary gap reports (shareable)","Batch history tracking","Priority support"] },
    { id:"enterprise", name:"Enterprise", price:"$399", per:"/mo", color:"#7c3aed", badge:"Full Stack",
      features:["Everything in Pro","White-label staff portal (your branding)","Multi-location management","LP direct ordering integration","Dedicated onboarding + account manager","Custom data export"] },
  ];
  const LP_PLANS = [
    { id:"basic", name:"Basic Listing", price:"Free", per:"", color:"#4a7c59", badge:"",
      features:["Products listed across all dispensaries","COA upload (manual)","Standard LP profile","Consumer visibility on product pages"] },
    { id:"analytics", name:"Analytics", price:"$299", per:"/mo", color:"#1d4ed8", badge:"Best Value",
      features:["Everything in Basic","✓ Verified LP badge on all SKUs","Aggregated consumer demand data","Dispensary gap analysis (which stores need your products)","Monthly terpene trend reports","Retailer outreach list"] },
    { id:"featured", name:"Featured Partner", price:"$499", per:"/mo", color:"#7c3aed", badge:"Maximum Reach",
      features:["Everything in Analytics","⭐ Featured placement in search + finder results","Co-branded consumer education content","Batch COA auto-import (API)","SR&ED-eligible data documentation","Quarterly strategy call"] },
  ];

;

  // ── STOCK ALERT MODAL ─────────────────────────────────────
  const StockAlertModal = () => {
    if (!showAlertModal || !alertProduct) return null;
    const existing = stockAlerts.find(a => a.productId === alertProduct.id && !a.fired);
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
        onClick={e=>{ if(e.target===e.currentTarget){ setShowAlertModal(false); setAlertDone(false); setAlertEmail(""); }}}>
        <div style={{ background:"#fff", borderRadius:16, padding:28, maxWidth:380, width:"100%" }}>
          {alertDone || existing ? (
            <div style={{ textAlign:"center" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🔔</div>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>Alert Set</h3>
              <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.7, marginBottom:20 }}>
                We will email you when <strong>{alertProduct.name}</strong> is back in stock at {DB_DISPENSARIES.find(d=>d.id===alertProduct.dispensary_id)?.name || "this dispensary"}.
              </p>
              <button onClick={()=>{ setShowAlertModal(false); setAlertDone(false); setAlertEmail(""); }}
                style={{ padding:"10px 28px", borderRadius:8, border:"none", background:"#1a2e1a", color:"#f0f4f0", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Done</button>
            </div>
          ) : (
            <>
              <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:300, color:"#1a2e1a", marginBottom:4 }}>🔔 Stock Alert</h3>
              <p style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.6, marginBottom:16 }}>
                Get notified when <strong>{alertProduct.name}</strong> is back in stock. Free, no account needed.
              </p>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>YOUR EMAIL</div>
              <input value={alertEmail} onChange={e=>setAlertEmail(e.target.value)}
                type="email" placeholder="you@email.com"
                onKeyDown={e=>{ if(e.key==="Enter" && alertEmail.includes("@")){ saveStockAlert({ productId:alertProduct.id, productName:alertProduct.name, brand:alertProduct.brand, dispensaryId:alertProduct.dispensary_id, email:alertEmail }); setAlertDone(true); }}}
                style={{ width:"100%", padding:"10px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:13, fontFamily:"'DM Mono',monospace", boxSizing:"border-box", outline:"none", marginBottom:10 }}
                autoFocus />
              <button onClick={()=>{
                if (!alertEmail.includes("@")) return;
                saveStockAlert({ productId:alertProduct.id, productName:alertProduct.name, brand:alertProduct.brand, dispensaryId:alertProduct.dispensary_id, email:alertEmail });
                setAlertDone(true);
                  fireEmailTrigger('stock_alert_set', { product: alertProduct?.name, email: alertEmail }); trackEvent('stock_alert_set', { product: alertProduct?.name, productId: alertProduct?.id });
              }} style={{ width:"100%", padding:"10px 0", borderRadius:8, border:"none", background: alertEmail.includes("@") ? "#1a2e1a" : "#e8e4de", color: alertEmail.includes("@") ? "#f0f4f0" : "#9ca3af", fontSize:13, fontFamily:"'DM Mono',monospace", cursor: alertEmail.includes("@") ? "pointer" : "default", marginBottom:8 }}>
                Notify Me →
              </button>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textAlign:"center" }}>Free for consumers. No spam, unsubscribe anytime.</div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── DISPENSARY ONBOARD MODAL ─────────────────────────────
  const ONBOARD_STEPS = [
    { label:"Your store", fields:[
      { key:"name", label:"Dispensary Name", placeholder:"Green Room Cannabis Co.", required:true },
      { key:"city", label:"City", placeholder:"Vancouver, BC", required:true },
    ]},
    { label:"Contact", fields:[
      { key:"email", label:"Work Email", placeholder:"manager@yourstore.com", type:"email", required:true },
      { key:"phone", label:"Phone", placeholder:"+1 604 555 0100", required:false },
    ]},
    { label:"Your setup", fields:[
      { key:"pos", label:"POS System", placeholder:"Cova / Greenline / Dutchie / Other", required:false },
      { key:"skus", label:"Approx. SKU Count", placeholder:"e.g. 150", required:false },
      { key:"interest", label:"What’s your biggest pain point?", placeholder:"Staff training, inventory data, customer recommendations...", required:false, textarea:true },
    ]},
  ];

  const DispensaryOnboardModal = () => {
    if (!showDispOnboard) return null;
    const step = ONBOARD_STEPS[dispOnboardStep];
    const isLast = dispOnboardStep === ONBOARD_STEPS.length - 1;

    if (dispOnboardDone) return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
        <div style={{ background:"#fff", borderRadius:20, padding:36, maxWidth:420, width:"100%", textAlign:"center" }}>
          <div style={{ fontSize:44, marginBottom:12 }}>🌿</div>
          <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>You&#39;re in the queue</h2>
          <p style={{ fontSize:13, color:"#6b7280", fontFamily:"'DM Mono',monospace", lineHeight:1.7, marginBottom:20 }}>
            We&#39;ll reach out to <strong>{dispOnboardData.email}</strong> within 1 business day to set up your staff portal and get your inventory live.
          </p>
          <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"12px 16px", marginBottom:20, fontSize:11, color:"#166534", fontFamily:"'DM Mono',monospace", textAlign:"left", lineHeight:1.8 }}>
            ✓ 14-day free trial — no credit card needed<br/>
            ✓ Onboarding call to import your inventory<br/>
            ✓ Staff portal live in under 24 hours
          </div>
          <button onClick={()=>{ setShowDispOnboard(false); setDispOnboardDone(false); setDispOnboardStep(0); setDispOnboardData({ name:"", city:"", email:"", phone:"", pos:"", skus:"", interest:"" }); }}
            style={{ padding:"12px 32px", borderRadius:10, border:"none", background:"#1a2e1a", color:"#f0f4f0", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Done</button>
        </div>
      </div>
    );

    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
        onClick={e=>{ if(e.target===e.currentTarget) setShowDispOnboard(false); }}>
        <div style={{ background:"#fff", borderRadius:20, padding:28, maxWidth:440, width:"100%" }}>
          {/* Progress */}
          <div style={{ display:"flex", gap:6, marginBottom:20 }}>
            {ONBOARD_STEPS.map((s,i) => (
              <div key={i} style={{ flex:1, height:3, borderRadius:3, background: i <= dispOnboardStep ? "#1a2e1a" : "#e8e4de", transition:"background 0.3s" }}/>
            ))}
          </div>
          <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.08em" }}>Step {dispOnboardStep+1} of {ONBOARD_STEPS.length}</div>
          <h3 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#1a2e1a", marginBottom:16 }}>
            {dispOnboardStep === 0 ? "Dispensary setup" : dispOnboardStep === 1 ? "How do we reach you?" : "Tell us about your store"}
          </h3>
          <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:20 }}>
            {step.fields.map(f => (
              <div key={f.key}>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>
                  {f.label}{f.required && <span style={{ color:"#dc2626" }}> *</span>}
                </div>
                {f.textarea ? (
                  <textarea value={dispOnboardData[f.key]} onChange={e=>setDispOnboardData(d=>({...d,[f.key]:e.target.value}))}
                    placeholder={f.placeholder} rows={3}
                    style={{ width:"100%", padding:"10px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:13, fontFamily:"'DM Mono',monospace", boxSizing:"border-box", outline:"none", resize:"vertical" }}/>
                ) : (
                  <input value={dispOnboardData[f.key]} onChange={e=>setDispOnboardData(d=>({...d,[f.key]:e.target.value}))}
                    type={f.type||"text"} placeholder={f.placeholder}
                    style={{ width:"100%", padding:"10px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:13, fontFamily:"'DM Mono',monospace", boxSizing:"border-box", outline:"none" }}/>
                )}
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {dispOnboardStep > 0 && (
              <button onClick={()=>setDispOnboardStep(s=>s-1)}
                style={{ flex:1, padding:"11px 0", borderRadius:10, border:"1px solid #e8e4de", background:"#fff", color:"#374151", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>← Back</button>
            )}
            <button onClick={()=>{
              const required = step.fields.filter(f=>f.required);
              const missing = required.find(f=>!dispOnboardData[f.key]?.trim());
              if (missing) return;
              if (isLast) { saveDispLead(dispOnboardData); setDispOnboardDone(true); fireEmailTrigger('dispensary_onboard_lead', { name: dispOnboardData.name, city: dispOnboardData.city, email: dispOnboardData.email }); trackEvent('dispensary_lead', { name: dispOnboardData.name, city: dispOnboardData.city, pos: dispOnboardData.pos }); }
              else setDispOnboardStep(s=>s+1);
            }} style={{ flex:2, padding:"11px 0", borderRadius:10, border:"none", background:"#1a2e1a", color:"#f0f4f0", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
              {isLast ? "Submit →" : "Next →"}
            </button>
          </div>
          {dispOnboardStep === 0 && (
            <div style={{ textAlign:"center", marginTop:10, fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Free 14-day trial. No credit card required.</div>
          )}
        </div>
      </div>
    );
  };

  // ── LP VERIFIED BADGE HELPER ─────────────────────────────
  const LPBadge = ({ lpName, size = "sm" }) => {
    const badge = lpVerifiedMap[lpName];
    if (!badge) return null;
    const configs = {
      featured: { bg:"#fef3c7", color:"#92400e", border:"#fde68a", icon:"⭐", label:"Featured" },
      partner:  { bg:"#eff6ff", color:"#1d4ed8", border:"#bfdbfe", icon:"✓", label:"Partner" },
      verified: { bg:"#f0fdf4", color:"#166534", border:"#bbf7d0", icon:"✓", label:"Verified" },
    };
    const cfg = configs[badge.tier] || configs.verified;
    const pad = size === "lg" ? "4px 12px" : "2px 7px";
    const fs = size === "lg" ? 11 : 9;
    return (
      <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:cfg.bg, color:cfg.color, border:"1px solid " + (cfg.border), padding:pad, borderRadius:20, fontSize:fs, fontFamily:"'DM Mono',monospace", flexShrink:0 }}>
        {cfg.icon} iTerps {cfg.label}
      </span>
    );
  };

  // ── FOUNDER DASHBOARD ─────────────────────────────────────
  const FounderDashboard = () => {
    const totalDisps = DB_DISPENSARIES.length;
    const totalSKUs = ALL_PRODUCTS.length;
    const totalProfiles = profiles.length;
    const labVerified = ALL_PRODUCTS.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").length;
    const verifiedPct = Math.round((labVerified/totalSKUs)*100);
    const importedCount = importedProducts.length;
    const cities = [...new Set(DB_DISPENSARIES.map(d=>d.city))];

    // Terpene demand vs supply gap
    const demandMap = {};
    const supplyMap = {};
    const TERPENE_KEYS = Object.keys(TERPENES);
    profiles.forEach(p => {
      const fp = buildTerpeneFingerprint(p);
      TERPENE_KEYS.forEach(t => { demandMap[t] = (demandMap[t]||0) + (fp[t]||0); });
    });
    ALL_PRODUCTS.forEach(p => {
      TERPENE_KEYS.forEach(t => { supplyMap[t] = (supplyMap[t]||0) + (p.terpenes[t]||0)*100; });
    });
    const gapData = TERPENE_KEYS.map(t => ({
      t,
      demand: Math.round((demandMap[t]||0) / Math.max(profiles.length,1)),
      supply: Math.round((supplyMap[t]||0) / totalSKUs * 10),
      color: TERPENES[t]?.color||"#4a7c59",
    })).sort((a,b) => (b.demand - b.supply) - (a.demand - a.supply));

    const dispStats = DB_DISPENSARIES.map(d => {
      const prods = ALL_PRODUCTS.filter(p=>p.dispensary_id===d.id);
      const verified = prods.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").length;
      const topTerp = (() => {
        const tf = {};
        prods.forEach(p => Object.keys(p.terpenes).forEach(t => tf[t]=(tf[t]||0)+1));
        return Object.entries(tf).sort((ea,eb)=>eb[1]-ea[1])[0]?.[0];
      })();
      return { ...d, skus: prods.length, verified, verifiedPct: prods.length?Math.round(verified/prods.length*100):0, topTerp };
    }).sort((a,b) => b.skus - a.skus);

    const cohortMap = {};
    profiles.forEach(p => {
      const fp = buildTerpeneFingerprint(p);
      const top = Object.entries(fp).sort((ea,eb)=>eb[1]-ea[1])[0]?.[0];
      if (top) cohortMap[top] = (cohortMap[top]||0)+1;
    });
    const cohorts = Object.entries(cohortMap).sort((ea,eb)=>eb[1]-ea[1]);

    // Grouped tab structure
    const TAB_GROUPS = [
      { label: "Command", tabs: [
        ["overview","📊 Overview"],
        ["roadmap","🗺 Roadmap"],
        ["grants","💸 Grants"],
        ["notes","📝 Notes"],
      ]},
      { label: "Sales", tabs: [
        ["revenue","💰 Revenue"],
        ["leads","📋 Leads"],
        ["intel","🎯 Intel"],
      ]},
      { label: "Data", tabs: [
        ["network","🏪 Network"],
        ["lps","🏭 LP Pipeline"],
        ["consumers","👤 Consumers"],
        ["gaps","🔬 Gaps"],
        ["skus","🧬 SKUs"],
        ["dispensaries","🏪 Dispensaries"],
        ["lp-roster","🏭 LP Roster"],
      ]},
      { label: "Build", tabs: [
        ["platform","⚙️ Platform"],
        ["coa-log","🧪 COA Log"],
        ["national","🇨🇦 National"],
      ]},
      { label: "Log", tabs: [
        ["events","📡 Events"],
      ]},
    ];
    const allTabs = TAB_GROUPS.flatMap(g => g.tabs);
    const currentGroupLabel = TAB_GROUPS.find(g => g.tabs.some(([k]) => k === founderTab))?.label || "";

    return (
      <div className="fade" style={{ minHeight:"100vh", background:"#0d1a0d", overflowX:"hidden" }}>

        {/* ── TOP BAR ── */}
        <div style={{ background:"#0d1a0d", borderBottom:"1px solid rgba(74,124,89,0.3)", padding:"0 24px", position:"sticky", top:0, zIndex:200 }}>
          {/* Row 1: branding + group tabs + exit */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", height:52, gap:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
              <div style={{ width:30, height:30, borderRadius:8, background:"linear-gradient(135deg,#4a7c59,#1a2e1a)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15 }}>⚗️</div>
              <div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, fontWeight:300, color:"#f0f4f0", lineHeight:1.2 }}>iTerps</div>
                <div style={{ fontSize:8, color:"#4a7c59", fontFamily:"'DM Mono',monospace", letterSpacing:"0.12em" }}>FOUNDER MODE</div>
              </div>
            </div>

            {/* Group pills */}
            <div style={{ display:"flex", gap:4, flex:1, justifyContent:"center", flexWrap:"wrap" }}>
              {TAB_GROUPS.map(g => {
                const isActive = g.tabs.some(([k]) => k === founderTab);
                return (
                  <button key={g.label} onClick={()=>setFounderTab(g.tabs[0][0])}
                    style={{ padding:"5px 14px", borderRadius:20, border:"1px solid " + (isActive?"rgba(74,124,89,0.8)":"rgba(74,124,89,0.2)"),
                      background: isActive ? "rgba(74,124,89,0.25)" : "transparent",
                      color: isActive ? "#4ade80" : "rgba(240,244,240,0.45)",
                      fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight: isActive?600:400,
                      transition:"all 0.15s", whiteSpace:"nowrap" }}>
                    {g.label}
                  </button>
                );
              })}
            </div>

            <div style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
              <div style={{ fontSize:9, fontFamily:"'DM Mono',monospace", color:"rgba(240,244,240,0.25)" }}>
                {totalDisps} stores · {totalSKUs} SKUs · {totalProfiles} profiles
              </div>
              <button onClick={()=>setFounderMode(false)}
                style={{ padding:"5px 14px", borderRadius:8, border:"1px solid rgba(74,124,89,0.4)", background:"transparent", color:"rgba(240,244,240,0.5)", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                Exit →
              </button>
            </div>
          </div>

          {/* Row 2: sub-tabs for current group */}
          <div style={{ display:"flex", gap:2, paddingBottom:0, borderTop:"1px solid rgba(74,124,89,0.1)" }}>
            {(TAB_GROUPS.find(g=>g.tabs.some(([k])=>k===founderTab))||TAB_GROUPS[0]).tabs.map(([k,l]) => (
              <button key={k} onClick={()=>setFounderTab(k)}
                style={{ padding:"7px 12px", borderRadius:0, border:"none", cursor:"pointer", fontSize:10,
                  fontFamily:"'DM Mono',monospace", whiteSpace:"nowrap",
                  background:"transparent",
                  color: founderTab===k ? "#4ade80" : "rgba(240,244,240,0.4)",
                  borderBottom: founderTab===k ? "2px solid #4a7c59" : "2px solid transparent",
                  fontWeight: founderTab===k ? 600 : 400,
                  transition:"all 0.12s" }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ maxWidth:1100, margin:"0 auto", padding:"28px 24px" }}>

          {/* ── OVERVIEW ── */}
          {founderTab === "overview" && (
            <div className="fade">
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
                <div>
                  <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:30, fontWeight:300, color:"#f0f4f0", margin:0 }}>Platform Overview</h1>
                  <p style={{ fontSize:11, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace", marginTop:4 }}>Live stats · {new Date().toLocaleDateString("en-CA",{weekday:"long",month:"long",day:"numeric"})}</p>
                </div>
                {/* Quick actions */}
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {[
                    { label:"🌿 Staff demo", action:()=>{ setFounderMode(false); setOnboardRole("staff"); setActiveDispensary("d170"); setOnboardDone(true); setPage("home"); setDemoRole("staff"); setDemoActive(true); setDemoStep(0); }},
                    { label:"🏭 LP demo", action:()=>{ setFounderMode(false); setOnboardRole("lp"); setActiveLp("Pure Sunfarms"); setOnboardDone(true); setPage("lp"); setDemoRole("lp"); setDemoActive(true); setDemoStep(0); }},
                    { label:"👤 Consumer demo", action:()=>{ setFounderMode(false); setConsumerMode(true); setOnboardRole("consumer"); setOnboardDone(true); setPage("consumer"); setConsumerTab("find"); setDemoRole("consumer"); setDemoActive(true); setDemoStep(0); }},
                    { label:"📱 Kiosk demo", action:()=>{ setFounderMode(false); setKioskMode(true); setKioskDispensary("d170"); setKioskPin("0000"); setConsumerMode(true); setOnboardDone(true); setPage("consumer"); setConsumerTab("find"); }},
                  ].map(({label,action})=>(
                    <button key={label} onClick={action}
                      style={{ padding:"7px 14px", borderRadius:8, border:"1px solid rgba(74,124,89,0.4)", background:"rgba(74,124,89,0.12)", color:"#4ade80", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer", fontWeight:600, transition:"all 0.15s" }}
                      onMouseEnter={e=>e.currentTarget.style.background="rgba(74,124,89,0.3)"}
                      onMouseLeave={e=>e.currentTarget.style.background="rgba(74,124,89,0.12)"}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* KPI grid */}
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:28 }}>
                {[
                  { label:"Dispensaries", value:totalDisps, sub:(cities.length) + " cities", color:"#4ade80", tab:"network" },
                  { label:"Total SKUs", value:totalSKUs, sub:importedCount>0?importedCount+" imported":"all platform data", color:"#60a5fa", tab:"skus" },
                  { label:"Lab Verified", value:(verifiedPct) + "%", sub:(labVerified) + " of " + (totalSKUs) + " SKUs", color:"#a78bfa", tab:"platform" },
                  { label:"Terpene Profiles", value:totalProfiles, sub:"consumer fingerprints", color:"#fb923c", tab:"consumers" },
                  { label:"BC Cities", value:cities.length, sub:"network coverage", color:"#f472b6", tab:"network" },
                  { label:"LP Partners", value:[...new Set(ALL_PRODUCTS.map(p=>p.cultivation?.lp).filter(Boolean))].length, sub:"brands in data", color:"#34d399", tab:"lp-roster" },
                ].map(({label,value,sub,color,tab})=>(
                  <button key={label} onClick={()=>setFounderTab(tab)}
                    style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"16px 18px", textAlign:"left", cursor:"pointer", transition:"all 0.15s" }}
                    onMouseEnter={e=>{ e.currentTarget.style.background="rgba(255,255,255,0.08)"; e.currentTarget.style.borderColor="rgba(74,124,89,0.4)"; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background="rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"; }}>
                    <div style={{ fontSize:22, fontWeight:700, color, fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{value}</div>
                    <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{label}</div>
                    <div style={{ fontSize:10, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{sub}</div>
                  </button>
                ))}
              </div>

              {/* Two-col layout: top dispensaries + top terpene demand */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>

                {/* Top stores by SKUs */}
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                  <div style={{ fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:14, letterSpacing:"0.06em" }}>TOP STORES BY SKU COUNT</div>
                  {dispStats.slice(0,7).map(d=>(
                    <div key={d.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                      <div>
                        <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{d.name}</div>
                        <div style={{ fontSize:9, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace" }}>{d.city}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#4ade80", fontFamily:"'DM Mono',monospace" }}>{d.skus}</div>
                        <div style={{ fontSize:9, color: d.verifiedPct>=70?"#4ade80":d.verifiedPct>=40?"#fbbf24":"#f87171", fontFamily:"'DM Mono',monospace" }}>{d.verifiedPct}% verified</div>
                      </div>
                    </div>
                  ))}
                  <button onClick={()=>setFounderTab("dispensaries")} style={{ marginTop:10, fontSize:10, fontFamily:"'DM Mono',monospace", color:"rgba(74,173,89,0.7)", background:"none", border:"none", cursor:"pointer" }}>View all dispensaries →</button>
                </div>

                {/* Top demand terpenes */}
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                  <div style={{ fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:14, letterSpacing:"0.06em" }}>HIGHEST DEMAND GAPS</div>
                  {gapData.slice(0,8).map(({t,demand,supply,color})=>{
                    const gap = demand - supply;
                    const isGap = gap > 5;
                    return (
                      <div key={t} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", background:color, flexShrink:0 }}/>
                          <span style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ fontSize:9, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>D:{demand} S:{supply}</div>
                          <div style={{ fontSize:10, fontWeight:600, fontFamily:"'DM Mono',monospace", color: isGap?"#f87171":"#4ade80" }}>{isGap?"+" + (gap) + " gap":"covered"}</div>
                        </div>
                      </div>
                    );
                  })}
                  <button onClick={()=>setFounderTab("gaps")} style={{ marginTop:10, fontSize:10, fontFamily:"'DM Mono',monospace", color:"rgba(74,173,89,0.7)", background:"none", border:"none", cursor:"pointer" }}>Full gap analysis →</button>
                </div>
              </div>

              {/* Recent activity + platform health row */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                {/* Recent events */}
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                  <div style={{ fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:12, letterSpacing:"0.06em" }}>RECENT ACTIVITY</div>
                  {sessionEvents.slice(-6).reverse().map((ev,i)=>(
                    <div key={i} style={{ display:"flex", gap:10, padding:"5px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                      <span style={{ fontSize:9, color:"rgba(240,244,240,0.25)", fontFamily:"'DM Mono',monospace", flexShrink:0, marginTop:2 }}>{new Date(ev.ts).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</span>
                      <span style={{ fontSize:10, color:"rgba(240,244,240,0.65)", fontFamily:"'DM Mono',monospace" }}>{ev.label}</span>
                    </div>
                  ))}
                  {sessionEvents.length === 0 && <div style={{ fontSize:10, color:"rgba(240,244,240,0.25)", fontFamily:"'DM Mono',monospace" }}>No events yet this session.</div>}
                  <button onClick={()=>setFounderTab("events")} style={{ marginTop:10, fontSize:10, fontFamily:"'DM Mono',monospace", color:"rgba(74,173,89,0.7)", background:"none", border:"none", cursor:"pointer" }}>Full event log →</button>
                </div>

                {/* Platform health snapshot */}
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                  <div style={{ fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:12, letterSpacing:"0.06em" }}>PLATFORM HEALTH</div>
                  {[
                    { label:"Lab-verified SKUs", value:(verifiedPct) + "%", ok: verifiedPct >= 50 },
                    { label:"Dispensaries with data", value:(dispStats.filter(d=>d.skus>0).length) + "/" + (totalDisps), ok: true },
                    { label:"Consumer profiles", value:totalProfiles, ok: totalProfiles > 0 },
                    { label:"Imported COAs", value:importedCount, ok: importedCount >= 0 },
                    { label:"Feedback flags", value:feedbackLog.length, ok: feedbackLog.length < 10 },
                    { label:"Data coverage", value:(cities.length) + " cities", ok: cities.length >= 10 },
                  ].map(({label,value,ok})=>(
                    <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                      <span style={{ fontSize:10, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace" }}>{label}</span>
                      <span style={{ fontSize:10, fontWeight:600, fontFamily:"'DM Mono',monospace", color: ok?"#4ade80":"#fbbf24" }}>{String(value)}</span>
                    </div>
                  ))}
                  <button onClick={()=>setFounderTab("platform")} style={{ marginTop:10, fontSize:10, fontFamily:"'DM Mono',monospace", color:"rgba(74,173,89,0.7)", background:"none", border:"none", cursor:"pointer" }}>Full platform report →</button>
                </div>
              </div>
            </div>
          )}

          {/* ── ROADMAP ── */}
          {founderTab === "roadmap" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Roadmap</h2>
                <p style={{ fontSize:11, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Feature backlog and immediate priorities. Click to toggle status.</p>
              </div>
              {[
                { phase:"NOW — Pre-revenue", color:"#4ade80", items:[
                  { label:"Incorporate in BC Registry", detail:"$350–$450 online. Prerequisite for everything else.", done: false },
                  { label:"Business bank account", detail:"Simplii or Koho Business. Need corp number first.", done: false },
                  { label:"SR&ED date-stamp", detail:"2 paragraphs: technical problem + novelty. Do before any other revenue.", done: false },
                  { label:"CIPO trademark — 'iTerps'", detail:"$500–$800. File as soon as incorporated.", done: false },
                  { label:"NRC IRAP call", detail:"1-877-994-4727. Call day after incorporating. Est. $18K–$29K.", done: false },
                  { label:"Futurpreneur application", detail:"futurpreneur.ca. $20K collateral-free + mentorship.", done: false },
                  { label:"5 in-person dispensary visits", detail:"Surrey + Abbotsford first. Pitch deck + kiosk demo.", done: false },
                  { label:"Privacy Policy + Terms of Service", detail:"Lawyer review, $800–$1,500.", done: false },
                ]},
                { phase:"WEEK 2–4 — First contracts", color:"#60a5fa", items:[
                  { label:"Stripe setup under corp name", detail:"Stripe Billing + Stripe Tax for GST.", done: false },
                  { label:"Dispensary SaaS agreement template", detail:"Lawyer-reviewed. Needed before first paid store.", done: false },
                  { label:"Data Processing Agreement", detail:"Required for any dispensary storing customer profiles.", done: false },
                  { label:"Supabase project (Toronto region)", detail:"Schema + RLS policies. Swap out local state.", done: false },
                  { label:"Join CCIA", detail:"Cannabis Council of Canada. Networking + credibility.", done: false },
                ]},
                { phase:"TECH — Backlog", color:"#a78bfa", items:[
                  { label:"Supabase auth swap", detail:"Replace useState with real auth. Gate manager view.", done: false },
                  { label:"COA admin dashboard", detail:"Review + approve LP-uploaded COAs before they go live.", done: false },
                  { label:"POS sync (Cova/Greenline)", detail:"Pull live inventory. Phase 2.", done: false },
                  { label:"Jurisdiction config layer", detail:"Toggle compliance rules per market. Germany/AUS next.", done: false },
                  { label:"White-label shell", detail:"$500–$1,500/mo + setup. For multi-location chains.", done: false },
                  { label:"Tolerance tracking", detail:"Consumer profile feature. Post-launch.", done: false },
                  { label:"Multi-language (FR/DE)", detail:"Required for national + EU expansion.", done: false },
                ]},
              ].map(({phase, color, items}) => (
                <div key={phase} style={{ marginBottom:28 }}>
                  <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color, fontWeight:600, letterSpacing:"0.08em", marginBottom:10, textTransform:"uppercase" }}>{phase}</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {items.map(({label, detail, done}) => (
                      <div key={label} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid " + (done?"rgba(74,173,89,0.4)":"rgba(255,255,255,0.08)"), borderRadius:8, padding:"10px 14px", display:"flex", alignItems:"flex-start", gap:12 }}>
                        <div style={{ width:16, height:16, borderRadius:4, border:"2px solid " + (done?"#4ade80":"rgba(255,255,255,0.2)"), background: done?"#4ade80":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                          {done && <span style={{ fontSize:9, color:"#0d1a0d", fontWeight:700 }}>✓</span>}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12, color: done?"rgba(240,244,240,0.4)":"#f0f4f0", fontFamily:"'DM Mono',monospace", fontWeight:done?400:600, textDecoration:done?"line-through":"none" }}>{label}</div>
                          <div style={{ fontSize:10, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── GRANTS ── */}
          {founderTab === "grants" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Grant Tracker</h2>
                <p style={{ fontSize:11, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Priority order. Max potential: ~$350K+. Most require incorporation first.</p>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:14 }}>
                {[
                  { rank:1, name:"SR&ED", amount:"$8K–$15K", timeline:"File with T2 Year 2", status:"Action now", statusColor:"#f87171", url:"", detail:"Document R&D work NOW — before any other revenue. Write 2 paragraphs: (1) technical problem you could not solve with existing knowledge, (2) systematic approach taken. Date-stamp everything. File with corporate tax return Year 2.", prereq:"Incorporation", prereqDone:false },
                  { rank:2, name:"NRC IRAP", amount:"$18K–$29K", timeline:"Call day after incorporating", status:"Pending corp", statusColor:"#fbbf24", url:"1-877-994-4727", detail:"NRC Industrial Research Assistance Program. Call the day after you incorporate. Don't wait. They assign an Industrial Technology Advisor (ITA). Prepare: company overview, technical innovation summary, planned headcount.", prereq:"Incorporation", prereqDone:false },
                  { rank:3, name:"Futurpreneur", amount:"$20K loan", timeline:"Apply now", status:"Apply now", statusColor:"#4ade80", url:"futurpreneur.ca", detail:"Collateral-free $20K business loan + 2-year mentorship from a business professional. No equity. Under-30 founder advantage. Complete the business plan module in the app and use it directly.", prereq:"None", prereqDone:true },
                  { rank:4, name:"BC IDMTC", amount:"25% BC labour", timeline:"Year 2", status:"Year 2", statusColor:"#60a5fa", url:"", detail:"Interactive Digital Media Tax Credit. 25% refundable tax credit on BC employee wages. Requires a BC corporation and BC-based employees. Apply retroactively in Year 2 once you have payroll.", prereq:"BC Corp + payroll", prereqDone:false },
                  { rank:5, name:"Innovate BC Ignite", amount:"Up to $300K", timeline:"After 5+ paying customers", status:"Future", statusColor:"#a78bfa", url:"innovatebc.ca", detail:"Equity-free grant for BC tech companies with demonstrated market traction. They want to see revenue, customer validation, and a growth plan. Prepare the investor deck tab here before applying.", prereq:"5+ paying customers", prereqDone:false },
                  { rank:6, name:"PacifiCan", amount:"Up to $3M", timeline:"Year 2 with revenue", status:"Future", statusColor:"#a78bfa", url:"pacifican.gc.ca", detail:"Pacific Economic Development Canada. Business Scale-up and Productivity (BSP) program. Major grants for BC companies. Need demonstrated revenue and jobs created. Year 2 target.", prereq:"Revenue + employees", prereqDone:false },
                ].map(g=>(
                  <div key={g.name} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:18 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <div style={{ width:22, height:22, borderRadius:6, background:"rgba(74,124,89,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:700 }}>{g.rank}</div>
                        <div style={{ fontSize:14, fontFamily:"'Fraunces',serif", fontWeight:400, color:"#f0f4f0" }}>{g.name}</div>
                      </div>
                      <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:g.statusColor, fontWeight:600, padding:"2px 8px", background:(g.statusColor) + "15", borderRadius:6 }}>{g.status}</div>
                    </div>
                    <div style={{ fontSize:18, fontWeight:700, color:"#4ade80", fontFamily:"'DM Mono',monospace", marginBottom:6 }}>{g.amount}</div>
                    <div style={{ fontSize:10, color:"rgba(240,244,240,0.55)", fontFamily:"'DM Mono',monospace", lineHeight:1.6, marginBottom:10 }}>{g.detail}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div style={{ fontSize:9, fontFamily:"'DM Mono',monospace", color: g.prereqDone?"#4ade80":"rgba(240,244,240,0.3)" }}>
                        {g.prereqDone ? "✓" : "⚠"} Prereq: {g.prereq}
                      </div>
                      {g.url && <div style={{ fontSize:9, fontFamily:"'DM Mono',monospace", color:"rgba(96,165,250,0.8)" }}>{g.url}</div>}
                    </div>
                    <div style={{ marginTop:8, fontSize:9, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace" }}>📅 {g.timeline}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── NOTES ── */}
          {founderTab === "notes" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Founder Notes</h2>
                <p style={{ fontSize:11, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Scratch pad. Session-persistent.</p>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                {/* Free-text scratch pad */}
                <div>
                  <div style={{ fontSize:10, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.06em" }}>Scratch Pad</div>
                  <textarea
                    value={founderNotes}
                    onChange={e=>{ setFounderNotes(e.target.value); try{localStorage.setItem("iterps_founder_notes",e.target.value);}catch (_e) {} }}
                    placeholder="Anything on your mind — meeting notes, ideas, questions..."
                    style={{ width:"100%", minHeight:280, background:"rgba(255,255,255,0.04)", border:"1px solid rgba(74,124,89,0.3)", borderRadius:10, padding:14, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontSize:12, lineHeight:1.7, resize:"vertical", outline:"none", boxSizing:"border-box" }}
                  />
                </div>

                {/* Dispensary visit log */}
                <div>
                  <div style={{ fontSize:10, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.06em" }}>Visit / Outreach Log</div>
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:10, padding:14, minHeight:280 }}>
                    {[
                      { date:"TBD", store:"Inspired Cannabis Surrey Fleetwood", status:"Cold", notes:"Flagship store, high foot traffic — priority target" },
                      { date:"TBD", store:"Inspired Cannabis Surrey Guildford", status:"Cold", notes:"Second Surrey location — visit same trip" },
                      { date:"TBD", store:"This Is Cannabis Abbotsford", status:"Cold", notes:"Abbotsford anchor — bring kiosk tablet demo" },
                      { date:"TBD", store:"BC Cannabis Store Abbotsford Highstreet", status:"Cold", notes:"Government retail — note different procurement process" },
                      { date:"TBD", store:"THC Canada (Vancouver)", status:"Cold", notes:"Dealr SKUs live — good demo store" },
                    ].map((v,i)=>(
                      <div key={i} style={{ padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:2 }}>
                          <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{v.store}</div>
                          <div style={{ fontSize:9, padding:"2px 7px", borderRadius:6, background:"rgba(251,191,36,0.1)", color:"#fbbf24", fontFamily:"'DM Mono',monospace" }}>{v.status}</div>
                        </div>
                        <div style={{ fontSize:10, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace" }}>{v.date} · {v.notes}</div>
                      </div>
                    ))}
                    <div style={{ marginTop:10, fontSize:10, color:"rgba(240,244,240,0.2)", fontFamily:"'DM Mono',monospace" }}>Edit visit log in scratch pad above ↑</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── REVENUE ── */}
          {founderTab === "revenue" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Revenue Tracker</h2>
                <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Editable funnel. All numbers persist across sessions.</p>
              </div>
              {(() => {
                const rd = revenueData;
                const dispMRR = rd.paidDisp * rd.dispPrice;
                const lpMRR = rd.paidLP * rd.lpPrice;
                const totalMRR = dispMRR + lpMRR;
                const arr = totalMRR * 12;
                const target6 = 5000;
                const pct6 = Math.min(100, Math.round(totalMRR/target6*100));

                return (
                  <div>
                    {/* MRR summary */}
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:12, marginBottom:24 }}>
                      {[
                        { label:"MRR", value:"$" + (totalMRR.toLocaleString()), color:"#4ade80" },
                        { label:"ARR", value:"$" + (arr.toLocaleString()), color:"#60a5fa" },
                        { label:"Disp MRR", value:"$" + (dispMRR.toLocaleString()), color:"#a78bfa" },
                        { label:"LP MRR", value:"$" + (lpMRR.toLocaleString()), color:"#fb923c" },
                        { label:"$5K/mo target", value:(pct6) + "%", color: pct6>=100?"#4ade80":pct6>=50?"#fbbf24":"#f87171" },
                      ].map(({label,value,color})=>(
                        <div key={label} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"14px 16px" }}>
                          <div style={{ fontSize:20, fontWeight:700, color, fontFamily:"'DM Mono',monospace" }}>{value}</div>
                          <div style={{ fontSize:10, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{label}</div>
                        </div>
                      ))}
                    </div>

                    {/* Editable inputs */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                      {/* Dispensary */}
                      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                        <div style={{ fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:14, letterSpacing:"0.06em" }}>DISPENSARY REVENUE</div>
                        {[
                          { key:"paidDisp", label:"Paid dispensaries", type:"number" },
                          { key:"dispPrice", label:"Avg price/location ($)", type:"number" },
                          { key:"dispPipeline", label:"Pipeline (prospects)", type:"number" },
                          { key:"dispChurn", label:"Churned this month", type:"number" },
                        ].map(({key,label})=>(
                          <div key={key} style={{ marginBottom:12 }}>
                            <div style={{ fontSize:10, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{label}</div>
                            <input type="number" value={rd[key]||0}
                              onChange={e=>setRevenueData(p=>({...p,[key]:Number(e.target.value)}))}
                              style={{ width:"100%", padding:"7px 10px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(74,124,89,0.3)", borderRadius:7, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontSize:13, outline:"none", boxSizing:"border-box" }}
                            />
                          </div>
                        ))}
                      </div>

                      {/* LP */}
                      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                        <div style={{ fontSize:11, color:"#60a5fa", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:14, letterSpacing:"0.06em" }}>LP REVENUE</div>
                        {[
                          { key:"paidLP", label:"Paid LP accounts", type:"number" },
                          { key:"lpPrice", label:"Avg price/LP ($)", type:"number" },
                          { key:"lpPipeline", label:"Pipeline (LPs in talks)", type:"number" },
                          { key:"lpChurn", label:"Churned this month", type:"number" },
                        ].map(({key,label})=>(
                          <div key={key} style={{ marginBottom:12 }}>
                            <div style={{ fontSize:10, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{label}</div>
                            <input type="number" value={rd[key]||0}
                              onChange={e=>setRevenueData(p=>({...p,[key]:Number(e.target.value)}))}
                              style={{ width:"100%", padding:"7px 10px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(74,124,89,0.3)", borderRadius:7, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontSize:13, outline:"none", boxSizing:"border-box" }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Pricing reminder */}
                    <div style={{ marginTop:16, padding:"12px 16px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10 }}>
                      <div style={{ fontSize:10, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace", lineHeight:1.8 }}>
                        <strong style={{ color:"rgba(240,244,240,0.6)" }}>Pricing reference:</strong> Dispensary Founding $59/mo (first 10 locked) · Dispensary Pro $99/mo · Chain $79/mo (3+ locations) · LP Founding $199/mo (first 5) · LP Partner $299/mo · Data Licensing $5K–$25K/year · White-label $500–$1,500/mo
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── LEADS ── */}
          {founderTab === "leads" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Dispensary Leads</h2>
                <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Onboarding leads, stock alert opt-ins, and product feedback flags.</p>
              </div>

              {(() => {
                const stockOpts = stockAlerts || [];
                const feedbacks = feedbackLog || [];
                const onboardLeads = dispOnboardLeads || [];

                return (
                  <>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10, marginBottom:20 }}>
                      {[
                        { label:"Onboard leads", value:onboardLeads.length, color:"#4ade80" },
                        { label:"Stock opt-ins", value:stockOpts.length, color:"#60a5fa" },
                        { label:"Feedback flags", value:feedbacks.length, color:"#fb923c" },
                        { label:"Total signals", value:onboardLeads.length + stockOpts.length + feedbacks.length, color:"#a78bfa" },
                      ].map(({label,value,color})=>(
                        <div key={label} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px" }}>
                          <div style={{ fontSize:22, fontWeight:700, color, fontFamily:"'DM Mono',monospace" }}>{value}</div>
                          <div style={{ fontSize:10, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>{label}</div>
                        </div>
                      ))}
                    </div>

                    {onboardLeads.length > 0 && (
                      <div style={{ marginBottom:20 }}>
                        <div style={{ fontSize:10, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:10 }}>DISPENSARY ONBOARD REQUESTS</div>
                        {onboardLeads.map((l,i)=>(
                          <div key={i} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:8, padding:"10px 14px", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
                            <div>
                              <div style={{ fontSize:12, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{l.storeName||l.name||"Unknown store"}</div>
                              <div style={{ fontSize:10, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace" }}>{l.contact||l.email||""} · {l.city||""}</div>
                            </div>
                            <div style={{ fontSize:9, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace" }}>{l.ts ? new Date(l.ts).toLocaleDateString() : ""}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {stockOpts.length > 0 && (
                      <div style={{ marginBottom:20 }}>
                        <div style={{ fontSize:10, color:"#60a5fa", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:10 }}>STOCK ALERT OPT-INS</div>
                        {stockOpts.map((s,i)=>(
                          <div key={i} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:8, padding:"10px 14px", marginBottom:6, display:"flex", justifyContent:"space-between" }}>
                            <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{s.productName||s.product||"Unknown product"} {s.brand ? "· " + (s.brand) : ""}</div>
                            <div style={{ fontSize:10, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace" }}>{s.email}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {feedbacks.length > 0 && (
                      <div>
                        <div style={{ fontSize:10, color:"#fb923c", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:10 }}>PRODUCT FEEDBACK</div>
                        {feedbacks.map((f,i)=>(
                          <div key={i} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:8, padding:"10px 14px", marginBottom:6 }}>
                            <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{f.product} — <span style={{ color: f.type==="positive"?"#4ade80":"#f87171" }}>{f.type}</span></div>
                            <div style={{ fontSize:10, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace", marginTop:4 }}>{f.note}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {onboardLeads.length===0 && stockOpts.length===0 && feedbacks.length===0 && (
                      <div style={{ textAlign:"center", padding:"60px 20px", color:"rgba(240,244,240,0.25)", fontFamily:"'DM Mono',monospace", fontSize:12 }}>
                        <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
                        No leads yet. Once dispensaries submit the onboarding form or opt into stock alerts, they appear here.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* ── INTEL ── */}
          {founderTab === "intel" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Competitive Intelligence</h2>
                <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Computed from live network data — highest-opportunity targets and coverage gaps.</p>
              </div>
              {(() => {
                const dispOpportunity = DB_DISPENSARIES.map(d => {
                  const prods = ALL_PRODUCTS.filter(p => p.dispensary_id === d.id);
                  const verified = prods.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").length;
                  const unverified = prods.length - verified;
                  const unverifiedPct = prods.length ? Math.round((unverified/prods.length)*100) : 0;
                  return { ...d, totalSKUs: prods.length, verified, unverified, unverifiedPct };
                }).filter(d => d.totalSKUs > 0).sort((a,b) => b.unverified - a.unverified).slice(0, 8);

                const platformLPs = new Set(ALL_PRODUCTS.map(p => p.cultivation?.lp).filter(Boolean));
                const allBCLPs = ["Pure Sunfarms","Tantalus Labs","Broken Coast","7ACRES","Citizen Stash","Qwest Cannabis","Carmel Cannabis","Pistol & Paris","Good Buds Company","Rubicon Organics","Decibel Cannabis","Grail Cannabis","General Admission","Tribal Seeds","Flowr","True Harvest","BZAM","Zenabis","Spinach","Original Stash"];
                const missingLPs = allBCLPs.filter(lp => !platformLPs.has(lp));

                const cityGaps = cities.map(city => {
                  const cityDisps = DB_DISPENSARIES.filter(d => d.city === city);
                  const cityProds = ALL_PRODUCTS.filter(p => cityDisps.some(d => d.id === p.dispensary_id));
                  const verified = cityProds.filter(p=>p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided").length;
                  return { city, stores: cityDisps.length, skus: cityProds.length, verified, pct: cityProds.length ? Math.round(verified/cityProds.length*100) : 0 };
                }).sort((a,b) => a.pct - b.pct);

                return (
                  <>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
                      {/* Top upgrade targets */}
                      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                        <div style={{ fontSize:10, color:"#f87171", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:12 }}>TOP UPGRADE TARGETS (unverified SKU ratio)</div>
                        {dispOpportunity.map(d=>(
                          <div key={d.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                            <div>
                              <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{d.name}</div>
                              <div style={{ fontSize:9, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace" }}>{d.city} · {d.totalSKUs} SKUs</div>
                            </div>
                            <div style={{ textAlign:"right" }}>
                              <div style={{ fontSize:12, fontWeight:700, color:"#f87171", fontFamily:"'DM Mono',monospace" }}>{d.unverified} unverified</div>
                              <div style={{ fontSize:9, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace" }}>{d.unverifiedPct}% of stock</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* City coverage gaps */}
                      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                        <div style={{ fontSize:10, color:"#fbbf24", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:12 }}>CITY COVERAGE GAPS (lowest verified %)</div>
                        {cityGaps.slice(0,8).map(c=>(
                          <div key={c.city} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                            <div>
                              <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{c.city}</div>
                              <div style={{ fontSize:9, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace" }}>{c.stores} stores · {c.skus} SKUs</div>
                            </div>
                            <div style={{ fontSize:12, fontWeight:700, color: c.pct<40?"#f87171":c.pct<70?"#fbbf24":"#4ade80", fontFamily:"'DM Mono',monospace" }}>{c.pct}% verified</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Missing LPs */}
                    <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                      <div style={{ fontSize:10, color:"#60a5fa", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:12 }}>LP COLD OUTREACH TARGETS (not yet on platform)</div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                        {missingLPs.map(lp=>(
                          <div key={lp} style={{ padding:"5px 12px", background:"rgba(96,165,250,0.08)", border:"1px solid rgba(96,165,250,0.2)", borderRadius:20, fontSize:10, fontFamily:"'DM Mono',monospace", color:"rgba(240,244,240,0.6)" }}>
                            {lp}
                          </div>
                        ))}
                        {missingLPs.length === 0 && <div style={{ fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace" }}>All major BC LPs represented ✓</div>}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── NETWORK ── */}
          {founderTab === "network" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Dispensary Network</h2>
                <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>{totalDisps} licensed retailers across {cities.length} BC cities</p>
              </div>
              <div style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, overflow:"hidden" }}>
                <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", padding:"10px 16px", borderBottom:"1px solid rgba(255,255,255,0.08)", fontSize:9, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace", letterSpacing:"0.06em", textTransform:"uppercase" }}>
                  <div>Store</div><div>SKUs</div><div>Verified</div><div>Top Terpene</div>
                </div>
                {dispStats.map(d=>(
                  <div key={d.id} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr", padding:"9px 16px", borderBottom:"1px solid rgba(255,255,255,0.04)", alignItems:"center" }}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div>
                      <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{d.name}</div>
                      <div style={{ fontSize:9, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace" }}>{d.city} · {d.chain||"Independent"}</div>
                    </div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#4ade80", fontFamily:"'DM Mono',monospace" }}>{d.skus}</div>
                    <div style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color: d.verifiedPct>=70?"#4ade80":d.verifiedPct>=40?"#fbbf24":"#f87171" }}>{d.verifiedPct}%</div>
                    <div style={{ fontSize:10, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace" }}>{d.topTerp||"—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── LP PIPELINE ── */}
          {founderTab === "lps" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>LP Pipeline</h2>
                <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Licensed producers in the platform + outreach status.</p>
              </div>
              {(() => {
                const lpMap = {};
                ALL_PRODUCTS.forEach(p => {
                  const lp = p.cultivation?.lp;
                  if (!lp) return;
                  if (!lpMap[lp]) lpMap[lp] = { name:lp, skus:0, verified:0, cats:new Set(), stores:new Set() };
                  lpMap[lp].skus++;
                  if (p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided") lpMap[lp].verified++;
                  lpMap[lp].cats.add(p.category);
                  lpMap[lp].stores.add(p.dispensary_id);
                });
                const lpList = Object.values(lpMap).map(lp => ({
                  ...lp, verifiedPct: Math.round(lp.verified/lp.skus*100),
                  cats: [...lp.cats], stores: lp.stores.size
                })).sort((a,b) => b.skus - a.skus);

                return (
                  <>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(100px,1fr))", gap:10, marginBottom:20 }}>
                      {[
                        { label:"LPs in system", value:lpList.length, color:"#4ade80" },
                        { label:"Fully verified", value:lpList.filter(l=>l.verifiedPct===100).length, color:"#60a5fa" },
                        { label:"Partial data", value:lpList.filter(l=>l.verifiedPct>0&&l.verifiedPct<100).length, color:"#fbbf24" },
                        { label:"Estimated only", value:lpList.filter(l=>l.verifiedPct===0).length, color:"#f87171" },
                      ].map(({label,value,color})=>(
                        <div key={label} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px" }}>
                          <div style={{ fontSize:20, fontWeight:700, color, fontFamily:"'DM Mono',monospace" }}>{value}</div>
                          <div style={{ fontSize:10, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>{label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, overflow:"hidden" }}>
                      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr", padding:"9px 16px", borderBottom:"1px solid rgba(255,255,255,0.08)", fontSize:9, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace", letterSpacing:"0.06em", textTransform:"uppercase" }}>
                        <div>LP Name</div><div>SKUs</div><div>Verified</div><div>Stores</div><div>Status</div>
                      </div>
                      {lpList.map(lp=>(
                        <div key={lp.name} style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr", padding:"8px 16px", borderBottom:"1px solid rgba(255,255,255,0.04)", alignItems:"center" }}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                          <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{lp.name}</div>
                          <div style={{ fontSize:12, fontWeight:700, color:"#60a5fa", fontFamily:"'DM Mono',monospace" }}>{lp.skus}</div>
                          <div style={{ fontSize:11, color: lp.verifiedPct>=70?"#4ade80":lp.verifiedPct>=30?"#fbbf24":"#f87171", fontFamily:"'DM Mono',monospace" }}>{lp.verifiedPct}%</div>
                          <div style={{ fontSize:11, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace" }}>{lp.stores}</div>
                          <div style={{ fontSize:9, padding:"2px 7px", borderRadius:5, display:"inline-block",
                            background: lp.verifiedPct===100?"rgba(74,173,89,0.15)":lp.verifiedPct>0?"rgba(251,191,36,0.12)":"rgba(248,113,113,0.12)",
                            color: lp.verifiedPct===100?"#4ade80":lp.verifiedPct>0?"#fbbf24":"#f87171", fontFamily:"'DM Mono',monospace" }}>
                            {lp.verifiedPct===100?"Verified":lp.verifiedPct>0?"Partial":"Estimated"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ── CONSUMERS ── */}
          {founderTab === "consumers" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Consumer Intelligence</h2>
                <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>{totalProfiles} terpene passports · cross-dispensary demand data</p>
              </div>
              {totalProfiles === 0 ? (
                <div style={{ textAlign:"center", padding:"60px 20px", color:"rgba(240,244,240,0.25)", fontFamily:"'DM Mono',monospace", fontSize:12 }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>👤</div>
                  No consumer profiles yet. Profiles are created when users complete the terpene finder.
                </div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                    <div style={{ fontSize:10, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:14 }}>TOP TERPENE COHORTS</div>
                    {cohorts.slice(0,8).map(([t,count])=>{
                      const pct = Math.round(count/totalProfiles*100);
                      return (
                        <div key={t} style={{ marginBottom:10 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                            <span style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                            <span style={{ fontSize:10, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>{count} ({pct}%)</span>
                          </div>
                          <div style={{ height:4, borderRadius:2, background:"rgba(255,255,255,0.06)", overflow:"hidden" }}>
                            <div style={{ height:"100%", borderRadius:2, background:TERPENES[t]?.color||"#4a7c59", width:(pct) + "%", transition:"width 0.3s" }}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                    <div style={{ fontSize:10, color:"#60a5fa", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:14 }}>PROFILE BREAKDOWN</div>
                    {profiles.slice(0,8).map((p,i)=>(
                      <div key={i} style={{ padding:"7px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                        <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{p.name||"Profile #" + (i+1)}</div>
                        <div style={{ fontSize:9, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace" }}>
                          {Object.entries(p.terpeneWeights||{}).slice(0,3).map(([t])=>t).join(" · ")||"No weights"} · {p.dispensaryId ? DB_DISPENSARIES.find(d=>d.id===p.dispensaryId)?.name||p.dispensaryId : "No store"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── GAP ANALYSIS ── */}
          {founderTab === "gaps" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Market Gap Analysis</h2>
                <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Consumer demand vs. current network supply — where the opportunity is.</p>
              </div>
              {totalProfiles === 0 && (
                <div style={{ background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.3)", borderRadius:10, padding:"12px 16px", marginBottom:20, fontSize:11, color:"rgba(251,191,36,0.8)", fontFamily:"'DM Mono',monospace" }}>
                  ⚠ No consumer profiles yet — demand signal is empty. Gap analysis will populate as users complete the terpene finder.
                </div>
              )}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                <div style={{ fontSize:10, color:"rgba(240,244,240,0.35)", fontFamily:"'DM Mono',monospace", marginBottom:16 }}>Bars above 0 = unmet demand. Red = significant gap. Green = well-covered.</div>
                {gapData.map(({t,demand,supply,color})=>{
                  const gap = demand - supply;
                  const isGap = gap > 10;
                  const isOver = gap < -10;
                  return (
                    <div key={t} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ width:8, height:8, borderRadius:"50%", background:color }}/>
                          <span style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                        </div>
                        <div style={{ display:"flex", gap:12, fontSize:10, fontFamily:"'DM Mono',monospace", color:"rgba(240,244,240,0.4)" }}>
                          <span>D:{demand}</span><span>S:{supply}</span>
                          <span style={{ color: isGap?"#f87171":isOver?"#60a5fa":"#4ade80", fontWeight:600 }}>
                            {isGap?"+" + (gap) + " gap":isOver?(gap) + " over":"balanced"}
                          </span>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:3, height:6, borderRadius:3, overflow:"hidden" }}>
                        <div style={{ flex:demand, background:color, opacity:0.7, borderRadius:3 }}/>
                        <div style={{ flex:Math.max(0,supply-demand), background:"rgba(96,165,250,0.4)", borderRadius:3 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── ALL SKUs ── */}
          {founderTab === "skus" && (
            <div className="fade" style={{ padding:"0 8px" }}>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>All SKUs</div>
                <div style={{ fontSize:11, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>{ALL_PRODUCTS.length} products · {labVerified} lab-verified ({verifiedPct}%)</div>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                  <thead>
                    <tr style={{ borderBottom:"1px solid rgba(255,255,255,0.1)" }}>
                      {["Brand","Product","Category","THC","Terpenes","Source","Dispensary"].map(h=>(
                        <th key={h} style={{ padding:"8px 10px", textAlign:"left", color:"rgba(240,244,240,0.35)", fontWeight:400, whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ALL_PRODUCTS.slice(0,100).map(p=>(
                      <tr key={p.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.7)" }}>{p.brand}</td>
                        <td style={{ padding:"7px 10px", color:"#f0f4f0", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</td>
                        <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.5)" }}>{p.category}</td>
                        <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.6)" }}>{p.thc}%</td>
                        <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.6)" }}>{p.total_terpenes ? (p.total_terpenes) + "%" : "—"}</td>
                        <td style={{ padding:"7px 10px" }}>
                          <span style={{ color: p.terpene_source==="lab_tested"?"#4ade80":p.terpene_source==="lp_provided"?"#60a5fa":"#fbbf24" }}>
                            {p.terpene_source==="lab_tested"?"Lab":p.terpene_source==="lp_provided"?"LP":"Est."}
                          </span>
                        </td>
                        <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.4)", maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {DB_DISPENSARIES.find(d=>d.id===p.dispensary_id)?.name||p.dispensary_id}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ALL_PRODUCTS.length > 100 && <div style={{ padding:"12px 10px", fontSize:10, color:"rgba(240,244,240,0.25)", fontFamily:"'DM Mono',monospace" }}>Showing 100 of {ALL_PRODUCTS.length} — use Explorer for full search</div>}
              </div>
            </div>
          )}

          {/* ── DB_DISPENSARIES TABLE ── */}
          {founderTab === "dispensaries" && (
            <div className="fade" style={{ padding:"0 8px" }}>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>All Dispensaries</div>
                <div style={{ fontSize:11, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>{totalDisps} stores · {cities.length} cities</div>
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                  <thead>
                    <tr style={{ borderBottom:"1px solid rgba(255,255,255,0.1)" }}>
                      {["Name","City","Chain","SKUs","Verified","Top Terpene"].map(h=>(
                        <th key={h} style={{ padding:"8px 10px", textAlign:"left", color:"rgba(240,244,240,0.35)", fontWeight:400, whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {dispStats.map(d=>(
                      <tr key={d.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}
                        onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <td style={{ padding:"7px 10px", color:"#f0f4f0", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{d.name}</td>
                        <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.6)" }}>{d.city}</td>
                        <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.4)" }}>{d.chain||"Independent"}</td>
                        <td style={{ padding:"7px 10px", color:"#4ade80", fontWeight:700 }}>{d.skus}</td>
                        <td style={{ padding:"7px 10px", color: d.verifiedPct>=70?"#4ade80":d.verifiedPct>=40?"#fbbf24":"#f87171" }}>{d.verifiedPct}%</td>
                        <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.5)" }}>{d.topTerp||"—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── LP ROSTER ── */}
          {founderTab === "lp-roster" && (
            <div className="fade" style={{ padding:"0 8px" }}>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>All LPs</div>
                <div style={{ fontSize:11, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Licensed producers with SKUs in system</div>
              </div>
              <div style={{ overflowX:"auto" }}>
                {(() => {
                  const lpMap = {};
                  ALL_PRODUCTS.forEach(p => {
                    const lp = p.cultivation?.lp; if (!lp) return;
                    if (!lpMap[lp]) lpMap[lp] = { name:lp, skus:0, verified:0, cats:new Set(), stores:new Set() };
                    lpMap[lp].skus++; if (p.terpene_source==="lab_tested"||p.terpene_source==="lp_provided") lpMap[lp].verified++;
                    lpMap[lp].cats.add(p.category); lpMap[lp].stores.add(p.dispensary_id);
                  });
                  const lpList = Object.values(lpMap).map(lp=>({...lp, verifiedPct:Math.round(lp.verified/lp.skus*100), cats:[...lp.cats], stores:lp.stores.size})).sort((a,b)=>b.skus-a.skus);
                  return (
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:10, fontFamily:"'DM Mono',monospace" }}>
                      <thead>
                        <tr style={{ borderBottom:"1px solid rgba(255,255,255,0.1)" }}>
                          {["LP Name","SKUs","Verified %","Stores","Categories","Status"].map(h=>(
                            <th key={h} style={{ padding:"8px 10px", textAlign:"left", color:"rgba(240,244,240,0.35)", fontWeight:400, whiteSpace:"nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {lpList.map(lp=>(
                          <tr key={lp.name} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}
                            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <td style={{ padding:"7px 10px", color:"#f0f4f0", maxWidth:200, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lp.name}</td>
                            <td style={{ padding:"7px 10px", color:"#60a5fa", fontWeight:700 }}>{lp.skus}</td>
                            <td style={{ padding:"7px 10px", color: lp.verifiedPct>=70?"#4ade80":lp.verifiedPct>=30?"#fbbf24":"#f87171" }}>{lp.verifiedPct}%</td>
                            <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.5)" }}>{lp.stores}</td>
                            <td style={{ padding:"7px 10px", color:"rgba(240,244,240,0.4)", maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lp.cats.slice(0,2).join(", ")}</td>
                            <td style={{ padding:"7px 10px" }}>
                              <span style={{ fontSize:9, padding:"2px 7px", borderRadius:5, background: lp.verifiedPct===100?"rgba(74,173,89,0.15)":lp.verifiedPct>0?"rgba(251,191,36,0.12)":"rgba(248,113,113,0.12)", color: lp.verifiedPct===100?"#4ade80":lp.verifiedPct>0?"#fbbf24":"#f87171" }}>
                                {lp.verifiedPct===100?"Verified":lp.verifiedPct>0?"Partial":"Estimated"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ── PLATFORM HEALTH ── */}
          {founderTab === "platform" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Platform Health</h2>
                <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Data quality, coverage, build status, and feedback.</p>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
                {/* Data quality */}
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                  <div style={{ fontSize:10, color:"#4ade80", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:14 }}>DATA QUALITY</div>
                  {(() => {
                    const labTested = ALL_PRODUCTS.filter(p=>p.terpene_source==="lab_tested").length;
                    const lpProvided = ALL_PRODUCTS.filter(p=>p.terpene_source==="lp_provided").length;
                    const estimated = ALL_PRODUCTS.filter(p=>p.terpene_source==="estimated").length;
                    return [
                      { label:"Lab tested", value:labTested, pct:Math.round(labTested/totalSKUs*100), color:"#4ade80" },
                      { label:"LP provided", value:lpProvided, pct:Math.round(lpProvided/totalSKUs*100), color:"#60a5fa" },
                      { label:"Estimated", value:estimated, pct:Math.round(estimated/totalSKUs*100), color:"#fbbf24" },
                    ].map(({label,value,pct,color})=>(
                      <div key={label} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:11, color:"rgba(240,244,240,0.6)", fontFamily:"'DM Mono',monospace" }}>{label}</span>
                          <span style={{ fontSize:11, color, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{value} ({pct}%)</span>
                        </div>
                        <div style={{ height:5, borderRadius:3, background:"rgba(255,255,255,0.06)" }}>
                          <div style={{ height:"100%", borderRadius:3, background:color, width:(pct) + "%" }}/>
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Coverage */}
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                  <div style={{ fontSize:10, color:"#60a5fa", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em", marginBottom:14 }}>COVERAGE</div>
                  {[
                    { label:"Total dispensaries", value:totalDisps },
                    { label:"With SKU data", value:dispStats.filter(d=>d.skus>0).length },
                    { label:"BC cities covered", value:cities.length },
                    { label:"LP brands in system", value:[...new Set(ALL_PRODUCTS.map(p=>p.cultivation?.lp).filter(Boolean))].length },
                    { label:"Consumer profiles", value:totalProfiles },
                    { label:"Imported COAs", value:importedCount },
                  ].map(({label,value})=>(
                    <div key={label} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                      <span style={{ fontSize:11, color:"rgba(240,244,240,0.5)", fontFamily:"'DM Mono',monospace" }}>{label}</span>
                      <span style={{ fontSize:12, fontWeight:700, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Feedback log */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:18 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                  <div style={{ fontSize:10, color:"#fb923c", fontFamily:"'DM Mono',monospace", fontWeight:600, letterSpacing:"0.06em" }}>USER FEEDBACK ({feedbackLog.length})</div>
                  {feedbackLog.length > 0 && (
                    <button onClick={()=>setFeedbackLog([])}
                      style={{ fontSize:9, fontFamily:"'DM Mono',monospace", color:"rgba(240,244,240,0.3)", background:"none", border:"1px solid rgba(255,255,255,0.1)", borderRadius:5, padding:"3px 8px", cursor:"pointer" }}>
                      Clear all
                    </button>
                  )}
                </div>
                {feedbackLog.length === 0 ? (
                  <div style={{ fontSize:10, color:"rgba(240,244,240,0.2)", fontFamily:"'DM Mono',monospace" }}>No feedback submitted yet.</div>
                ) : feedbackLog.map((f,i)=>(
                  <div key={i} style={{ padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                    <div style={{ fontSize:11, color:"#f0f4f0", fontFamily:"'DM Mono',monospace" }}>{f.feedback||"(no message)"} {f.productName ? "— " + (f.productName) : ""}</div>
                    <div style={{ fontSize:9, color:"rgba(240,244,240,0.3)", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{f.context||""} · {f.ts?new Date(f.ts).toLocaleString():""}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── COA LOG ── */}
          {founderTab === "coa-log" && (
            <div className="fade">
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                <div>
                  <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>COA Submission Log</h2>
                  <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>Every parse attempt logged with extraction quality metrics.</p>
                </div>
                <button onClick={()=>{ setCoaLog([]); try{localStorage.removeItem("iterps_coa_log");}catch (_e) {} }}
                  style={{ padding:"6px 14px", borderRadius:8, border:"1px solid rgba(248,113,113,0.3)", background:"transparent", color:"#f87171", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                  Clear log
                </button>
              </div>
              {coaLog.length === 0 ? (
                <div style={{ textAlign:"center", padding:"60px 20px" }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>🧪</div>
                  <div style={{ fontSize:12, color:"rgba(240,244,240,0.25)", fontFamily:"'DM Mono',monospace" }}>No COA submissions yet. Upload a COA via the LP Portal to see parse results here.</div>
                </div>
              ) : coaLog.slice().reverse().map((entry,i)=>(
                <div key={i} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"14px 16px", marginBottom:10 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                    <div style={{ fontSize:12, color:"#f0f4f0", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{entry.filename||"Unknown file"}</div>
                    <div style={{ fontSize:10, color: entry.success?"#4ade80":"#f87171", fontFamily:"'DM Mono',monospace" }}>{entry.success?"✓ Parsed":"✗ Failed"}</div>
                  </div>
                  <div style={{ fontSize:10, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace", lineHeight:1.8 }}>
                    {entry.lab && <span>Lab: {entry.lab} · </span>}
                    {entry.terpeneCount && <span>{entry.terpeneCount} terpenes extracted · </span>}
                    {entry.ts && <span>{new Date(entry.ts).toLocaleString()}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── NATIONAL ── */}
          {founderTab === "national" && (
            <div className="fade">
              <div style={{ marginBottom:20 }}>
                <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>National Rollout</h2>
                <p style={{ fontSize:11, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>BC → Canada → Global. Expansion sequencing and compliance readiness.</p>
              </div>

              {/* Phase timeline */}
              {[
                { phase:"Phase 1 — BC (Now)", color:"#4ade80", status:"Active", items:[
                  "55+ verified BC dispensaries across 20+ cities",
                  "334 SKUs with terpene data (lab + estimated)",
                  "Cannabis Act compliant — zero effect/mood language",
                  "Kiosk mode for in-store tablet deployment",
                  "LP portal with COA parser (Anandia, Steep Hill, Eurofins formats)",
                ]},
                { phase:"Phase 2 — Alberta + Ontario (Q3 2026)", color:"#60a5fa", status:"Planning", items:[
                  "Alberta Gaming Liquor and Cannabis (AGLC) licensed retailers",
                  "Ontario Cannabis Store (OCS) private retail network",
                  "Same Cannabis Act framework — minimal compliance delta",
                  "Target: 25+ Alberta stores, 50+ Ontario stores at launch",
                  "Partnership target: Spiritleaf national chain (already in data)",
                ]},
                { phase:"Phase 3 — National (2027)", color:"#a78bfa", status:"Future", items:[
                  "Saskatchewan, Manitoba, Quebec, Atlantic provinces",
                  "French language support for Quebec market",
                  "National LP agreements (Canopy, Tilray, Cronos, Aurora)",
                  "Data licensing revenue from national LP analytics",
                  "Target: 500+ locations, 5K+ SKUs",
                ]},
                { phase:"Phase 4 — International (2028+)", color:"#fb923c", status:"Future", items:[
                  "Germany (legalization framework live as of April 2024)",
                  "Netherlands (licensed cultivation pilot program)",
                  "Australia (TGA medicinal — different compliance layer)",
                  "US states (state-by-state, starting with OR, WA, CO)",
                  "Jurisdiction config layer — toggle compliance rules per market",
                ]},
              ].map(({phase, color, status, items})=>(
                <div key={phase} style={{ marginBottom:20, background:"rgba(255,255,255,0.03)", border:"1px solid " + (color) + "30", borderRadius:12, padding:18 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                    <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, fontWeight:400, color:"#f0f4f0" }}>{phase}</div>
                    <div style={{ fontSize:9, padding:"3px 10px", borderRadius:10, background:(color) + "20", color, fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{status}</div>
                  </div>
                  {items.map((item,i)=>(
                    <div key={i} style={{ display:"flex", gap:10, padding:"4px 0" }}>
                      <span style={{ color, fontSize:10, flexShrink:0, marginTop:2 }}>→</span>
                      <span style={{ fontSize:11, color:"rgba(240,244,240,0.6)", fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>{item}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* ── EVENTS ── */}
          {founderTab === "events" && (
            <div className="fade">
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
                <div>
                  <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#f0f4f0", marginBottom:4 }}>Event Stream</h2>
                  <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace" }}>All tracked interactions this session. Persists to localStorage.</p>
                </div>
                <button onClick={()=>{ setSessionEvents([]); try{localStorage.removeItem("iterps_session_events");}catch (_e) {} }}
                  style={{ padding:"6px 14px", borderRadius:8, border:"1px solid rgba(248,113,113,0.3)", background:"transparent", color:"#f87171", fontSize:10, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                  Clear events
                </button>
              </div>
              {sessionEvents.length === 0 ? (
                <div style={{ textAlign:"center", padding:"60px 20px", color:"rgba(240,244,240,0.25)", fontFamily:"'DM Mono',monospace", fontSize:12 }}>No events this session.</div>
              ) : (
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, overflow:"hidden" }}>
                  {sessionEvents.slice().reverse().map((ev,i)=>(
                    <div key={i} style={{ display:"flex", gap:16, padding:"9px 16px", borderBottom:"1px solid rgba(255,255,255,0.04)", alignItems:"center" }}>
                      <span style={{ fontSize:9, color:"rgba(240,244,240,0.25)", fontFamily:"'DM Mono',monospace", flexShrink:0, minWidth:55 }}>{new Date(ev.ts).toLocaleTimeString()}</span>
                      <span style={{ fontSize:11, color:"rgba(240,244,240,0.65)", fontFamily:"'DM Mono',monospace" }}>{ev.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    );
  };


  // ── BATCH TRACKER PAGE ───────────────────────────────────
  const BatchTrackerPage = () => {
    const selLP = batchTrackerLP, setSelLP = setBatchTrackerLP;
    const selBrand = batchTrackerBrand, setSelBrand = setBatchTrackerBrand;
    const selProduct = batchTrackerProduct, setSelProduct = setBatchTrackerProduct;
    const selTerpene = batchTrackerTerpene, setSelTerpene = setBatchTrackerTerpene;

    // LP role: lock to their own LP — do not expose competitor LP data
    // Staff role: scope to brands carried at their store
    const batchPool = (() => {
      if (onboardRole === "lp" && activeLp) return ALL_PRODUCTS.filter(p => p.cultivation?.lp === activeLp);
      if (onboardRole === "staff" && activeDispensary) return ALL_PRODUCTS.filter(p => p.dispensary_id === activeDispensary);
      return ALL_PRODUCTS;
    })();

    // LP dropdown: hide other LPs when logged in as LP
    const allLPs = onboardRole === "lp" && activeLp
      ? [activeLp]
      : ["all", ...new Set(batchPool.map(p => p.cultivation?.lp).filter(Boolean)).values()].sort((a,b) => a==="all"?-1:b==="all"?1:a.localeCompare(b));
    const brandsForLP = selLP === "all"
      ? ["all", ...new Set(batchPool.map(p => p.brand)).values()].sort((a,b) => a==="all"?-1:b==="all"?1:a.localeCompare(b))
      : ["all", ...new Set(batchPool.filter(p => p.cultivation?.lp === selLP).map(p => p.brand)).values()].sort((a,b) => a==="all"?-1:b==="all"?1:a.localeCompare(b));

    const filteredProducts = batchPool.filter(p => {
      if (selLP !== "all" && p.cultivation?.lp !== selLP) return false;
      if (selBrand !== "all" && p.brand !== selBrand) return false;
      return true;
    });

    // Group by name+brand to show unique strains (not per-dispensary duplicates)
    const uniqueStrains = [];
    const seen = new Set();
    filteredProducts.forEach(p => {
      const key = (p.brand) + "|" + (p.name);
      if (!seen.has(key)) { seen.add(key); uniqueStrains.push(p); }
    });

    // When a product is selected, find all SKUs for that strain across dispensaries
    const selectedStrainSKUs = selProduct
      ? batchPool.filter(p => p.brand === selProduct.brand && p.name === selProduct.name)
      : [];

    const batchKey = selProduct ? (selProduct.brand) + "|" + (selProduct.name) : null;
    const history = batchKey ? (batchHistory[batchKey] || []) : [];

    // Terpene filter options from current product
    const terpeneOptions = selProduct
      ? ["all", ...Object.keys(selProduct.terpenes).sort()]
      : ["all"];

    // For drift chart
    const allTerps = history.length > 0
      ? [...new Set(history.flatMap(b => Object.keys(b.terpenes)))].filter(t => selTerpene === "all" || t === selTerpene).sort()
      : Object.keys(selProduct?.terpenes || {}).sort();
    const maxV = history.length > 0
      ? Math.max(...history.flatMap(b => allTerps.map(t => b.terpenes[t] || 0)), 0.01)
      : 0.01;

    const logCurrentBatch = () => {
      if (!selProduct) return;
      const key = (selProduct.brand) + "|" + (selProduct.name);
      const existing = batchHistory[key] || [];
      if (existing.some(b => b.batch === selProduct.batch)) return;
      const entry = {
        batch: selProduct.batch || "Unknown",
        lot: selProduct.lot || null,
        tested: selProduct.tested || new Date().toISOString().split("T")[0],
        thc: selProduct.thc,
        cbd: selProduct.cbd,
        total_terpenes: selProduct.total_terpenes,
        terpenes: { ...selProduct.terpenes },
        lab: selProduct.lab || null,
        loggedAt: new Date().toISOString(),
      };
      const updated = { ...batchHistory, [key]: [...existing, entry] };
      setBatchHistory(updated);
      try { localStorage.setItem("iterps_batches", JSON.stringify(updated)); } catch (_e) {}
    };

    const currentBatchLogged = selProduct
      ? (batchHistory[(selProduct.brand) + "|" + (selProduct.name)] || []).some(b => b.batch === selProduct.batch)
      : false;

    return (
      <div className="fade" style={{ maxWidth:900, margin:"0 auto", padding:"28px 20px" }}>
        {/* Header */}
        <button onClick={()=>setPage("dashboard")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:20}}>← Dashboard</button>
          <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:"#1a2e1a", marginBottom:4 }}>Batch Tracker</h1>
          <p style={{ fontSize:12, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
            Track terpene profile drift across batches — filter by LP, brand, or strain.
          </p>
        </div>

        {/* Expiry / freshness alerts */}
        {(() => {
          const today = new Date();
          const expiring = batchPool.filter(p => {
            if (!p.packaged_date) return false;
            const packed = new Date(p.packaged_date);
            const ageMonths = (today - packed) / (1000*60*60*24*30.5);
            return ageMonths >= 10; // flag products packaged 10+ months ago
          });
          const noLab = batchPool.filter(p => p.terpene_source === "estimated");
          if (!expiring.length && !noLab.length) return null;
          return (
            <div style={{ marginBottom:16, display:"flex", flexDirection:"column", gap:8 }}>
              {expiring.length > 0 && (
                <div style={{ background:"#fef3c7", border:"1px solid #fcd34d", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:18 }}>⚠️</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"#92400e", fontFamily:"'DM Mono',monospace" }}>
                      {expiring.length} product{expiring.length!==1?"s":""} packaged 10+ months ago
                    </div>
                    <div style={{ fontSize:11, color:"#b45309", marginTop:2 }}>
                      {expiring.slice(0,3).map(p=>p.name).join(", ")}{expiring.length>3?" +" + (expiring.length-3) + " more":""}
                    </div>
                  </div>
                </div>
              )}
              {noLab.length > 0 && (
                <div style={{ background:"#f0fdf4", border:"1px solid #86efac", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:18 }}>🧪</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:"#15803d", fontFamily:"'DM Mono',monospace" }}>
                      {noLab.length} product{noLab.length!==1?"s":""} with estimated terpene data — upload COA to verify
                    </div>
                    <div style={{ fontSize:11, color:"#16a34a", marginTop:2 }}>
                      <button onClick={()=>setPage("lp")} style={{ background:"none", border:"none", cursor:"pointer", color:"#16a34a", textDecoration:"underline", fontSize:11, padding:0, fontFamily:"'DM Mono',monospace" }}>
                        Go to LP Portal → Upload COA
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Filter bar */}
        <div style={{ background:"#fff", border:"1px solid #e8e4de", borderRadius:14, padding:"16px 18px", marginBottom:20, display:"flex", gap:12, flexWrap:"wrap", alignItems:"flex-end" }}>

          {/* LP dropdown */}
          <div style={{ flex:"1 1 180px", minWidth:160 }}>
            <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.08em" }}>Licensed Producer</div>
            <select value={selLP} onChange={e=>{ setSelLP(e.target.value); setSelBrand("all"); setSelProduct(null); }}
              style={{ width:"100%", padding:"8px 10px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", color:"#1a2e1a", outline:"none", cursor:"pointer" }}>
              {allLPs.map(lp => <option key={lp} value={lp}>{lp === "all" ? "All LPs" : lp}</option>)}
            </select>
          </div>

          {/* Brand dropdown */}
          <div style={{ flex:"1 1 160px", minWidth:140 }}>
            <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.08em" }}>Brand</div>
            <select value={selBrand} onChange={e=>{ setSelBrand(e.target.value); setSelProduct(null); }}
              style={{ width:"100%", padding:"8px 10px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", color:"#1a2e1a", outline:"none", cursor:"pointer" }}>
              {brandsForLP.map(b => <option key={b} value={b}>{b === "all" ? "All Brands" : b}</option>)}
            </select>
          </div>

          {/* Strain dropdown */}
          <div style={{ flex:"2 1 220px", minWidth:180 }}>
            <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.08em" }}>
              Strain / Product {uniqueStrains.length > 0 && <span style={{ color:"#4a7c59" }}>({uniqueStrains.length} available)</span>}
            </div>
            <select value={selProduct ? (selProduct.brand) + "|" + (selProduct.name) : "none"}
              onChange={e=>{
                if (e.target.value === "none") { setSelProduct(null); return; }
                const [brand, name] = e.target.value.split("|");
                const prod = batchPool.find(p => p.brand === brand && p.name === name);
                setSelProduct(prod || null);
                setSelTerpene("all");
              }}
              style={{ width:"100%", padding:"8px 10px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", color:"#1a2e1a", outline:"none", cursor:"pointer" }}>
              <option value="none">— Select a strain —</option>
              {uniqueStrains.sort((a,b) => a.name.localeCompare(b.name)).map(p => (
                <option key={(p.brand) + "|" + (p.name)} value={(p.brand) + "|" + (p.name)}>
                  {p.name} — {p.brand}
                </option>
              ))}
            </select>
          </div>

          {/* Terpene filter (only when product selected) */}
          {selProduct && (
            <div style={{ flex:"1 1 140px", minWidth:120 }}>
              <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.08em" }}>Terpene Focus</div>
              <select value={selTerpene} onChange={e=>setSelTerpene(e.target.value)}
                style={{ width:"100%", padding:"8px 10px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", color:"#1a2e1a", outline:"none", cursor:"pointer" }}>
                {terpeneOptions.map(t => <option key={t} value={t}>{t === "all" ? "All Terpenes" : t}</option>)}
              </select>
            </div>
          )}

          {selProduct && (
            <button onClick={()=>setSelProduct(null)}
              style={{ padding:"8px 14px", borderRadius:8, border:"1px solid #e8e4de", background:"#fff", color:"#9ca3af", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer", alignSelf:"flex-end" }}>
              ✕ Clear
            </button>
          )}
        </div>

        {/* No product selected — show strain grid */}
        {!selProduct && (
          <div>
            {uniqueStrains.length === 0 ? (
              <div style={{ textAlign:"center", padding:"48px 20px", color:"#9ca3af", fontFamily:"'DM Mono',monospace", fontSize:13 }}>
                No products match this filter.
              </div>
            ) : (
              <>
                <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:12 }}>
                  {uniqueStrains.length} strains · select one above or click a card to view batch history
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:10 }}>
                  {uniqueStrains.sort((a,b)=>a.name.localeCompare(b.name)).map(p => {
                    const key = (p.brand) + "|" + (p.name);
                    const logged = batchHistory[key] || [];
                    const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
                    const tInfo = topT ? TERPENES[topT[0]] : null;
                    return (
                      <div key={key} className="card card-click" style={{ padding:"14px 16px", borderLeft:"3px solid " + (tInfo?.color||"#e8e4de") }}
                        onClick={()=>{ setSelProduct(p); setSelTerpene("all"); }}>
                        <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:2 }}>{p.brand}</div>
                        <div style={{ fontFamily:"'Fraunces',serif", fontSize:15, fontWeight:400, color:"#1a2e1a", marginBottom:4 }}>{p.name}</div>
                        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                          {topT && (
                            <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>
                              <span style={{ width:6, height:6, borderRadius:"50%", background:tInfo?.color||"#4a7c59", display:"inline-block" }}/>
                              {topT[0]}
                            </span>
                          )}
                          <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#22c55e" }}>{p.thc}% THC</span>
                          {logged.length > 0
                            ? <span style={{ fontSize:9, background:"#f0fdf4", color:"#166534", border:"1px solid #bbf7d0", padding:"1px 7px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>{logged.length} batch{logged.length>1?"es":""} logged</span>
                            : <span style={{ fontSize:9, background:"#f7f5f0", color:"#9ca3af", border:"1px solid #e8e4de", padding:"1px 7px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>No batches yet</span>
                          }
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Product selected — show full batch detail */}
        {selProduct && (
          <div className="fade">
            {/* Product header */}
            <div style={{ background:"linear-gradient(135deg,#1a2e1a,#2d4a2d)", borderRadius:14, padding:"20px 22px", marginBottom:16, color:"#f0f4f0", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
              <div>
                <div style={{ fontSize:9, opacity:0.5, fontFamily:"'DM Mono',monospace", marginBottom:4, letterSpacing:"0.08em" }}>
                  {selProduct.brand} · {selProduct.cultivation?.lp}
                </div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:24, fontWeight:300, marginBottom:8 }}>{selProduct.name}</div>
                <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                  <div><span style={{ fontFamily:"'Fraunces',serif", fontSize:22, color:"#4ade80" }}>{selProduct.thc}%</span> <span style={{ fontSize:9, opacity:0.5, fontFamily:"'DM Mono',monospace" }}>THC</span></div>
                  {selProduct.total_terpenes && <div><span style={{ fontFamily:"'Fraunces',serif", fontSize:22, color:"#86efac" }}>{selProduct.total_terpenes.toFixed(2)}%</span> <span style={{ fontSize:9, opacity:0.5, fontFamily:"'DM Mono',monospace" }}>Total Terps</span></div>}
                  <div><span style={{ fontFamily:"'Fraunces',serif", fontSize:22, color:"#a7f3d0" }}>{selectedStrainSKUs.length}</span> <span style={{ fontSize:9, opacity:0.5, fontFamily:"'DM Mono',monospace" }}>Dispensaries</span></div>
                </div>
              </div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {!currentBatchLogged && (
                  <button onClick={logCurrentBatch}
                    style={{ padding:"8px 16px", borderRadius:8, border:"1px solid rgba(74,222,128,0.4)", background:"rgba(74,222,128,0.1)", color:"#4ade80", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                    + Log Batch {selProduct.batch}
                  </button>
                )}
                <button onClick={()=>{ setSelectedProduct(selProduct); setPage("product"); }}
                  style={{ padding:"8px 16px", borderRadius:8, border:"1px solid rgba(240,244,240,0.2)", background:"transparent", color:"rgba(240,244,240,0.7)", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                  View Full Profile →
                </button>
              </div>
            </div>

            {/* Current terpene snapshot */}
            <div style={{ background:"#fff", border:"1px solid #e8e4de", borderRadius:12, padding:18, marginBottom:14 }}>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:12, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                Current Batch Terpene Snapshot — {selProduct.batch || "No batch #"}
              </div>
              {Object.entries(selProduct.terpenes)
                .filter(([t]) => selTerpene === "all" || t === selTerpene)
                .sort((ea,eb)=>eb[1]-ea[1])
                .map(([t, v]) => {
                  const info = TERPENES[t];
                  const pct = Math.round(v * 100);
                  // Compare to history avg if available
                  const histVals = history.map(b => b.terpenes[t] || 0);
                  const histAvg = histVals.length > 0 ? histVals.reduce((a,b)=>a+b,0)/histVals.length : null;
                  const drift = histAvg !== null ? ((v - histAvg) / Math.max(histAvg, 0.001) * 100).toFixed(0) : null;
                  return (
                    <div key={t} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                      <div style={{ width:8, height:8, borderRadius:"50%", background:info?.color||"#4a7c59", flexShrink:0 }}/>
                      <div style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace", width:200, flexShrink:0 }}>{t}</div>
                      <div style={{ flex:1, height:6, background:"#f3f4f6", borderRadius:3 }}>
                        <div style={{ height:6, background:info?.color||"#4a7c59", borderRadius:3, width:(Math.min(pct * 2, 100)) + "%", opacity:0.8 }}/>
                      </div>
                      <div style={{ fontSize:11, fontFamily:"'DM Mono',monospace", color:"#374151", minWidth:55, textAlign:"right" }}>{pct}mg/g</div>
                      {drift !== null && (
                        <div style={{ fontSize:9, fontFamily:"'DM Mono',monospace", minWidth:48, textAlign:"right",
                          color: Math.abs(Number(drift)) > 15 ? "#dc2626" : Math.abs(Number(drift)) > 5 ? "#f59e0b" : "#16a34a" }}>
                          {Number(drift) > 0 ? "↑" + (drift) + "%" : Number(drift) < 0 ? "↓" + (Math.abs(Number(drift))) + "%" : "Stable"}
                        </div>
                      )}
                    </div>
                  );
                })}
              {history.length > 0 && (() => {
                const flagKey = (selProduct.brand) + "|" + (selProduct.name) + "|" + (selProduct.batch);
                const isFlagged = flaggedDrifts[flagKey];
                // Calculate max drift for current batch vs history avg
                const maxDriftTerp = Object.entries(selProduct.terpenes).map(([t,v]) => {
                  const histVals = history.map(b => b.terpenes[t]||0);
                  if (!histVals.length) return { t, drift: 0 };
                  const avg = histVals.reduce((a,b)=>a+b,0)/histVals.length;
                  return { t, drift: avg > 0 ? Math.abs((v-avg)/avg*100) : 0 };
                }).sort((a,b)=>b.drift-a.drift)[0];
                const maxDrift = maxDriftTerp?.drift || 0;
                const severity = maxDrift > 25 ? "high" : maxDrift > 12 ? "medium" : "low";

                return (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:10, flexWrap:"wrap", gap:8 }}>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
                      Drift % vs. avg of {history.length} logged batch{history.length>1?"es":""}
                    </div>
                    <button onClick={()=>{ const next={...flaggedDrifts,[flagKey]:{product:selProduct.name,brand:selProduct.brand,batch:selProduct.batch,drift:maxDrift.toFixed(0),severity,flaggedAt:new Date().toISOString()}}; setFlaggedDrifts(next); try{localStorage.setItem("iterps_drift_flags",JSON.stringify(next));}catch (_e) {}}}
                      style={{ padding:"5px 12px", borderRadius:8, border:"1px solid " + (isFlagged?"#bbf7d0":"#e8e4de"), background:isFlagged?"#f0fdf4":"#fff", cursor:"pointer", fontSize:10, fontFamily:"'DM Mono',monospace", color:isFlagged?"#166534":"#6b7280", display:"flex", alignItems:"center", gap:5 }}>
                      {isFlagged ? "✓ Flagged — Budtenders Alerted" : "🚩 Flag Drift Alert"}
                    </button>
                  </div>
                );
              })()}
              {history.length === 0 && (
                <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginTop:8 }}>Log a batch above to start tracking drift</div>
              )}
            </div>

            {/* Batch history */}
            {history.length === 0 ? (
              <div style={{ background:"#f7f5f0", border:"1px solid #e8e4de", borderRadius:12, padding:"28px 20px", textAlign:"center", color:"#9ca3af", fontFamily:"'DM Mono',monospace", fontSize:12 }}>
                <div style={{ fontSize:24, marginBottom:8 }}>📋</div>
                No batches logged for {selProduct.name} yet.
                {!currentBatchLogged && (
                  <div style={{ marginTop:10 }}>
                    <button onClick={logCurrentBatch}
                      style={{ padding:"8px 20px", borderRadius:8, border:"1px solid #e8e4de", background:"#fff", color:"#374151", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
                      Log Current Batch ({selProduct.batch || "this batch"})
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Drift chart — only with 2+ batches */}
                {history.length >= 2 && (
                  <div style={{ background:"#fff", border:"1px solid #e8e4de", borderRadius:12, padding:18, marginBottom:14 }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:14, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                      Terpene Drift — {history.length} Batches
                    </div>
                    <div style={{ overflowX:"auto" }}>
                      <div style={{ minWidth: Math.max(400, history.length * 80), display:"flex", flexDirection:"column", gap:10 }}>
                        {allTerps.map(t => {
                          const info = TERPENES[t];
                          const vals = history.map(b => b.terpenes[t]||0);
                          const minV = Math.min(...vals), maxBV = Math.max(...vals);
                          const driftAmt = maxBV - minV;
                          const driftPct = maxV > 0 ? Math.round(driftAmt/maxV*100) : 0;
                          return (
                            <div key={t}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                  <div style={{ width:7, height:7, borderRadius:"50%", background:info?.color||"#4a7c59" }}/>
                                  <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                                </div>
                                <span style={{ fontSize:9, fontFamily:"'DM Mono',monospace",
                                  color: driftPct > 20 ? "#dc2626" : driftPct > 10 ? "#f59e0b" : "#16a34a" }}>
                                  {driftPct > 0 ? "±" + (driftPct) + "% drift" : "Stable across batches"}
                                </span>
                              </div>
                              <div style={{ display:"flex", gap:3, alignItems:"flex-end", height:32 }}>
                                {vals.map((v, i) => (
                                  <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                                    <div style={{ width:"100%", position:"relative", height:24, background:"#f7f5f0", borderRadius:3, overflow:"hidden" }}>
                                      <div style={{ position:"absolute", bottom:0, left:0, right:0,
                                        background: info?.color||"#4a7c59", opacity: 0.5 + (v/maxV)*0.5,
                                        borderRadius:3, height:(maxV>0?(v/maxV)*100:0) + "%" }}/>
                                    </div>
                                    <div style={{ fontSize:8, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textAlign:"center", whiteSpace:"nowrap", overflow:"hidden", maxWidth:"100%" }}>
                                      {(v*100).toFixed(0)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        {/* Batch labels */}
                        <div style={{ display:"flex", gap:3, marginTop:4 }}>
                          {history.map((b, i) => (
                            <div key={i} style={{ flex:1, fontSize:8, color:"#9ca3af", fontFamily:"'DM Mono',monospace", textAlign:"center", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", borderTop:"1px solid #f3f4f6", paddingTop:4 }}>
                              {b.batch || "B" + (i+1)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Batch table */}
                <div style={{ background:"#fff", border:"1px solid #e8e4de", borderRadius:12, overflow:"hidden" }}>
                  <div style={{ padding:"12px 18px", borderBottom:"1px solid #f3f4f6", fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                    Batch Log
                  </div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11, fontFamily:"'DM Mono',monospace" }}>
                      <thead>
                        <tr style={{ borderBottom:"1px solid #f3f4f6" }}>
                          {["Batch #","Tested","THC %","Total Terps","Top Terpene","Lab","Logged"].map(h => (
                            <th key={h} style={{ textAlign:"left", padding:"8px 14px", fontSize:9, color:"#9ca3af", letterSpacing:"0.06em", fontWeight:400 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...history].reverse().map((b, i) => {
                          const topT = Object.entries(b.terpenes).sort(([,a],[,bb])=>bb-a)[0];
                          const isCurrent = b.batch === selProduct.batch;
                          return (
                            <tr key={i} style={{ borderBottom:"1px solid #f9fafb", background: isCurrent ? "#f0fdf4" : "transparent" }}>
                              <td style={{ padding:"10px 14px", color: isCurrent?"#166534":"#374151", fontWeight: isCurrent?600:400 }}>
                                {b.batch||"—"}
                                {isCurrent && <span style={{ fontSize:8, background:"#bbf7d0", color:"#166534", padding:"1px 6px", borderRadius:8, marginLeft:6 }}>current</span>}
                              </td>
                              <td style={{ padding:"10px 14px", color:"#6b7280" }}>{b.tested||"—"}</td>
                              <td style={{ padding:"10px 14px", color:"#22c55e", fontWeight:600 }}>{b.thc}%</td>
                              <td style={{ padding:"10px 14px", color:"#6b7280" }}>{b.total_terpenes?(b.total_terpenes.toFixed(2)) + "%":"—"}</td>
                              <td style={{ padding:"10px 14px" }}>
                                {topT && (
                                  <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
                                    <span style={{ width:6,height:6,borderRadius:"50%",background:TERPENES[topT[0]]?.color||"#4a7c59",display:"inline-block" }}/>
                                    {topT[0]} · {(topT[1]*100).toFixed(0)}mg/g
                                  </span>
                                )}
                              </td>
                              <td style={{ padding:"10px 14px", color:"#9ca3af" }}>{b.lab||"—"}</td>
                              <td style={{ padding:"10px 14px", color:"#9ca3af", fontSize:9 }}>{b.loggedAt?new Date(b.loggedAt).toLocaleDateString("en-CA"):"—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Dispensary availability */}
                {selectedStrainSKUs.length > 1 && (
                  <div style={{ marginTop:14, background:"#fff", border:"1px solid #e8e4de", borderRadius:12, padding:16 }}>
                    <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                      Available At ({selectedStrainSKUs.length} locations)
                    </div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                      {selectedStrainSKUs.map(sku => {
                        const disp = DB_DISPENSARIES.find(d=>d.id===sku.dispensary_id);
                        return disp ? (
                          <div key={sku.id} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", background:"#f7f5f0", borderRadius:8, border:"1px solid #e8e4de", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}
                            onClick={()=>{ setDispDetailId(disp.id); }}>
                            <span style={{ width:6,height:6,borderRadius:"50%",background:sku.in_stock?"#22c55e":"#ef4444",display:"inline-block" }}/>
                            {disp.name} · <span style={{ color:"#9ca3af", fontSize:10 }}>{disp.city}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };



  // ── FEEDBACK FLAG SYSTEM ──────────────────────────────────
  const FlagButton = ({ context, productId, productName, style={} }) => (
    <button
      onClick={() => { setFeedbackModal({ context, productId, productName }); setFeedbackText(""); setFeedbackSent(false); }}
      title="Flag an issue with this result"
      style={{ background:"none", border:"1px solid #e8e4de", borderRadius:8, padding:"4px 10px", fontSize:10,
        fontFamily:"'DM Mono',monospace", color:"#9ca3af", cursor:"pointer", display:"inline-flex", alignItems:"center",
        gap:4, transition:"all 0.15s", ...style }}
      onMouseEnter={e=>{ e.currentTarget.style.borderColor="#ef4444"; e.currentTarget.style.color="#ef4444"; }}
      onMouseLeave={e=>{ e.currentTarget.style.borderColor="#e8e4de"; e.currentTarget.style.color="#9ca3af"; }}>
      ⚑ Flag
    </button>
  );

  // ── GLOBAL SEARCH MODAL ──────────────────────────────────
  const GlobalSearchModal = () => {
    if (!showGlobalSearch) return null;
    const q = globalSearch.toLowerCase().trim();
    const results = q.length < 2 ? [] : ALL_PRODUCTS.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.brand?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.plant_type?.toLowerCase().includes(q) ||
      (p.aroma||[]).some(a => a.toLowerCase().includes(q)) ||
      Object.keys(p.terpenes||{}).some(t => t.toLowerCase().includes(q)) ||
      p.genetics?.lineage?.toLowerCase().includes(q) ||
      p.cultivation?.lp?.toLowerCase().includes(q)
    ).slice(0, 12);
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9800, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"60px 16px 16px" }}
        onClick={e=>{ if(e.target===e.currentTarget){ setShowGlobalSearch(false); setGlobalSearch(""); }}}>
        <div style={{ background:"#fff", borderRadius:16, width:"100%", maxWidth:600, boxShadow:"0 24px 60px rgba(0,0,0,0.25)", overflow:"hidden" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"14px 16px", borderBottom:"1px solid #f3f4f6" }}>
            <span style={{ fontSize:16, color:"#9ca3af" }}>🔍</span>
            <input autoFocus value={globalSearch} onChange={e=>setGlobalSearch(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Escape"){ setShowGlobalSearch(false); setGlobalSearch(""); }}}
              placeholder="Search products, brands, terpenes, aromas, genetics…"
              style={{ flex:1, border:"none", outline:"none", fontSize:14, fontFamily:"'DM Mono',monospace", color:"#1a2e1a", background:"transparent" }} />
            {globalSearch && <button onClick={()=>setGlobalSearch("")} style={{ background:"none", border:"none", color:"#9ca3af", cursor:"pointer", fontSize:16, lineHeight:1, padding:0 }}>✕</button>}
          </div>
          {q.length < 2 && (
            <div style={{ padding:"24px 16px", textAlign:"center", color:"#9ca3af", fontSize:12, fontFamily:"'DM Mono',monospace" }}>
              Type at least 2 characters to search {ALL_PRODUCTS.length} products
            </div>
          )}
          {q.length >= 2 && results.length === 0 && (
            <div style={{ padding:"32px 16px", textAlign:"center" }}>
              <div style={{ fontSize:28, marginBottom:10 }}>🔍</div>
              <div style={{ fontSize:13, color:"#1a2e1a", fontFamily:"'Fraunces',serif", marginBottom:6 }}>No products found for "{q}"</div>
              <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:16 }}>Try a terpene name, brand, aroma, or category</div>
              <button onClick={()=>{ setShowGlobalSearch(false); setPage("explorer"); }} style={{ background:"none", border:"1px solid #e8e4de", color:"#4a7c59", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace", padding:"7px 16px", borderRadius:8 }}>Browse all {ALL_PRODUCTS.length} products →</button>
            </div>
          )}
          {results.length > 0 && (
            <div style={{ maxHeight:420, overflowY:"auto" }}>
              {results.map(p => {
                const topT = Object.entries(p.terpenes||{}).sort((ea,eb)=>eb[1]-ea[1])[0];
                const ti = topT ? TERPENES[topT[0]] : null;
                const f = getFreshness(p);
                return (
                  <div key={p.id} onClick={()=>{ viewProduct(p, "explorer"); setShowGlobalSearch(false); setGlobalSearch(""); }}
                    style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderBottom:"1px solid #f9fafb", cursor:"pointer", transition:"background 0.1s" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f9fafb"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{ width:36, height:36, borderRadius:10, background:ti ? (ti.color) + "20" : "#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <span style={{ width:10, height:10, borderRadius:"50%", background:ti?.color||"#9ca3af", display:"inline-block" }} />
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                        <span style={{ fontSize:13, fontFamily:"'Fraunces',serif", color:"#1a2e1a", fontWeight:400 }}>{p.name}</span>
                        {f && <span style={{ fontSize:9, color:f.color, fontFamily:"'DM Mono',monospace", padding:"1px 5px", borderRadius:4, background:(f.color) + "15" }}>{f.label}</span>}
                      </div>
                      <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>
                        {p.brand} · {p.category} · {topT ? topT[0] : "—"}
                      </div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontSize:13, color:"#22c55e", fontWeight:600, fontFamily:"'Fraunces',serif" }}>{p.thc}%</div>
                      <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>THC</div>
                    </div>
                  </div>
                );
              })}
              {ALL_PRODUCTS.filter(p => { const q2=globalSearch.toLowerCase(); return p.name?.toLowerCase().includes(q2)||p.brand?.toLowerCase().includes(q2); }).length > 12 && (
                <div style={{ padding:"10px 16px", textAlign:"center" }}>
                  <button onClick={()=>{ setSearch(globalSearch); setPage("explorer"); setShowGlobalSearch(false); }}
                    style={{ background:"none", border:"none", color:"#4a7c59", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
                    See all results in Explorer →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── LOG FOR CUSTOMER MODAL ────────────────────────────────
  const LogForCustomerModal = () => {
    if (!logForCustModal) return null;
    const product = logForCustModal.product;
    const preselectedCustomer = logForCustModal.preselectedCustomer || null;
    const search = logForCustSearch.toLowerCase();

    // Mode A: product is set → pick customer (from ProductPage / FinderPage)
    // Mode B: preselectedCustomer is set, no product → pick product from inventory
    const filteredCustomers = profiles.filter(p =>
      !search || (p.name||"").toLowerCase().includes(search) || (p.phone||"").includes(search)
    );
    const filteredProducts = product ? [] : (activeDispensary
      ? ALL_PRODUCTS.filter(p => p.dispensary_id === activeDispensary)
      : ALL_PRODUCTS
    ).filter(p => !search || p.name.toLowerCase().includes(search) || (p.brand||"").toLowerCase().includes(search));

    const [pickedProduct, setPickedProduct] = React.useState(null);
    const resolvedProduct = product || pickedProduct;
    const resolvedCustomer = preselectedCustomer || null;

    const doLog = (customer, prod) => {
      const alreadyLogged = (customer.productHistory||[]).some(h=>h.productId===prod.id);
      if (!alreadyLogged) {
        addProductToHistory(customer.phone, prod, "noted");
        trackEvent("product_logged_for_customer", { product: prod.name, customer: customer.name });
      }
      setLogForCustDone({ customerName: customer.name||"Customer", productName: prod.name });
    };

    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9200, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
        onClick={e=>{ if(e.target===e.currentTarget){ setLogForCustModal(null); setLogForCustSearch(""); setLogForCustDone(null); }}}>
        <div style={{ background:"#fff", borderRadius:16, padding:24, maxWidth:420, width:"100%", maxHeight:"82vh", display:"flex", flexDirection:"column" }}>
          {logForCustDone ? (
            <div style={{ textAlign:"center", padding:"20px 0" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>✅</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, color:"#1a2e1a", marginBottom:6 }}>Logged for {logForCustDone.customerName}</div>
              <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>{logForCustDone.productName} added to their history</div>
              <button onClick={()=>{ setLogForCustModal(null); setLogForCustSearch(""); setLogForCustDone(null); }}
                style={{ padding:"9px 24px", borderRadius:8, border:"none", background:"#1a2e1a", color:"#fff", fontSize:12, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>Done</button>
            </div>
          ) : preselectedCustomer && !resolvedProduct ? (
            /* Mode B step 1: pick product */
            <>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, color:"#1a2e1a", marginBottom:4 }}>Log product for {preselectedCustomer.name}</div>
                <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>Search your inventory</div>
              </div>
              <input value={logForCustSearch} onChange={e=>setLogForCustSearch(e.target.value)} placeholder="Search product name or brand…" autoFocus
                style={{ padding:"9px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", marginBottom:12, outline:"none" }} />
              <div style={{ overflowY:"auto", flex:1 }}>
                {filteredProducts.slice(0,12).map(p => (
                  <div key={p.id} onClick={()=>{ doLog(preselectedCustomer, p); }}
                    style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", borderRadius:8, cursor:"pointer", marginBottom:4, background:"#f9fafb", border:"1px solid #e8e4de" }}
                    onMouseEnter={e=>e.currentTarget.style.background="#f0fdf4"} onMouseLeave={e=>e.currentTarget.style.background="#f9fafb"}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace" }}>{p.name}</div>
                      <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{p.brand} · {p.category} · {p.thc}% THC</div>
                    </div>
                    <div style={{ fontSize:10, color:"#4a7c59", fontFamily:"'DM Mono',monospace", fontWeight:600 }}>Log →</div>
                  </div>
                ))}
                {filteredProducts.length === 0 && <div style={{ textAlign:"center", padding:"24px 0", fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>No products found. Try a different search.</div>}
              </div>
              <div style={{ display:"flex", justifyContent:"flex-start", marginTop:12, paddingTop:12, borderTop:"1px solid #e8e4de" }}>
                <button onClick={()=>{ setLogForCustModal(null); setLogForCustSearch(""); }}
                  style={{ fontSize:11, fontFamily:"'DM Mono',monospace", background:"none", border:"none", color:"#9ca3af", cursor:"pointer" }}>Cancel</button>
              </div>
            </>
          ) : (
            /* Mode A: pick customer (product already known) */
            <>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, color:"#1a2e1a", marginBottom:4 }}>Log for customer</div>
                <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{resolvedProduct?.name} · {resolvedProduct?.brand}</div>
              </div>
              <input value={logForCustSearch} onChange={e=>setLogForCustSearch(e.target.value)} placeholder="Search by name or phone…" autoFocus
                style={{ padding:"9px 12px", border:"1px solid #e8e4de", borderRadius:8, fontSize:12, fontFamily:"'DM Mono',monospace", marginBottom:12, outline:"none" }} />
              <div style={{ overflowY:"auto", flex:1 }}>
                {filteredCustomers.length === 0 && (
                  <div style={{ textAlign:"center", padding:"24px 0", fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
                    {profiles.length === 0 ? "No customer profiles yet. Create one in Customers →" : "No matching customers."}
                  </div>
                )}
                {filteredCustomers.slice(0,12).map(customer => {
                  const alreadyLogged = (customer.productHistory||[]).some(h=>h.productId===resolvedProduct?.id);
                  return (
                    <div key={customer.phone||customer.id}
                      onClick={()=>{ if(resolvedProduct) doLog(customer, resolvedProduct); }}
                      style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", borderRadius:8, cursor:"pointer", marginBottom:4,
                        background: alreadyLogged ? "#f0fdf4" : "#f9fafb", border:"1px solid " + (alreadyLogged?"#bbf7d0":"#e8e4de"), transition:"all 0.1s" }}
                      onMouseEnter={e=>e.currentTarget.style.background=alreadyLogged?"#dcfce7":"#f0fdf4"}
                      onMouseLeave={e=>e.currentTarget.style.background=alreadyLogged?"#f0fdf4":"#f9fafb"}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color:"#1a2e1a", fontFamily:"'DM Mono',monospace" }}>{customer.name||"Unnamed"}</div>
                        <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
                          {customer.phone ? "📞 " + (customer.phone) : ""}{(customer.productHistory||[]).length > 0 ? " · " + ((customer.productHistory||[]).length) + " products" : ""}
                        </div>
                      </div>
                      <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color: alreadyLogged?"#15803d":"#4a7c59", fontWeight:600 }}>
                        {alreadyLogged ? "✓ Logged" : "Log →"}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12, paddingTop:12, borderTop:"1px solid #e8e4de" }}>
                <button onClick={()=>{ setLogForCustModal(null); setLogForCustSearch(""); }}
                  style={{ fontSize:11, fontFamily:"'DM Mono',monospace", background:"none", border:"none", color:"#9ca3af", cursor:"pointer" }}>Cancel</button>
                <button onClick={()=>{ setPage("customers"); setLogForCustModal(null); setLogForCustSearch(""); }}
                  style={{ fontSize:11, fontFamily:"'DM Mono',monospace", background:"none", border:"1px solid #e8e4de", borderRadius:6, padding:"5px 12px", color:"#374151", cursor:"pointer" }}>
                  + New customer →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── TOAST SYSTEM ─────────────────────────────────────────

  const ToastContainer = () => (
    <div style={{ position:"fixed", bottom:24, right:20, zIndex:9999, display:"flex", flexDirection:"column", gap:8, pointerEvents:"none" }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding:"11px 18px", borderRadius:10, fontSize:12, fontFamily:"'DM Mono',monospace",
          boxShadow:"0 4px 20px rgba(0,0,0,0.15)", pointerEvents:"auto",
          background: t.type==="success"?"#1a2e1a": t.type==="warning"?"#92400e": t.type==="error"?"#7f1d1d":"#1e293b",
          color:"#fff", display:"flex", alignItems:"center", gap:10, maxWidth:340,
          animation:"fadeInUp 0.2s ease"
        }}>
          <span>{t.type==="success"?"✓":t.type==="warning"?"⚠":t.type==="error"?"✗":"ℹ"}</span>
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );

  const FeedbackModal = () => {
    if (!feedbackModal) return null;
    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:9100, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
        onClick={e => { if (e.target===e.currentTarget) setFeedbackModal(null); }}>
        <div style={{ background:"#fff", borderRadius:16, maxWidth:440, width:"100%", padding:28, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
          {feedbackSent ? (
            <div className="fade" style={{ textAlign:"center", padding:"12px 0" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>✓</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, color:"#1a2e1a", marginBottom:6 }}>Got it — thank you</div>
              <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:20 }}>This will help us improve the recommendations.</div>
              <button onClick={() => setFeedbackModal(null)} className="btn-primary" style={{ padding:"9px 28px" }}>Close</button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                  {feedbackModal.context}
                </div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, color:"#1a2e1a", marginBottom:2 }}>
                  {feedbackModal.productName || "Flag an issue"}
                </div>
                <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>What didn&#39;t feel right about this result?</div>
              </div>

              {/* Quick reason chips */}
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
                {["Wrong aroma match","THC too high","THC too low","Not in stock","Wrong category","Terpene data seems off","Other"].map(reason => (
                  <button key={reason}
                    onClick={() => setFeedbackText(t => t ? t : reason)}
                    style={{ fontSize:10, padding:"4px 10px", borderRadius:20, border:"1px solid #e8e4de",
                      background: feedbackText===reason?"#1a2e1a":"#f7f5f0",
                      color: feedbackText===reason?"#fff":"#374151",
                      fontFamily:"'DM Mono',monospace", cursor:"pointer", transition:"all 0.15s" }}>
                    {reason}
                  </button>
                ))}
              </div>

              <textarea
                value={feedbackText}
                onChange={e => setFeedbackText(e.target.value)}
                placeholder="Tell us more (optional)..."
                rows={3}
                style={{ width:"100%", padding:"10px 12px", border:"1px solid #e8e4de", borderRadius:10, fontSize:12,
                  fontFamily:"'DM Mono',monospace", resize:"vertical", outline:"none", boxSizing:"border-box", marginBottom:14 }}
              />

              <div style={{ display:"flex", gap:8 }}>
                <button className="btn-ghost" onClick={() => setFeedbackModal(null)}>Cancel</button>
                <button className="btn-primary" style={{ flex:1 }}
                  onClick={() => {
                    if (!feedbackText.trim()) return;
                    const entry = {
                      id: Date.now(),
                      context: feedbackModal.context,
                      productId: feedbackModal.productId,
                      productName: feedbackModal.productName,
                      feedback: feedbackText,
                      ts: new Date().toISOString()
                    };
                    setFeedbackLog(prev => [entry, ...prev]);
                    fireEmailTrigger("feedback_flagged", entry);
                    setFeedbackSent(true);
                  }}>
                  Send Feedback →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── LANDING PAGE ──────────────────────────────────────────
  const LandingPage = () => {
    const sections = {
      dispensary: {
        headline: "Your staff already know the customer wants something earthy. Now they can prove it.",
        sub: "iTerps gives dispensary teams a terpene-first product finder, customer passport system, staff training, and real COA data — in one tool built for the floor.",
        cta: "Book a Demo",
        ctaAction: () => setShowDispOnboard(true),
        secondaryCta: "See how it works →",
        secondaryAction: () => { setOnboardRole("staff"); setOnboardDone(true); setPage("finder"); },
        points: [
          { icon:"🔍", title:"Strain Finder", body:"A guided 5-question matcher that recommends products by terpene profile, format, and potency — not by strain name." },
          { icon:"🪪", title:"Terpene Passport", body:"Build a chemical fingerprint for regular customers. They scan a QR, you see their full preference history instantly." },
          { icon:"📊", title:"Batch Tracker", body:"Track terpene drift across batches. Know before your customers do when a cultivar profile has shifted." },
          { icon:"🎓", title:"Staff Training", body:"Structured terpene science modules and a 20-question flash quiz. New budtenders onboard faster, experienced ones go deeper." },
          { icon:"📈", title:"Network Benchmarking", body:"See how your store terpene diversity compares to the BC network average. Find the gaps before your competition does." },
          { icon:"✓", title:"Cannabis Act Compliant", body:"Every consumer-facing line follows Health Canada promotional restrictions. No effect claims, no lifestyle language." },
        ],
        proof: [
          { stat:"314", label:"BC SKUs tracked" },
          { stat:"108", label:"Dispensaries in network" },
          { stat:"9", label:"Terpenes mapped" },
          { stat:"100%", label:"Lab-sourced data" },
        ]
      },
      lp: {
        headline: "Your COA data deserves to be seen. Right now it’s filed in a PDF.",
        sub: "iTerps gives Licensed Producers and craft growers a verified presence in the only platform built around terpene chemistry — not THC percentage.",
        cta: "Submit Your COA Data",
        ctaAction: () => { setOnboardRole("lp"); setOnboardDone(true); setPage("lp"); setLpTabState("upload"); },
        secondaryCta: "View LP Portal →",
        secondaryAction: () => { setOnboardRole("lp"); setOnboardDone(true); setPage("lp"); },
        points: [
          { icon:"⚡", title:"AI COA Parser", body:"Drag in a PDF from any accredited Canadian lab. Our AI extracts terpene values, batch data, and lab metadata in seconds." },
          { icon:"🔬", title:"Batch Consistency Scoring", body:"Track your terpene profile across harvests. Document consistency for retailer and investor conversations." },
          { icon:"🏷", title:"Verified Brand Badge", body:"LP-verified data gets a badge that appears across all retailer and consumer-facing views — earned by data, not paid for." },
          { icon:"📡", title:"Consumer Match Data", body:"See which terpene profiles consumers are searching for most. Understand demand before it hits your sales reps." },
          { icon:"📋", title:"Retailer Intelligence", body:"Know which stores carry your closest competitors and where your profile is genuinely differentiated." },
          { icon:"🌍", title:"Built for Global Scale", body:"Compliance framework designed to layer jurisdiction rules on top — Canada now, Germany, Netherlands, and AU/US states next." },
        ],
        proof: [
          { stat:"239", label:"BC licence holders" },
          { stat:"100+", label:"BC Direct Delivery LPs" },
          { stat:"$35", label:"Per batch verification" },
          { stat:"Free", label:"Basic listing" },
        ]
      },
      consumer: {
        headline: "Find cannabis that actually works for you.",
        sub: "Not by strain name. Not by THC percentage. By what it actually smells like — and the chemistry behind it.",
        cta: "Find My Strain",
        ctaAction: () => { setConsumerMode(true); setOnboardRole("consumer"); setOnboardDone(true); setPage("consumer"); setConsumerTab("find"); },
        secondaryCta: "New to cannabis? Start here →",
        secondaryAction: () => { setNewbieModeActive(true); setPage("newbie"); setOnboardDone(true); },
        points: [
          { icon:"👃", title:"Aroma-First Matching", body:"Answer five questions about how things smell and how you want to use it. We match on chemistry, not marketing." },
          { icon:"🪪", title:"Terpene Passport", body:"Save your preferences and what you&#39;ve tried. Build a profile that gets more accurate the more you use it." },
          { icon:"📱", title:"Show a Budtender", body:"Your passport generates a QR code. Scan it at any participating store and your profile loads instantly." },
          { icon:"🔍", title:"Find Similar Products", body:"Loved something? Find everything with a matching terpene profile across every store in the network." },
          { icon:"🌱", title:"New to Cannabis?", body:"A simpler 3-question guide that skips the jargon and starts with what actually matters: how it smells and how you want to use it." },
          { icon:"✓", title:"No Effect Claims", body:"We never tell you cannabis will make you feel a certain way. We show you the chemistry. You decide." },
        ],
        proof: [
          { stat:"314", label:"Products with terpene data" },
          { stat:"5 min", label:"Average finder time" },
          { stat:"Free", label:"Always" },
          { stat:"BC", label:"Network coverage" },
        ]
      }
    };

    const s = sections[landingSection];

    return (
      <div className="fade">
        {/* Hero */}
        <div style={{ background:"linear-gradient(160deg,#0f1f0f 0%,#1a2e1a 60%,#2d4a2d 100%)", padding:"64px 20px 52px", textAlign:"center" }}>
          <div style={{ maxWidth:700, margin:"0 auto" }}>
            {/* Switcher */}
            <div style={{ display:"inline-flex", gap:2, background:"rgba(255,255,255,0.08)", borderRadius:12, padding:3, marginBottom:32 }}>
              {[["dispensary","🌿 Dispensaries"],["lp","🏭 Producers"],["consumer","👤 Consumers"]].map(([k,l]) => (
                <button key={k} onClick={() => setLandingSection(k)}
                  style={{ padding:"7px 16px", borderRadius:9, border:"none", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer",
                    background: landingSection===k ? "#fff" : "transparent",
                    color: landingSection===k ? "#1a2e1a" : "rgba(255,255,255,0.55)",
                    fontWeight: landingSection===k ? 600 : 400, transition:"all 0.2s" }}>
                  {l}
                </button>
              ))}
            </div>

            <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:38, fontWeight:300, color:"#f0f4f0", marginBottom:16, lineHeight:1.2 }}>
              {s.headline}
            </h1>
            <p style={{ fontSize:14, color:"rgba(240,244,240,0.6)", lineHeight:1.7, marginBottom:32, fontFamily:"'DM Mono',monospace", maxWidth:560, margin:"0 auto 32px" }}>
              {s.sub}
            </p>
            <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
              <button className="btn-primary"
                style={{ background:"#f0f4f0", color:"#1a2e1a", padding:"13px 28px", fontSize:13, fontWeight:600 }}
                onClick={s.ctaAction}>
                {s.cta} →
              </button>
              <button onClick={s.secondaryAction}
                style={{ padding:"13px 22px", background:"transparent", color:"rgba(240,244,240,0.7)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:10, fontSize:13, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
                {s.secondaryCta}
              </button>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div style={{ background:"#1a2e1a", padding:"16px 20px" }}>
          <div style={{ maxWidth:700, margin:"0 auto", display:"flex", justifyContent:"center", gap:32, flexWrap:"wrap" }}>
            {s.proof.map(({ stat, label }) => (
              <div key={label} style={{ textAlign:"center" }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, color:"#4ade80", fontWeight:300 }}>{stat}</div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,0.4)", fontFamily:"'DM Mono',monospace", textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ maxWidth:860, margin:"0 auto", padding:"48px 20px" }}>
          {/* Features grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:14, marginBottom:48 }}>
            {s.points.map(({ icon, title, body }) => (
              <div key={title} style={{ background:"#fff", border:"1px solid #e8e4de", borderRadius:14, padding:"20px 18px" }}>
                <div style={{ fontSize:24, marginBottom:10 }}>{icon}</div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#1a2e1a", marginBottom:6 }}>{title}</div>
                <div style={{ fontSize:12, color:"#6b7280", lineHeight:1.6 }}>{body}</div>
              </div>
            ))}
          </div>

          {/* Social proof placeholder */}
          <div style={{ background:"#f7f5f0", border:"1px solid #e8e4de", borderRadius:16, padding:"28px 32px", marginBottom:36, textAlign:"center" }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:10, textTransform:"uppercase", letterSpacing:"0.08em" }}>Founding Partner Program</div>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, color:"#1a2e1a", marginBottom:8 }}>Be the first dispensary in the network.</div>
            <div style={{ fontSize:12, color:"#6b7280", maxWidth:480, margin:"0 auto 20px", lineHeight:1.6 }}>
              We&#39;re onboarding our first BC dispensary partners now. 12 months free access, founding partner designation, and direct input on the product roadmap.
            </div>
            <button className="btn-primary" style={{ padding:"11px 28px" }} onClick={() => setShowDispOnboard(true)}>
              Apply for Founding Partner Access →
            </button>
          </div>

          {/* Compliance footer */}
          <div style={{ background:"#fff", border:"1px solid #bbf7d0", borderRadius:12, padding:"14px 18px", display:"flex", gap:12, alignItems:"flex-start" }}>
            <span style={{ fontSize:18, flexShrink:0 }}>⚖️</span>
            <p style={{ fontSize:11, color:"#374151", lineHeight:1.6, margin:0 }}>
              iTerps is an educational platform providing factual information about cannabis chemical composition. It does not promote or encourage consumption. All content follows the Cannabis Act (S.C. 2018, c. 16). No health claims are made. Cannabis should only be purchased from licensed retailers in accordance with applicable law.
            </p>
          </div>
        </div>
      </div>
    );
  };



  // ══════════════════════════════════════════════════════════════
  // B2B FEATURE 1 — SEND TO PHONE MODAL
  // ══════════════════════════════════════════════════════════════
  const SendToPhoneModal = () => {
    if (!smsModal) return null;
    const { products, customerName } = smsModal;
    const preview = products.slice(0,3).map((p,i) => {
      const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1]).slice(0,2).map(([t])=>t).join(", ");
      const disp = DB_DISPENSARIES.find(d=>d.id===p.dispensary_id);
      return (i+1) + ". " + (p.name) + " by " + (p.brand) + "\\n   Terpenes: " + (topT) + "\\n   📍 " + (disp?.name||"—") + ", " + (disp?.city||"—") + "\\n   THC: " + (p.thc) + "%";
    }).join("\n\n");
    const smsBody = "Your iTerps recommendations" + (customerName ? " for " + (customerName) : "") + ":\\n\\n" + (preview) + "\\n\\nProfile built at your local dispensary using iTerps terpene matching.";

    return (
      <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:9200,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}
        onClick={e=>{ if(e.target===e.currentTarget){ setSmsModal(null); setSmsSent(false); setSmsPhone(""); }}}>
        <div style={{ background:"#fff",borderRadius:16,maxWidth:420,width:"100%",padding:28,boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
          {smsSent ? (
            <div className="fade" style={{ textAlign:"center",padding:"12px 0" }}>
              <div style={{ fontSize:40,marginBottom:12 }}>📱</div>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:22,color:"#1a2e1a",marginBottom:6 }}>Recommendations sent</div>
              <div style={{ fontSize:12,color:"#6b7280",fontFamily:"'DM Mono',monospace",marginBottom:20 }}>
                Customer can view their top picks and find the products in-store.
              </div>
              <button onClick={()=>{ setSmsModal(null); setSmsSent(false); setSmsPhone(""); }} className="btn-primary" style={{ padding:"9px 28px" }}>Done</button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom:18 }}>
                <div style={{ fontFamily:"'Fraunces',serif",fontSize:20,color:"#1a2e1a",marginBottom:4 }}>Send to customer&#39;s phone</div>
                <div style={{ fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>
                  Top {Math.min(3,products.length)} recommendations as a text message — no app required
                </div>
              </div>
              {/* Preview */}
              <div style={{ background:"#f7f5f0",borderRadius:10,padding:"12px 14px",marginBottom:16,fontSize:10,fontFamily:"'DM Mono',monospace",color:"#374151",lineHeight:1.7,whiteSpace:"pre-wrap",maxHeight:160,overflowY:"auto" }}>
                {smsBody}
              </div>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.08em" }}>Customer phone number</div>
                <input value={smsPhone} onChange={e=>setSmsPhone(e.target.value.replace(/\D/g,"").slice(0,11))}
                  placeholder="e.g. 6041234567"
                  style={{ width:"100%",padding:"10px 12px",border:"1px solid #e8e4de",borderRadius:10,fontSize:13,fontFamily:"'DM Mono',monospace",outline:"none",boxSizing:"border-box" }}/>
              </div>
              <div style={{ padding:"10px 14px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:8,marginBottom:16,fontSize:10,color:"#166534",fontFamily:"'DM Mono',monospace",lineHeight:1.5 }}>
                In production this triggers a real SMS via Twilio. For now the message is logged and displayed above. Customer number is not stored.
              </div>
              <div style={{ display:"flex",gap:8 }}>
                <button className="btn-ghost" onClick={()=>{ setSmsModal(null); setSmsPhone(""); }}>Cancel</button>
                <button className="btn-primary" style={{ flex:1 }} disabled={smsPhone.length < 10}
                  onClick={()=>{
                    if (smsPhone.length < 10) return;
                    fireEmailTrigger("sms_recommendations_sent", { phone: smsPhone.slice(0,3)+"*****"+smsPhone.slice(-2), products: products.slice(0,3).map(p=>p.name), customerName });
                    setSmsSent(true);
                    showToast("Recommendations sent to customer phone", "success");
                  }}>
                  📱 Send Recommendations →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // B2B FEATURE 2 — REORDER INTELLIGENCE (inserted into Retailer inventory tab)
  // ══════════════════════════════════════════════════════════════
  const ReorderIntelSection = ({ dispId }) => {
    const dispProds = ALL_PRODUCTS.filter(p => dispId==="all" || p.dispensary_id===dispId);

    // For each product find all batches of same name+brand across the network
    // Flag ones where terpene profile has drifted >15% cosine distance from another batch
    const alerts = [];
    const seen = new Set();
    dispProds.forEach(p => {
      const key = (p.brand) + "|" + (p.name);
      if (seen.has(key)) return;
      seen.add(key);
      const allBatches = ALL_PRODUCTS.filter(x => x.brand===p.brand && x.name===p.name && x.id!==p.id);
      if (allBatches.length === 0) return;
      allBatches.forEach(other => {
        const sim = cosineSimilarity(p.terpenes, other.terpenes);
        if (sim < 85) {
          alerts.push({
            product: p, other,
            sim,
            drift: 100 - sim,
            severity: sim < 60 ? "high" : sim < 75 ? "medium" : "low"
          });
        }
      });
    });
    alerts.sort((a,b) => a.sim - b.sim);

    return (
      <div>
        <div style={{ marginBottom:16 }}>
          <h3 style={{ fontFamily:"'Fraunces',serif",fontSize:18,fontWeight:400,color:"#1a2e1a",marginBottom:4 }}>Batch Drift Alerts</h3>
          <p style={{ fontSize:11,color:"#6b7280",fontFamily:"'DM Mono',monospace" }}>
            Flags when two batches of the same product have significantly different terpene profiles. Helps buying decisions before committing to a new stock order.
          </p>
        </div>
        {alerts.length === 0 ? (
          <div style={{ background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:12,padding:"20px 24px",textAlign:"center" }}>
            <div style={{ fontSize:24,marginBottom:8 }}>✓</div>
            <div style={{ fontSize:13,color:"#166534",fontFamily:"'DM Mono',monospace" }}>No significant batch drift detected across current inventory</div>
          </div>
        ) : (
          <div>
            <div style={{ display:"flex",gap:8,marginBottom:12,flexWrap:"wrap" }}>
              {[["high","High drift","#ef4444"],["medium","Medium drift","#f59e0b"],["low","Low drift","#6b7280"]].map(([sev,label,color])=>{
                const n = alerts.filter(a=>a.severity===sev).length;
                return n > 0 ? (
                  <div key={sev} style={{ fontSize:10,padding:"4px 12px",background:(color) + "15",border:"1px solid " + (color) + "40",borderRadius:20,fontFamily:"'DM Mono',monospace",color }}>
                    {n} {label}
                  </div>
                ) : null;
              })}
            </div>
            {alerts.slice(0,8).map((a,i) => {
              const color = a.severity==="high"?"#ef4444":a.severity==="medium"?"#f59e0b":"#6b7280";
              const topDiff = Object.entries(a.product.terpenes).map(([t,v]) => ({
                t, diff: v - (a.other.terpenes[t]||0)
              })).sort((x,y) => Math.abs(y.diff)-Math.abs(x.diff)).slice(0,3);
              return (
                <div key={i} style={{ background:"#fff",border:"1px solid " + (color) + "30",borderLeft:"3px solid " + (color),borderRadius:10,padding:"14px 16px",marginBottom:10 }}>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap" }}>
                    <div>
                      <div style={{ fontFamily:"'Fraunces',serif",fontSize:15,color:"#1a2e1a",marginBottom:2 }}>{a.product.name}</div>
                      <div style={{ fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>{a.product.brand} · Batch {a.product.batch||"—"} vs {a.other.batch||"—"}</div>
                    </div>
                    <div style={{ textAlign:"right",flexShrink:0 }}>
                      <div style={{ fontSize:18,fontWeight:600,fontFamily:"'Fraunces',serif",color }}>{a.drift.toFixed(0)}%</div>
                      <div style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>profile drift</div>
                    </div>
                  </div>
                  <div style={{ marginTop:10,display:"flex",gap:8,flexWrap:"wrap" }}>
                    {topDiff.map(({t,diff}) => {
                      const ti = TERPENES[t];
                      const isPos = diff > 0;
                      return (
                        <div key={t} style={{ display:"flex",alignItems:"center",gap:4,padding:"3px 8px",background: isPos?"#f0fdf4":"#fef2f2",borderRadius:8,border: isPos?"1px solid #86efac":"1px solid #fca5a5" }}>
                          {ti&&<div style={{ width:5,height:5,borderRadius:"50%",background:ti.color }}/>}
                          <span style={{ fontSize:9,fontFamily:"'DM Mono',monospace",color:"#374151" }}>{t.split(" ").pop()}</span>
                          <span style={{ fontSize:9,fontFamily:"'DM Mono',monospace",color: isPos?"#166534":"#991b1b",fontWeight:600 }}>
                            {isPos?"+":""}{(diff*10).toFixed(1)}mg/g
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop:8,fontSize:10,color:"#6b7280",fontFamily:"'DM Mono',monospace" }}>
                    {a.severity==="high" ? "⚠ Significant change — taste profile and consumer experience will differ. Verify with supplier before restocking." :
                     a.severity==="medium" ? "Note: Moderate drift detected. May be acceptable — check with regulars who know this product." :
                     "Minor variation — within normal harvest-to-harvest range for this cultivar."}
                  </div>
                  {/* Flag as drifted action */}
                  {(() => {
                    const flagKey = (a.product.brand) + "|" + (a.product.name) + "|" + (a.product.batch);
                    const isFlagged = flaggedDrifts[flagKey];
                    return (
                      <div style={{marginTop:10,display:"flex",alignItems:"center",gap:8}}>
                        <button onClick={e=>{ e.stopPropagation(); const next={...flaggedDrifts,[flagKey]:{product:a.product.name,brand:a.product.brand,batch:a.product.batch,drift:a.drift.toFixed(0),severity:a.severity,flaggedAt:new Date().toISOString()}}; setFlaggedDrifts(next); try{localStorage.setItem("iterps_drift_flags",JSON.stringify(next));}catch (_e) {} }}
                          style={{padding:"5px 12px",borderRadius:8,border:"1px solid " + (isFlagged?"#bbf7d0":"#fca5a5"),background:isFlagged?"#f0fdf4":"#fef2f2",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",color:isFlagged?"#166534":"#b91c1c",fontWeight:600}}>
                          {isFlagged ? "✓ Flagged — Budtenders Alerted" : "🚩 Flag as Drifted"}
                        </button>
                        {isFlagged && <span style={{fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>Alert active — budtenders will see this at point of sale</span>}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // B2B FEATURE 3 — STAFF PERFORMANCE DASHBOARD
  // ══════════════════════════════════════════════════════════════
  const StaffPerformancePage = () => {
    const allModules = ["Terpene Science Fundamentals","Cannabinoid Pharmacology","Cultivation & Cure Science","Consumer Matching Methodology","Cannabis Act Compliance"];
    const certList = Object.values(completedCerts).sort((a,b)=>new Date(b.date)-new Date(a.date));

    // Build staff roster from cert records + quiz log
    const staffMap = {};
    certList.forEach(c => {
      if (!staffMap[c.staffName]) staffMap[c.staffName] = { name:c.staffName, certs:[], quizScores:[] };
      staffMap[c.staffName].certs.push(c);
    });
    staffQuizLog.forEach(q => {
      if (!staffMap[q.staffName]) staffMap[q.staffName] = { name:q.staffName, certs:[], quizScores:[] };
      staffMap[q.staffName].quizScores.push(q);
    });
    const staffRoster = Object.values(staffMap).sort((a,b)=>a.name.localeCompare(b.name));

    const completedModules = (staff) => {
      const mods = new Set();
      staff.certs.forEach(c => c.modules.forEach(m => mods.add(m)));
      return mods;
    };

    const bestQuizScore = (staff) => staff.quizScores.length
      ? Math.max(...staff.quizScores.map(q=>q.pct))
      : null;

    return (
      <div className="fade" style={{ maxWidth:900,margin:"0 auto",padding:"28px 20px" }}>
        <button onClick={()=>setPage("dashboard")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:20}}>← Dashboard</button>
          <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:28,fontWeight:300,color:"#1a2e1a",marginBottom:4 }}>Staff Performance</h1>
          <p style={{ fontSize:12,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>
            Module completion, certification status, and quiz scores across your team.
          </p>
        </div>

        {/* KPI bar */}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:24 }}>
          {[
            { label:"Staff Records", value:staffRoster.length, color:"#1a2e1a" },
            { label:"Certs Issued", value:certList.length, color:"#4a7c59" },
            { label:"Quiz Attempts", value:staffQuizLog.length, color:"#3b82f6" },
            { label:"Fully Certified", value:staffRoster.filter(s=>completedModules(s).size===allModules.length).length, color:"#22c55e" },
          ].map(k=>(
            <div key={k.label} className="card" style={{ padding:"16px 18px" }}>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:26,fontWeight:300,color:k.color,marginBottom:2 }}>{k.value}</div>
              <div style={{ fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>{k.label}</div>
            </div>
          ))}
        </div>

        {staffRoster.length === 0 ? (
          <div style={{ background:"#f7f5f0",border:"1px solid #e8e4de",borderRadius:14,padding:"40px 24px",textAlign:"center" }}>
            <div style={{ fontSize:36,marginBottom:12 }}>👥</div>
            <div style={{ fontFamily:"'Fraunces',serif",fontSize:20,color:"#1a2e1a",marginBottom:8 }}>No staff records yet</div>
            <div style={{ fontSize:12,color:"#6b7280",fontFamily:"'DM Mono',monospace",marginBottom:20 }}>
              Issue certifications from the Certifications page to begin tracking team progress.
            </div>
            <button className="btn-primary" onClick={()=>setPage("certs")}>Issue First Certification →</button>
          </div>
        ) : (
          <>
            {/* Module coverage heatmap */}
            <div className="card" style={{ padding:20,marginBottom:16 }}>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:14 }}>Module Coverage</div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%",borderCollapse:"collapse",fontSize:11,fontFamily:"'DM Mono',monospace" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign:"left",padding:"6px 8px",color:"#9ca3af",fontWeight:400,fontSize:9,textTransform:"uppercase",whiteSpace:"nowrap" }}>Staff</th>
                      {allModules.map(m=>(
                        <th key={m} style={{ padding:"6px 6px",color:"#9ca3af",fontWeight:400,fontSize:8,textAlign:"center",maxWidth:80 }}>
                          <div style={{ writingMode:"vertical-rl",transform:"rotate(180deg)",height:70,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1.2 }}>{m}</div>
                        </th>
                      ))}
                      <th style={{ padding:"6px 8px",color:"#9ca3af",fontWeight:400,fontSize:9,textAlign:"center" }}>Best Quiz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffRoster.map(staff=>{
                      const mods = completedModules(staff);
                      const quiz = bestQuizScore(staff);
                      const pct = Math.round((mods.size/allModules.length)*100);
                      return (
                        <tr key={staff.name} style={{ borderTop:"1px solid #f3f4f6" }}>
                          <td style={{ padding:"10px 8px",fontWeight:500,color:"#1a2e1a",whiteSpace:"nowrap" }}>
                            <div>{staff.name}</div>
                            <div style={{ fontSize:9,color:"#9ca3af",marginTop:1 }}>{pct}% complete</div>
                          </td>
                          {allModules.map(m=>(
                            <td key={m} style={{ padding:"10px 6px",textAlign:"center" }}>
                              <div style={{ width:20,height:20,borderRadius:5,margin:"0 auto",
                                background: mods.has(m)?"#4a7c59":"#f3f4f6",
                                display:"flex",alignItems:"center",justifyContent:"center" }}>
                                {mods.has(m)&&<span style={{ color:"#fff",fontSize:10 }}>✓</span>}
                              </div>
                            </td>
                          ))}
                          <td style={{ padding:"10px 8px",textAlign:"center" }}>
                            {quiz !== null ? (
                              <span style={{ fontSize:12,fontWeight:600,color:quiz>=80?"#22c55e":quiz>=60?"#f59e0b":"#ef4444",fontFamily:"'Fraunces',serif" }}>
                                {quiz}%
                              </span>
                            ) : <span style={{ color:"#d1d5db",fontSize:10 }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Quiz Leaderboard */}
            {staffQuizLog.length > 0 && (() => {
              const lb = Object.values(staffMap).map(s => {
                const scores = s.quizScores;
                const best = scores.length ? Math.max(...scores.map(q=>q.pct)) : null;
                const avg = scores.length ? Math.round(scores.reduce((a,q)=>a+q.pct,0)/scores.length) : null;
                const mods = new Set(); s.certs.forEach(c=>c.modules.forEach(m=>mods.add(m)));
                return { name:s.name, best, avg, attempts:scores.length, modules:mods.size };
              }).filter(s=>s.best!==null).sort((a,b)=>b.best-a.best);
              if (!lb.length) return null;
              return (
                <div className="card" style={{ padding:20, marginBottom:16 }}>
                  <div style={{ fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:14 }}>🏆 Quiz Leaderboard</div>
                  {lb.map((s,i)=>(
                    <div key={s.name} style={{ display:"flex",alignItems:"center",gap:12,padding:"9px 0",borderBottom:i<lb.length-1?"1px solid #f3f4f6":"none" }}>
                      <div style={{ fontSize:18,fontFamily:"'Fraunces',serif",color:i===0?"#f59e0b":i===1?"#9ca3af":i===2?"#b45309":"#d1d5db",width:28,flexShrink:0,textAlign:"center",fontWeight:600 }}>
                        {i===0?"🥇":i===1?"🥈":i===2?"🥉":"#" + (i+1)}
                      </div>
                      <div style={{ flex:1,minWidth:0 }}>
                        <div style={{ fontSize:13,color:"#1a2e1a",fontWeight:500 }}>{s.name}</div>
                        <div style={{ fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>{s.attempts} attempt{s.attempts!==1?"s":""} · {s.modules}/5 modules</div>
                      </div>
                      <div style={{ textAlign:"right",flexShrink:0 }}>
                        <div style={{ fontSize:18,fontWeight:600,fontFamily:"'Fraunces',serif",color:s.best>=80?"#22c55e":s.best>=60?"#f59e0b":"#ef4444" }}>{s.best}%</div>
                        <div style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>best · {s.avg}% avg</div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Recent quiz attempts */}
            {staffQuizLog.length > 0 && (
              <div className="card" style={{ padding:20 }}>
                <div style={{ fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:12 }}>Recent Quiz Attempts</div>
                {staffQuizLog.slice(0,8).map((q,i)=>(
                  <div key={i} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:"1px solid #f3f4f6" }}>
                    <div>
                      <div style={{ fontSize:12,color:"#374151",fontFamily:"'DM Mono',monospace" }}>{q.staffName}</div>
                      <div style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>{q.ts?.slice(0,10)}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:16,fontWeight:600,fontFamily:"'Fraunces',serif",color:q.pct>=80?"#22c55e":q.pct>=60?"#f59e0b":"#ef4444" }}>{q.pct}%</div>
                      <div style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>{q.score}/{q.total} correct</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // B2B FEATURE 4 — DEMAND GAP REPORT
  // ══════════════════════════════════════════════════════════════
  const DemandGapPage = () => {
    const [selDisp, setSelDisp] = useState(demandGapDisp || "all");

    // Build demand signal from consumer profiles + finder completions
    const demandTerpenes = {};
    profiles.forEach(prof => {
      const answers = prof.finderAnswers || prof.consumerAnswers || {};
      const vibeOpt = (CONSUMER_QUESTIONS||[]).find ? null : null;
      // Score terpenes from vibe answer
      if (answers.vibe) {
        const vibeData = {
          earthy:    { terps:["β-Myrcene","α-Humulene","Linalool"], weight:3 },
          citrus:    { terps:["(+)-Limonene","Terpinolene","β-Ocimene"], weight:3 },
          pine:      { terps:["α-Pinene","Terpinolene"], weight:3 },
          spice:     { terps:["β-Caryophyllene","(+)-Limonene"], weight:3 },
          floral:    { terps:["Linalool","α-Bisabolol","β-Ocimene"], weight:3 },
          fuel:      { terps:["β-Myrcene","β-Caryophyllene","α-Humulene"], weight:3 },
        };
        const vd = vibeData[answers.vibe];
        if (vd) vd.terps.forEach(t => { demandTerpenes[t] = (demandTerpenes[t]||0) + vd.weight; });
      }
      // Score from feeling answer
      if (answers.feeling) {
        const feelData = {
          relax:  ["β-Myrcene","Linalool","α-Bisabolol"],
          energy: ["Terpinolene","(+)-Limonene","β-Ocimene"],
          focus:  ["α-Pinene","(+)-Limonene"],
          social: ["(+)-Limonene","β-Caryophyllene"],
          body:   ["β-Myrcene","α-Humulene"],
          sleep:  ["Linalool","α-Bisabolol"],
        };
        const fd = feelData[answers.feeling];
        if (fd) fd.forEach((t,i) => { demandTerpenes[t] = (demandTerpenes[t]||0) + (3-i); });
      }
    });

    // Supply signal — terpene presence in inventory
    // LP role: supply comparison is always their own products vs market demand
    const supplyBase = (onboardRole === "lp" && activeLp) ? ALL_PRODUCTS.filter(p=>p.cultivation?.lp===activeLp) : ALL_PRODUCTS;
    const supplyDisp = selDisp === "all" ? supplyBase : supplyBase.filter(p=>p.dispensary_id===selDisp);
    const supplyTerpenes = {};
    supplyDisp.forEach(p => Object.entries(p.terpenes).forEach(([t,v]) => {
      supplyTerpenes[t] = (supplyTerpenes[t]||0) + v;
    }));

    const maxDemand = Math.max(...Object.values(demandTerpenes), 1);
    const maxSupply = Math.max(...Object.values(supplyTerpenes), 0.001);

    const gaps = Object.keys(TERPENES).map(t => ({
      t,
      demand: (demandTerpenes[t]||0) / maxDemand * 100,
      supply: (supplyTerpenes[t]||0) / maxSupply * 100,
      gap: ((demandTerpenes[t]||0) / maxDemand * 100) - ((supplyTerpenes[t]||0) / maxSupply * 100),
    })).sort((a,b) => b.gap - a.gap);

    const dispOptions = ["all", ...new Set(ALL_PRODUCTS.map(p=>p.dispensary_id))].map(id => ({
      id, label: id==="all" ? "Entire Network" : DB_DISPENSARIES.find(d=>d.id===id)?.name || id
    }));

    return (
      <div className="fade" style={{ maxWidth:900,margin:"0 auto",padding:"28px 20px" }}>
        <button onClick={()=>setPage(onboardRole==="lp"?"lp":"dashboard")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:20}}>← {onboardRole==="lp"?"LP Portal":"Dashboard"}</button>
          <div style={{ marginBottom:20 }}>
          <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:28,fontWeight:300,color:"#1a2e1a",marginBottom:4 }}>Demand Gap Report</h1>
          <p style={{ fontSize:12,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>
            What consumers are searching for vs what is currently stocked. Based on {profiles.length} consumer profiles.
          </p>
          {onboardRole === "lp" && activeLp && (
            <div style={{ marginTop:10, padding:"8px 14px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:8, fontSize:11, fontFamily:"'DM Mono',monospace", color:"#1d4ed8" }}>
              🏭 Supply data showing <strong>{activeLp}</strong> products only. Demand signals are from the full BC consumer network.
            </div>
          )}
        </div>

        {profiles.length === 0 ? (
          <div style={{ background:"#f7f5f0",border:"1px solid #e8e4de",borderRadius:14,padding:"40px 24px",textAlign:"center" }}>
            <div style={{ fontSize:36,marginBottom:12 }}>📊</div>
            <div style={{ fontFamily:"'Fraunces',serif",fontSize:20,color:"#1a2e1a",marginBottom:8 }}>No consumer profiles yet</div>
            <div style={{ fontSize:12,color:"#6b7280",fontFamily:"'DM Mono',monospace" }}>
              Consumer profiles are created when people complete the terpene finder. As your network grows, demand signals will appear here.
            </div>
          </div>
        ) : (
          <>
            {/* Dispensary filter */}
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.08em" }}>View for</div>
              <select value={selDisp} onChange={e=>setSelDisp(e.target.value)}
                style={{ padding:"9px 12px",border:"1px solid #e8e4de",borderRadius:8,fontSize:12,fontFamily:"'DM Mono',monospace",background:"#fff",outline:"none",cursor:"pointer",minWidth:240 }}>
                {dispOptions.map(d=><option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>

            {/* Gap chart */}
            <div className="card" style={{ padding:20,marginBottom:16 }}>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:4 }}>Demand vs Supply Index</div>
              <div style={{ fontSize:11,color:"#6b7280",fontFamily:"'DM Mono',monospace",marginBottom:16 }}>
                Bars above zero = unmet demand. Red = significant gap — consider sourcing more of this terpene profile.
              </div>
              {gaps.map(({t,demand,supply,gap})=>{
                const ti = TERPENES[t];
                const isGap = gap > 10;
                const isOverstock = gap < -10;
                return (
                  <div key={t} style={{ marginBottom:14 }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                        <div style={{ width:8,height:8,borderRadius:"50%",background:ti?.color||"#6b7280" }}/>
                        <span style={{ fontSize:12,color:"#374151",fontFamily:"'DM Mono',monospace" }}>{t}</span>
                        {isGap && <span style={{ fontSize:8,background:"#fef2f2",color:"#991b1b",border:"1px solid #fca5a5",padding:"1px 6px",borderRadius:10,fontFamily:"'DM Mono',monospace" }}>GAP</span>}
                        {isOverstock && <span style={{ fontSize:8,background:"#eff6ff",color:"#1d4ed8",border:"1px solid #bfdbfe",padding:"1px 6px",borderRadius:10,fontFamily:"'DM Mono',monospace" }}>OVERSUPPLIED</span>}
                      </div>
                      <span style={{ fontSize:10,color:isGap?"#ef4444":isOverstock?"#3b82f6":"#9ca3af",fontFamily:"'DM Mono',monospace",fontWeight:600 }}>
                        {gap > 0 ? "+" : ""}{gap.toFixed(0)} pts
                      </span>
                    </div>
                    <div style={{ display:"flex",gap:4,alignItems:"center" }}>
                      <div style={{ flex:1,height:6,background:"#f3f4f6",borderRadius:4,overflow:"hidden",position:"relative" }}>
                        <div style={{ position:"absolute",inset:0,display:"flex",alignItems:"center" }}>
                          <div style={{ height:"100%",width:(demand) + "%",background:(ti?.color||"#6b7280") + "40",borderRadius:4,transition:"width 0.5s" }}/>
                        </div>
                        <div style={{ position:"absolute",inset:0,display:"flex",alignItems:"center" }}>
                          <div style={{ height:"100%",width:(supply) + "%",background:ti?.color||"#6b7280",borderRadius:4,opacity:0.7,transition:"width 0.5s" }}/>
                        </div>
                      </div>
                    </div>
                    <div style={{ display:"flex",gap:12,marginTop:2 }}>
                      <span style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>Demand: {demand.toFixed(0)}</span>
                      <span style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>Supply: {supply.toFixed(0)}</span>
                    </div>
                  </div>
                );
              })}
              <div style={{ marginTop:12,display:"flex",gap:16,fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>
                <span>░ = Consumer demand signal</span>
                <span>█ = Current inventory supply</span>
              </div>
            </div>

            {/* Actionable recommendations */}
            <div className="card" style={{ padding:20, marginBottom:14 }}>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:12 }}>Buying Recommendations</div>
              {gaps.filter(g=>g.gap>8).length === 0 ? (
                <div style={{ fontSize:12,color:"#6b7280",fontFamily:"'DM Mono',monospace" }}>Supply is well-matched to current consumer demand across all terpene profiles.</div>
              ) : gaps.filter(g=>g.gap>8).slice(0,4).map(({t,gap})=>{
                const ti = TERPENES[t];
                const topProds = ALL_PRODUCTS.filter(p=>p.terpenes[t] && p.terpenes[t]>0.2).sort((a,b)=>b.terpenes[t]-a.terpenes[t]).slice(0,3);
                return (
                  <div key={t} style={{ padding:"12px 0",borderBottom:"1px solid #f3f4f6" }}>
                    <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:6 }}>
                      <div style={{ width:8,height:8,borderRadius:"50%",background:ti?.color||"#6b7280" }}/>
                      <span style={{ fontSize:13,fontWeight:600,color:"#1a2e1a" }}>{t}</span>
                      <span style={{ fontSize:10,color:"#ef4444",fontFamily:"'DM Mono',monospace" }}>+{gap.toFixed(0)}pt unmet demand</span>
                    </div>
                    <div style={{ fontSize:11,color:"#6b7280",marginBottom:6 }}>
                      Top network products with this profile:
                    </div>
                    <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                      {topProds.map(p=>(
                        <button key={p.id} onClick={()=>{ viewProduct(p) }}
                          style={{ fontSize:10,padding:"3px 10px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:20,cursor:"pointer",fontFamily:"'DM Mono',monospace",color:"#166534" }}>
                          {p.name} · {p.brand}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* LP Sourcing Suggestions */}
            {gaps.filter(g=>g.gap>8).length > 0 && (() => {
              // For each top gap terpene, find which LPs have the strongest profiles
              const topGaps = gaps.filter(g=>g.gap>8).slice(0,3);
              const lpStrengths = {};
              topGaps.forEach(({t}) => {
                const lpProds = ALL_PRODUCTS.filter(p=>p.terpenes[t]>0.15 && p.cultivation?.lp);
                lpProds.forEach(p => {
                  const lp = p.cultivation.lp;
                  if (!lpStrengths[lp]) lpStrengths[lp]={lp, terpenes:{}, skus:0, avgTerp:0, totalTerp:0};
                  lpStrengths[lp].terpenes[t]=(lpStrengths[lp].terpenes[t]||0)+1;
                  lpStrengths[lp].skus++;
                  lpStrengths[lp].totalTerp+=(p.total_terpenes||0);
                });
              });
              const lpRecs = Object.values(lpStrengths)
                .map(lp=>({...lp, score: Object.keys(lp.terpenes).length * 10 + lp.skus, avgTerp:lp.skus?lp.totalTerp/lp.skus:0}))
                .sort((a,b)=>b.score-a.score).slice(0,3);
              if (!lpRecs.length) return null;
              return (
                <div className="card" style={{padding:20}}>
                  <div style={{fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:4}}>LP Sourcing Suggestions</div>
                  <div style={{fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",marginBottom:14}}>LPs whose catalogues best fill your current demand gaps</div>
                  {lpRecs.map((rec,i)=>{
                    const gapTerps = Object.keys(rec.terpenes);
                    return (
                      <div key={rec.lp} style={{display:"flex",alignItems:"flex-start",gap:14,padding:"12px 0",borderBottom:i<lpRecs.length-1?"1px solid #f3f4f6":"none"}}>
                        <div style={{width:32,height:32,borderRadius:10,background:i===0?"#1a2e1a":i===1?"#f0fdf4":"#f7f5f0",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          <span style={{fontSize:14,color:i===0?"#4ade80":"#4a7c59"}}>{i===0?"★":i===1?"✦":"·"}</span>
                        </div>
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                            <span style={{fontFamily:"'Fraunces',serif",fontSize:14,color:"#1a2e1a"}}>{rec.lp}</span>
                            <span style={{fontSize:9,background:"#f0fdf4",color:"#166534",border:"1px solid #bbf7d0",padding:"2px 7px",borderRadius:20,fontFamily:"'DM Mono',monospace"}}>{rec.skus} matching SKUs</span>
                            {rec.avgTerp>0&&<span style={{fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace"}}>avg {rec.avgTerp.toFixed(2)}% terp</span>}
                          </div>
                          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                            {gapTerps.map(t=>{const ti=TERPENES[t];return(
                              <span key={t} style={{fontSize:9,padding:"2px 8px",borderRadius:20,background:(ti?.color||"#6b7280") + "15",color:ti?.color||"#6b7280",border:"1px solid " + (ti?.color||"#6b7280") + "30",fontFamily:"'DM Mono',monospace"}}>
                                {t.split(" ").pop()} ✓
                              </span>
                            );}).filter(Boolean)}
                          </div>
                        </div>
                        <button onClick={()=>setPage("lp")}
                          style={{padding:"5px 12px",borderRadius:8,border:"1px solid #e8e4de",background:"transparent",cursor:"pointer",fontSize:10,fontFamily:"'DM Mono',monospace",color:"#4a7c59",flexShrink:0}}>
                          Contact →
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </>
        )}
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // B2B FEATURE 5 — BC TERPENE DEMAND INDEX (LP Portal tab)
  // ══════════════════════════════════════════════════════════════
  const BCDemandIndexSection = () => {
    const quarter = "Q1 2026";
    const terpeneStats = Object.entries(TERPENES).map(([name, info]) => {
      const prods = ALL_PRODUCTS.filter(p=>p.terpenes[name]);
      const avgConc = prods.length ? prods.reduce((s,p)=>s+(p.terpenes[name]||0),0)/prods.length : 0;
      const skuCount = prods.length;
      const verifiedCount = prods.filter(p=>p.terpene_source==="lab_tested").length;
      return { name, info, avgConc, skuCount, verifiedCount };
    }).sort((a,b)=>b.skuCount-a.skuCount);

    const categoryBreakdown = {};
    ALL_PRODUCTS.forEach(p=>{ categoryBreakdown[p.category]=(categoryBreakdown[p.category]||0)+1; });

    const cultivationBreakdown = {};
    ALL_PRODUCTS.forEach(p=>{ const m=p.cultivation?.method||"Unknown"; cultivationBreakdown[m]=(cultivationBreakdown[m]||0)+1; });

    const topBrands = (() => {
      const b = {}; ALL_PRODUCTS.forEach(p=>{ if(!b[p.brand]) b[p.brand]={count:0,terp:0}; b[p.brand].count++; if(p.total_terpenes) b[p.brand].terp+=p.total_terpenes; });
      return Object.entries(b).map(([brand,{count,terp}])=>({ brand, count, avgTerp:count?terp/count:0 })).sort((a,b)=>b.count-a.count).slice(0,8);
    })();

    return (
      <div className="fade">
        <div style={{ marginBottom:20 }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8 }}>
            <div>
              <h2 style={{ fontFamily:"'Fraunces',serif",fontSize:22,fontWeight:300,color:"#1a2e1a",marginBottom:4 }}>BC Terpene Demand Index</h2>
              <p style={{ fontSize:12,color:"#6b7280",fontFamily:"'DM Mono',monospace" }}>
                Network intelligence derived from {ALL_PRODUCTS.length} verified SKUs across {DB_DISPENSARIES.length} BC dispensaries.
              </p>
            </div>
            <div style={{ fontSize:9,background:"#f0fdf4",border:"1px solid #bbf7d0",color:"#166534",padding:"4px 12px",borderRadius:20,fontFamily:"'DM Mono',monospace",alignSelf:"flex-start" }}>
              {quarter} · BC Network
            </div>
          </div>
        </div>

        {/* Terpene league table */}
        <div className="card" style={{ padding:20,marginBottom:14 }}>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:12 }}>Terpene Presence Index</div>
          <div style={{ fontSize:10,color:"#6b7280",fontFamily:"'DM Mono',monospace",marginBottom:14 }}>SKU count, average concentration, and lab verification rate across the BC network.</div>
          {terpeneStats.map(({name,info,avgConc,skuCount,verifiedCount},i)=>(
            <div key={name} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid #f3f4f6" }}>
              <div style={{ fontSize:11,color:"#9ca3af",fontFamily:"'DM Mono',monospace",width:18,textAlign:"right",flexShrink:0 }}>#{i+1}</div>
              <div style={{ width:8,height:8,borderRadius:"50%",background:info.color,flexShrink:0 }}/>
              <div style={{ flex:1,minWidth:0 }}>
                <div style={{ fontSize:12,color:"#374151",fontFamily:"'DM Mono',monospace",fontWeight:500 }}>{name}</div>
              </div>
              <div style={{ textAlign:"right",flexShrink:0 }}>
                <div style={{ fontSize:11,color:"#1a2e1a",fontFamily:"'DM Mono',monospace",fontWeight:600 }}>{skuCount} SKUs</div>
                <div style={{ fontSize:9,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>avg {(avgConc*10).toFixed(1)}mg/g · {verifiedCount} verified</div>
              </div>
              <div style={{ width:80,flexShrink:0 }}>
                <div style={{ height:4,background:"#f3f4f6",borderRadius:4,overflow:"hidden" }}>
                  <div style={{ height:"100%",width:((skuCount/terpeneStats[0].skuCount)*100) + "%",background:info.color,borderRadius:4 }}/>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Category and cultivation split */}
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14 }}>
          <div className="card" style={{ padding:20 }}>
            <div style={{ fontFamily:"'Fraunces',serif",fontSize:14,color:"#1a2e1a",marginBottom:12 }}>Product Category Mix</div>
            {Object.entries(categoryBreakdown).sort((ea,eb)=>eb[1]-ea[1]).map(([cat,n])=>(
              <div key={cat} style={{ display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #f9f7f4" }}>
                <span style={{ fontSize:11,color:"#374151",fontFamily:"'DM Mono',monospace" }}>{cat}</span>
                <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                  <div style={{ width:40,height:3,background:"#f3f4f6",borderRadius:4,overflow:"hidden" }}>
                    <div style={{ height:"100%",width:((n/ALL_PRODUCTS.length)*100) + "%",background:"#4a7c59",borderRadius:4 }}/>
                  </div>
                  <span style={{ fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",width:24,textAlign:"right" }}>{n}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="card" style={{ padding:20 }}>
            <div style={{ fontFamily:"'Fraunces',serif",fontSize:14,color:"#1a2e1a",marginBottom:12 }}>Cultivation Method</div>
            {Object.entries(cultivationBreakdown).sort((ea,eb)=>eb[1]-ea[1]).map(([method,n])=>(
              <div key={method} style={{ display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #f9f7f4" }}>
                <span style={{ fontSize:11,color:"#374151",fontFamily:"'DM Mono',monospace" }}>{method}</span>
                <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                  <div style={{ width:40,height:3,background:"#f3f4f6",borderRadius:4,overflow:"hidden" }}>
                    <div style={{ height:"100%",width:((n/ALL_PRODUCTS.length)*100) + "%",background:"#3b82f6",borderRadius:4 }}/>
                  </div>
                  <span style={{ fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",width:24,textAlign:"right" }}>{n}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top brands by terpene avg */}
        <div className="card" style={{ padding:20,marginBottom:14 }}>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:16,color:"#1a2e1a",marginBottom:4 }}>Brand Terpene Performance</div>
          <div style={{ fontSize:10,color:"#6b7280",fontFamily:"'DM Mono',monospace",marginBottom:12 }}>Average total terpene % across lab-tested SKUs by brand.</div>
          {topBrands.map(({brand,count,avgTerp},i)=>(
            <div key={brand} style={{ display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px solid #f3f4f6" }}>
              <div style={{ fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace",width:18,textAlign:"right" }}>#{i+1}</div>
              <div style={{ flex:1,fontSize:12,color:"#374151",fontFamily:"'DM Mono',monospace" }}>{brand}</div>
              <div style={{ fontSize:10,color:"#9ca3af",fontFamily:"'DM Mono',monospace" }}>{count} SKUs</div>
              <div style={{ fontSize:12,fontWeight:600,color:"#4a7c59",fontFamily:"'Fraunces',serif",minWidth:50,textAlign:"right" }}>
                {avgTerp>0 ? ((avgTerp).toFixed(2)) + "%" : "—"}
              </div>
            </div>
          ))}
        </div>

        <div style={{ background:"#f7f5f0",border:"1px solid #e8e4de",borderRadius:10,padding:"12px 16px",fontSize:10,color:"#6b7280",fontFamily:"'DM Mono',monospace",lineHeight:1.6 }}>
          Data derived from {ALL_PRODUCTS.filter(p=>p.terpene_source==="lab_tested").length} lab-verified SKUs. Anonymized and aggregated — no individual consumer data included. Proprietary iTerps network intelligence.
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  // B2B FEATURE 6 — NATIONAL ROLLOUT TRACKER (Founder Mode)
  // ══════════════════════════════════════════════════════════════
  const NationalRolloutPage = () => {
    const provinces = [
      { code:"BC", name:"British Columbia", status:"active", stores:180, lps:100, priority:1,
        notes:"Fully privatized. Home market. First partner conversations in progress.",
        milestones:["First dispensary partner","10 verified SKUs","First LP submission","25 dispensaries","Provincial network live"],
        completed:[true,false,false,false,false] },
      { code:"AB", name:"Alberta", status:"next", stores:700, lps:80, priority:2,
        notes:"Fully privatized — largest private market in Canada. Operators are entrepreneurial, fast to adopt.",
        milestones:["BC validated product","Warm intro to AB retailer","First AB dispensary","20 AB dispensaries"],
        completed:[false,false,false,false] },
      { code:"ON", name:"Ontario", status:"planned", stores:1700, lps:120, priority:3,
        notes:"Largest market by volume. OCS model creates friction. More conservative operators. Enter after AB traction.",
        milestones:["AB traction established","OCS relationship mapped","First ON dispensary","50 ON dispensaries"],
        completed:[false,false,false,false] },
      { code:"SK", name:"Saskatchewan", status:"planned", stores:45, lps:20, priority:4,
        notes:"Fully privatized. Small but fast-moving market. Good test for mid-Canada expansion.",
        milestones:["ON foothold","First SK dispensary"],
        completed:[false,false] },
      { code:"MB", name:"Manitoba", status:"planned", stores:30, lps:15, priority:5,
        notes:"Small privatized market. Logical with SK.",
        milestones:["SK active","First MB dispensary"],
        completed:[false,false] },
      { code:"QC", name:"Québec", status:"future", stores:110, lps:40, priority:6,
        notes:"SAQ-Cannabis model, language barrier, distinct regulatory culture. Requires French UI and QC-specific compliance layer.",
        milestones:["French UI complete","QC compliance layer","First QC partner"],
        completed:[false,false,false] },
      { code:"ATL", name:"Atlantic Provinces", status:"future", stores:80, lps:25, priority:7,
        notes:"NS, NB, PEI, NL. Mix of government and private models. Bundle as a region.",
        milestones:["National traction","First ATL partner"],
        completed:[false,false] },
    ];

    const statusColor = { active:"#22c55e", next:"#f59e0b", planned:"#3b82f6", future:"#9ca3af" };
    const statusLabel = { active:"Active", next:"Next", planned:"Planned", future:"Future" };

    const totalStores = provinces.reduce((s,p)=>s+p.stores,0);
    const totalLPs = provinces.reduce((s,p)=>s+p.lps,0);
    const activeStores = provinces.filter(p=>p.status==="active").reduce((s,p)=>s+p.stores,0);

    return (
      <div className="fade" style={{ maxWidth:900,margin:"0 auto",padding:"28px 20px" }}>
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:28,fontWeight:300,color:"#f0f4f0",marginBottom:4 }}>National Rollout</h1>
          <p style={{ fontSize:12,color:"rgba(240,244,240,0.4)",fontFamily:"'DM Mono',monospace" }}>
            Province-by-province expansion pipeline. Same federal licence structure, same Cannabis Act, same lab networks.
          </p>
        </div>

        {/* National TAM */}
        <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:24 }}>
          {[
            { label:"Total Canadian Stores", value:totalStores.toLocaleString(), color:"#4ade80" },
            { label:"Total LPs", value:totalLPs, color:"#60a5fa" },
            { label:"Current Market", value:activeStores, color:"#22c55e" },
            { label:"Provinces Mapped", value:provinces.length, color:"#a78bfa" },
          ].map(k=>(
            <div key={k.label} style={{ background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"16px 18px" }}>
              <div style={{ fontFamily:"'Fraunces',serif",fontSize:24,fontWeight:300,color:k.color,marginBottom:2 }}>{k.value}</div>
              <div style={{ fontSize:9,color:"rgba(240,244,240,0.35)",fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:"0.08em" }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Province cards */}
        {provinces.map(p=>{
          const completedCount = p.completed.filter(Boolean).length;
          const pct = Math.round((completedCount/p.milestones.length)*100);
          return (
            <div key={p.code} style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.06)",
              borderLeft:"3px solid " + (statusColor[p.status]),borderRadius:12,padding:"16px 18px",marginBottom:10 }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:10 }}>
                <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                  <div style={{ fontFamily:"'Fraunces',serif",fontSize:20,color:"#f0f4f0" }}>{p.name}</div>
                  <div style={{ fontSize:8,background:(statusColor[p.status]) + "20",color:statusColor[p.status],padding:"2px 8px",borderRadius:10,fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:"0.08em",border:"1px solid " + (statusColor[p.status]) + "30" }}>
                    {statusLabel[p.status]}
                  </div>
                </div>
                <div style={{ display:"flex",gap:16,flexShrink:0 }}>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:14,fontFamily:"'Fraunces',serif",color:"#4ade80" }}>{p.stores}</div>
                    <div style={{ fontSize:8,color:"rgba(240,244,240,0.3)",fontFamily:"'DM Mono',monospace" }}>STORES</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontSize:14,fontFamily:"'Fraunces',serif",color:"#60a5fa" }}>{p.lps}</div>
                    <div style={{ fontSize:8,color:"rgba(240,244,240,0.3)",fontFamily:"'DM Mono',monospace" }}>LPs</div>
                  </div>
                </div>
              </div>
              <div style={{ fontSize:11,color:"rgba(240,244,240,0.45)",fontFamily:"'DM Mono',monospace",marginBottom:10,lineHeight:1.5 }}>{p.notes}</div>
              {/* Milestone track */}
              <div style={{ display:"flex",gap:4,flexWrap:"wrap" }}>
                {p.milestones.map((m,i)=>(
                  <div key={m} style={{ display:"flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:8,
                    background: p.completed[i]?"rgba(74,222,128,0.15)":"rgba(255,255,255,0.04)",
                    border: p.completed[i]?"1px solid rgba(74,222,128,0.3)":"1px solid rgba(255,255,255,0.08)" }}>
                    <span style={{ fontSize:9,color:p.completed[i]?"#4ade80":"rgba(240,244,240,0.3)",fontFamily:"'DM Mono',monospace" }}>
                      {p.completed[i]?"✓":i+1}
                    </span>
                    <span style={{ fontSize:9,color:p.completed[i]?"rgba(240,244,240,0.7)":"rgba(240,244,240,0.3)",fontFamily:"'DM Mono',monospace" }}>
                      {m}
                    </span>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              <div style={{ marginTop:8,height:2,background:"rgba(255,255,255,0.06)",borderRadius:4,overflow:"hidden" }}>
                <div style={{ height:"100%",width:(pct) + "%",background:statusColor[p.status],transition:"width 0.5s" }}/>
              </div>
            </div>
          );
        })}
      </div>
    );
  };



  // ══════════════════════════════════════════════════════════════
  // GUIDED DEMO TOUR — 3 AUDIENCES
  // ══════════════════════════════════════════════════════════════

  const DEMO_STEPS_STAFF = [
    {
      id: "welcome",
      title: "iTerps for Dispensaries",
      body: "A 7-step walkthrough built for dispensary staff. Watch the finder work, see customer profiles, batch drift detection, network benchmarking, and staff training — all in one platform.",
      cta: "Get started →",
    },
    {
      id: "home",
      title: "Step 1 — Your Daily Dashboard",
      body: "Today’s date, freshness alerts for aging SKUs, terpene frequency across your inventory, and quick access to every tool. Managers see batch logs and irradiation breakdowns on the same screen.",
      cta: "Next →",
      action: "setPage", page: "home",
    },
    {
      id: "finder",
      title: "Step 2 — Staff Strain Finder",
      body: "Customer walks in and says 'something earthy, not too strong, in a pre-roll.' Five questions — the algorithm scores all 334 SKUs and returns ranked matches with percentage scores.",
      cta: "Open Finder →",
      action: "setPage", page: "finder",
    },
    {
      id: "explorer",
      title: "Step 3 — Product Explorer",
      body: "Browse all 334 SKUs filtered by terpene, potency, category, grow method, city, and brand. Click any product for full lab data, batch history, irradiation status, and similar products.",
      cta: "Open Explorer →",
      action: "setPage", page: "explorer",
    },
    {
      id: "profile",
      title: "Step 4 — Customer Profiles",
      body: "Save each customer terpene fingerprint after a match. Phone number is the key — no app needed. Next visit, pull up their passport, see what they liked, and get instant new recommendations.",
      cta: "See Customers →",
      action: "setPage", page: "customers",
    },
    {
      id: "drift",
      title: "Step 5 — Batch Drift Detection",
      body: "Reordered a bestseller and the new batch smells different? Batch Drift compares consecutive batches by terpene profile — flag it before a loyal customer notices and asks questions.",
      cta: "Open Batch Tracker →",
      action: "setRetailTab_reorder",
    },
    {
      id: "benchmark",
      title: "Step 6 — Network Benchmarking",
      body: "Compare your store's terpene coverage against the BC network average. Know exactly which terpenes you're under-indexed on and which LP to source from.",
      cta: "See Benchmarking →",
      action: "setPage", page: "benchmark",
    },
    {
      id: "training",
      title: "Step 7 — Staff Training & Certs",
      body: "Structured modules, a 20-question flash quiz, and printable certificates with verifiable IDs. The Staff Performance page tracks who has been trained and their quiz scores.",
      cta: "See Training →",
      action: "setPage", page: "training",
    },
    {
      id: "done",
      title: "Ready to get started?",
      body: "Real BC lab data. Cannabis Act compliant. No contract. First month free. Fill out the onboarding form and we will have you set up within 1 business day.",
      cta: "Get Set Up →",
      action: "onboard",
    },
  ];

  const DEMO_STEPS_LP = [
    {
      id: "welcome",
      title: "iTerps for Licensed Producers",
      body: "A 5-step walkthrough for LPs. See how your COA data gets submitted, verified, and surfaced to budtenders and consumers across BC — in real time.",
      cta: "Get started →",
    },
    {
      id: "lp_overview",
      title: "Step 1 — LP Portal Overview",
      body: "Your home base. See all your verified SKUs in the network, lab coverage %, which batches are active, and your visibility score across BC dispensaries.",
      cta: "Open LP Portal →",
      action: "setLpTab", lpTab: "overview",
    },
    {
      id: "lp_upload",
      title: "Step 2 — AI COA Parser",
      body: "Upload a PDF Certificate of Analysis from your lab. The AI extracts all 21 terpene values, 14 cannabinoids, batch number, lot, lab name, and quality flags — in seconds.",
      cta: "See COA Parser →",
      action: "setLpTab", lpTab: "upload",
    },
    {
      id: "lp_data",
      title: "Step 3 — Data Status",
      body: "Track every SKU in the network — which have verified lab data, which are running on estimated profiles, and which dispensaries are carrying each product.",
      cta: "See Data Status →",
      action: "setLpTab", lpTab: "data",
    },
    {
      id: "lp_index",
      title: "Step 4 — BC Demand Index",
      body: "Proprietary network intelligence. Terpene demand rankings across 334 SKUs and 97 dispensaries. Know which terpene profiles are under-supplied in your target markets before you plan next season.",
      cta: "See Demand Index →",
      action: "setLpTab", lpTab: "index",
    },
    {
      id: "lp_done",
      title: "Data partnership, not advertising",
      body: "No pay-to-play. No promoted placement. Accurate lab data earns visibility — verified SKUs surface first in every finder, explorer, and consumer search across the platform.",
      cta: "Request a Walkthrough →",
      action: "setLpTab", lpTab: "demo",
    },
  ];

  const DEMO_STEPS_CONSUMER = [
    {
      id: "welcome",
      title: "Find cannabis that actually works for you",
      body: "iTerps uses terpene science — the actual chemical compounds responsible for how a strain smells and what makes each one unique. No mood language. No guessing.",
      cta: "Show me →",
    },
    {
      id: "c_match",
      title: "Step 1 — Terpene Match",
      body: "Answer 5 questions about what you are drawn to smell and how you want to use it. The algorithm scores every verified SKU and returns ranked matches with percentage scores.",
      cta: "Try the Matcher →",
      action: "setConsumerTab", tab: "find",
    },
    {
      id: "c_browse",
      title: "Step 2 — Browse & Filter",
      body: "Browse all 334 verified SKUs — filter by city, aroma family, product type, brand, or grow method. Sort by terpene content, THC, or freshness.",
      cta: "Browse Products →",
      action: "setConsumerTab", tab: "browse",
    },
    {
      id: "c_passport",
      title: "Step 3 — Your Terpene Passport",
      body: "Save your preferences to a passport with just a phone number — no account, no app. Next time you’re at a store, your budtender can pull it up instantly and know exactly what to recommend.",
      cta: "See Passport →",
      action: "setConsumerTab", tab: "profile",
    },
    {
      id: "c_done",
      title: "Free. Always.",
      body: "iTerps is 100% free for consumers. No account required to match. Create a passport to save your history and rate what you&#39;ve tried. Your data is private and never sold.",
      cta: "Start Matching →",
      action: "exitToConsumer",
    },
  ];

  const DemoTour = () => {
    if (!demoActive) return null;

    const steps = demoRole === "lp" ? DEMO_STEPS_LP
                : demoRole === "consumer" ? DEMO_STEPS_CONSUMER
                : DEMO_STEPS_STAFF;

    const step = steps[demoStep];
    if (!step) return null;

    const progress = (demoStep / (steps.length - 1)) * 100;
    const isFirst = demoStep === 0;
    const isLast  = demoStep === steps.length - 1;

    const roleColor = demoRole === "lp" ? "#60a5fa"
                    : demoRole === "consumer" ? "#f472b6"
                    : "#4ade80";

    const roleBg = demoRole === "lp" ? "#0f172a"
                 : demoRole === "consumer" ? "#1a0a1a"
                 : "#1a2e1a";

    const roleLabel = demoRole === "lp" ? "LP Demo"
                    : demoRole === "consumer" ? "Consumer Demo"
                    : "Dispensary Demo";

    const navigate = (s) => {
      if (!s.action) return;
      if (s.action === "setPage") {
        // If we are in consumer mode and navigating to a consumer-aware page, handle properly
        if (demoRole === "consumer") {
          setConsumerMode(true);
          if (s.page === "explorer") setConsumerTab("browse");
          else if (s.page === "home") { setConsumerTab("find"); }
        }
        setPage(s.page);
      }
      if (s.action === "setRetailTab_reorder") {
        setConsumerMode(false);
        setPage("retailer");
        setRetTab("reorder");
      }
      if (s.action === "setLpTab") {
        setConsumerMode(false);
        setPage("lp");
        setLpTabState(s.lpTab);
      }
      if (s.action === "setConsumerTab") {
        setConsumerMode(true);
        setPage("consumer");
        setConsumerTab(s.tab);
      }
      if (s.action === "onboard") {
        setShowDispOnboard(true);
        setDemoActive(false);
        setDemoStep(0);
      }
      if (s.action === "exitToConsumer") {
        setConsumerMode(true);
        setConsumerTab("find");
        setPage("consumer");
        setDemoActive(false);
        setDemoStep(0);
      }
    };

    const advance = () => {
      const next = steps[demoStep + 1];
      if (!next) { setDemoActive(false); setDemoStep(0); return; }
      navigate(next);
      setDemoStep(s => s + 1);
    };

    const goBack = () => {
      const prev = steps[demoStep - 1];
      if (!prev) return;
      navigate(prev);
      setDemoStep(s => Math.max(0, s - 1));
    };

    const exit = () => {
      setDemoActive(false);
      setDemoStep(0);
      setSmsModal(null);
      setSmsPhone("");
      setSmsSent(false);
      // If consumer demo, stay in consumer mode but go to home
      if (demoRole === "consumer") { setPage("consumer"); }
    };

    return (
      <div style={{
        position: "fixed", bottom: 16, right: 16, zIndex: 9500,
        width: "min(340px, calc(100vw - 32px))", background: roleBg,
        borderRadius: 18, boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
        overflow: "hidden", fontFamily: "'DM Mono',monospace",
      }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: "rgba(255,255,255,0.08)" }}>
          <div style={{ height: "100%", width: (progress) + "%", background: roleColor, transition: "width 0.4s ease" }}/>
        </div>

        <div style={{ padding: "18px 22px" }}>
          {/* Header row */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 12 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ fontSize:8, color: roleColor, textTransform:"uppercase", letterSpacing:"0.12em", background:(roleColor) + "18", border:"1px solid " + (roleColor) + "30", padding:"2px 8px", borderRadius:10 }}>
                {roleLabel}
              </div>
              {/* Step dots */}
              <div style={{ display:"flex", gap: 3 }}>
                {steps.map((_, i) => (
                  <div key={i} style={{
                    width: i === demoStep ? 14 : 5, height: 5, borderRadius: 3,
                    background: i < demoStep ? roleColor : i === demoStep ? roleColor : "rgba(255,255,255,0.12)",
                    transition: "all 0.3s ease"
                  }}/>
                ))}
              </div>
            </div>
            <button onClick={exit} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.25)", cursor:"pointer", fontSize:16, lineHeight:1, padding:0 }}>✕</button>
          </div>

          {/* Step label */}
          <div style={{ fontSize:9, color: roleColor, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom: 5 }}>
            {isFirst ? roleLabel : isLast ? "Finish" : "Step " + (demoStep) + " of " + (steps.length - 2)}
          </div>

          {/* Title */}
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: 17, fontWeight: 300, color: "#f0f4f0", marginBottom: 8, lineHeight: 1.3 }}>
            {step.title}
          </div>

          {/* Body */}
          <div style={{ fontSize: 11, color: "rgba(240,244,240,0.58)", lineHeight: 1.65, marginBottom: 16 }}>
            {step.body}
          </div>

          {/* Actions */}
          <div style={{ display:"flex", gap: 8 }}>
            {!isFirst && (
              <button onClick={goBack}
                style={{ padding:"8px 14px", borderRadius:9, border:"1px solid rgba(255,255,255,0.1)", background:"transparent",
                  color:"rgba(255,255,255,0.35)", fontSize:11, cursor:"pointer" }}>
                ← Back
              </button>
            )}
            <button onClick={advance}
              style={{ flex:1, padding:"10px 0", borderRadius:9,
                background: isLast ? roleColor : (roleColor) + "28",
                border: isLast ? "none" : "1px solid " + (roleColor) + "40",
                color: isLast ? roleBg : roleColor,
                fontSize: 12, fontWeight: 600, cursor:"pointer", transition:"all 0.15s" }}>
              {step.cta}
            </button>
          </div>
        </div>
      </div>
    );
  };


  // ── FEATURE 1: SIMILAR TO THIS MODAL ─────────────────────
  const SimilarToThisModal = () => {
    if (!similarModal) return null;
    const p = similarModal;
    const similars = ALL_PRODUCTS
      .filter(q => q.id !== p.id && q.terpene_source === "lab_tested")
      .map(q => ({ ...q, sim: cosineSimilarity(p.terpenes, q.terpenes) }))
      .sort((a, b) => b.sim - a.sim)
      .slice(0, 8);

    return (
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:9000, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
        onClick={e => { if (e.target === e.currentTarget) setSimilarModal(null); }}>
        <div style={{ background:"#fff", borderRadius:18, maxWidth:640, width:"100%", maxHeight:"85vh", overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:"0 24px 80px rgba(0,0,0,0.22)" }}>
          <div style={{ background:"linear-gradient(135deg,#1a2e1a,#2d4a2d)", padding:"20px 24px", flexShrink:0 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <div style={{ fontSize:9, color:"rgba(255,255,255,0.5)", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>SIMILAR TERPENE PROFILES TO</div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:300, color:"#fff" }}>{p.name}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", fontFamily:"'DM Mono',monospace", marginTop:2 }}>{p.brand}</div>
              </div>
              <button onClick={() => setSimilarModal(null)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.5)", fontSize:20, cursor:"pointer", lineHeight:1 }}>✕</button>
            </div>
            <div style={{ marginTop:12, display:"flex", gap:6, flexWrap:"wrap" }}>
              {Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1]).slice(0,3).map(([t,v]) => (
                <span key={t} style={{ fontSize:10, background:"rgba(255,255,255,0.1)", color:"rgba(255,255,255,0.8)", padding:"3px 10px", borderRadius:20, fontFamily:"'DM Mono',monospace" }}>
                  {t} {(v*100).toFixed(0)}mg/g
                </span>
              ))}
            </div>
          </div>
          <div style={{ overflowY:"auto", padding:"16px 20px", display:"flex", flexDirection:"column", gap:10 }}>
            {similars.map(q => {
              const topT = Object.entries(q.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
              const tInfo = topT ? TERPENES[topT[0]] : null;
              const disp = DB_DISPENSARIES.find(d => d.id === q.dispensary_id);
              const pct = Math.round(q.sim * 100);
              return (
                <div key={q.id} style={{ display:"flex", gap:12, alignItems:"center", padding:"12px 14px", background:"#f7f5f0", borderRadius:12, border:"1px solid #e8e4de", cursor:"pointer" }}
                  onClick={() => { setSimilarModal(null); setSelectedProduct(q); setPage("product"); }}>
                  <div style={{ width:42, height:42, borderRadius:10, background:(tInfo?.color||"#4a7c59") + "20", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <div style={{ width:14, height:14, borderRadius:"50%", background:tInfo?.color||"#4a7c59" }}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:1 }}>{q.brand} · {q.category}</div>
                    <div style={{ fontFamily:"'Fraunces',serif", fontSize:14, color:"#1a2e1a", marginBottom:2 }}>{q.name}</div>
                    <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{disp?.name} · {disp?.city}</div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, color: pct >= 80?"#16a34a":pct>=60?"#ca8a04":"#6b7280" }}>{pct}%</div>
                    <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>match</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding:"12px 20px", borderTop:"1px solid #f3f4f6", flexShrink:0, background:"#fafafa" }}>
            <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
              Similarity calculated using cosine distance across full terpene profile · tap any result to view full product details
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── FEATURE 2: TERPENE FLASH QUIZ ────────────────────────
  // Build a flat question bank from all TRAINING_MODULES + terpene data
  const buildFlashBank = () => {
    const bank = [];
    // From training module quizzes
    TRAINING_MODULES.forEach(mod => {
      mod.quiz.forEach(q => bank.push({ ...q, source: mod.title }));
    });
    // From terpene data — "Which terpene smells like X?"
    Object.entries(TERPENES).forEach(([name, t]) => {
      if (!t.plain) return;
      const others = Object.keys(TERPENES).filter(n => n !== name);
      const wrongs = others.sort(() => Math.random()-0.5).slice(0,3);
      const correctIdx = Math.floor(Math.random()*4);
      const opts = [...wrongs];
      opts.splice(correctIdx, 0, name);
      bank.push({
        q: "Which terpene is described as: \"" + (t.plain.split('.')[0]) + "\"?",
        options: opts,
        correct: correctIdx,
        source: "Terpene Identification"
      });
    });
    return bank.sort(() => Math.random()-0.5).slice(0, 20);
  };

  const TerpeneFlashQuiz = () => {
    if (!quizFlashActive) return (
      <div style={{ maxWidth:560, margin:"0 auto", padding:"28px 20px", textAlign:"center" }}>
        <div style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:"#1a2e1a", marginBottom:8 }}>Terpene Flash Quiz</div>
        <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:28 }}>20 randomised questions drawn from all training modules and terpene identification. Timed round, no penalties for guessing.</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:28, maxWidth:360, margin:"0 auto 28px" }}>
          {[["📚","20 Questions","Mixed from all modules"],["⏱","~5 min","At your own pace"],["🎯","Instant feedback","Explanation on every answer"]].map(([icon,label,sub])=>(
            <div key={label} style={{ background:"#f7f5f0", borderRadius:12, padding:"14px 10px" }}>
              <div style={{ fontSize:22, marginBottom:4 }}>{icon}</div>
              <div style={{ fontSize:12, fontFamily:"'Fraunces',serif", color:"#1a2e1a", marginBottom:2 }}>{label}</div>
              <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{sub}</div>
            </div>
          ))}
        </div>
        <button className="btn-primary" style={{ padding:"12px 36px", fontSize:13 }}
          onClick={() => {
            const bank = buildFlashBank();
            setQuizFlashBank(bank);
            setQuizFlashIdx(0);
            setQuizFlashAnswer(null);
            setQuizFlashScore(0);
            setQuizFlashDone(false);
            setQuizFlashActive(true);
          }}>
          Start Quiz →
        </button>
      </div>
    );

    if (quizFlashDone) {
      const pct = Math.round((quizFlashScore / quizFlashBank.length) * 100);
      const grade = pct >= 85 ? "Excellent" : pct >= 70 ? "Good" : pct >= 50 ? "Needs Review" : "Keep Studying";
      const gradeColor = pct >= 85 ? "#16a34a" : pct >= 70 ? "#ca8a04" : pct >= 50 ? "#ea580c" : "#dc2626";
      return (
        <div style={{ maxWidth:480, margin:"0 auto", padding:"28px 20px", textAlign:"center" }} className="fade">
          <div style={{ fontSize:48, marginBottom:12 }}>{pct >= 85 ? "🏆" : pct >= 70 ? "🎯" : pct >= 50 ? "📚" : "💪"}</div>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:28, fontWeight:300, color:gradeColor, marginBottom:4 }}>{grade}</div>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:48, fontWeight:300, color:"#1a2e1a", marginBottom:4 }}>{quizFlashScore}<span style={{ fontSize:20, color:"#9ca3af" }}>/{quizFlashBank.length}</span></div>
          <div style={{ fontSize:12, color:"#6b7280", fontFamily:"'DM Mono',monospace", marginBottom:28 }}>{pct}% correct</div>
          <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
            <button className="btn-ghost" onClick={() => setQuizFlashActive(false)}>Back</button>
            <button className="btn-primary" onClick={() => {
              const bank = buildFlashBank();
              setQuizFlashBank(bank);
              setQuizFlashIdx(0);
              setQuizFlashAnswer(null);
              setQuizFlashScore(0);
              setQuizFlashDone(false);
            }}>Try Again →</button>
          </div>
        </div>
      );
    }

    const q = quizFlashBank[quizFlashIdx];
    if (!q) return null;
    const isAnswered = quizFlashAnswer !== null;

    return (
      <div style={{ maxWidth:560, margin:"0 auto", padding:"20px 16px" }} className="fade">
        {/* Progress bar */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
          <div style={{ flex:1, height:4, background:"#f3f4f6", borderRadius:2 }}>
            <div style={{ height:4, background:"#4a7c59", borderRadius:2, width:(((quizFlashIdx)/quizFlashBank.length)*100) + "%", transition:"width 0.3s" }}/>
          </div>
          <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace", flexShrink:0 }}>{quizFlashIdx+1}/{quizFlashBank.length}</div>
          <div style={{ fontSize:10, color:"#4a7c59", fontFamily:"'DM Mono',monospace", flexShrink:0 }}>Score: {quizFlashScore}</div>
        </div>

        {/* Source tag */}
        <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.08em" }}>{q.source}</div>

        {/* Question */}
        <div style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:400, color:"#1a2e1a", marginBottom:20, lineHeight:1.4 }}>{q.q}</div>

        {/* Options */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
          {q.options.map((opt, idx) => {
            let cls = "quiz-opt";
            let extra = {};
            if (isAnswered) {
              if (idx === q.correct) extra = { borderColor:"#22c55e", background:"#f0fdf4", color:"#065f46" };
              else if (idx === quizFlashAnswer) extra = { borderColor:"#ef4444", background:"#fef2f2", color:"#991b1b" };
              else extra = { opacity:0.5 };
            }
            return (
              <button key={idx} className={cls} style={{ ...extra }}
                disabled={isAnswered}
                onClick={() => {
                  setQuizFlashAnswer(idx);
                  if (idx === q.correct) setQuizFlashScore(s => s+1);
                }}>
                <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, color:"#9ca3af", marginRight:8 }}>{String.fromCharCode(65+idx)}.</span>
                {opt}
                {isAnswered && idx === q.correct && <span style={{ marginLeft:8 }}>✓</span>}
                {isAnswered && idx === quizFlashAnswer && idx !== q.correct && <span style={{ marginLeft:8 }}>✗</span>}
              </button>
            );
          })}
        </div>

        {/* Explanation + Next */}
        {isAnswered && (
          <div className="fade">
            {q.explanation && (
              <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:12, color:"#065f46", lineHeight:1.5 }}>
                {q.explanation}
              </div>
            )}
            <button className="btn-primary" style={{ width:"100%" }}
              onClick={() => {
                if (quizFlashIdx < quizFlashBank.length - 1) {
                  setQuizFlashIdx(i => i+1);
                  setQuizFlashAnswer(null);
                } else {
                  setQuizFlashDone(true);
                  saveQuizScore("Staff", quizFlashScore, quizFlashBank.length);
                  trackEvent("quiz_completed", { score: quizFlashScore, total: quizFlashBank.length, pct: Math.round((quizFlashScore/quizFlashBank.length)*100), mode: "staff" });
                }
              }}>
              {quizFlashIdx < quizFlashBank.length - 1 ? "Next →" : "See Results →"}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── FEATURE 3: DISPENSARY BENCHMARKING ───────────────────
  const BenchmarkingSection = () => {
    const TERPENE_KEYS = ["β-Myrcene","β-Caryophyllene","(+)-Limonene","α-Pinene","Terpinolene","Linalool","β-Ocimene","α-Humulene","α-Bisabolol"];

    // Network-wide averages (all lab-tested SKUs)
    const labSKUs = ALL_PRODUCTS.filter(p => p.terpene_source === "lab_tested");
    const networkAvg = {};
    TERPENE_KEYS.forEach(t => {
      const vals = labSKUs.map(p => p.terpenes[t]||0);
      networkAvg[t] = vals.reduce((a,b)=>a+b,0)/Math.max(vals.length,1);
    });
    const networkTerpDiv = new Set(labSKUs.flatMap(p => Object.keys(p.terpenes).filter(t=>p.terpenes[t]>0))).size;
    const networkLabRate = Math.round((labSKUs.length / Math.max(ALL_PRODUCTS.length,1))*100);

    // Selected dispensary stats
    const selDisp = DB_DISPENSARIES.find(d => d.id === benchmarkDispId);
    const dispSKUs = benchmarkDispId === "all" ? labSKUs : labSKUs.filter(p => p.dispensary_id === benchmarkDispId);
    const dispAll = benchmarkDispId === "all" ? ALL_PRODUCTS : ALL_PRODUCTS.filter(p => p.dispensary_id === benchmarkDispId);

    const dispAvg = {};
    TERPENE_KEYS.forEach(t => {
      const vals = dispSKUs.map(p => p.terpenes[t]||0);
      dispAvg[t] = vals.reduce((a,b)=>a+b,0)/Math.max(vals.length,1);
    });
    const dispTerpDiv = new Set(dispSKUs.flatMap(p => Object.keys(p.terpenes).filter(t=>p.terpenes[t]>0))).size;
    const dispLabRate = Math.round((dispSKUs.length / Math.max(dispAll.length,1))*100);

    // Diversity score (0–100): unique terpenes present / total possible × 100
    const diversityScore = Math.round((dispTerpDiv / (TERPENE_KEYS.length * 3)) * 100);
    const networkDivScore = Math.round((networkTerpDiv / (TERPENE_KEYS.length * 3)) * 100);

    // Top and gap terpenes vs network
    const terpDiffs = TERPENE_KEYS.map(t => ({ t, diff: (dispAvg[t]||0) - (networkAvg[t]||0) }));
    const strengths = terpDiffs.filter(d=>d.diff>0).sort((a,b)=>b.diff-a.diff).slice(0,3);
    const gaps = terpDiffs.filter(d=>d.diff<=0).sort((a,b)=>a.diff-b.diff).slice(0,3);

    return (
      <div style={{ maxWidth:860, margin:"0 auto", padding:"28px 20px" }}>
        <button onClick={()=>setPage("dashboard")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:20}}>← Dashboard</button>
        <div style={{ marginBottom:20 }}>
          <h1 style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300, color:"#1a2e1a", marginBottom:4 }}>Network Benchmarking</h1>
          <p style={{ fontSize:12, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Compare terpene diversity and coverage against the BC network average.</p>
        </div>

        {/* Dispensary selector */}
        <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap", alignItems:"flex-end" }}>
          <div style={{ flex:"1 1 240px" }}>
            <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.08em" }}>Dispensary</div>
            <select value={benchmarkDispId} onChange={e=>setBenchmarkDispId(e.target.value)}
              style={{ width:"100%", padding:"9px 12px", border:"1px solid #e8e4de", borderRadius:10, fontSize:12, fontFamily:"'DM Mono',monospace", background:"#fff", outline:"none", cursor:"pointer" }}>
              <option value="all">— BC Network Average —</option>
              {DB_DISPENSARIES.sort((a,b)=>a.name.localeCompare(b.name)).map(d=>(
                <option key={d.id} value={d.id}>{d.name} · {d.city}</option>
              ))}
            </select>
          </div>

        </div>

        {/* Score cards */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:20 }}>
          {[
            { label:"SKUs Tracked", val:dispAll.length, net:ALL_PRODUCTS.length, unit:"" },
            { label:"Lab Verified", val:(dispLabRate) + "%", net:(networkLabRate) + "%", unit:"" },
            { label:"Terpene Diversity", val:dispTerpDiv, net:networkTerpDiv, unit:" unique terps" },
            { label:"Diversity Score", val:diversityScore, net:networkDivScore, unit:"/100" },
          ].map(({ label, val, net, unit }) => (
            <div key={label} style={{ background:"#fff", border:"1px solid #e8e4de", borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:6, textTransform:"uppercase" }}>{label}</div>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:24, color:"#1a2e1a", marginBottom:2 }}>{val}{unit}</div>
              <div style={{ fontSize:10, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Network: {net}{unit}</div>
            </div>
          ))}
        </div>

        {/* Terpene coverage bars */}
        <div style={{ background:"#fff", border:"1px solid #e8e4de", borderRadius:12, padding:"18px 20px", marginBottom:16 }}>
          <div style={{ fontSize:10, fontFamily:"'DM Mono',monospace", color:"#9ca3af", fontWeight:600, marginBottom:14, textTransform:"uppercase", letterSpacing:"0.08em" }}>
            Terpene Coverage vs Network Average
          </div>
          {TERPENE_KEYS.map(t => {
            const dVal = dispAvg[t]||0;
            const nVal = networkAvg[t]||0;
            const maxV = Math.max(dVal, nVal, 0.01);
            const info = TERPENES[t];
            const diff = dVal - nVal;
            return (
              <div key={t} style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <div style={{ width:7, height:7, borderRadius:"50%", background:info?.color||"#4a7c59" }}/>
                    <span style={{ fontSize:11, color:"#374151", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                  </div>
                  <span style={{ fontSize:10, fontFamily:"'DM Mono',monospace",
                    color: diff > 0.01?"#16a34a": diff < -0.01?"#dc2626":"#9ca3af" }}>
                    {diff > 0.001 ? "+" + ((diff*100).toFixed(0)) + "mg/g" : diff < -0.001 ? ((diff*100).toFixed(0)) + "mg/g" : "On par"}
                  </span>
                </div>
                <div style={{ position:"relative", height:8, background:"#f3f4f6", borderRadius:4 }}>
                  {/* Network bar */}
                  <div style={{ position:"absolute", left:0, top:1, height:6, background:"#e5e7eb", borderRadius:3, width:((nVal/maxV)*100) + "%" }}/>
                  {/* Dispensary bar */}
                  <div style={{ position:"absolute", left:0, top:1, height:6, background:info?.color||"#4a7c59", borderRadius:3, width:((dVal/maxV)*100) + "%", opacity:0.8 }}/>
                </div>
                <div style={{ display:"flex", gap:12, marginTop:3 }}>
                  <span style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>
                    {benchmarkDispId==="all"?"Network":"Store"}: {(dVal*100).toFixed(1)}mg/g
                  </span>
                  {benchmarkDispId !== "all" && <span style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Network avg: {(nVal*100).toFixed(1)}mg/g</span>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Strengths & gaps */}
        {benchmarkDispId !== "all" && (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:10, color:"#065f46", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:10, textTransform:"uppercase" }}>Terpene Strengths</div>
              {strengths.length > 0 ? strengths.map(({t, diff}) => (
                <div key={t} style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                  <span style={{ color:"#1a2e1a" }}>{t}</span>
                  <span style={{ color:"#16a34a", fontWeight:600 }}>+{(diff*100).toFixed(0)}mg/g above avg</span>
                </div>
              )) : <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>No data yet</div>}
            </div>
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:12, padding:"14px 16px" }}>
              <div style={{ fontSize:10, color:"#991b1b", fontFamily:"'DM Mono',monospace", fontWeight:600, marginBottom:10, textTransform:"uppercase" }}>Coverage Gaps</div>
              {gaps.length > 0 ? gaps.map(({t, diff}) => (
                <div key={t} style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:12, fontFamily:"'DM Mono',monospace" }}>
                  <span style={{ color:"#1a2e1a" }}>{t}</span>
                  <span style={{ color:"#dc2626", fontWeight:600 }}>{(diff*100).toFixed(0)}mg/g below avg</span>
                </div>
              )) : <div style={{ fontSize:11, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>No gaps detected</div>}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── FEATURE 4: NEW TO CANNABIS MODE ──────────────────────
  const NEWBIE_QUESTIONS = [
    { id:"format", question:"How are you thinking of using it?", emoji:"🌿",
      options:[
        { label:"Dried flower",        value:"flower",      categories:["Dried Flower"],               desc:"Smoked or vaporized — full terpene variety, broad product selection" },
        { label:"Pre-roll",            value:"preroll",     categories:["Pre-Roll"],                   desc:"Ready to use from the package — no prep needed" },
        { label:"Vape pen",            value:"vape",        categories:["Vape Cart","Disposable Vape"],desc:"Discreet, no combustion, faster onset than edibles" },
        { label:"Edible or capsule",   value:"edible",      categories:["Edible"],                     desc:"No inhalation — onset 30 min to 2 hours, longer duration" },
        { label:"Oil or tincture",     value:"oil",         categories:["Oil"],                        desc:"A few drops under the tongue or added to food — easy to dose" },
        { label:"Beverage",            value:"beverage",    categories:["Beverage"],                   desc:"Infused drinks — often faster onset than solid edibles" },
        { label:"Not sure yet",        value:"any",         categories:null,                           desc:"Show me everything, I will decide" },
      ]},
    { id:"aroma", question:"What kind of smell appeals to you?", emoji:"👃",
      options:[
        { label:"Citrusy & fresh",     value:"citrus",  terps:["(+)-Limonene","Terpinolene"],       aromas:["Citrus","Lemon","Mango","Tropical","Orange"] },
        { label:"Earthy & woody",      value:"earthy",  terps:["β-Myrcene","β-Caryophyllene"],      aromas:["Earthy","Woody","Pine","Herbal","Hash"] },
        { label:"Floral & sweet",      value:"floral",  terps:["Linalool","α-Bisabolol"],            aromas:["Floral","Lavender","Sweet","Vanilla","Berry"] },
        { label:"Fruity & candy",      value:"fruity",  terps:["(+)-Limonene","β-Ocimene"],         aromas:["Berry","Grape","Candy","Fruity","Banana"] },
        { label:"No preference",       value:"any",     terps:[], aromas:[] },
      ]},
    { id:"thc", question:"Where do you want to start with THC?", emoji:"📊",
      subtitle:"Health Canada recommends starting with a lower amount and waiting to understand your response",
      options:[
        { label:"Lower THC — under 15%",   value:"low",  thcMax:15,  desc:"Often recommended as a starting point — easier to gauge your response" },
        { label:"Mid-range — 15 to 20%",   value:"mid",  thcMax:20,  desc:"Common range in Canadian stores" },
        { label:"Not sure — show me options", value:"any", thcMax:null, desc:"Show me everything" },
      ]},
  ];

  const NewbieFinder = () => {
    const [step, setStep] = React.useState(0);
    const [answers, setAnswers] = React.useState({});
    const [results, setResults] = React.useState(null);

    const q = NEWBIE_QUESTIONS[step];

    const score = (p) => {
      let s = 0;
      const fa = answers.format;
      if (fa && fa !== "any") {
        const fq = NEWBIE_QUESTIONS[0].options.find(o=>o.value===fa);
        if (fq?.categories && !fq.categories.includes(p.category)) return -999;
      }
      const aa = answers.aroma;
      if (aa && aa !== "any") {
        const aq = NEWBIE_QUESTIONS[1].options.find(o=>o.value===aa);
        aq?.terps?.forEach((t,i) => { if (p.terpenes[t]) s += (2-i) * p.terpenes[t] * 15; });
        const pa = (p.aroma||[]).map(a=>a.toLowerCase());
        s += (aq?.aromas||[]).filter(a => pa.some(pa=>pa.includes(a.toLowerCase()))).length * 10;
      }
      const ta = answers.thc;
      if (ta && ta !== "any") {
        const tq = NEWBIE_QUESTIONS[2].options.find(o=>o.value===ta);
        if (tq?.thcMax && p.thc > tq.thcMax) s -= (p.thc - tq.thcMax) * 4;
        else if (tq?.thcMax) s += 20;
      }
      if (p.terpene_source === "lab_tested") s += 8;
      return s;
    };

    const runResults = (finalAnswers) => {
      const scored = ALL_PRODUCTS
        .filter(p => p.in_stock && score(p) > -999)
        .map(p => ({ ...p, _score: score(p) }))
        .sort((a,b) => b._score - a._score)
        .slice(0, 6);
      setResults(scored);
    };

    if (results) return (
      <div className="fade" style={{ maxWidth:660, margin:"0 auto", padding:"20px 16px" }}>
        <div style={{ marginBottom:16 }}>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#1a2e1a", marginBottom:4 }}>Your Matches</div>
          <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>Based on your preferences. Tap any product to see details.</div>
        </div>
        <div style={{ background:"#fffbeb", border:"1px solid #fcd34d", borderRadius:10, padding:"10px 14px", marginBottom:16, fontSize:11, fontFamily:"'DM Mono',monospace", color:"#92400e" }}>
          💡 These are starting suggestions only. Everyone responds differently to cannabis. Health Canada recommends starting low and going slow regardless of THC percentage.
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10, marginBottom:16 }}>
          {results.map(p => {
            const topT = Object.entries(p.terpenes).sort((ea,eb)=>eb[1]-ea[1])[0];
            const tInfo = topT ? TERPENES[topT[0]] : null;
            const disp = DB_DISPENSARIES.find(d=>d.id===p.dispensary_id);
            return (
              <div key={p.id} className="card card-click" style={{ padding:14, borderLeft:"3px solid " + (tInfo?.color||"#4a7c59") }}
                onClick={() => { setCbViewProd(p); setNewbieModeActive(false); setConsumerMode(true); setPage("consumer"); setConsumerTab("browse"); }}>
                <div style={{ fontSize:9, color:"#9ca3af", fontFamily:"'DM Mono',monospace", marginBottom:2 }}>{p.brand} · {p.category}</div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:14, color:"#1a2e1a", marginBottom:6 }}>{p.name}</div>
                <div style={{ fontSize:11, color:"#22c55e", fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{p.thc}% THC · {p.cultivation?.method}</div>
                <div style={{ fontSize:10, color:"#6b7280", fontFamily:"'DM Mono',monospace" }}>{disp?.name}</div>
                {topT && (
                  <div style={{ marginTop:6, display:"inline-flex", alignItems:"center", gap:5, background:(tInfo?.color) + "18", borderRadius:6, padding:"2px 8px" }}>
                    <div style={{ width:6,height:6,borderRadius:"50%",background:tInfo?.color }}/>
                    <span style={{ fontSize:9, color:tInfo?.color, fontFamily:"'DM Mono',monospace" }}>{topT[0].replace("β-","").replace("α-","").replace("(+)-","")}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button className="btn-ghost" onClick={()=>{ setStep(0); setAnswers({}); setResults(null); }}>Start Over</button>
          <button className="btn-primary" onClick={()=>setNewbieModeActive(false)}>Browse All Products →</button>
        </div>
      </div>
    );

    return (
      <div className="fade" style={{ maxWidth:520, margin:"0 auto", padding:"28px 20px" }}>
        {/* Progress dots */}
        <div style={{ display:"flex", gap:6, marginBottom:24, justifyContent:"center" }}>
          {NEWBIE_QUESTIONS.map((_,i) => (
            <div key={i} style={{ width: i===step?20:8, height:8, borderRadius:4, background:i<=step?"#4a7c59":"#e8e4de", transition:"all 0.2s" }}/>
          ))}
        </div>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ fontSize:32, marginBottom:8 }}>{q.emoji}</div>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#1a2e1a", marginBottom:q.subtitle?6:0 }}>{q.question}</div>
          {q.subtitle && <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"'DM Mono',monospace" }}>{q.subtitle}</div>}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {q.options.map(opt => (
            <button key={opt.value}
              style={{ textAlign:"left", padding:"14px 16px", background:"#f7f5f0", border:"2px solid #e8e4de", borderRadius:12, cursor:"pointer", transition:"all 0.15s" }}
              onMouseEnter={e=>e.currentTarget.style.borderColor="#4a7c59"}
              onMouseLeave={e=>e.currentTarget.style.borderColor="#e8e4de"}
              onClick={() => {
                const next = { ...answers, [q.id]: opt.value };
                setAnswers(next);
                if (step < NEWBIE_QUESTIONS.length - 1) {
                  setStep(s => s+1);
                } else {
                  runResults(next);
                }
              }}>
              <div style={{ fontSize:13, color:"#1a2e1a", fontFamily:"'DM Mono',monospace", marginBottom:3 }}>{opt.label}</div>
              <div style={{ fontSize:11, color:"#9ca3af" }}>{opt.desc}</div>
            </button>
          ))}
        </div>
        {step > 0 && (
          <button className="btn-ghost" style={{ marginTop:14, width:"100%" }} onClick={()=>setStep(s=>s-1)}>← Back</button>
        )}
      </div>
    );
  };

  // ── FEATURE 5: PASSPORT QR CODE ──────────────────────────
  const PassportQRSection = ({ profile }) => {
    const data = JSON.stringify({
      n: profile.name,
      ph: profile.phone,
      fp: buildTerpeneFingerprint(profile),
      ts: Date.now(),
    });
    const encoded = encodeURIComponent(data);
    const qrUrl = "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=" + (encoded);

    return (
      <div style={{ marginTop:16, background:"linear-gradient(135deg,#1a2e1a,#2d4a2d)", borderRadius:14, padding:"20px", textAlign:"center" }}>
        <div style={{ fontSize:9, color:"rgba(255,255,255,0.4)", fontFamily:"'DM Mono',monospace", letterSpacing:"0.1em", marginBottom:6 }}>TERPENE PASSPORT QR</div>
        <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, color:"#fff", marginBottom:4 }}>{profile.name}</div>
        <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", fontFamily:"'DM Mono',monospace", marginBottom:14 }}>Show a budtender to instantly share your terpene profile</div>
        <img src={qrUrl} alt="Passport QR" style={{ width:160, height:160, borderRadius:10, background:"#fff", padding:8 }}/>
        <div style={{ marginTop:12, display:"flex", flexDirection:"column", gap:6 }}>
          {buildTerpeneFingerprint(profile)?.topTerpenes?.slice(0,3).map(([t,v]) => {
            const info = TERPENES[t];
            return (
              <div key={t} style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"center" }}>
                <div style={{ width:7,height:7,borderRadius:"50%",background:info?.color||"#4ade80" }}/>
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.7)", fontFamily:"'DM Mono',monospace" }}>{t}</span>
                <span style={{ fontSize:11, color:"#4ade80", fontFamily:"'DM Mono',monospace" }}>{(v*100).toFixed(0)}mg/g</span>
              </div>
            );
          })}
        </div>
        <button onClick={()=>setPassportQR(false)}
          style={{ marginTop:14, padding:"7px 20px", borderRadius:8, border:"1px solid rgba(255,255,255,0.2)", background:"transparent", color:"rgba(255,255,255,0.6)", fontSize:11, fontFamily:"'DM Mono',monospace", cursor:"pointer" }}>
          Hide QR
        </button>
      </div>
    );
  };


  // ── RENDER ─────────────────────────────────────────────────
  const closeRegModal = () => { setShowRegisterModal(false); setRegisterFor(null); };
  // Grouped nav: budtender row always visible, manager row collapses/expands
  const budtenderNavItems = [["home","🏠"],["finder","🌿 Finder"],["explorer","Explorer"],["customers","Customers"],["training","Training"],["quiz","Flash Quiz"]];
  const managerNavItems   = [["dashboard","Dashboard"],["retailer","Retailer"],["compare","Compare"],["batches","Batches"],["benchmark","Benchmark"],["staffperf","Staff Perf"],["demand","Demand Gap"],["data","Export"],["certs","Certs"]];
  const lpNavItems = [["lp","LP Portal"],["explorer","Explorer"],["compare","Compare"],["batches","Batch Tracker"],["demand","Demand Gap"],["algorithm","Algorithm"]];;
  const navItems = onboardRole === "lp" ? lpNavItems : budtenderNavItems;

  // Founder mode — full takeover
  const founderScreen = (
    <div style={{ fontFamily:"'DM Mono',monospace", minHeight:"100vh" }}>
      <style>{STYLES}</style>
      <FounderDashboard />
    </div>
  );

  // ── SITE PASSWORD GATE ──────────────────────────────────────
  const SITE_PASSWORD = "iterps2025";
  const [siteUnlocked, setSiteUnlocked] = useState(() => {
    try { return localStorage.getItem("iterps_site_access") === SITE_PASSWORD; } catch (_e) { return false; }
  });
  const [sitePasswordInput, setSitePasswordInput] = useState("");
  const [sitePasswordError, setSitePasswordError] = useState(false);

  if (!siteUnlocked) {
    return (
      <div style={{ minHeight:"100vh", background:"#0d1a0d", display:"flex", alignItems:"center",
        justifyContent:"center", fontFamily:"'DM Mono',monospace" }}>
        <style>{STYLES}</style>
        <div style={{ textAlign:"center", padding:"40px 28px", maxWidth:360, width:"100%" }}>
          <div style={{ width:56, height:56, borderRadius:14, background:"rgba(74,124,89,0.2)",
            border:"1px solid rgba(74,124,89,0.4)", display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:26, margin:"0 auto 20px" }}>🌿</div>
          <div style={{ fontFamily:"'Fraunces',serif", fontSize:26, fontWeight:300,
            color:"#f0f4f0", marginBottom:6 }}>iTerps</div>
          <div style={{ fontSize:11, color:"rgba(240,244,240,0.35)", marginBottom:32,
            letterSpacing:"0.06em" }}>PRIVATE BETA</div>
          <input
            type="password"
            value={sitePasswordInput}
            onChange={e => { setSitePasswordInput(e.target.value); setSitePasswordError(false); }}
            onKeyDown={e => {
              if (e.key === "Enter") {
                if (sitePasswordInput === SITE_PASSWORD) {
                  try { localStorage.setItem("iterps_site_access", SITE_PASSWORD); } catch (_e) {}
                  setSiteUnlocked(true);
                } else {
                  setSitePasswordError(true);
                  setSitePasswordInput("");
                }
              }
            }}
            placeholder="Enter access password"
            autoFocus
            style={{ width:"100%", padding:"12px 16px", borderRadius:10,
              border:"1px solid " + (sitePasswordError ? "#f87171" : "rgba(74,124,89,0.4)"),
              background:"rgba(255,255,255,0.05)", color:"#f0f4f0",
              fontFamily:"'DM Mono',monospace", fontSize:14, marginBottom:10,
              outline:"none", boxSizing:"border-box" }}
          />
          {sitePasswordError && (
            <div style={{ fontSize:11, color:"#f87171", marginBottom:10 }}>Incorrect password</div>
          )}
          <button
            onClick={() => {
              if (sitePasswordInput === SITE_PASSWORD) {
                try { localStorage.setItem("iterps_site_access", SITE_PASSWORD); } catch (_e) {}
                setSiteUnlocked(true);
              } else {
                setSitePasswordError(true);
                setSitePasswordInput("");
              }
            }}
            style={{ width:"100%", padding:"12px 0", borderRadius:10, border:"none",
              background:"#4a7c59", color:"#fff", fontFamily:"'DM Mono',monospace",
              fontSize:13, cursor:"pointer", letterSpacing:"0.04em" }}>
            Enter →
          </button>
          <div style={{ fontSize:10, color:"rgba(240,244,240,0.2)", marginTop:20 }}>
            For authorized access only
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily:"'DM Mono',monospace", minHeight:"100vh" }}>
      <style>{STYLES}</style>

      {/* ── FOUNDER PASSPHRASE MODAL ── */}
      {showFounderUnlock && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onClick={e=>{ if(e.target===e.currentTarget){ setShowFounderUnlock(false); setFounderPassphrase(""); setFounderPassphraseError(false); }}}>
          <div style={{ background:"#0d1a0d", border:"1px solid rgba(74,124,89,0.4)", borderRadius:16, padding:32, maxWidth:380, width:"100%", textAlign:"center" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>⚗️</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:300, color:"#f0f4f0", marginBottom:6 }}>Founder Access</h2>
            <p style={{ fontSize:12, color:"rgba(240,244,240,0.4)", fontFamily:"'DM Mono',monospace", marginBottom:20, lineHeight:1.6 }}>Enter your passphrase to unlock the full platform view.</p>
            <input
              type="password"
              value={founderPassphrase}
              onChange={e=>{ setFounderPassphrase(e.target.value); setFounderPassphraseError(false); }}
              onKeyDown={e=>{ if(e.key==="Enter"){ if(founderPassphrase===FOUNDER_PASSPHRASE){ setFounderMode(true); setShowFounderUnlock(false); setFounderPassphrase(""); } else { setFounderPassphraseError(true); }}}}
              placeholder="Passphrase"
              style={{ width:"100%", padding:"11px 14px", border:"1px solid " + (founderPassphraseError?"#f87171":"rgba(74,124,89,0.4)"), borderRadius:10, fontSize:13, fontFamily:"'DM Mono',monospace", background:"rgba(255,255,255,0.05)", color:"#f0f4f0", boxSizing:"border-box", outline:"none", marginBottom:8, textAlign:"center", letterSpacing:"0.15em" }}
              autoFocus
            />
            {founderPassphraseError && <div style={{ fontSize:11, color:"#f87171", fontFamily:"'DM Mono',monospace", marginBottom:10 }}>Incorrect passphrase.</div>}
            <button onClick={()=>{ if(founderPassphrase===FOUNDER_PASSPHRASE){ setFounderMode(true); setShowFounderUnlock(false); setFounderPassphrase(""); setFounderPassphraseError(false); } else { setFounderPassphraseError(true); }}}
              style={{ width:"100%", padding:"11px 0", borderRadius:10, border:"none", background:"#1a2e1a", color:"#f0f4f0", fontSize:13, fontFamily:"'DM Mono',monospace", cursor:"pointer", marginBottom:10 }}>
              Unlock →
            </button>
            <button onClick={()=>{ setShowFounderUnlock(false); setFounderPassphrase(""); setFounderPassphraseError(false); }}
              style={{ background:"none", border:"none", color:"rgba(240,244,240,0.3)", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace" }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {!ageOk ? ageGateScreen : !showAbout ? aboutScreen : founderMode ? founderScreen : (kioskMode && kioskWelcome) ? <KioskWelcomeScreen /> : !onboardDone ? onboardScreen : (

      <>
      <CompoundModal />
      <GlobalSearchModal />
      <CultivationModal />
      <QRModal />
      <SimilarToThisModal />
      <ToastContainer />
      <LogForCustomerModal />
      <FeedbackModal />
      <SendToPhoneModal />
      <DemoTour />
      <TrainingModal />
      <StockAlertModal />
      <DispensaryOnboardModal />

      {/* ── CONSUMER NAV ── */}
      {consumerMode ? (
        <nav style={{ background:"#1a2e1a", padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:100 }}>
          <button onClick={()=>{ if(logoClickTimer.current){ clearTimeout(logoClickTimer.current); logoClickTimer.current=null; setShowFounderUnlock(true); } else { logoClickTimer.current=setTimeout(()=>{ logoClickTimer.current=null; setPage("home"); setConsumerTab("find"); },280); }}} style={{ display:"flex", alignItems:"center", gap:8, background:"none", border:"none", cursor:"pointer", flexShrink:0 }}>
            <div style={{ width:28, height:28, borderRadius:7, background:"rgba(255,255,255,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>⚗️</div>
            <span style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#fff" }}>iTerps</span>
            {kioskMode && kioskDispensary
              ? <span style={{ fontSize:9, background:"#f59e0b", color:"#fff", padding:"2px 7px", borderRadius:10, fontFamily:"'DM Mono',monospace" }}>📍 {DB_DISPENSARIES.find(d=>d.id===kioskDispensary)?.name||"Kiosk"}</span>
              : <span style={{ fontSize:9, background:"#4a7c59", color:"#fff", padding:"2px 7px", borderRadius:10, fontFamily:"'DM Mono',monospace" }}>Consumer</span>
            }
          </button>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <button onClick={()=>{ setConsumerMode(true); setPage("consumer"); setDemoRole("consumer"); setDemoActive(true); setDemoStep(0); setConsumerTab("find"); }}
              style={{ display:"flex", alignItems:"center", gap:4, padding:"5px 10px", borderRadius:8,
                border:"1px solid rgba(244,114,182,0.4)", background:"rgba(244,114,182,0.12)", cursor:"pointer",
                fontSize:10, fontFamily:"'DM Mono',monospace", color:"#f472b6", flexShrink:0, fontWeight:600 }}>
              ▶ Tour
            </button>
            <button onClick={()=>{ setShowGlobalSearch(true); setGlobalSearch(""); }}
              style={{ display:"flex", alignItems:"center", justifyContent:"center", width:30, height:30, borderRadius:8, border:"1px solid rgba(255,255,255,0.2)", background:"transparent", cursor:"pointer", fontSize:13, flexShrink:0, color:"rgba(255,255,255,0.7)" }}>
              🔍
            </button>
            {[["find","🌿 Match"],["browse","Browse"],["profile","My Selections"],["newbie","New to Cannabis?"]].map(([tab, label]) => (
              <button key={tab} onClick={()=>{ setConsumerTab(tab); setPage("consumer"); }}
                style={{ padding:"6px 12px", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontFamily:"'DM Mono',monospace",
                  background: consumerTab===tab ? "rgba(255,255,255,0.2)" : "transparent",
                  color: consumerTab===tab ? "#fff" : "rgba(255,255,255,0.55)",
                  fontWeight: consumerTab===tab ? 600 : 400, transition:"all 0.15s" }}>
                {label}
              </button>
            ))}
          </div>
            {kioskMode
              ? <button onClick={()=>{ const pin = prompt("Admin PIN to exit kiosk:"); if(pin===kioskPin){ setKioskMode(false); setKioskDispensary(""); setKioskPin(""); setOnboardDone(false); setOnboardRole(""); setOnboardStep(0); setConsumerMode(false); }}}
                  style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(255,165,0,0.4)", background:"rgba(255,165,0,0.1)", cursor:"pointer", fontSize:10, fontFamily:"'DM Mono',monospace", color:"rgba(255,165,0,0.9)", marginLeft:4 }}>🔒 Admin</button>
              : <button onClick={()=>{ setOnboardDone(false); setOnboardRole(""); setOnboardStep(0); setConsumerMode(false); setDemoActive(false); setDemoStep(0); }} style={{ padding:"5px 10px", borderRadius:8, border:"1px solid rgba(255,255,255,0.25)", background:"rgba(255,255,255,0.06)", cursor:"pointer", fontSize:10, fontFamily:"'DM Mono',monospace", color:"rgba(255,255,255,0.7)", marginLeft:4 }}>← Switch role</button>
            }
        </nav>
      ) : (
        /* ── STAFF NAV ── */
        <nav style={{ background:"#fff", borderBottom:"1px solid #e8e4de", padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:100 }}>
          <button onClick={()=>{ if(logoClickTimer.current){ clearTimeout(logoClickTimer.current); logoClickTimer.current=null; setShowFounderUnlock(true); } else { logoClickTimer.current=setTimeout(()=>{ logoClickTimer.current=null; setOnboardDone(false); setOnboardRole(""); setOnboardStep(0); setDemoActive(false); setDemoStep(0); },280); }}} title="Click: sign out | Double-click: founder access"
            style={{ display:"flex", alignItems:"center", gap:8, background:"none", border:"none", cursor:"pointer", flexShrink:0 }}>
            <div style={{ width:28, height:28, borderRadius:7, background:"#1a2e1a", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>⚗️</div>
            <span style={{ fontFamily:"'Fraunces',serif", fontSize:17, fontWeight:400, color:"#1a2e1a" }}>iTerps</span>
            {onboardRole === "lp" && <span style={{ fontSize:9, background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe", padding:"2px 7px", borderRadius:10, fontFamily:"'DM Mono',monospace" }}>LP</span>}
            {onboardRole === "staff" && <span style={{ fontSize:9, background:"#f0fdf4", color:"#166534", border:"1px solid #bbf7d0", padding:"2px 7px", borderRadius:10, fontFamily:"'DM Mono',monospace" }}>Staff</span>}
          </button>
          <div style={{ display:"flex", gap:4, alignItems:"center" }}>

            <button onClick={()=>{ setDemoRole("staff"); setDemoActive(true); setDemoStep(0); setPage("home"); setOnboardDone(true); }}
              title="Start dispensary demo tour"
              style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 12px", borderRadius:8,
                border:"1px solid #4a7c59", background:"#f0fdf4", cursor:"pointer",
                fontSize:11, fontFamily:"'DM Mono',monospace", color:"#166534", flexShrink:0,
                fontWeight:600, transition:"all 0.15s" }}
              onMouseEnter={e=>{ e.currentTarget.style.background="#4a7c59"; e.currentTarget.style.color="#fff"; }}
              onMouseLeave={e=>{ e.currentTarget.style.background="#f0fdf4"; e.currentTarget.style.color="#166534"; }}>
              ▶ Demo
            </button>
            <button onClick={()=>{ setShowGlobalSearch(true); setGlobalSearch(""); }}
              title="Search all products (⌘K)"
              style={{ display:"flex", alignItems:"center", justifyContent:"center", width:32, height:32, borderRadius:8, border:"1px solid #e8e4de", background:"transparent", cursor:"pointer", fontSize:14, flexShrink:0, color:"#6b7280" }}>
              🔍
            </button>
            {/* ── Grouped nav: budtender row + manager row ── */}
            <div style={{ display:"flex", flexDirection:"column", gap:0, flex:1, overflow:"hidden" }}>
              {/* Row 1: Budtender tools (always visible) */}
              <div style={{ display:"flex", gap:2, alignItems:"center", overflowX:"auto", maxWidth:"calc(100vw - 200px)", scrollbarWidth:"none" }}>
                {(onboardRole === "lp" ? lpNavItems : budtenderNavItems).map(([p,l])=>(
                  <button key={p} className={"nav-btn " + (page===p?"active":"")} onClick={()=>setPage(p)} style={{ whiteSpace:"nowrap", fontSize:11, padding:"6px 10px" }}>{l}</button>
                ))}
                {onboardRole !== "lp" && (
                  <button onClick={()=>setManagerMode(m=>!m)}
                    style={{ whiteSpace:"nowrap", fontSize:10, padding:"5px 9px", borderRadius:8, border:"1px solid " + (managerMode?"#86efac":"#e8e4de"), background:managerMode?"#f0fdf4":"transparent", color:managerMode?"#15803d":"#9ca3af", cursor:"pointer", fontFamily:"'DM Mono',monospace", flexShrink:0, fontWeight:managerMode?600:400, marginLeft:2 }}>
                    {managerMode ? "▲ Manager" : "▼ Manager"}
                  </button>
                )}
              </div>
              {/* Row 2: Manager tools (only in manager mode) */}
              {managerMode && onboardRole !== "lp" && (
                <div style={{ display:"flex", gap:2, alignItems:"center", overflowX:"auto", maxWidth:"calc(100vw - 200px)", scrollbarWidth:"none", paddingTop:2, borderTop:"1px solid #f0fdf4" }}>
                  {managerNavItems.map(([p,l])=>(
                    <button key={p} className={"nav-btn " + (page===p?"active":"")} onClick={()=>setPage(p)} style={{ whiteSpace:"nowrap", fontSize:10, padding:"5px 8px", color:page===p?"#15803d":"#6b7280" }}>{l}</button>
                  ))}
                </div>
              )}
            </div>
          
            <button onClick={()=>{ setOnboardDone(false); setOnboardRole(""); setOnboardStep(0); setDemoActive(false); setDemoStep(0); }} style={{ padding:"5px 10px", borderRadius:8, border:"1px solid #e8e4de", background:"transparent", cursor:"pointer", fontSize:10, fontFamily:"'DM Mono',monospace", color:"#9ca3af", flexShrink:0, marginLeft:4 }}>Sign out</button>
            {onboardRole === "lp" && activeLp && (
              <button onClick={()=>{ setActiveLp(""); setOnboardDone(false); setOnboardStep(2); }}
                style={{ padding:"5px 10px", borderRadius:8, border:"1px solid #bfdbfe", background:"#eff6ff", cursor:"pointer", fontSize:10, fontFamily:"'DM Mono',monospace", color:"#1d4ed8", flexShrink:0, marginLeft:4, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                title="Click to switch LP">
                🏭 {activeLp.split(" ").slice(0,2).join(" ")}
              </button>
            )}
            {activeDispensary && onboardRole === "staff" && (
              <button onClick={()=>{ setActiveDispensary(""); setRetSelDisp(""); setOnboardDone(false); setOnboardStep(2); }}
                style={{ padding:"5px 10px", borderRadius:8, border:"1px solid #e8e4de", background:"#f9fafb", cursor:"pointer", fontSize:10, fontFamily:"'DM Mono',monospace", color:"#374151", flexShrink:0, marginLeft:4, maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                title="Click to switch store">
                📍 {DB_DISPENSARIES.find(d=>d.id===activeDispensary)?.name||"Store"}
              </button>
            )}
          </div>
        </nav>
      )}

      <main style={{ flex:1 }}>
        {consumerMode ? (
          <>
            {consumerTab === "find" && <ConsumerMode />}
            {consumerTab === "browse"  && <ConsumerBrowse />}
            {consumerTab === "profile" && <ConsumerProfile />}
            {consumerTab === "newbie"  && <div className="fade" style={{minHeight:"60vh",padding:"20px 16px",maxWidth:580,margin:"0 auto"}}>
              <button onClick={()=>{ setConsumerTab("find"); setPage("consumer"); }} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:16}}>← Back</button>
              <NewbieFinder /></div>}
            {page === "product" && selectedProduct && <ProductPage p={selectedProduct} />}
          </>
        ) : (
          <>
            {dispDetailId ? <DispensaryPage dispId={dispDetailId} /> : (
            <>
            {/* SHARED */}
            {page==="home"     && <HomePage />}
            {page==="explorer" && <ExplorerPage />}
            {page==="product"  && selectedProduct && <ProductPage p={selectedProduct} />}
            {page==="compare"  && <ComparePage />}
            {page==="batches"  && <BatchTrackerPage />}
            {page==="algorithm"&& <AlgorithmPage />}

            {/* STAFF-ONLY */}
            {onboardRole !== "lp" && page==="finder"    && <FinderPage />}
            {onboardRole !== "lp" && page==="retailer"  && <RetailerPage />}
            {onboardRole !== "lp" && page==="customers" && <CustomersPage />}
            {onboardRole !== "lp" && page==="training"  && <TrainingPage />}
            {onboardRole !== "lp" && page==="quiz"      && <div className="fade" style={{maxWidth:660,margin:"0 auto",padding:"20px 16px"}}>
              <button onClick={()=>setPage("training")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:16}}>← Training</button>
              <TerpeneFlashQuiz /></div>}
            {onboardRole !== "lp" && page==="benchmark" && <BenchmarkingSection />}
            {onboardRole !== "lp" && page==="staffperf" && <StaffPerformancePage />}
            {onboardRole !== "lp" && page==="dashboard" && <DashboardPage />}
            {onboardRole !== "lp" && page==="data"      && <DataExportPage />}
            {onboardRole !== "lp" && page==="certs"     && <CertificationPage />}
            {onboardRole !== "lp" && page==="demand"    && <DemandGapPage />}
            {onboardRole !== "lp" && page==="newbie"    && <div className="fade" style={{minHeight:"60vh",padding:"20px 16px",maxWidth:580,margin:"0 auto"}}>
              <button onClick={()=>setPage("finder")} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",color:"#6b7280",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:12,marginBottom:16}}>← Back</button>
              <NewbieFinder /></div>}

            {/* LP PORTAL - both roles can access */}
            {page==="lp" && <LPPortal />}
            {onboardRole === "lp" && page==="demand" && <DemandGapPage />}
            </>
            )}
          </>
        )}
      </main>

      {/* ── MOBILE BOTTOM NAV (staff + LP only, not consumer) ── */}
      {!consumerMode && (
        <nav style={{ display:"none", position:"fixed", bottom:0, left:0, right:0, background:"#fff", borderTop:"1px solid #e8e4de", zIndex:200, padding:"6px 0 env(safe-area-inset-bottom,6px)" }}
          className="mobile-bottom-nav">
          {(onboardRole === "lp" ? [
            ["lp","🏭","LP Portal"],
            ["explorer","🔍","Explorer"],
            ["compare","⚖️","Compare"],
            ["batches","📊","Batches"],
            ["demand","💡","Demand"],
          ] : [
            ["finder","🌿","Finder"],
            ["customers","👤","Customers"],
            ["retailer","📋","Inventory"],
            ["explorer","🔍","Explorer"],
            ["training","🎓","Training"],
          ]).map(([dest, icon, label]) => (
            <button key={dest} onClick={()=>setPage(dest)}
              style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2, padding:"6px 4px", border:"none", background:"transparent", cursor:"pointer",
                color: page===dest ? "#4a7c59" : "#9ca3af", transition:"color 0.15s" }}>
              <span style={{ fontSize:18, lineHeight:1 }}>{icon}</span>
              <span style={{ fontSize:9, fontFamily:"'DM Mono',monospace", fontWeight: page===dest ? 600 : 400 }}>{label}</span>
            </button>
          ))}
        </nav>
      )}

      <footer style={{ background:"#1a2e1a", color:"rgba(240,244,240,0.5)", fontSize:10, fontFamily:"'DM Mono',monospace", padding:"20px 24px", textAlign:"center", lineHeight:1.8 }}>
        <p>iTerps is an educational platform providing factual information about cannabis products and their chemical composition.</p>
        <p>It does not promote or encourage cannabis consumption. Cannabis should only be purchased from licensed retailers in accordance with Canadian law.</p>
        <p style={{ marginTop:6, color:"rgba(240,244,240,0.25)" }}>Cannabis Act (S.C. 2018, c. 16) · BC Cannabis Retail Framework · Health Canada Compliant · IUPAC Nomenclature Standard · © 2025 iTerps</p>
      </footer>
      </>
      )}
    </div>
  );
}
